import React, { createContext, useContext, useState, useEffect } from "react";

export type Language = "en" | "fr" | "es" | "de" | "it" | "zh" | "ja" | "ar";

export const translations: Record<Language, Record<string, string>> = {
  en: {
    "seo.title": "StoryHeroes – Personalized Storybooks for Kids",
    "seo.description": "Turn your child into the hero of a magical storybook. Upload a photo, pick an adventure theme, and get a beautifully illustrated personalized PDF in minutes.",
    // Hero Section
    "hero.badge": "✨ Personalized Storybooks",
    "hero.title": "Turn Your Child Into a Storybook Hero",
    "hero.title.create": "Create",
    "hero.title.magical": "Magical Stories",
    "hero.title.starring": "Starring Your Child",
    "hero.subtitle": "Upload a photo, choose an adventure, and receive a stunning illustrated PDF or hardcover book in minutes.",
    "hero.cta": "Create Your Story",
    "hero.freePreview": "Create & preview your book for free",
    "hero.privacyStamp": "🔒 Photos deleted after generation",
    "hero.themes": "50+ Themes",
    "hero.aiArt": "Custom Illustrations",
    "hero.pdfDownload": "Instant PDF",
    "hero.features.personalized": "Personalized",
    "hero.features.ai": "Custom Art",
    "hero.features.instant": "Instant PDF",
    // Transform Section
    "transform.badge": "✨ See the Magic",
    "transform.title": "From Photo to Storybook Character",
    "transform.subtitle": "We transform your child's photo into a beautiful illustrated character",
    "transform.pixar": "Pixar Style",
    "transform.watercolor": "Watercolor Style",
    "transform.anime": "Anime Style",
    // Showcase Section
    "showcase.title": "Beautiful Books, Digital & Printed",
    "showcase.subtitle": "Get your personalized storybook as a digital PDF or a beautifully printed hardcover",
    "showcase.pdfTitle": "Digital PDF",
    "showcase.pdfDesc": "Instant download, read on any device",
    "showcase.printTitle": "Printed Book",
    "showcase.printDesc": "Premium quality hardcover delivered to your door",
    "showcase.pages": "32 pages",
    "showcase.popular": "Most Popular",
    // Reviews Section
    "reviews.badge": "⭐ Loved by Parents",
    "reviews.title": "What Parents Are Saying",
    "reviews.subtitle": "Join thousands of happy families who created magical stories",
    "reviews.child": "Child age:",
    "reviews.1.text": "My daughter absolutely loved seeing herself as the hero! The illustrations are stunning and the story was perfect for her age.",
    "reviews.1.childAge": "5 years",
    "reviews.2.text": "An incredible gift idea! My son reads his story every night before bed. The quality of the PDF is amazing.",
    "reviews.2.childAge": "4 years",
    "reviews.3.text": "I was blown away by the quality. It perfectly captured my child's likeness in the illustrations. Highly recommend!",
    "reviews.3.childAge": "6 years",
    "reviews.4.text": "We've ordered three books now, one for each of our kids. They fight over who gets to read theirs first!",
    "reviews.4.childAge": "3, 5, 7 years",
    "reviews.5.text": "The watercolor style is absolutely gorgeous. It looks like a professional children's book illustrator made it.",
    "reviews.5.childAge": "4 years",
    "reviews.6.text": "Best birthday gift ever! My son's face when he saw himself in the story was priceless.",
    "reviews.6.childAge": "6 years",
    // Trust Section
    "trust.gift.title": "Perfect Gift",
    "trust.gift.desc": "A unique, personalized gift that children treasure for years",
    "trust.ai.title": "Stunning Art",
    "trust.ai.desc": "Our technology creates stunning illustrations in your child's likeness",
    "trust.love.title": "Made with Love",
    "trust.love.desc": "Every story is crafted with care to inspire imagination and joy",
    // CTA Section
    "cta.title": "Ready to Create Magic?",
    "cta.subtitle": "Turn your child into the hero of their very own storybook adventure",
    "cta.button": "Start Creating Now",
    "cta.note": "No account needed • Ready in 5 minutes",
    
    // Language Selector
    "lang.title": "Choose Your Language",
    "lang.subtitle": "Select your preferred language to continue",
    
    // Navigation
    "nav.backToHome": "Back to Home",
    
    // Footer
    "footer.copyright": "© 2024 StoryHeroes. All rights reserved.",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "Terms of Service",
    "footer.contact": "Contact",
    "footer.trackOrder": "Track Order",
    
    // Privacy Page
    "privacy.title": "Privacy Policy",
    "privacy.updated": "Last updated: January 2024",
    "privacy.section1.title": "1. Information We Collect",
    "privacy.section1.intro": "We collect the following information when you use our service:",
    "privacy.section1.item1": "Photos you upload to create personalized storybooks",
    "privacy.section1.item2": "Child's name and age for story personalization",
    "privacy.section1.item3": "Email address for order confirmation and delivery",
    "privacy.section1.item4": "Payment information (processed securely through Stripe)",
    "privacy.section2.title": "2. How We Use Your Information",
    "privacy.section2.intro": "We use your information to:",
    "privacy.section2.item1": "Generate personalized storybooks",
    "privacy.section2.item2": "Process payments and deliver your PDF storybook",
    "privacy.section2.item3": "Send order confirmations and updates",
    "privacy.section2.item4": "Improve our service and user experience",
    "privacy.section2.item5": "Comply with legal obligations",
    "privacy.section3.title": "3. Data Storage and Security",
    "privacy.section3.text": "We take data security seriously. All uploaded photos and personal information are encrypted and stored securely. We use industry-standard security measures to protect your data from unauthorized access.",
    "privacy.section4.title": "4. Third-Party Services",
    "privacy.section4.text": "We use trusted third-party services including Stripe for payment processing and specialized services for story generation. These services have their own privacy policies and security measures.",
    "privacy.section5.title": "5. Data Retention",
    "privacy.section5.text": "We retain your data only as long as necessary to provide our services and comply with legal obligations. You can request deletion of your data at any time by contacting us.",
    "privacy.section6.title": "6. Children's Privacy",
    "privacy.section6.text": "We are committed to protecting children's privacy. We do not knowingly collect personal information from children under 13 without parental consent. Parents have full control over their child's information.",
    "privacy.section7.title": "7. Your Rights",
    "privacy.section7.intro": "You have the right to:",
    "privacy.section7.item1": "Access your personal data",
    "privacy.section7.item2": "Request correction of inaccurate data",
    "privacy.section7.item3": "Request deletion of your data",
    "privacy.section7.item4": "Opt-out of marketing communications",
    "privacy.section8.title": "8. Contact Us",
    "privacy.section8.text": "If you have any questions about this Privacy Policy, please contact us through our contact page.",
    
    // Terms Page
    "terms.title": "Terms of Service",
    "terms.updated": "Last updated: January 2024",
    "terms.section1.title": "1. Acceptance of Terms",
    "terms.section1.text": "By using StoryHeroes, you agree to these Terms of Service. If you do not agree, please do not use our service.",
    "terms.section2.title": "2. Service Description",
    "terms.section2.text": "StoryHeroes provides personalized storybooks for children. We create custom illustrated stories based on photos and information you provide.",
    "terms.section3.title": "3. User Responsibilities",
    "terms.section3.intro": "You agree to:",
    "terms.section3.item1": "Provide accurate information",
    "terms.section3.item2": "Only upload photos you have rights to use",
    "terms.section3.item3": "Not use the service for illegal purposes",
    "terms.section3.item4": "Not upload inappropriate or offensive content",
    "terms.section3.item5": "Respect intellectual property rights",
    "terms.section4.title": "4. Payment and Refunds",
    "terms.section4.text": "All payments are processed securely through Stripe. Due to the personalized nature of our service, refunds are only available in case of technical issues preventing delivery of your storybook.",
    "terms.section5.title": "5. Intellectual Property",
    "terms.section5.intro": "Regarding intellectual property:",
    "terms.section5.item1": "You retain rights to photos you upload",
    "terms.section5.item2": "Generated stories and illustrations are for personal use only",
    "terms.section5.item3": "You may not resell or redistribute generated content",
    "terms.section5.item4": "StoryHeroes retains rights to the service and technology",
    "terms.section5.item5": "You grant us license to use uploaded content to generate your storybook",
    "terms.section6.title": "6. Limitation of Liability",
    "terms.section6.text": "StoryHeroes is provided 'as is' without warranties. We are not liable for any indirect, incidental, or consequential damages arising from use of our service.",
    "terms.section7.title": "7. Content Guidelines",
    "terms.section7.text": "We reserve the right to refuse service or remove content that violates our guidelines, including inappropriate, offensive, or copyrighted material.",
    "terms.section8.title": "8. Changes to Terms",
    "terms.section8.text": "We may update these Terms of Service. Continued use of the service after changes constitutes acceptance of new terms.",
    "terms.section9.title": "9. Termination",
    "terms.section9.text": "We reserve the right to terminate or suspend access to our service for violations of these terms or for any other reason at our discretion.",
    "terms.section10.title": "10. Contact",
    "terms.section10.text": "For questions about these Terms of Service, please contact us through our contact page.",
    
    // Wizard Steps
    "step.addCharacters": "Add Characters",
    "step.chooseStory": "Choose Story",
    "step.pickTitle": "Pick Title",
    "step.chooseStyle": "Choose Style",
    "step.review": "Review",
    "step.storyOutline": "Story Outline",
    "step.previewPdf": "Preview PDF",
    
    // Photo Upload
    "photo.title": "Add Your Characters",
    "photo.subtitle": "Upload a photo and enter the details for each character in your story.",
    "photo.privacyNotice": "🔒 Your photos are used only for illustration and are deleted after generation.",
    "photo.addPhoto": "Add Photo",
    "photo.dragDrop": "or drag & drop",
    "photo.dropHere": "Drop here!",
    "photo.browse": "Browse",
    "photo.camera": "Camera",
    "photo.upload": "Upload",
    "photo.name": "Name",
    "photo.namePlaceholder": "Enter name",
    "photo.age": "Age",
    "photo.agePlaceholder": "Enter age",
    "photo.gender": "Character Type",
    "photo.boy": "Boy",
    "photo.girl": "Girl",
    "photo.pet": "Pet",
    "photo.addChild": "Add Another Character",
    "photo.savedKids": "Your Saved Characters",
    
    // Family Import
    "familyImport.button": "Import from Group Photo",
    
    // Style Selection
    "style.choose": "Choose Your Art Style",
    "style.subtitleSelected": "See how your {{count}} character{{plural}} look in this style",
    "style.subtitleUnselected": "Pick an illustration style for your storybook",
    "style.yourCharactersIn": "Your characters in {{style}}",
    "style.creatingName": "Creating {{name}}...",
    "style.altCharacterInStyle": "{{name}} in {{style}} style",
    "style.failedShort": "Failed",
    "style.retry": "Retry",
    "style.hoverHint": "Hover over images to see them larger",
    "style.preserveHint": "Character descriptions will be preserved for consistency",
    "style.tryAgain": "Try Again",
    "style.continueWith": "Continue with {{style}}",
    "style.switchHint": "You can switch styles at any time before continuing",
    
    // Title Selection
    "title.choose": "Choose Your Title",
    "title.subtitle": "Pick the perfect title for your personalized storybook",
    "title.creating": "Creating magical titles...",
    "title.selected": "Selected",
    "title.regenerate": "Regenerate Titles",
    "title.usingSuggested": "Using suggested titles",
    "title.pickFavorite": "Pick your favorite from these suggestions!",
    
    // Story Themes
    "themes.title": "Choose a Theme for {{hero}}",
    "themes.subtitle": "Pick an adventure theme for your {{count}} {{characterWord}}. Each theme creates unique storie{{plural}}.",
    "themes.changeTheme": "Change Theme",
    "themes.whatsStoryAbout": "What should {{hero}}'s story be about?",
    "themes.yourIdeas": "Your Story Ideas",
    "themes.ideaHelperText": "Describe the adventure you'd like. Be as creative as you want!",
    "themes.ideaPlaceholder": "e.g. A brave knight who befriends a dragon and together they save a magical kingdom...",
    "themes.ideaHint": "Great start! Click below to get enhanced story ideas.",
    "themes.generateRequired": "Write your own idea or click below to get inspired!",
    "themes.generatingIdeas": "Generating ideas...",
    "themes.completeMyIdeas": "Complete My Ideas",
    "themes.inspireMe": "Inspire Me!",
    "themes.chooseIdea": "Choose a Story Idea",
    "themes.usingPreMade": "Using pre-made ideas",
    "themes.greatIdeas": "Here are some great story ideas!",
    
    // Outline
    "outline.title": "Story Outline",
    "outline.subtitle": "Review and customize the pages of your storybook",
    "outline.generating": "Creating your story outline...",
    "outline.titlePage": "Title Page",
    "outline.ending": "The End",
    "outline.page": "Page",
    "outline.happy": "Happy with the outline?",
    "outline.happyDesc": "You can edit any page text above. When you're ready, click next to generate illustrations!",
    "outline.sceneDescription": "Scene Description",
    "outline.whatYouWillSee": "What you'll see on this page",
    // Preview
    "preview.title": "Review Your Story",
    "preview.subtitle": "Everything looks good? Let's create your illustrated storybook!",
    "preview.storyTheme": "Story Theme",
    "preview.yourHeroes": "Your Heroes",
    "preview.chosenStyle": "Art Style",
    "preview.ready": "Everything looks great! ✨",
    "preview.readyDesc": "We'll create a magical story starring {{names}} in {{style}} style.",
    
    // PDF Preview
    "pdfPreview.bookReady": "Your book is ready!",
    "pdfPreview.finishingIllustrations": "Finishing illustrations...",
    "pdfPreview.reviewMessage": "Review your storybook and download when ready.",
    "pdfPreview.preparing": "Preparing...",
    "pdfPreview.happyWithBook": "I'm Happy With My Book!",
    "pdfPreview.paymentComplete": "Payment Complete!",
    "pdfPreview.generatingPdf": "Generating PDF...",
    "pdfPreview.downloadPdf": "Download PDF",
    "pdfPreview.loveIt": "Love it? Get your copy!",
    "pdfPreview.processing": "Processing...",
    "pdfPreview.digitalPdf": "Digital PDF",
    "pdfPreview.printedBook": "Printed Book",
    "pdfPreview.pdfDownloadShortly": "Your PDF will download shortly.",
    "pdfPreview.tryAgainMoment": "Please try again in a moment.",
    "pdfPreview.paymentSuccess": "Payment Successful!",
    "pdfPreview.pdfReady": "\"{{title}}\" is ready to download",
    "pdfPreview.readyNextBook": "Ready for your next book!",
    "pdfPreview.canCreateNew": "You can create a new storybook anytime.",
    
    // Navigation
    "nav.back": "Back",
    "nav.next": "Next",
    "nav.generateIllustrations": "Generate Illustrations",
    
    // Download
    "download.pdf": "Download PDF",
    
    // Complete
    "complete.title": "Your Book is Ready! 🎉",
    "complete.subtitle": "A magical storybook starring {{names}} has been created!",
    "complete.download": "Download PDF",
    "complete.startOver": "Create Another Book",
    
    // Generating
    "generating.preparingStory": "Preparing your story...",
    "generating.creatingIllustrations": "Creating illustrations...",
    "generating.creatingPreviewIllustrations": "Creating preview illustrations...",
    "generating.creatingCover": "Creating cover...",
    "generating.coverCreated": "Cover created!",
    "generating.backCoverCreated": "Back cover created!",
    
    // Finalize
    "finalize.paymentConfirmed": "Payment confirmed!",
    "finalize.generatingStory": "Generating your full story...",
    "finalize.creatingIllustrations": "Creating illustrations...",
    "finalize.addingFinishingTouches": "Adding finishing touches...",
    "finalize.creatingBackCover": "Creating back cover...",
    "finalize.bookReadyReview": "Your book is ready to review!",
    "finalize.preparingPdf": "Preparing your PDF...",
    
    // Resume from email
    "resume.loadingBook": "Loading your book...",
    "resume.stillGenerating": "Still creating your book!",
    "resume.stillGeneratingDesc": "Your illustrations are still being generated in the background. You'll receive an email when it's ready.",
    "resume.checkBackLater": "You can close this page and check back later.",
    // Illustration
    "illustration.noRedosLeft": "No redos left",
    "illustration.limitReached": "You've used all your illustration redos. Purchase more to continue.",
    
    // Toast Messages
    "toast.paymentCancelled": "Payment Cancelled",
    "toast.tryAgainReady": "Your book is ready when you are. Try again anytime!",
    "toast.redoCreditsAdded": "Redo Credits Added!",
    "toast.threeMoreRedos": "You now have 3 more illustration redos.",
    "toast.paymentSuccessful": "Payment Successful!",
    "toast.pdfReadyDownload": "Your PDF is ready to download.",
    "toast.downloadStarted": "Download Started",
    "toast.couldntDownload": "Couldn't Download",
    "toast.failedGenerate": "Failed to generate. Please try again.",
    "toast.pleaseRetry": "Please try again.",
    "toast.storyCreated": "Story Created!",
    "toast.previewStory": "Preview your storybook below.",
    "toast.fillIllustrations": "Generating remaining illustrations...",
    "toast.generationFailed": "Generation Failed",
    "toast.generationTimeout": "Generation timed out. Please try again.",
    "toast.paymentFailed": "Payment Failed",
    "toast.somethingWrong": "Something Went Wrong",
    "toast.createBookAgain": "Please try creating your book again.",
    "toast.errorGenerating": "There was an error generating your book.",
    "toast.printOrderConfirmed": "Print Order Confirmed!",
    "toast.bookPrintedShipped": "Your book will be printed and shipped soon.",
    "toast.paymentSuccessContact": "Payment was successful. Contact support if needed.",
    "toast.preparingPrintFiles": "Preparing Print Files",
    "toast.readyForPrinting": "Getting your book ready for printing...",
    "toast.orderFailed": "Order Failed",
    "toast.paymentError": "Payment Error",
    "toast.tryAgainLater": "Please try again later.",
    "toast.coverRegenerated": "Cover Regenerated!",
    "toast.newCoverReady": "Your new cover is ready.",
    "toast.pageRegenerated": "Page {{page}} Regenerated!",
    "toast.feedbackApplied": "Your feedback has been applied.",
    "toast.freshIllustration": "A fresh illustration has been created.",
    "toast.regenerationFailed": "Regeneration Failed",
    "toast.couldNotRegenerate": "Could not regenerate the illustration. Please try again.",
    "toast.backCoverFailed": "Back Cover Failed",
    "toast.backCoverFrontUsed": "The front cover will be used instead.",
    "toast.backCoverReady": "Back Cover Ready!",
    "toast.backCoverGenerated": "Your back cover has been generated.",
    "toast.frontCoverRequired": "Front Cover Required",
    "toast.frontCoverFirst": "Generate a front cover first.",
    "toast.backCoverRegenerated": "Back Cover Regenerated!",
    "toast.backCoverFeedbackApplied": "Your feedback has been applied to the back cover.",
    "toast.freshBackCover": "A fresh back cover has been created.",
    "toast.backCoverRegenerateFailed": "Could not regenerate the back cover.",
    "toast.allIllustrationsComplete": "All Illustrations Complete!",
    "toast.storybookReady": "Your storybook is fully illustrated.",
    "toast.someIllustrationsIncomplete": "Some Illustrations Incomplete",
    "toast.pagesMissing": "{{count}} pages are still missing illustrations.",
    "toast.bookNotComplete": "Book Not Complete",
    "toast.generateFirst": "Please generate your story first.",
    "toast.preparingBook": "Preparing Your Book",
    "toast.uploadingPrint": "Uploading print files...",
    "toast.missingInfoCheckout": "Missing information for checkout.",
    "toast.failedCheckout": "Failed to create checkout. Please try again.",
    "toast.checkEmail": "Check your email for the PDF link too!",
    "toast.couldntSendPdf": "Couldn't send PDF. Try downloading instead.",
    "toast.emailSent": "Email Sent!",
    "toast.bookSentTo": "Your book has been sent to {{email}}.",
    "toast.generatingPdf": "Generating PDF...",
    
    // Paywall Popup
    "paywall.title": "Finalize {{names}}'s Book!",
    "paywall.subtitle": "Your story preview is ready! Get the full illustrated PDF to keep forever.",
    "paywall.digitalPdf": "Digital PDF — $9.99",
    "paywall.price": "$9.99",
    "paywall.benefit1": "Full illustrated storybook (20+ pages)",
    "paywall.benefit2": "High-quality PDF you can print at home",
    "paywall.benefit3": "Instant download after purchase",
    "paywall.processing": "Processing...",
    "paywall.getBook": "Get My Book — $9.99",
    "paywall.stopTitle": "Leave your story?",
    "paywall.stopDesc": "{{names}}'s personalized storybook will be lost if you leave now.",
    "paywall.noContinue": "No, continue",
    "paywall.yesStop": "Yes, leave",
    
    // Upsell Popup (Hardcover)
    "upsell.title": "Order a Printed Book!",
    "upsell.subtitle": "You've got your digital book! Want a premium hardcover delivered to your door?",
    "upsell.specialOffer": "Special Offer",
    "upsell.addPrinted": "Add Printed Book – $19.99",
    "upsell.shippingNote": "Shipping calculated at checkout",
    "upsell.benefit1": "Premium hardcover book",
    "upsell.benefit2": "Professional printing quality",
    "upsell.benefit3": "A keepsake they'll treasure forever",
    "upsell.benefit4": "Worldwide shipping available",
    "upsell.addButton": "Add Printed Copy – $19.99",
    "upsell.noThanks": "No thanks, I'll keep my digital copy",
    
    // Theme Categories
    "category.friendship": "Friendship",
    "category.growingUp": "Growing Up",
    "category.kindness": "Kindness & Emotions",
    "category.values": "Values",
    "category.adventure": "Adventure",
    "category.fantasy": "Fantasy",
    "category.humor": "Humor",
    "category.learning": "Learning",
    "category.diversity": "Diversity & Inclusion",
    "category.nature": "Nature & Environment",
    "category.problemSolving": "Problem Solving",
    "category.lifeExperiences": "Life Experiences",
    "category.cultural": "Cultural Stories",
    
    // Theme Titles & Descriptions
    "theme.makingFriends.title": "Making Friends",
    "theme.makingFriends.desc": "A story about making new friends and the joy of connection",
    "theme.bestFriends.title": "Best Friends",
    "theme.bestFriends.desc": "An adventure celebrating the bond of best friends",
    "theme.sharing.title": "Sharing",
    "theme.sharing.desc": "Learning the happiness that comes from sharing with others",
    "theme.siblings.title": "Siblings",
    "theme.siblings.desc": "A heartwarming tale about the special bond between siblings",
    "theme.sayingSorry.title": "Saying Sorry",
    "theme.sayingSorry.desc": "Learning when and how to apologize and make things right",
    "theme.beingYourself.title": "Being Yourself",
    "theme.beingYourself.desc": "Discovering the magic of being true to who you are",
    "theme.tryingNew.title": "Trying New Things",
    "theme.tryingNew.desc": "An exciting journey of stepping outside your comfort zone",
    "theme.overcomingFear.title": "Overcoming Fear",
    "theme.overcomingFear.desc": "A brave adventure about facing and conquering fears",
    "theme.findingTalent.title": "Finding Your Talent",
    "theme.findingTalent.desc": "Discovering a special talent that makes you unique",
    "theme.confidence.title": "Confidence",
    "theme.confidence.desc": "Building self-confidence and believing in yourself",
    "theme.understandingFeelings.title": "Understanding Feelings",
    "theme.understandingFeelings.desc": "Learning to recognize and understand emotions",
    "theme.helpingOthers.title": "Helping Others",
    "theme.helpingOthers.desc": "The joy and reward of lending a helping hand",
    "theme.dealingAnger.title": "Dealing with Anger",
    "theme.dealingAnger.desc": "Healthy ways to manage and express anger",
    "theme.copingSadness.title": "Coping with Sadness",
    "theme.copingSadness.desc": "Finding comfort and hope during sad times",
    "theme.showingEmpathy.title": "Showing Empathy",
    "theme.showingEmpathy.desc": "Understanding and caring about how others feel",
    "theme.honesty.title": "Honesty",
    "theme.honesty.desc": "The importance of telling the truth",
    "theme.responsibility.title": "Responsibility",
    "theme.responsibility.desc": "Learning to take responsibility for your actions",
    "theme.respect.title": "Respect",
    "theme.respect.desc": "Treating others with kindness and respect",
    "theme.perseverance.title": "Perseverance",
    "theme.perseverance.desc": "Never giving up even when things get tough",
    "theme.goodChoices.title": "Making Good Choices",
    "theme.goodChoices.desc": "Learning to make wise decisions",
    "theme.jungleAdventure.title": "Jungle Adventure",
    "theme.jungleAdventure.desc": "An exciting expedition through a wild jungle",
    "theme.desertJourney.title": "Desert Journey",
    "theme.desertJourney.desc": "A thrilling journey across a vast desert",
    "theme.oceanQuest.title": "Ocean Quest",
    "theme.oceanQuest.desc": "Diving deep into an underwater ocean adventure",
    "theme.spaceExploration.title": "Space Exploration",
    "theme.spaceExploration.desc": "Blasting off on an amazing space adventure",
    "theme.mountainExpedition.title": "Mountain Expedition",
    "theme.mountainExpedition.desc": "Climbing to the top of a magnificent mountain",
    "theme.magicalWorlds.title": "Magical Worlds",
    "theme.magicalWorlds.desc": "Exploring enchanted lands full of wonder",
    "theme.talkingAnimals.title": "Talking Animals",
    "theme.talkingAnimals.desc": "Adventures with animals who can talk",
    "theme.wizardsSpells.title": "Wizards & Spells",
    "theme.wizardsSpells.desc": "Learning magic from wise wizards",
    "theme.mythicalCreatures.title": "Mythical Creatures",
    "theme.mythicalCreatures.desc": "Meeting dragons, unicorns and other magical beings",
    "theme.timeTravel.title": "Time Travel",
    "theme.timeTravel.desc": "Traveling through time to amazing eras",
    "theme.sillyAnimals.title": "Silly Animals",
    "theme.sillyAnimals.desc": "Hilarious adventures with funny animals",
    "theme.wordplayJokes.title": "Wordplay & Jokes",
    "theme.wordplayJokes.desc": "A story filled with puns, jokes and laughs",
    "theme.absurdSituations.title": "Absurd Situations",
    "theme.absurdSituations.desc": "Wacky and wonderfully absurd adventures",
    "theme.mischievous.title": "Mischievous Adventures",
    "theme.mischievous.desc": "Playful pranks and mischievous fun",
    "theme.unexpectedSurprises.title": "Unexpected Surprises",
    "theme.unexpectedSurprises.desc": "A story full of delightful twists and surprises",
    "theme.numbersCounting.title": "Numbers & Counting",
    "theme.numbersCounting.desc": "A fun adventure with numbers and counting",
    "theme.lettersReading.title": "Letters & Reading",
    "theme.lettersReading.desc": "Discovering the magic of letters and words",
    "theme.scienceExperiments.title": "Science Experiments",
    "theme.scienceExperiments.desc": "Exciting discoveries through science",
    "theme.history.title": "History",
    "theme.history.desc": "Traveling back in time to learn about history",
    "theme.problemSolvingSkills.title": "Problem Solving",
    "theme.problemSolvingSkills.desc": "Using creativity and logic to solve problems",
    "theme.differentCultures.title": "Different Cultures",
    "theme.differentCultures.desc": "Exploring and celebrating cultures around the world",
    "theme.differentAbilities.title": "Different Abilities",
    "theme.differentAbilities.desc": "Celebrating what makes everyone uniquely capable",
    "theme.familyDiversity.title": "Family Diversity",
    "theme.familyDiversity.desc": "All kinds of families and the love they share",
    "theme.genderExpression.title": "Gender Expression",
    "theme.genderExpression.desc": "Being free to express who you truly are",
    "theme.acceptanceBelonging.title": "Acceptance & Belonging",
    "theme.acceptanceBelonging.desc": "Finding your place and being accepted",
    "theme.forestsTrees.title": "Forests & Trees",
    "theme.forestsTrees.desc": "Adventures in magical forests and ancient trees",
    "theme.oceansSealife.title": "Oceans & Sea Life",
    "theme.oceansSealife.desc": "Discovering the wonders beneath the waves",
    "theme.animalsHabitats.title": "Animals & Habitats",
    "theme.animalsHabitats.desc": "Learning about animals and where they live",
    "theme.weatherSeasons.title": "Weather & Seasons",
    "theme.weatherSeasons.desc": "Exploring the magic of changing seasons",
    "theme.protectingPlanet.title": "Protecting the Planet",
    "theme.protectingPlanet.desc": "Taking care of our beautiful Earth",
    "theme.mysteriesClues.title": "Mysteries & Clues",
    "theme.mysteriesClues.desc": "Solving exciting mysteries with clever clues",
    "theme.puzzlesRiddles.title": "Puzzles & Riddles",
    "theme.puzzlesRiddles.desc": "Brain-teasing puzzles and tricky riddles",
    "theme.causeEffect.title": "Cause & Effect",
    "theme.causeEffect.desc": "Understanding how actions lead to outcomes",
    "theme.teamProblemSolving.title": "Team Problem Solving",
    "theme.teamProblemSolving.desc": "Working together to solve big challenges",
    "theme.creativeSolutions.title": "Creative Solutions",
    "theme.creativeSolutions.desc": "Thinking outside the box to find answers",
    "theme.startingSchool.title": "Starting School",
    "theme.startingSchool.desc": "The exciting adventure of starting school",
    "theme.movingHome.title": "Moving Home",
    "theme.movingHome.desc": "Adjusting to a new home and neighborhood",
    "theme.losingPet.title": "Losing a Pet",
    "theme.losingPet.desc": "Coping with the loss of a beloved pet",
    "theme.newSibling.title": "New Sibling",
    "theme.newSibling.desc": "Welcoming a new baby brother or sister",
    "theme.dealingChange.title": "Dealing with Change",
    "theme.dealingChange.desc": "Learning to embrace and adapt to changes in life",
    "theme.fairyTales.title": "Fairy Tales",
    "theme.fairyTales.desc": "Classic fairy tale adventures reimagined",
    "theme.folktales.title": "Folktales",
    "theme.folktales.desc": "Traditional stories passed down through generations",
    "theme.mythsLegends.title": "Myths & Legends",
    "theme.mythsLegends.desc": "Epic tales from mythology and legend",
    "theme.fablesMorals.title": "Fables & Morals",
    "theme.fablesMorals.desc": "Stories with valuable life lessons",
    "theme.holidayTraditions.title": "Holiday Traditions",
    "theme.holidayTraditions.desc": "Celebrating special holidays and traditions",
    
    // Style Cards
    "styleCard.pixar.name": "Pixar Style",
    "styleCard.pixar.desc": "Fun 3D cartoon style inspired by animated movies",
    "styleCard.watercolor.name": "Watercolor Classic",
    "styleCard.watercolor.desc": "Soft, dreamy watercolor illustrations",
    "styleCard.anime.name": "Anime Style",
    "styleCard.anime.desc": "Vibrant Japanese anime-inspired art",
    
    // Generating Book Popup
    "generating.title": "Creating Your Story...",
    "generating.subtitle": "We're crafting a magical adventure just for you!",
    "generating.step.preparingStory": "Preparing your story...",
    "generating.step.creatingIllustrations": "Creating illustrations...",
    "generating.step.addingDetails": "Adding details...",
    "generating.step.finishingTouches": "Finishing touches...",
    
    // Finalizing Book Popup
    "finalizing.title": "Finalizing Your Book...",
    "finalizing.subtitle": "Creating {{names}}'s personalized storybook",
    "finalizing.step1": "Setting up your story...",
    "finalizing.step2": "Generating story text...",
    "finalizing.step3": "Creating illustrations...",
    "finalizing.step4": "Adding finishing touches...",
    "finalizing.step5": "Preparing your book...",
    "finalizing.illustrations": "{{completed}} of {{total}} illustrations complete",
    "finalizing.percentComplete": "{{percent}}% complete",
    "finalizing.emailWhenReady": "Email me when it's ready",
    "finalizing.emailWhenReadyHint": "We'll finish in the background and email you when your book is ready.",
    "finalizing.safeToClose": "Your book is being created on our servers. You can safely close this tab — we'll keep working and email you when it's done!",
    "finalizing.emailConfirmTitle": "You're all set!",
    "finalizing.emailConfirmSubtitle": "You can close this page now.",
    "finalizing.emailConfirmDesc": "We'll continue generating your book in the background and send you an email with a link to download it when it's ready.",
    "finalizing.emailConfirmNote": "Check your inbox in a few minutes.",
    "toast.backgroundStarted": "Background Generation Started",
    "toast.backgroundStartedDesc": "We'll email you when your book is ready!",
    "toast.backgroundFailed": "Could not start background generation. Please keep this page open.",
    
    // PDF / Book Pages
    "pdf.adventure": "A personalized adventure",
    "pdf.theEnd": "The End",
    "pdf.storyFor": "A story for",
    
    // Back Cover
    "backCover.title": "Back Cover",
    
    // Illustration Feedback
    "illustration.regenerating": "Regenerating illustration...",
    "illustration.redoCover": "Redo Cover",
    "illustration.redoPage": "Redo Page",
    "illustration.redo": "Redo",
    "illustration.buyMoreDescription": "Need more changes? Get 3 additional redos.",
    "illustration.buyMore": "Buy 3 More Redos",
    "illustration.lastRedo": "This is your last redo! Make it count.",
    "illustration.placeholder": "Describe what you'd like to change...",
    "illustration.applyChanges": "Apply Changes",
    
    // Style Edit Feedback
    "styleEdit.edit": "Edit",
    "styleEdit.placeholder": "Describe how you'd like to change this character...",
    "styleEdit.apply": "Apply Changes",
    
    // Thank You Popup
    "thankYou.title": "Thank You!",
    "thankYou.subtitle": "{{names}}'s storybook adventure is ready!",
    "thankYou.emailResent": "Email resent!",
    "thankYou.emailFailed": "Email failed",
    "thankYou.emailSent": "Email sent!",
    "thankYou.emailPending": "Sending email...",
    "thankYou.resending": "Resending...",
    "thankYou.resendEmail": "Resend Email",
    "thankYou.emailFailedDesc": "We couldn't send the email to {{email}}. Try downloading instead.",
    "thankYou.emailDesc": "Your book has been sent to {{email}}.",
    "thankYou.emailPendingDesc": "We're sending your book to {{email}}...",
    "thankYou.printOrdered": "Printed copy ordered!",
    "thankYou.printDesc": "Your printed book will be shipped soon.",
    "thankYou.generatingPdf": "Generating PDF...",
    "thankYou.finishingIllustrations": "Finishing illustrations...",
    "thankYou.downloadBook": "Download Book",
    "thankYou.createAnother": "Create Another Book",
    "thankYou.hopeLove": "We hope {{names}} loves their story!",
    "thankYou.finalizing": "We're finalizing your book...",
    "thankYou.emailWhenReady": "You'll receive it by email at {{email}}",
    "thankYou.doNotClose": "Please don't close this window while we prepare your book",
    "thankYou.bookOnItsWay": "Your book is on its way!",
    "thankYou.sentTo": "Sent to {{email}}",
    "thankYou.illustrationProgress": "Illustrations: {{completed}} / {{total}}",
    
    // Email Modal
    "email.getBook": "Get Your Book",
    "email.sendToEmail": "We'll send your storybook to your email",
    "email.processingPayment": "Processing your payment...",
    "email.pleaseWait": "Please wait, do not close this window.",
    "email.bookSentAfterPayment": "Your storybook will be sent to your email after payment.",
    "email.yourName": "Your Name",
    "email.namePlaceholder": "Enter your name",
    "email.emailAddress": "Email Address",
    "email.emailPlaceholder": "Enter your email",
    "email.marketingConsent": "I'd like to receive updates about new features and stories",
    "email.continuePayment": "Continue to Payment",
    "email.redirect": "You'll be redirected to our secure payment page",
    "email.stepPreparing": "Preparing your book...",
    "email.stepCreating": "Creating checkout...",
    "email.stepRedirecting": "Redirecting to payment...",

    // Print Payment Progress
    "printPayment.title": "Preparing Your Order",
    "printPayment.subtitle": "Setting up your printed book checkout",
    "printPayment.stepUploading": "Preparing print files...",
    "printPayment.stepCreating": "Creating checkout...",
    "printPayment.stepRedirecting": "Opening checkout...",
    
    // Maintenance Mode
    "maintenance.title": "We're Making Magic!",
    "maintenance.crafting": "We're crafting something amazing. Check back soon!",
    "maintenance.devAccess": "Developer Access",
    "maintenance.enterPassword": "Enter password",
    "maintenance.accessSite": "Access Site",
    "maintenance.incorrectPassword": "Incorrect password",
    "maintenance.comingSoon": "Coming back soon!",
    
    // Contact Page
    "contact.title": "Contact Us",
    "contact.subtitle": "Have a question? We'd love to hear from you!",
    "contact.emailUs": "Email Us",
    "contact.responseTime": "Response Time",
    "contact.within24": "Within 24 hours",
    "contact.liveChat": "Live Chat",
    "contact.chatHours": "Mon-Fri, 9am-5pm EST",
    "contact.sendMessage": "Send us a Message",
    "contact.yourName": "Your Name",
    "contact.namePlaceholder": "John Doe",
    "contact.email": "Email",
    "contact.emailPlaceholder": "john@example.com",
    "contact.subject": "Subject",
    "contact.subjectPlaceholder": "How can we help?",
    "contact.message": "Message",
    "contact.messagePlaceholder": "Tell us more...",
    "contact.sending": "Sending...",
    "contact.send": "Send Message",
    "contact.sent": "Message sent! We'll get back to you soon.",
    "contact.faq": "Frequently Asked Questions",
    "contact.faq1.q": "How long does it take to create a book?",
    "contact.faq1.a": "Your personalized storybook is generated in just a few minutes using our advanced technology.",
    "contact.faq2.q": "What happens to my photos?",
    "contact.faq2.a": "Your photos are used only for illustration generation and are automatically deleted after your book is created.",
    "contact.faq3.q": "Can I get a printed copy?",
    "contact.faq3.a": "Yes! You can order a premium hardcover printed copy delivered right to your door.",
    
    // Not Found Page
    "notFound.title": "404",
    "notFound.message": "Oops! This page doesn't exist.",
    "notFound.backHome": "Go back home",
    
    // Payment Success Page
    "payment.orderConfirmed": "Order Confirmed!",
    "payment.printDelivery": "Your printed book will be shipped soon.",
    "payment.orderIssue": "Order Issue",
    "payment.issueDesc": "There was an issue processing your order. Please contact support.",
    "payment.downloadReady": "Download Ready!",
    "payment.digitalDelivery": "Your digital book is ready to download.",
    "payment.submitting": "Processing Your Order...",
    "payment.paymentSuccess": "Your payment was successful! Setting up your order...",
    "payment.contactSupport": "Please contact support with your session ID:",
    "payment.backHome": "Back to Home",
    "payment.digitalThankYou": "Thank you for your purchase! Your digital storybook is ready.",
    "payment.printThankYou": "Thank you for your purchase! Your printed book will be shipped soon.",
    "payment.orderDetails": "Order Details",
    "payment.bookTitle": "Personalized Storybook",
    "payment.orderId": "Order ID:",
    "payment.createAnother": "Create Another Book",
    
    // Shipping Address
    "shipping.title": "Shipping Address",
    "shipping.for": "For:",
    "shipping.fullName": "Full Name",
    "shipping.nameRequired": "Name is required",
    "shipping.emailRequired": "Email is required",
    "shipping.emailInvalid": "Please enter a valid email",
    "shipping.streetRequired": "Street address is required",
    "shipping.cityRequired": "City is required",
    "shipping.stateRequired": "State is required",
    "shipping.postalRequired": "Postal code is required",
    "shipping.phoneRequired": "Phone number is required",
    "shipping.email": "Email",
    "shipping.streetAddress": "Street Address",
    "shipping.apartment": "Apartment / Suite",
    "shipping.city": "City",
    "shipping.postalCode": "Postal Code",
    "shipping.country": "Country",
    "shipping.stateProvince": "State / Province",
    "shipping.optional": "Optional",
    "shipping.phoneNumber": "Phone Number",
    "shipping.phoneHint": "Required for delivery notifications",
    "shipping.cancel": "Cancel",
    "shipping.continuePayment": "Continue to Payment",
    "shipping.processing": "Processing...",
    "shipping.state": "State",
    "shipping.selectCountry": "Select country",
    "shipping.selectState": "Select state",
    "shipping.deliveryOptions": "Delivery Options",
    "shipping.chooseDelivery": "Choose Delivery",
    "shipping.loadingOptions": "Loading options...",
    "shipping.selectDeliverySpeed": "Choose your preferred delivery speed:",
    "shipping.businessDays": "business days",
    "shipping.estimateUnavailable": "Estimate unavailable",
    "shipping.back": "Back",
    "shipping.included": "Included",
    
    // PDF Preview
    "pdfPreview.printComingSoon": "Coming soon!",
    
    // Queue Status
    "queue.position": "Position in queue: {{position}}",
    "queue.waiting": "Waiting in queue...",

    // Order Tracking
    "trackOrder.title": "Track My Order",
    "trackOrder.subtitle": "Enter your email to see your order history",
    "trackOrder.emailPlaceholder": "your@email.com",
    "trackOrder.search": "Search",
    "trackOrder.error": "Something went wrong. Please try again.",
    "trackOrder.noOrders": "No Orders Found",
    "trackOrder.noOrdersDesc": "We couldn't find any orders with this email address.",
    "trackOrder.createBook": "Create Your First Book",
    "trackOrder.foundOrders": "{{count}} order(s) found",
    "trackOrder.digital": "Digital PDF",
    "trackOrder.printed": "Printed Book",
    "trackOrder.status.pending": "Pending",
    "trackOrder.status.paid": "Paid",
    "trackOrder.status.fulfilled": "Fulfilled",
    "trackOrder.status.shipped": "Shipped",

    // Footer
    "footer.myBooks": "My Account",

    // Auth
    "auth.loginTitle": "Welcome Back",
    "auth.loginSubtitle": "Sign in to access your books",
    "auth.signupTitle": "Create Account",
    "auth.signupSubtitle": "Sign up to save your books and characters",
    "auth.forgotTitle": "Reset Password",
    "auth.forgotSubtitle": "Enter your email to receive a reset link",
    "auth.displayName": "Display name",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.loginButton": "Sign In",
    "auth.signupButton": "Create Account",
    "auth.resetButton": "Send Reset Link",
    "auth.forgotPassword": "Forgot your password?",
    "auth.noAccount": "Don't have an account?",
    "auth.signupLink": "Sign up",
    "auth.hasAccount": "Already have an account?",
    "auth.loginLink": "Sign in",
    "auth.backToLogin": "Back to login",
    "auth.error": "Error",
    "auth.errorDesc": "Something went wrong. Please try again.",
    "auth.welcomeTitle": "Welcome!",
    "auth.welcomeDesc": "Your account has been created successfully.",
    "auth.resetSent": "Reset Link Sent",
    "auth.resetSentDesc": "Check your email for the password reset link.",

    // Dashboard
    "dashboard.title": "My Dashboard",
    "dashboard.subtitle": "Manage your books and characters",
    "dashboard.logout": "Sign Out",
    "dashboard.newBook": "New Book",
    "dashboard.myBooks": "My Books",
    "dashboard.myKids": "My Characters",
    "dashboard.inProgress": "In Progress",
    "dashboard.digital": "Digital",
    "dashboard.printed": "Printed",
    "dashboard.noInProgress": "No books in progress",
    "dashboard.noDigital": "No digital books yet",
    "dashboard.noPrinted": "No printed books yet",
    "dashboard.createFirst": "Create Your First Book",
    "dashboard.untitled": "Untitled Book",
    "dashboard.addChild": "Add Child",
    "dashboard.editChild": "Edit Child",
    "dashboard.addFirstChild": "Add Your First Child",
    "dashboard.noKids": "No saved characters yet",
    "dashboard.childFormDesc": "Save your child's info to quickly create new books",
    "dashboard.photo": "Photo",
    "dashboard.yearsOld": "years old",
    "dashboard.savedStyles": "Saved styles:",
    "dashboard.save": "Save",
    "dashboard.childAdded": "Character added!",
    "dashboard.childUpdated": "Character updated!",
    "dashboard.childRemoved": "Character removed",
    "dashboard.confirmDelete": "Remove character?",
    "dashboard.confirmDeleteDesc": "This will permanently remove this character and their saved styles.",
    "dashboard.delete": "Remove",
    "dashboard.generateStyle": "Generate Style",
    "dashboard.regenerateStyle": "Regenerate",
    "dashboard.selectStyle": "Select art style",
    "dashboard.generatingStyle": "Generating style...",
    "dashboard.styleGenerated": "Style generated!",
    "dashboard.needsPhoto": "Upload a photo first",
    "dashboard.profile": "Profile",
    "dashboard.personalInfo": "Personal Information",
    "dashboard.fullName": "Full Name",
    "dashboard.fullNamePlaceholder": "Enter your full name",
    "dashboard.email": "Email",
    "dashboard.phone": "Phone Number",
    "dashboard.phonePlaceholder": "Enter your phone number",
    "dashboard.shippingAddress": "Shipping Address",
    "dashboard.addressLine1": "Address Line 1",
    "dashboard.addressLine1Placeholder": "Street address",
    "dashboard.addressLine2": "Address Line 2",
    "dashboard.addressLine2Placeholder": "Apartment, suite, etc. (optional)",
    "dashboard.city": "City",
    "dashboard.cityPlaceholder": "City",
    "dashboard.state": "State / Province",
    "dashboard.statePlaceholder": "State / Province",
    "dashboard.postalCode": "Postal Code",
    "dashboard.postalCodePlaceholder": "Postal code",
    "dashboard.country": "Country",
    "dashboard.countryPlaceholder": "Country",
    "dashboard.saveProfile": "Save Profile",
    "dashboard.saved": "Saved!",
    "dashboard.profileSaved": "Profile saved!",
    "dashboard.profileSaveError": "Failed to save profile",
    "dashboard.continueBook": "Continue",
    "dashboard.downloadPdf": "Download",
    "dashboard.trackOrder": "Track",
    "dashboard.statusPending": "In Progress",
    "dashboard.statusGenerating": "Generating",
    "dashboard.statusProcessing": "Processing",
    "dashboard.statusPaid": "Paid",
    "dashboard.statusCompleted": "Completed",
    "dashboard.statusFulfilled": "Fulfilled",
    "dashboard.statusShipped": "Shipped",
    "dashboard.buyHardCopy": "Buy Hard Copy",
  },
  fr: {
    "seo.title": "StoryHeroes – Livres Personnalisés pour Enfants",
    "seo.description": "Faites de votre enfant le héros d'un livre d'histoires magique. Téléchargez une photo, choisissez un thème et obtenez un PDF personnalisé illustré en quelques minutes.",
    // Hero Section
    "hero.badge": "✨ Livres Personnalisés",
    "hero.title": "Transformez Votre Enfant en Héros de Livre",
    "hero.title.create": "Créez des",
    "hero.title.magical": "Histoires Magiques",
    "hero.title.starring": "Avec Votre Enfant en Vedette",
    "hero.subtitle": "Téléchargez une photo, choisissez une aventure et recevez un superbe PDF illustré ou un livre relié en quelques minutes.",
    "hero.cta": "Créer Votre Histoire",
    "hero.freePreview": "Créez et prévisualisez votre livre gratuitement",
    "hero.privacyStamp": "🔒 Photos supprimées après génération",
    "hero.themes": "50+ Thèmes",
    "hero.aiArt": "Illustrations Personnalisées",
    "hero.pdfDownload": "PDF Instantané",
    "hero.features.personalized": "Personnalisé",
    "hero.features.ai": "Art Personnalisé",
    "hero.features.instant": "PDF Instantané",
    "transform.badge": "✨ Voyez la Magie",
    "transform.title": "De la Photo au Personnage de Livre",
    "transform.subtitle": "Nous transformons la photo de votre enfant en un magnifique personnage illustré",
    "transform.pixar": "Style Pixar",
    "transform.watercolor": "Style Aquarelle",
    "transform.anime": "Style Anime",
    "showcase.title": "De Beaux Livres, Numériques et Imprimés",
    "showcase.subtitle": "Obtenez votre livre personnalisé en PDF numérique ou en couverture rigide imprimée",
    "showcase.pdfTitle": "PDF Numérique",
    "showcase.pdfDesc": "Téléchargement instantané, lisible sur tous les appareils",
    "showcase.printTitle": "Livre Imprimé",
    "showcase.printDesc": "Couverture rigide premium livrée chez vous",
    "showcase.pages": "32 pages",
    "showcase.popular": "Le Plus Populaire",
    "reviews.badge": "⭐ Adoré par les Parents",
    "reviews.title": "Ce que Disent les Parents",
    "reviews.subtitle": "Rejoignez des milliers de familles heureuses qui ont créé des histoires magiques",
    "reviews.child": "Âge de l'enfant :",
    "reviews.1.text": "Ma fille a adoré se voir en héroïne ! Les illustrations sont magnifiques et l'histoire était parfaite pour son âge.",
    "reviews.1.childAge": "5 ans",
    "reviews.2.text": "Une idée de cadeau incroyable ! Mon fils lit son histoire chaque soir avant de dormir. La qualité du PDF est incroyable.",
    "reviews.2.childAge": "4 ans",
    "reviews.3.text": "J'ai été époustouflée par la qualité. La ressemblance de mon enfant a été parfaitement capturée. Je recommande vivement !",
    "reviews.3.childAge": "6 ans",
    "reviews.4.text": "Nous avons commandé trois livres, un pour chacun de nos enfants. Ils se battent pour savoir qui lit le sien en premier !",
    "reviews.4.childAge": "3, 5, 7 ans",
    "reviews.5.text": "Le style aquarelle est absolument magnifique. On dirait qu'un illustrateur professionnel l'a fait.",
    "reviews.5.childAge": "4 ans",
    "reviews.6.text": "Le meilleur cadeau d'anniversaire ! Le visage de mon fils quand il s'est vu dans l'histoire n'avait pas de prix.",
    "reviews.6.childAge": "6 ans",
    "trust.gift.title": "Cadeau Parfait",
    "trust.gift.desc": "Un cadeau unique et personnalisé que les enfants chérissent pendant des années",
    "trust.ai.title": "Art Époustouflant",
    "trust.ai.desc": "Notre technologie crée de superbes illustrations à l'image de votre enfant",
    "trust.love.title": "Fait avec Amour",
    "trust.love.desc": "Chaque histoire est conçue avec soin pour inspirer l'imagination et la joie",
    "cta.title": "Prêt à Créer de la Magie ?",
    "cta.subtitle": "Transformez votre enfant en héros de sa propre aventure",
    "cta.button": "Commencer Maintenant",
    "cta.note": "Pas de compte nécessaire • Prêt en 5 minutes",
    
    // Language Selector
    "lang.title": "Choisissez Votre Langue",
    "lang.subtitle": "Sélectionnez votre langue préférée pour continuer",
    
    // Navigation
    "nav.backToHome": "Retour à l'Accueil",
    
    // Footer
    "footer.copyright": "© 2024 StoryHeroes. Tous droits réservés.",
    "footer.privacy": "Politique de Confidentialité",
    "footer.terms": "Conditions d'Utilisation",
    "footer.contact": "Contact",
    "footer.trackOrder": "Suivre Commande",
    
    // Privacy Page
    "privacy.title": "Politique de Confidentialité",
    "privacy.updated": "Dernière mise à jour : Janvier 2024",
    "privacy.section1.title": "1. Informations Collectées",
    "privacy.section1.intro": "Nous collectons les informations suivantes lorsque vous utilisez notre service :",
    "privacy.section1.item1": "Photos que vous téléchargez pour créer des livres personnalisés",
    "privacy.section1.item2": "Nom et âge de l'enfant pour la personnalisation de l'histoire",
    "privacy.section1.item3": "Adresse e-mail pour la confirmation et la livraison de commande",
    "privacy.section1.item4": "Informations de paiement (traitées en toute sécurité via Stripe)",
    "privacy.section2.title": "2. Utilisation de Vos Informations",
    "privacy.section2.intro": "Nous utilisons vos informations pour :",
    "privacy.section2.item1": "Générer des livres d'histoires personnalisés",
    "privacy.section2.item2": "Traiter les paiements et livrer votre livre PDF",
    "privacy.section2.item3": "Envoyer des confirmations de commande et des mises à jour",
    "privacy.section2.item4": "Améliorer notre service et l'expérience utilisateur",
    "privacy.section2.item5": "Respecter les obligations légales",
    "privacy.section3.title": "3. Stockage et Sécurité des Données",
    "privacy.section3.text": "Nous prenons la sécurité des données au sérieux. Toutes les photos téléchargées et informations personnelles sont cryptées et stockées en toute sécurité. Nous utilisons des mesures de sécurité standard pour protéger vos données contre tout accès non autorisé.",
    "privacy.section4.title": "4. Services Tiers",
    "privacy.section4.text": "Nous utilisons des services tiers de confiance, notamment Stripe pour le traitement des paiements et des services spécialisés pour la génération d'histoires. Ces services ont leurs propres politiques de confidentialité et mesures de sécurité.",
    "privacy.section5.title": "5. Conservation des Données",
    "privacy.section5.text": "Nous conservons vos données uniquement le temps nécessaire pour fournir nos services et respecter les obligations légales. Vous pouvez demander la suppression de vos données à tout moment en nous contactant.",
    "privacy.section6.title": "6. Confidentialité des Enfants",
    "privacy.section6.text": "Nous nous engageons à protéger la vie privée des enfants. Nous ne collectons pas sciemment d'informations personnelles auprès d'enfants de moins de 13 ans sans le consentement parental. Les parents ont un contrôle total sur les informations de leur enfant.",
    "privacy.section7.title": "7. Vos Droits",
    "privacy.section7.intro": "Vous avez le droit de :",
    "privacy.section7.item1": "Accéder à vos données personnelles",
    "privacy.section7.item2": "Demander la correction de données inexactes",
    "privacy.section7.item3": "Demander la suppression de vos données",
    "privacy.section7.item4": "Vous désabonner des communications marketing",
    "privacy.section8.title": "8. Nous Contacter",
    "privacy.section8.text": "Si vous avez des questions concernant cette Politique de Confidentialité, veuillez nous contacter via notre page de contact.",
    
    // Terms Page
    "terms.title": "Conditions d'Utilisation",
    "terms.updated": "Dernière mise à jour : Janvier 2024",
    "terms.section1.title": "1. Acceptation des Conditions",
    "terms.section1.text": "En utilisant StoryHeroes, vous acceptez ces Conditions d'Utilisation. Si vous n'êtes pas d'accord, veuillez ne pas utiliser notre service.",
    "terms.section2.title": "2. Description du Service",
    "terms.section2.text": "StoryHeroes fournit des livres d'histoires personnalisés pour les enfants. Nous créons des histoires illustrées personnalisées basées sur les photos et informations que vous fournissez.",
    "terms.section3.title": "3. Responsabilités de l'Utilisateur",
    "terms.section3.intro": "Vous acceptez de :",
    "terms.section3.item1": "Fournir des informations exactes",
    "terms.section3.item2": "Télécharger uniquement des photos que vous avez le droit d'utiliser",
    "terms.section3.item3": "Ne pas utiliser le service à des fins illégales",
    "terms.section3.item4": "Ne pas télécharger de contenu inapproprié ou offensant",
    "terms.section3.item5": "Respecter les droits de propriété intellectuelle",
    "terms.section4.title": "4. Paiement et Remboursements",
    "terms.section4.text": "Tous les paiements sont traités en toute sécurité via Stripe. En raison de la nature personnalisée de notre service, les remboursements ne sont disponibles qu'en cas de problèmes techniques empêchant la livraison de votre livre.",
    "terms.section5.title": "5. Propriété Intellectuelle",
    "terms.section5.intro": "Concernant la propriété intellectuelle :",
    "terms.section5.item1": "Vous conservez les droits sur les photos que vous téléchargez",
    "terms.section5.item2": "Les histoires et illustrations générées sont à usage personnel uniquement",
    "terms.section5.item3": "Vous ne pouvez pas revendre ou redistribuer le contenu généré",
    "terms.section5.item4": "StoryHeroes conserve les droits sur le service et la technologie",
    "terms.section5.item5": "Vous nous accordez une licence pour utiliser le contenu téléchargé afin de générer votre livre",
    "terms.section6.title": "6. Limitation de Responsabilité",
    "terms.section6.text": "StoryHeroes est fourni 'tel quel' sans garanties. Nous ne sommes pas responsables des dommages indirects, accessoires ou consécutifs résultant de l'utilisation de notre service.",
    "terms.section7.title": "7. Directives de Contenu",
    "terms.section7.text": "Nous nous réservons le droit de refuser le service ou de supprimer du contenu qui viole nos directives, y compris du matériel inapproprié, offensant ou protégé par des droits d'auteur.",
    "terms.section8.title": "8. Modifications des Conditions",
    "terms.section8.text": "Nous pouvons mettre à jour ces Conditions d'Utilisation. L'utilisation continue du service après les modifications constitue l'acceptation des nouvelles conditions.",
    "terms.section9.title": "9. Résiliation",
    "terms.section9.text": "Nous nous réservons le droit de résilier ou de suspendre l'accès à notre service en cas de violation de ces conditions ou pour toute autre raison à notre discrétion.",
    "terms.section10.title": "10. Contact",
    "terms.section10.text": "Pour toute question concernant ces Conditions d'Utilisation, veuillez nous contacter via notre page de contact.",

    // Wizard Steps
    "step.addCharacters": "Ajouter Personnages",
    "step.chooseStory": "Choisir l'Histoire",
    "step.pickTitle": "Choisir le Titre",
    "step.chooseStyle": "Choisir le Style",
    "step.review": "Vérifier",
    "step.storyOutline": "Plan de l'Histoire",
    "step.previewPdf": "Aperçu PDF",

    // Photo Upload
    "photo.title": "Ajoutez Vos Personnages",
    "photo.subtitle": "Téléchargez une photo et entrez les détails de chaque personnage de votre histoire.",
    "photo.privacyNotice": "🔒 Vos photos ne sont utilisées que pour l'illustration et sont supprimées après la génération.",
    "photo.addPhoto": "Ajouter Photo",
    "photo.dragDrop": "ou glisser-déposer",
    "photo.dropHere": "Déposez ici !",
    "photo.browse": "Parcourir",
    "photo.camera": "Caméra",
    "photo.upload": "Télécharger",
    "photo.name": "Nom",
    "photo.namePlaceholder": "Entrez le nom",
    "photo.age": "Âge",
    "photo.agePlaceholder": "Entrez l'âge",
    "photo.gender": "Type de Personnage",
    "photo.boy": "Garçon",
    "photo.girl": "Fille",
    "photo.pet": "Animal",
    "photo.addChild": "Ajouter un Personnage",
    "familyImport.button": "Importer depuis Photo de Groupe",

    // Style Selection
    "style.choose": "Choisissez Votre Style Artistique",
    "style.subtitleSelected": "Voyez comment vos {{count}} personnage{{plural}} apparaissent dans ce style",
    "style.subtitleUnselected": "Choisissez un style d'illustration pour votre livre",
    "style.yourCharactersIn": "Vos personnages en {{style}}",
    "style.creatingName": "Création de {{name}}...",
    "style.altCharacterInStyle": "{{name}} en style {{style}}",
    "style.failedShort": "Échoué",
    "style.retry": "Réessayer",
    "style.hoverHint": "Survolez les images pour les agrandir",
    "style.preserveHint": "Les descriptions des personnages seront préservées pour la cohérence",
    "style.tryAgain": "Réessayer",
    "style.continueWith": "Continuer avec {{style}}",
    "style.switchHint": "Vous pouvez changer de style à tout moment avant de continuer",

    // Title Selection
    "title.choose": "Choisissez Votre Titre",
    "title.subtitle": "Choisissez le titre parfait pour votre livre personnalisé",
    "title.creating": "Création de titres magiques...",
    "title.selected": "Sélectionné",
    "title.regenerate": "Régénérer les Titres",
    "title.usingSuggested": "Titres suggérés",
    "title.pickFavorite": "Choisissez votre préféré parmi ces suggestions !",

    // Story Themes
    "themes.title": "Choisissez un Thème pour {{hero}}",
    "themes.subtitle": "Choisissez un thème d'aventure pour vos {{count}} {{characterWord}}. Chaque thème crée des histoires uniques.",
    "themes.changeTheme": "Changer de Thème",
    "themes.whatsStoryAbout": "De quoi devrait parler l'histoire de {{hero}} ?",
    "themes.yourIdeas": "Vos Idées d'Histoire",
    "themes.ideaHelperText": "Décrivez l'aventure que vous aimeriez. Soyez aussi créatif que vous voulez !",
    "themes.ideaPlaceholder": "ex. Un chevalier courageux qui se lie d'amitié avec un dragon et ensemble ils sauvent un royaume magique...",
    "themes.ideaHint": "Bon début ! Cliquez ci-dessous pour des idées améliorées.",
    "themes.generateRequired": "Écrivez votre propre idée ou cliquez ci-dessous pour vous inspirer !",
    "themes.generatingIdeas": "Génération d'idées...",
    "themes.completeMyIdeas": "Compléter Mes Idées",
    "themes.inspireMe": "Inspirez-moi !",
    "themes.chooseIdea": "Choisissez une Idée d'Histoire",
    "themes.usingPreMade": "Idées pré-faites",
    "themes.greatIdeas": "Voici de superbes idées d'histoires !",

    // Outline
    "outline.title": "Plan de l'Histoire",
    "outline.subtitle": "Vérifiez et personnalisez les pages de votre livre",
    "outline.generating": "Création du plan de votre histoire...",
    "outline.titlePage": "Page de Titre",
    "outline.ending": "Fin",
    "outline.page": "Page",
    "outline.happy": "Satisfait du plan ?",
    "outline.happyDesc": "Vous pouvez modifier le texte de chaque page ci-dessus. Quand vous êtes prêt, cliquez sur suivant pour générer les illustrations !",
    "outline.sceneDescription": "Description de la scène",
    "outline.whatYouWillSee": "Ce que vous verrez sur cette page",

    // Preview
    "preview.title": "Vérifiez Votre Histoire",
    "preview.subtitle": "Tout est bon ? Créons votre livre illustré !",
    "preview.storyTheme": "Thème de l'Histoire",
    "preview.yourHeroes": "Vos Héros",
    "preview.chosenStyle": "Style Artistique",
    "preview.ready": "Tout est parfait ! ✨",
    "preview.readyDesc": "Nous allons créer une histoire magique avec {{names}} en style {{style}}.",

    // PDF Preview
    "pdfPreview.bookReady": "Votre livre est prêt !",
    "pdfPreview.finishingIllustrations": "Finition des illustrations...",
    "pdfPreview.reviewMessage": "Vérifiez votre livre et téléchargez quand vous êtes prêt.",
    "pdfPreview.preparing": "Préparation...",
    "pdfPreview.happyWithBook": "Je suis satisfait de mon livre !",
    "pdfPreview.paymentComplete": "Paiement effectué !",
    "pdfPreview.generatingPdf": "Génération du PDF...",
    "pdfPreview.downloadPdf": "Télécharger le PDF",
    "pdfPreview.loveIt": "Vous aimez ? Obtenez votre copie !",
    "pdfPreview.processing": "Traitement...",
    "pdfPreview.digitalPdf": "PDF Numérique",
    "pdfPreview.printedBook": "Livre Imprimé",
    "pdfPreview.pdfDownloadShortly": "Votre PDF sera téléchargé sous peu.",
    "pdfPreview.tryAgainMoment": "Veuillez réessayer dans un moment.",
    "pdfPreview.paymentSuccess": "Paiement réussi !",
    "pdfPreview.pdfReady": "\"{{title}}\" est prêt à télécharger",
    "pdfPreview.readyNextBook": "Prêt pour votre prochain livre !",
    "pdfPreview.canCreateNew": "Vous pouvez créer un nouveau livre à tout moment.",

    // Navigation
    "nav.back": "Retour",
    "nav.next": "Suivant",
    "nav.generateIllustrations": "Générer les Illustrations",
    "download.pdf": "Télécharger le PDF",
    "complete.title": "Votre Livre est Prêt ! 🎉",
    "complete.subtitle": "Un livre magique avec {{names}} a été créé !",
    "complete.download": "Télécharger le PDF",
    "complete.startOver": "Créer un Autre Livre",
    "generating.preparingStory": "Préparation de votre histoire...",
    "generating.creatingIllustrations": "Création des illustrations...",
    "generating.creatingPreviewIllustrations": "Création des illustrations de prévisualisation...",
    "generating.creatingCover": "Création de la couverture...",
    "generating.coverCreated": "Couverture créée !",
    "generating.backCoverCreated": "Couverture arrière créée !",
    "finalize.paymentConfirmed": "Paiement confirmé !",
    "finalize.generatingStory": "Génération de votre histoire complète...",
    "finalize.creatingIllustrations": "Création des illustrations...",
    "finalize.addingFinishingTouches": "Touches finales...",
    "finalize.creatingBackCover": "Création de la couverture arrière...",
    "finalize.bookReadyReview": "Votre livre est prêt à être vérifié !",
    "finalize.preparingPdf": "Préparation de votre PDF...",
    "resume.loadingBook": "Chargement de votre livre...",
    "resume.stillGenerating": "Votre livre est encore en cours de création !",
    "resume.stillGeneratingDesc": "Vos illustrations sont encore en cours de génération. Vous recevrez un e-mail quand ce sera prêt.",
    "resume.checkBackLater": "Vous pouvez fermer cette page et revenir plus tard.",
    "illustration.noRedosLeft": "Plus de refaire disponible",
    "illustration.limitReached": "Vous avez utilisé toutes vos reprises d'illustration. Achetez-en plus pour continuer.",

    // Toast Messages
    "toast.paymentCancelled": "Paiement Annulé",
    "toast.tryAgainReady": "Votre livre vous attend. Réessayez quand vous voulez !",
    "toast.redoCreditsAdded": "Crédits de Reprise Ajoutés !",
    "toast.threeMoreRedos": "Vous avez maintenant 3 reprises d'illustration supplémentaires.",
    "toast.paymentSuccessful": "Paiement Réussi !",
    "toast.pdfReadyDownload": "Votre PDF est prêt à télécharger.",
    "toast.downloadStarted": "Téléchargement Commencé",
    "toast.couldntDownload": "Impossible de Télécharger",
    "toast.failedGenerate": "Échec de la génération. Veuillez réessayer.",
    "toast.pleaseRetry": "Veuillez réessayer.",
    "toast.storyCreated": "Histoire Créée !",
    "toast.previewStory": "Prévisualisez votre livre ci-dessous.",
    "toast.fillIllustrations": "Génération des illustrations restantes...",
    "toast.generationFailed": "Échec de la Génération",
    "toast.generationTimeout": "La génération a expiré. Veuillez réessayer.",
    "toast.paymentFailed": "Échec du Paiement",
    "toast.somethingWrong": "Quelque Chose s'est Mal Passé",
    "toast.createBookAgain": "Veuillez réessayer de créer votre livre.",
    "toast.errorGenerating": "Une erreur s'est produite lors de la génération de votre livre.",
    "toast.printOrderConfirmed": "Commande d'Impression Confirmée !",
    "toast.bookPrintedShipped": "Votre livre sera imprimé et expédié bientôt.",
    "toast.paymentSuccessContact": "Paiement réussi. Contactez le support si nécessaire.",
    "toast.preparingPrintFiles": "Préparation des Fichiers d'Impression",
    "toast.readyForPrinting": "Préparation de votre livre pour l'impression...",
    "toast.orderFailed": "Échec de la Commande",
    "toast.paymentError": "Erreur de Paiement",
    "toast.tryAgainLater": "Veuillez réessayer plus tard.",
    "toast.coverRegenerated": "Couverture Régénérée !",
    "toast.newCoverReady": "Votre nouvelle couverture est prête.",
    "toast.pageRegenerated": "Page {{page}} Régénérée !",
    "toast.feedbackApplied": "Vos commentaires ont été appliqués.",
    "toast.freshIllustration": "Une nouvelle illustration a été créée.",
    "toast.regenerationFailed": "Échec de la Régénération",
    "toast.couldNotRegenerate": "Impossible de régénérer l'illustration. Veuillez réessayer.",
    "toast.backCoverFailed": "Échec de la Couverture Arrière",
    "toast.backCoverFrontUsed": "La couverture avant sera utilisée à la place.",
    "toast.backCoverReady": "Couverture Arrière Prête !",
    "toast.backCoverGenerated": "Votre couverture arrière a été générée.",
    "toast.frontCoverRequired": "Couverture Avant Requise",
    "toast.frontCoverFirst": "Générez d'abord une couverture avant.",
    "toast.backCoverRegenerated": "Couverture Arrière Régénérée !",
    "toast.backCoverFeedbackApplied": "Vos commentaires ont été appliqués à la couverture arrière.",
    "toast.freshBackCover": "Une nouvelle couverture arrière a été créée.",
    "toast.backCoverRegenerateFailed": "Impossible de régénérer la couverture arrière.",
    "toast.allIllustrationsComplete": "Toutes les Illustrations Terminées !",
    "toast.storybookReady": "Votre livre est entièrement illustré.",
    "toast.someIllustrationsIncomplete": "Certaines Illustrations Incomplètes",
    "toast.pagesMissing": "{{count}} pages manquent encore d'illustrations.",
    "toast.bookNotComplete": "Livre Non Terminé",
    "toast.generateFirst": "Veuillez d'abord générer votre histoire.",
    "toast.preparingBook": "Préparation de Votre Livre",
    "toast.uploadingPrint": "Téléchargement des fichiers d'impression...",
    "toast.missingInfoCheckout": "Informations manquantes pour le paiement.",
    "toast.failedCheckout": "Échec de la création du paiement. Veuillez réessayer.",
    "toast.checkEmail": "Vérifiez aussi votre e-mail pour le lien PDF !",
    "toast.couldntSendPdf": "Impossible d'envoyer le PDF. Essayez de télécharger.",
    "toast.emailSent": "E-mail Envoyé !",
    "toast.bookSentTo": "Votre livre a été envoyé à {{email}}.",
    "toast.generatingPdf": "Génération du PDF...",
    "paywall.title": "Finalisez le livre de {{names}} !",
    "paywall.subtitle": "Votre aperçu est prêt ! Obtenez le PDF illustré complet à garder pour toujours.",
    "paywall.digitalPdf": "PDF Numérique — 9,99 $",
    "paywall.price": "9,99 $",
    "paywall.benefit1": "Livre illustré complet (20+ pages)",
    "paywall.benefit2": "PDF haute qualité imprimable à la maison",
    "paywall.benefit3": "Téléchargement instantané après achat",
    "paywall.processing": "Traitement en cours...",
    "paywall.getBook": "Obtenir mon livre — 9,99 $",
    "paywall.stopTitle": "Quitter votre histoire ?",
    "paywall.stopDesc": "Le livre personnalisé de {{names}} sera perdu si vous partez maintenant.",
    "paywall.noContinue": "Non, continuer",
    "paywall.yesStop": "Oui, quitter",
    "upsell.title": "Commandez un livre imprimé !",
    "upsell.subtitle": "Vous avez votre livre numérique ! Voulez-vous un beau livre relié livré chez vous ?",
    "upsell.specialOffer": "Offre Spéciale",
    "upsell.addPrinted": "Ajouter le livre imprimé – 19,99 $",
    "upsell.shippingNote": "Frais de livraison calculés au paiement",
    "upsell.benefit1": "Livre à couverture rigide premium",
    "upsell.benefit2": "Qualité d'impression professionnelle",
    "upsell.benefit3": "Un souvenir qu'ils chériront pour toujours",
    "upsell.benefit4": "Livraison mondiale disponible",
    "upsell.addButton": "Ajouter une copie imprimée – 19,99 $",
    "upsell.noThanks": "Non merci, je garde ma copie numérique",

    // Style Cards
    "styleCard.pixar.name": "Style Pixar",
    "styleCard.pixar.desc": "Style cartoon 3D amusant inspiré des films d'animation",
    "styleCard.watercolor.name": "Aquarelle Classique",
    "styleCard.watercolor.desc": "Illustrations aquarelle douces et rêveuses",
    "styleCard.anime.name": "Style Anime",
    "styleCard.anime.desc": "Art vibrant inspiré de l'anime japonais",

    // Generating Book Popup
    "generating.title": "Création de Votre Histoire...",
    "generating.subtitle": "Nous créons une aventure magique rien que pour vous !",
    "generating.step.preparingStory": "Préparation de votre histoire...",
    "generating.step.creatingIllustrations": "Création des illustrations...",
    "generating.step.addingDetails": "Ajout des détails...",
    "generating.step.finishingTouches": "Touches finales...",

    // Finalizing Book Popup
    "finalizing.title": "Finalisation de Votre Livre...",
    "finalizing.subtitle": "Création du livre personnalisé de {{names}}",
    "finalizing.step1": "Mise en place de votre histoire...",
    "finalizing.step2": "Génération du texte...",
    "finalizing.step3": "Création des illustrations...",
    "finalizing.step4": "Touches finales...",
    "finalizing.step5": "Préparation de votre livre...",
    "finalizing.illustrations": "{{completed}} sur {{total}} illustrations terminées",
    "finalizing.percentComplete": "{{percent}}% terminé",
    "finalizing.emailWhenReady": "M'envoyer un email quand c'est prêt",
    "finalizing.emailWhenReadyHint": "Nous terminerons en arrière-plan et vous enverrons un email quand votre livre sera prêt.",
    "finalizing.safeToClose": "Votre livre est en cours de création sur nos serveurs. Vous pouvez fermer cet onglet en toute sécurité — nous continuerons et vous enverrons un email quand ce sera prêt !",
    "finalizing.emailConfirmTitle": "C'est tout bon !",
    "finalizing.emailConfirmSubtitle": "Vous pouvez fermer cette page.",
    "finalizing.emailConfirmDesc": "Nous continuerons à générer votre livre en arrière-plan et vous enverrons un email avec un lien de téléchargement quand il sera prêt.",
    "finalizing.emailConfirmNote": "Vérifiez votre boîte de réception dans quelques minutes.",
    "toast.backgroundStarted": "Génération en arrière-plan lancée",
    "toast.backgroundStartedDesc": "Nous vous enverrons un email quand votre livre sera prêt !",
    "toast.backgroundFailed": "Impossible de lancer la génération en arrière-plan. Veuillez garder cette page ouverte.",

    // PDF / Book Pages
    "pdf.adventure": "Une aventure personnalisée",
    "pdf.theEnd": "Fin",
    "pdf.storyFor": "Une histoire pour",

    // Back Cover
    "backCover.title": "Couverture Arrière",

    // Illustration Feedback (extended)
    "illustration.regenerating": "Régénération de l'illustration...",
    "illustration.redoCover": "Refaire la Couverture",
    "illustration.redoPage": "Refaire la Page",
    "illustration.redo": "Refaire",
    "illustration.buyMoreDescription": "Besoin de plus de changements ? Obtenez 3 reprises supplémentaires.",
    "illustration.buyMore": "Acheter 3 Reprises",
    "illustration.lastRedo": "C'est votre dernière reprise ! Faites-en bon usage.",
    "illustration.placeholder": "Décrivez ce que vous aimeriez changer...",
    "illustration.applyChanges": "Appliquer les Changements",

    // Style Edit Feedback
    "styleEdit.edit": "Modifier",
    "styleEdit.placeholder": "Décrivez comment vous aimeriez changer ce personnage...",
    "styleEdit.apply": "Appliquer les Changements",

    // Thank You Popup
    "thankYou.title": "Merci !",
    "thankYou.subtitle": "L'aventure de {{names}} est prête !",
    "thankYou.emailResent": "E-mail renvoyé !",
    "thankYou.emailFailed": "Échec de l'e-mail",
    "thankYou.emailSent": "E-mail envoyé !",
    "thankYou.emailPending": "Envoi de l'e-mail...",
    "thankYou.resending": "Renvoi en cours...",
    "thankYou.resendEmail": "Renvoyer l'E-mail",
    "thankYou.emailFailedDesc": "Impossible d'envoyer l'e-mail à {{email}}. Essayez de télécharger.",
    "thankYou.emailDesc": "Votre livre a été envoyé à {{email}}.",
    "thankYou.emailPendingDesc": "Nous envoyons votre livre à {{email}}...",
    "thankYou.printOrdered": "Copie imprimée commandée !",
    "thankYou.printDesc": "Votre livre imprimé sera expédié bientôt.",
    "thankYou.generatingPdf": "Génération du PDF...",
    "thankYou.finishingIllustrations": "Finition des illustrations...",
    "thankYou.downloadBook": "Télécharger le Livre",
    "thankYou.createAnother": "Créer un Autre Livre",
    "thankYou.hopeLove": "Nous espérons que {{names}} adorera son histoire !",
    "thankYou.finalizing": "Nous finalisons votre livre...",
    "thankYou.emailWhenReady": "Vous le recevrez par e-mail à {{email}}",
    "thankYou.doNotClose": "Veuillez ne pas fermer cette fenêtre pendant la préparation de votre livre",
    "thankYou.bookOnItsWay": "Votre livre est en route !",
    "thankYou.sentTo": "Envoyé à {{email}}",
    "thankYou.illustrationProgress": "Illustrations : {{completed}} / {{total}}",

    // Email Modal
    "email.getBook": "Obtenez Votre Livre",
    "email.sendToEmail": "Nous enverrons votre livre à votre e-mail",
    "email.processingPayment": "Traitement de votre paiement...",
    "email.pleaseWait": "Veuillez patienter, ne fermez pas cette fenêtre.",
    "email.bookSentAfterPayment": "Votre livre sera envoyé à votre e-mail après le paiement.",
    "email.yourName": "Votre Nom",
    "email.namePlaceholder": "Entrez votre nom",
    "email.emailAddress": "Adresse E-mail",
    "email.emailPlaceholder": "Entrez votre e-mail",
    "email.marketingConsent": "J'aimerais recevoir des mises à jour sur les nouvelles fonctionnalités et histoires",
    "email.continuePayment": "Continuer vers le Paiement",
    "email.redirect": "Vous serez redirigé vers notre page de paiement sécurisée",
    "email.stepPreparing": "Préparation de votre livre...",
    "email.stepCreating": "Création du paiement...",
    "email.stepRedirecting": "Redirection vers le paiement...",

    "printPayment.title": "Préparation de votre commande",
    "printPayment.subtitle": "Configuration du paiement de votre livre imprimé",
    "printPayment.stepUploading": "Préparation des fichiers d'impression...",
    "printPayment.stepCreating": "Création du paiement...",
    "printPayment.stepRedirecting": "Ouverture du paiement...",

    // Maintenance Mode
    "maintenance.title": "Nous Créons de la Magie !",
    "maintenance.crafting": "Nous préparons quelque chose d'incroyable. Revenez bientôt !",
    "maintenance.devAccess": "Accès Développeur",
    "maintenance.enterPassword": "Entrez le mot de passe",
    "maintenance.accessSite": "Accéder au Site",
    "maintenance.incorrectPassword": "Mot de passe incorrect",
    "maintenance.comingSoon": "De retour bientôt !",

    // Contact Page
    "contact.title": "Contactez-nous",
    "contact.subtitle": "Une question ? Nous serions ravis de vous entendre !",
    "contact.emailUs": "Envoyez-nous un E-mail",
    "contact.responseTime": "Temps de Réponse",
    "contact.within24": "Sous 24 heures",
    "contact.liveChat": "Chat en Direct",
    "contact.chatHours": "Lun-Ven, 9h-17h",
    "contact.sendMessage": "Envoyez-nous un Message",
    "contact.yourName": "Votre Nom",
    "contact.namePlaceholder": "Jean Dupont",
    "contact.email": "E-mail",
    "contact.emailPlaceholder": "jean@exemple.com",
    "contact.subject": "Sujet",
    "contact.subjectPlaceholder": "Comment pouvons-nous vous aider ?",
    "contact.message": "Message",
    "contact.messagePlaceholder": "Dites-nous en plus...",
    "contact.sending": "Envoi en cours...",
    "contact.send": "Envoyer le Message",
    "contact.sent": "Message envoyé ! Nous vous répondrons bientôt.",
    "contact.faq": "Questions Fréquentes",
    "contact.faq1.q": "Combien de temps faut-il pour créer un livre ?",
    "contact.faq1.a": "Votre livre personnalisé est généré en quelques minutes grâce à notre technologie avancée.",
    "contact.faq2.q": "Que se passe-t-il avec mes photos ?",
    "contact.faq2.a": "Vos photos ne sont utilisées que pour la génération des illustrations et sont automatiquement supprimées après la création de votre livre.",
    "contact.faq3.q": "Puis-je obtenir une copie imprimée ?",
    "contact.faq3.a": "Oui ! Vous pouvez commander une copie imprimée premium en couverture rigide livrée chez vous.",

    // Not Found Page
    "notFound.title": "404",
    "notFound.message": "Oups ! Cette page n'existe pas.",
    "notFound.backHome": "Retour à l'accueil",

    // Payment Success Page
    "payment.orderConfirmed": "Commande Confirmée !",
    "payment.printDelivery": "Votre livre imprimé sera expédié bientôt.",
    "payment.orderIssue": "Problème de Commande",
    "payment.issueDesc": "Un problème est survenu lors du traitement de votre commande. Veuillez contacter le support.",
    "payment.downloadReady": "Téléchargement Prêt !",
    "payment.digitalDelivery": "Votre livre numérique est prêt à télécharger.",
    "payment.submitting": "Traitement de Votre Commande...",
    "payment.paymentSuccess": "Votre paiement a réussi ! Configuration de votre commande...",
    "payment.contactSupport": "Veuillez contacter le support avec votre ID de session :",
    "payment.backHome": "Retour à l'Accueil",
    "payment.digitalThankYou": "Merci pour votre achat ! Votre livre numérique est prêt.",
    "payment.printThankYou": "Merci pour votre achat ! Votre livre imprimé sera expédié bientôt.",
    "payment.orderDetails": "Détails de la Commande",
    "payment.bookTitle": "Livre Personnalisé",
    "payment.orderId": "ID de Commande :",
    "payment.createAnother": "Créer un Autre Livre",

    // Shipping Address
    "shipping.title": "Adresse de Livraison",
    "shipping.for": "Pour :",
    "shipping.fullName": "Nom Complet",
    "shipping.nameRequired": "Le nom est requis",
    "shipping.emailRequired": "L'e-mail est requis",
    "shipping.emailInvalid": "Veuillez entrer un e-mail valide",
    "shipping.streetRequired": "L'adresse est requise",
    "shipping.cityRequired": "La ville est requise",
    "shipping.stateRequired": "La région est requise",
    "shipping.postalRequired": "Le code postal est requis",
    "shipping.phoneRequired": "Le numéro de téléphone est requis",
    "shipping.email": "E-mail",
    "shipping.streetAddress": "Adresse",
    "shipping.apartment": "Appartement / Suite",
    "shipping.city": "Ville",
    "shipping.postalCode": "Code Postal",
    "shipping.country": "Pays",
    "shipping.stateProvince": "Région / Province",
    "shipping.optional": "Facultatif",
    "shipping.phoneNumber": "Numéro de Téléphone",
    "shipping.phoneHint": "Requis pour les notifications de livraison",
    "shipping.cancel": "Annuler",
    "shipping.continuePayment": "Continuer vers le Paiement",
    "shipping.processing": "Traitement...",
    "shipping.state": "État",
    "shipping.selectCountry": "Sélectionner un pays",
    "shipping.selectState": "Sélectionner un état",
    "shipping.deliveryOptions": "Options de Livraison",
    "shipping.chooseDelivery": "Choisir la Livraison",
    "shipping.loadingOptions": "Chargement...",
    "shipping.selectDeliverySpeed": "Choisissez votre vitesse de livraison :",
    "shipping.businessDays": "jours ouvrables",
    "shipping.estimateUnavailable": "Estimation non disponible",
    "shipping.back": "Retour",
    "shipping.included": "Inclus",

    // PDF Preview
    "pdfPreview.printComingSoon": "Bientôt disponible !",

    // Queue Status
    "queue.position": "Position dans la file : {{position}}",
    "queue.waiting": "En attente dans la file...",

    // Order Tracking
    "trackOrder.title": "Suivre Ma Commande",
    "trackOrder.subtitle": "Entrez votre email pour voir l'historique de vos commandes",
    "trackOrder.emailPlaceholder": "votre@email.com",
    "trackOrder.search": "Rechercher",
    "trackOrder.error": "Une erreur est survenue. Veuillez réessayer.",
    "trackOrder.noOrders": "Aucune Commande Trouvée",
    "trackOrder.noOrdersDesc": "Aucune commande n'a été trouvée avec cette adresse email.",
    "trackOrder.createBook": "Créez Votre Premier Livre",
    "trackOrder.foundOrders": "{{count}} commande(s) trouvée(s)",
    "trackOrder.digital": "PDF Numérique",
    "trackOrder.printed": "Livre Imprimé",
    "trackOrder.status.pending": "En attente",
    "trackOrder.status.paid": "Payé",
    "trackOrder.status.fulfilled": "Livré",
    "trackOrder.status.shipped": "Expédié",
    "footer.myBooks": "Mon Compte",
    "auth.loginTitle": "Bon Retour",
    "auth.loginSubtitle": "Connectez-vous pour accéder à vos livres",
    "auth.signupTitle": "Créer un Compte",
    "auth.signupSubtitle": "Inscrivez-vous pour sauvegarder vos livres et personnages",
    "auth.forgotTitle": "Réinitialiser le Mot de Passe",
    "auth.forgotSubtitle": "Entrez votre email pour recevoir un lien de réinitialisation",
    "auth.displayName": "Nom d'affichage",
    "auth.email": "Email",
    "auth.password": "Mot de passe",
    "auth.loginButton": "Se Connecter",
    "auth.signupButton": "Créer un Compte",
    "auth.resetButton": "Envoyer le Lien",
    "auth.forgotPassword": "Mot de passe oublié ?",
    "auth.noAccount": "Pas encore de compte ?",
    "auth.signupLink": "S'inscrire",
    "auth.hasAccount": "Déjà un compte ?",
    "auth.loginLink": "Se connecter",
    "auth.backToLogin": "Retour à la connexion",
    "auth.error": "Erreur",
    "auth.errorDesc": "Quelque chose s'est mal passé. Réessayez.",
    "auth.welcomeTitle": "Bienvenue !",
    "auth.welcomeDesc": "Votre compte a été créé avec succès.",
    "auth.resetSent": "Lien Envoyé",
    "auth.resetSentDesc": "Vérifiez votre email pour le lien de réinitialisation.",
    "dashboard.title": "Mon Tableau de Bord",
    "dashboard.subtitle": "Gérez vos livres et personnages",
    "dashboard.logout": "Déconnexion",
    "dashboard.newBook": "Nouveau Livre",
    "dashboard.myBooks": "Mes Livres",
    "dashboard.myKids": "Mes Personnages",
    "dashboard.inProgress": "En Cours",
    "dashboard.digital": "Numérique",
    "dashboard.printed": "Imprimé",
    "dashboard.noInProgress": "Aucun livre en cours",
    "dashboard.noDigital": "Aucun livre numérique",
    "dashboard.noPrinted": "Aucun livre imprimé",
    "dashboard.createFirst": "Créez Votre Premier Livre",
    "dashboard.untitled": "Livre Sans Titre",
    "dashboard.addChild": "Ajouter un Enfant",
    "dashboard.editChild": "Modifier l'Enfant",
    "dashboard.addFirstChild": "Ajoutez Votre Premier Enfant",
    "dashboard.noKids": "Aucun personnage sauvegardé",
    "dashboard.childFormDesc": "Enregistrez les infos de votre enfant pour créer des livres rapidement",
    "dashboard.photo": "Photo",
    "dashboard.yearsOld": "ans",
    "dashboard.savedStyles": "Styles sauvegardés :",
    "dashboard.save": "Enregistrer",
    "dashboard.childAdded": "Personnage ajouté !",
    "dashboard.childUpdated": "Personnage mis à jour !",
    "dashboard.childRemoved": "Personnage supprimé",
    "dashboard.confirmDelete": "Supprimer le personnage ?",
    "dashboard.confirmDeleteDesc": "Cela supprimera définitivement ce personnage et ses styles sauvegardés.",
    "dashboard.delete": "Supprimer",
    "dashboard.generateStyle": "Générer le Style",
    "dashboard.regenerateStyle": "Régénérer",
    "dashboard.selectStyle": "Choisir un style artistique",
    "dashboard.generatingStyle": "Génération du style...",
    "dashboard.styleGenerated": "Style généré !",
    "dashboard.needsPhoto": "Ajoutez d'abord une photo",
    "dashboard.profile": "Profil",
    "dashboard.personalInfo": "Informations personnelles",
    "dashboard.fullName": "Nom complet",
    "dashboard.fullNamePlaceholder": "Entrez votre nom complet",
    "dashboard.email": "E-mail",
    "dashboard.phone": "Numéro de téléphone",
    "dashboard.phonePlaceholder": "Entrez votre numéro de téléphone",
    "dashboard.shippingAddress": "Adresse de livraison",
    "dashboard.addressLine1": "Adresse ligne 1",
    "dashboard.addressLine1Placeholder": "Adresse",
    "dashboard.addressLine2": "Adresse ligne 2",
    "dashboard.addressLine2Placeholder": "Appartement, suite, etc. (facultatif)",
    "dashboard.city": "Ville",
    "dashboard.cityPlaceholder": "Ville",
    "dashboard.state": "État / Province",
    "dashboard.statePlaceholder": "État / Province",
    "dashboard.postalCode": "Code postal",
    "dashboard.postalCodePlaceholder": "Code postal",
    "dashboard.country": "Pays",
    "dashboard.countryPlaceholder": "Pays",
    "dashboard.saveProfile": "Enregistrer le profil",
    "dashboard.saved": "Enregistré !",
    "dashboard.profileSaved": "Profil enregistré !",
    "dashboard.profileSaveError": "Échec de l'enregistrement du profil",
    "dashboard.continueBook": "Continuer",
    "dashboard.downloadPdf": "Télécharger",
    "dashboard.trackOrder": "Suivre",
    "dashboard.statusPending": "En cours",
    "dashboard.statusGenerating": "Génération",
    "dashboard.statusProcessing": "Traitement",
    "dashboard.statusPaid": "Payé",
    "dashboard.statusCompleted": "Terminé",
    "dashboard.statusFulfilled": "Livré",
    "dashboard.statusShipped": "Expédié",
    "dashboard.buyHardCopy": "Commander en dur",

    // Theme Categories
    "category.friendship": "Amitié",
    "category.growingUp": "Grandir",
    "category.kindness": "Gentillesse & Émotions",
    "category.values": "Valeurs",
    "category.adventure": "Aventure",
    "category.fantasy": "Fantaisie",
    "category.humor": "Humour",
    "category.learning": "Apprentissage",
    "category.diversity": "Diversité & Inclusion",
    "category.nature": "Nature & Environnement",
    "category.problemSolving": "Résolution de Problèmes",
    "category.lifeExperiences": "Expériences de Vie",
    "category.cultural": "Histoires Culturelles",

    // Theme Titles & Descriptions
    "theme.makingFriends.title": "Se Faire des Amis",
    "theme.makingFriends.desc": "Une histoire sur comment se faire de nouveaux amis et la joie de la connexion",
    "theme.bestFriends.title": "Meilleurs Amis",
    "theme.bestFriends.desc": "Une aventure célébrant le lien entre meilleurs amis",
    "theme.sharing.title": "Partager",
    "theme.sharing.desc": "Apprendre le bonheur de partager avec les autres",
    "theme.siblings.title": "Frères et Sœurs",
    "theme.siblings.desc": "Un conte émouvant sur le lien spécial entre frères et sœurs",
    "theme.sayingSorry.title": "Dire Pardon",
    "theme.sayingSorry.desc": "Apprendre quand et comment s'excuser et arranger les choses",
    "theme.beingYourself.title": "Être Soi-Même",
    "theme.beingYourself.desc": "Découvrir la magie d'être fidèle à qui vous êtes",
    "theme.tryingNew.title": "Essayer de Nouvelles Choses",
    "theme.tryingNew.desc": "Un voyage passionnant hors de sa zone de confort",
    "theme.overcomingFear.title": "Surmonter la Peur",
    "theme.overcomingFear.desc": "Une aventure courageuse pour affronter et vaincre ses peurs",
    "theme.findingTalent.title": "Trouver Son Talent",
    "theme.findingTalent.desc": "Découvrir un talent spécial qui vous rend unique",
    "theme.confidence.title": "Confiance en Soi",
    "theme.confidence.desc": "Construire la confiance en soi et croire en soi-même",
    "theme.understandingFeelings.title": "Comprendre les Émotions",
    "theme.understandingFeelings.desc": "Apprendre à reconnaître et comprendre les émotions",
    "theme.helpingOthers.title": "Aider les Autres",
    "theme.helpingOthers.desc": "La joie et la récompense de donner un coup de main",
    "theme.dealingAnger.title": "Gérer la Colère",
    "theme.dealingAnger.desc": "Des façons saines de gérer et exprimer la colère",
    "theme.copingSadness.title": "Surmonter la Tristesse",
    "theme.copingSadness.desc": "Trouver du réconfort et de l'espoir dans les moments tristes",
    "theme.showingEmpathy.title": "Montrer de l'Empathie",
    "theme.showingEmpathy.desc": "Comprendre et se soucier de ce que ressentent les autres",
    "theme.honesty.title": "Honnêteté",
    "theme.honesty.desc": "L'importance de dire la vérité",
    "theme.responsibility.title": "Responsabilité",
    "theme.responsibility.desc": "Apprendre à assumer la responsabilité de ses actes",
    "theme.respect.title": "Respect",
    "theme.respect.desc": "Traiter les autres avec gentillesse et respect",
    "theme.perseverance.title": "Persévérance",
    "theme.perseverance.desc": "Ne jamais abandonner même quand les choses sont difficiles",
    "theme.goodChoices.title": "Faire les Bons Choix",
    "theme.goodChoices.desc": "Apprendre à prendre de bonnes décisions",
    "theme.jungleAdventure.title": "Aventure dans la Jungle",
    "theme.jungleAdventure.desc": "Une expédition passionnante à travers une jungle sauvage",
    "theme.desertJourney.title": "Voyage dans le Désert",
    "theme.desertJourney.desc": "Un voyage palpitant à travers un vaste désert",
    "theme.oceanQuest.title": "Quête Océanique",
    "theme.oceanQuest.desc": "Plonger au fond d'une aventure sous-marine",
    "theme.spaceExploration.title": "Exploration Spatiale",
    "theme.spaceExploration.desc": "Décoller pour une incroyable aventure spatiale",
    "theme.mountainExpedition.title": "Expédition en Montagne",
    "theme.mountainExpedition.desc": "Grimper au sommet d'une montagne magnifique",
    "theme.magicalWorlds.title": "Mondes Magiques",
    "theme.magicalWorlds.desc": "Explorer des terres enchantées pleines de merveilles",
    "theme.talkingAnimals.title": "Animaux Parlants",
    "theme.talkingAnimals.desc": "Des aventures avec des animaux qui parlent",
    "theme.wizardsSpells.title": "Sorciers et Sortilèges",
    "theme.wizardsSpells.desc": "Apprendre la magie auprès de sages sorciers",
    "theme.mythicalCreatures.title": "Créatures Mythiques",
    "theme.mythicalCreatures.desc": "Rencontrer des dragons, des licornes et d'autres êtres magiques",
    "theme.timeTravel.title": "Voyage dans le Temps",
    "theme.timeTravel.desc": "Voyager à travers le temps vers des époques incroyables",
    "theme.sillyAnimals.title": "Animaux Rigolos",
    "theme.sillyAnimals.desc": "Des aventures hilarantes avec des animaux drôles",
    "theme.wordplayJokes.title": "Jeux de Mots et Blagues",
    "theme.wordplayJokes.desc": "Une histoire pleine de calembours, de blagues et de rires",
    "theme.absurdSituations.title": "Situations Absurdes",
    "theme.absurdSituations.desc": "Des aventures loufoques et merveilleusement absurdes",
    "theme.mischievous.title": "Aventures Espiègle",
    "theme.mischievous.desc": "Des farces ludiques et du plaisir espiègle",
    "theme.unexpectedSurprises.title": "Surprises Inattendues",
    "theme.unexpectedSurprises.desc": "Une histoire pleine de rebondissements et de surprises",
    "theme.numbersCounting.title": "Chiffres et Comptage",
    "theme.numbersCounting.desc": "Une aventure amusante avec les chiffres et le comptage",
    "theme.lettersReading.title": "Lettres et Lecture",
    "theme.lettersReading.desc": "Découvrir la magie des lettres et des mots",
    "theme.scienceExperiments.title": "Expériences Scientifiques",
    "theme.scienceExperiments.desc": "Des découvertes passionnantes grâce à la science",
    "theme.history.title": "Histoire",
    "theme.history.desc": "Remonter le temps pour découvrir l'histoire",
    "theme.problemSolvingSkills.title": "Résolution de Problèmes",
    "theme.problemSolvingSkills.desc": "Utiliser la créativité et la logique pour résoudre des problèmes",
    "theme.differentCultures.title": "Différentes Cultures",
    "theme.differentCultures.desc": "Explorer et célébrer les cultures du monde entier",
    "theme.differentAbilities.title": "Différentes Capacités",
    "theme.differentAbilities.desc": "Célébrer ce qui rend chacun unique",
    "theme.familyDiversity.title": "Diversité Familiale",
    "theme.familyDiversity.desc": "Toutes sortes de familles et l'amour qu'elles partagent",
    "theme.genderExpression.title": "Expression de Genre",
    "theme.genderExpression.desc": "Être libre d'exprimer qui vous êtes vraiment",
    "theme.acceptanceBelonging.title": "Acceptation et Appartenance",
    "theme.acceptanceBelonging.desc": "Trouver sa place et être accepté",
    "theme.forestsTrees.title": "Forêts et Arbres",
    "theme.forestsTrees.desc": "Des aventures dans des forêts magiques et des arbres anciens",
    "theme.oceansSealife.title": "Océans et Vie Marine",
    "theme.oceansSealife.desc": "Découvrir les merveilles sous les vagues",
    "theme.animalsHabitats.title": "Animaux et Habitats",
    "theme.animalsHabitats.desc": "Apprendre sur les animaux et où ils vivent",
    "theme.weatherSeasons.title": "Météo et Saisons",
    "theme.weatherSeasons.desc": "Explorer la magie des saisons changeantes",
    "theme.protectingPlanet.title": "Protéger la Planète",
    "theme.protectingPlanet.desc": "Prendre soin de notre belle Terre",
    "theme.mysteriesClues.title": "Mystères et Indices",
    "theme.mysteriesClues.desc": "Résoudre des mystères passionnants avec des indices malins",
    "theme.puzzlesRiddles.title": "Puzzles et Devinettes",
    "theme.puzzlesRiddles.desc": "Des casse-têtes et des devinettes retorses",
    "theme.causeEffect.title": "Cause et Effet",
    "theme.causeEffect.desc": "Comprendre comment les actions mènent à des résultats",
    "theme.teamProblemSolving.title": "Résolution en Équipe",
    "theme.teamProblemSolving.desc": "Travailler ensemble pour résoudre de grands défis",
    "theme.creativeSolutions.title": "Solutions Créatives",
    "theme.creativeSolutions.desc": "Penser autrement pour trouver des réponses",
    "theme.startingSchool.title": "Commencer l'École",
    "theme.startingSchool.desc": "L'aventure passionnante de commencer l'école",
    "theme.movingHome.title": "Déménager",
    "theme.movingHome.desc": "S'adapter à une nouvelle maison et un nouveau quartier",
    "theme.losingPet.title": "Perdre un Animal",
    "theme.losingPet.desc": "Faire face à la perte d'un animal bien-aimé",
    "theme.newSibling.title": "Nouveau Frère ou Sœur",
    "theme.newSibling.desc": "Accueillir un nouveau bébé frère ou sœur",
    "theme.dealingChange.title": "Faire Face au Changement",
    "theme.dealingChange.desc": "Apprendre à accepter et s'adapter aux changements",
    "theme.fairyTales.title": "Contes de Fées",
    "theme.fairyTales.desc": "Des aventures classiques de contes de fées réinventées",
    "theme.folktales.title": "Contes Populaires",
    "theme.folktales.desc": "Des histoires traditionnelles transmises de génération en génération",
    "theme.mythsLegends.title": "Mythes et Légendes",
    "theme.mythsLegends.desc": "Des récits épiques de la mythologie et des légendes",
    "theme.fablesMorals.title": "Fables et Morales",
    "theme.fablesMorals.desc": "Des histoires avec de précieuses leçons de vie",
    "theme.holidayTraditions.title": "Traditions de Fêtes",
    "theme.holidayTraditions.desc": "Célébrer les fêtes et traditions spéciales",
  },
  es: {
    "seo.title": "StoryHeroes – Libros de Cuentos Personalizados para Niños",
    "seo.description": "Convierte a tu hijo en el héroe de un libro de cuentos mágico. Sube una foto, elige una aventura y obtén un PDF personalizado e ilustrado en minutos.",
    "hero.badge": "✨ Cuentos Personalizados",
    "hero.title": "Convierte a Tu Hijo en un Héroe de Cuento",
    "hero.title.create": "Crea",
    "hero.title.magical": "Historias Mágicas",
    "hero.title.starring": "Protagonizadas por Tu Hijo",
    "hero.subtitle": "Sube una foto, elige una aventura y recibe un increíble PDF ilustrado o libro de tapa dura en minutos.",
    "hero.cta": "Crear Tu Historia",
    "hero.freePreview": "Crea y previsualiza tu libro gratis",
    "hero.privacyStamp": "🔒 Fotos eliminadas tras la generación",
    "hero.themes": "50+ Temas",
    "hero.aiArt": "Ilustraciones Personalizadas",
    "hero.pdfDownload": "PDF Instantáneo",
    "hero.features.personalized": "Personalizado",
    "hero.features.ai": "Arte Personalizado",
    "hero.features.instant": "PDF Instantáneo",
    "transform.badge": "✨ Ve la Magia",
    "transform.title": "De Foto a Personaje de Cuento",
    "transform.subtitle": "Transformamos la foto de tu hijo en un hermoso personaje ilustrado",
    "transform.pixar": "Estilo Pixar",
    "transform.watercolor": "Estilo Acuarela",
    "transform.anime": "Estilo Anime",
    "showcase.title": "Hermosos Libros, Digitales e Impresos",
    "showcase.subtitle": "Obtén tu libro personalizado como PDF digital o en tapa dura impresa",
    "showcase.pdfTitle": "PDF Digital",
    "showcase.pdfDesc": "Descarga instantánea, lee en cualquier dispositivo",
    "showcase.printTitle": "Libro Impreso",
    "showcase.printDesc": "Tapa dura premium entregada en tu puerta",
    "showcase.pages": "32 páginas",
    "showcase.popular": "Más Popular",
    "reviews.badge": "⭐ Amado por los Padres",
    "reviews.title": "Lo que Dicen los Padres",
    "reviews.subtitle": "Únete a miles de familias felices que crearon historias mágicas",
    "reviews.child": "Edad del niño:",
    "reviews.1.text": "¡A mi hija le encantó verse como la heroína! Las ilustraciones son impresionantes y la historia fue perfecta para su edad.",
    "reviews.1.childAge": "5 años",
    "reviews.2.text": "¡Una idea de regalo increíble! Mi hijo lee su historia cada noche antes de dormir. La calidad del PDF es asombrosa.",
    "reviews.2.childAge": "4 años",
    "reviews.3.text": "Me quedé impresionada con la calidad. Capturó perfectamente el parecido de mi hijo. ¡Lo recomiendo!",
    "reviews.3.childAge": "6 años",
    "reviews.4.text": "Hemos pedido tres libros, uno para cada uno de nuestros hijos. ¡Se pelean por quién lee el suyo primero!",
    "reviews.4.childAge": "3, 5, 7 años",
    "reviews.5.text": "El estilo acuarela es absolutamente precioso. Parece hecho por un ilustrador profesional de libros infantiles.",
    "reviews.5.childAge": "4 años",
    "reviews.6.text": "¡El mejor regalo de cumpleaños! La cara de mi hijo al verse en la historia no tenía precio.",
    "reviews.6.childAge": "6 años",
    "trust.gift.title": "Regalo Perfecto",
    "trust.gift.desc": "Un regalo único y personalizado que los niños atesoran durante años",
    "trust.ai.title": "Arte Impresionante",
    "trust.ai.desc": "Nuestra tecnología crea ilustraciones impresionantes a imagen de tu hijo",
    "trust.love.title": "Hecho con Amor",
    "trust.love.desc": "Cada historia está creada con cuidado para inspirar imaginación y alegría",
    "cta.title": "¿Listo para Crear Magia?",
    "cta.subtitle": "Convierte a tu hijo en el héroe de su propia aventura",
    "cta.button": "Empezar a Crear",
    "cta.note": "Sin cuenta necesaria • Listo en 5 minutos",
    
    // Language Selector
    "lang.title": "Elige Tu Idioma",
    "lang.subtitle": "Selecciona tu idioma preferido para continuar",
    
    // Navigation
    "nav.backToHome": "Volver al Inicio",
    
    // Footer
    "footer.copyright": "© 2024 StoryHeroes. Todos los derechos reservados.",
    "footer.privacy": "Política de Privacidad",
    "footer.terms": "Términos de Servicio",
    "footer.contact": "Contacto",
    "footer.trackOrder": "Rastrear Pedido",
    
    // Privacy Page
    "privacy.title": "Política de Privacidad",
    "privacy.updated": "Última actualización: Enero 2024",
    "privacy.section1.title": "1. Información que Recopilamos",
    "privacy.section1.intro": "Recopilamos la siguiente información cuando usas nuestro servicio:",
    "privacy.section1.item1": "Fotos que subes para crear libros personalizados",
    "privacy.section1.item2": "Nombre y edad del niño para la personalización de la historia",
    "privacy.section1.item3": "Dirección de correo electrónico para confirmación y entrega del pedido",
    "privacy.section1.item4": "Información de pago (procesada de forma segura a través de Stripe)",
    "privacy.section2.title": "2. Cómo Usamos Tu Información",
    "privacy.section2.intro": "Usamos tu información para:",
    "privacy.section2.item1": "Generar libros de cuentos personalizados",
    "privacy.section2.item2": "Procesar pagos y entregar tu libro PDF",
    "privacy.section2.item3": "Enviar confirmaciones de pedido y actualizaciones",
    "privacy.section2.item4": "Mejorar nuestro servicio y experiencia de usuario",
    "privacy.section2.item5": "Cumplir con obligaciones legales",
    "privacy.section3.title": "3. Almacenamiento y Seguridad de Datos",
    "privacy.section3.text": "Nos tomamos la seguridad de los datos en serio. Todas las fotos subidas e información personal están encriptadas y almacenadas de forma segura. Utilizamos medidas de seguridad estándar de la industria para proteger tus datos contra accesos no autorizados.",
    "privacy.section4.title": "4. Servicios de Terceros",
    "privacy.section4.text": "Utilizamos servicios de terceros confiables, incluyendo Stripe para el procesamiento de pagos y servicios especializados para la generación de historias. Estos servicios tienen sus propias políticas de privacidad y medidas de seguridad.",
    "privacy.section5.title": "5. Retención de Datos",
    "privacy.section5.text": "Conservamos tus datos solo el tiempo necesario para proporcionar nuestros servicios y cumplir con obligaciones legales. Puedes solicitar la eliminación de tus datos en cualquier momento contactándonos.",
    "privacy.section6.title": "6. Privacidad de los Niños",
    "privacy.section6.text": "Estamos comprometidos con la protección de la privacidad de los niños. No recopilamos conscientemente información personal de niños menores de 13 años sin el consentimiento de los padres. Los padres tienen control total sobre la información de sus hijos.",
    "privacy.section7.title": "7. Tus Derechos",
    "privacy.section7.intro": "Tienes derecho a:",
    "privacy.section7.item1": "Acceder a tus datos personales",
    "privacy.section7.item2": "Solicitar la corrección de datos inexactos",
    "privacy.section7.item3": "Solicitar la eliminación de tus datos",
    "privacy.section7.item4": "Optar por no recibir comunicaciones de marketing",
    "privacy.section8.title": "8. Contáctanos",
    "privacy.section8.text": "Si tienes alguna pregunta sobre esta Política de Privacidad, contáctanos a través de nuestra página de contacto.",
    
    // Terms Page
    "terms.title": "Términos de Servicio",
    "terms.updated": "Última actualización: Enero 2024",
    "terms.section1.title": "1. Aceptación de Términos",
    "terms.section1.text": "Al usar StoryHeroes, aceptas estos Términos de Servicio. Si no estás de acuerdo, por favor no uses nuestro servicio.",
    "terms.section2.title": "2. Descripción del Servicio",
    "terms.section2.text": "StoryHeroes proporciona libros de cuentos personalizados para niños. Creamos historias ilustradas personalizadas basadas en las fotos e información que proporcionas.",
    "terms.section3.title": "3. Responsabilidades del Usuario",
    "terms.section3.intro": "Aceptas:",
    "terms.section3.item1": "Proporcionar información precisa",
    "terms.section3.item2": "Subir solo fotos que tengas derecho a usar",
    "terms.section3.item3": "No usar el servicio con fines ilegales",
    "terms.section3.item4": "No subir contenido inapropiado u ofensivo",
    "terms.section3.item5": "Respetar los derechos de propiedad intelectual",
    "terms.section4.title": "4. Pago y Reembolsos",
    "terms.section4.text": "Todos los pagos se procesan de forma segura a través de Stripe. Debido a la naturaleza personalizada de nuestro servicio, los reembolsos solo están disponibles en caso de problemas técnicos que impidan la entrega de tu libro.",
    "terms.section5.title": "5. Propiedad Intelectual",
    "terms.section5.intro": "Respecto a la propiedad intelectual:",
    "terms.section5.item1": "Conservas los derechos sobre las fotos que subes",
    "terms.section5.item2": "Las historias e ilustraciones generadas son solo para uso personal",
    "terms.section5.item3": "No puedes revender o redistribuir el contenido generado",
    "terms.section5.item4": "StoryHeroes conserva los derechos sobre el servicio y la tecnología",
    "terms.section5.item5": "Nos otorgas licencia para usar el contenido subido para generar tu libro",
    "terms.section6.title": "6. Limitación de Responsabilidad",
    "terms.section6.text": "StoryHeroes se proporciona 'tal cual' sin garantías. No somos responsables de ningún daño indirecto, incidental o consecuente que surja del uso de nuestro servicio.",
    "terms.section7.title": "7. Directrices de Contenido",
    "terms.section7.text": "Nos reservamos el derecho de rechazar el servicio o eliminar contenido que viole nuestras directrices, incluyendo material inapropiado, ofensivo o con derechos de autor.",
    "terms.section8.title": "8. Cambios en los Términos",
    "terms.section8.text": "Podemos actualizar estos Términos de Servicio. El uso continuado del servicio después de los cambios constituye la aceptación de los nuevos términos.",
    "terms.section9.title": "9. Terminación",
    "terms.section9.text": "Nos reservamos el derecho de terminar o suspender el acceso a nuestro servicio por violaciones de estos términos o por cualquier otra razón a nuestra discreción.",
    "terms.section10.title": "10. Contacto",
    "terms.section10.text": "Para preguntas sobre estos Términos de Servicio, contáctanos a través de nuestra página de contacto.",

    // Wizard Steps
    "step.addCharacters": "Agregar Personajes",
    "step.chooseStory": "Elegir Historia",
    "step.pickTitle": "Elegir Título",
    "step.chooseStyle": "Elegir Estilo",
    "step.review": "Revisar",
    "step.storyOutline": "Esquema de la Historia",
    "step.previewPdf": "Vista Previa PDF",
    "photo.title": "Agrega Tus Personajes",
    "photo.subtitle": "Sube una foto e ingresa los detalles de cada personaje de tu historia.",
    "photo.privacyNotice": "🔒 Tus fotos se usan solo para ilustración y se eliminan después de la generación.",
    "photo.addPhoto": "Agregar Foto",
    "photo.dragDrop": "o arrastra y suelta",
    "photo.dropHere": "¡Suelta aquí!",
    "photo.browse": "Explorar",
    "photo.camera": "Cámara",
    "photo.upload": "Subir",
    "photo.name": "Nombre",
    "photo.namePlaceholder": "Ingresa el nombre",
    "photo.age": "Edad",
    "photo.agePlaceholder": "Ingresa la edad",
    "photo.gender": "Tipo de Personaje",
    "photo.boy": "Niño",
    "photo.girl": "Niña",
    "photo.pet": "Mascota",
    "photo.addChild": "Agregar Otro Personaje",
    "photo.savedKids": "Tus Personajes Guardados",
    "familyImport.button": "Importar desde Foto Grupal",
    "style.choose": "Elige Tu Estilo Artístico",
    "style.subtitleSelected": "Mira cómo se ven tus {{count}} personaje{{plural}} en este estilo",
    "style.subtitleUnselected": "Elige un estilo de ilustración para tu libro",
    "style.yourCharactersIn": "Tus personajes en {{style}}",
    "style.creatingName": "Creando {{name}}...",
    "style.altCharacterInStyle": "{{name}} en estilo {{style}}",
    "style.failedShort": "Fallido",
    "style.retry": "Reintentar",
    "style.hoverHint": "Pasa el mouse sobre las imágenes para verlas más grandes",
    "style.preserveHint": "Las descripciones de los personajes se preservarán para consistencia",
    "style.tryAgain": "Intentar de Nuevo",
    "style.continueWith": "Continuar con {{style}}",
    "style.switchHint": "Puedes cambiar de estilo en cualquier momento antes de continuar",
    "title.choose": "Elige Tu Título",
    "title.subtitle": "Elige el título perfecto para tu libro personalizado",
    "title.creating": "Creando títulos mágicos...",
    "title.selected": "Seleccionado",
    "title.regenerate": "Regenerar Títulos",
    "title.usingSuggested": "Títulos sugeridos",
    "title.pickFavorite": "¡Elige tu favorito de estas sugerencias!",
    "themes.title": "Elige un Tema para {{hero}}",
    "themes.subtitle": "Elige un tema de aventura para tus {{count}} {{characterWord}}. Cada tema crea historias únicas.",
    "themes.changeTheme": "Cambiar Tema",
    "themes.whatsStoryAbout": "¿De qué debería tratar la historia de {{hero}}?",
    "themes.yourIdeas": "Tus Ideas de Historia",
    "themes.ideaHelperText": "Describe la aventura que te gustaría. ¡Sé tan creativo como quieras!",
    "themes.ideaPlaceholder": "ej. Un caballero valiente que se hace amigo de un dragón y juntos salvan un reino mágico...",
    "themes.ideaHint": "¡Buen comienzo! Haz clic abajo para obtener ideas mejoradas.",
    "themes.generateRequired": "¡Escribe tu propia idea o haz clic abajo para inspirarte!",
    "themes.generatingIdeas": "Generando ideas...",
    "themes.completeMyIdeas": "Completar Mis Ideas",
    "themes.inspireMe": "¡Inspírame!",
    "themes.chooseIdea": "Elige una Idea de Historia",
    "themes.usingPreMade": "Ideas prediseñadas",
    "themes.greatIdeas": "¡Aquí hay algunas ideas geniales!",
    "outline.title": "Esquema de la Historia",
    "outline.subtitle": "Revisa y personaliza las páginas de tu libro",
    "outline.generating": "Creando el esquema de tu historia...",
    "outline.titlePage": "Página de Título",
    "outline.ending": "Fin",
    "outline.page": "Página",
    "outline.happy": "¿Satisfecho con el esquema?",
    "outline.happyDesc": "Puedes editar el texto de cualquier página arriba. Cuando estés listo, ¡haz clic en siguiente para generar las ilustraciones!",
    "outline.sceneDescription": "Descripción de la escena",
    "outline.whatYouWillSee": "Lo que verás en esta página",
    "preview.title": "Revisa Tu Historia",
    "preview.subtitle": "¿Todo se ve bien? ¡Creemos tu libro ilustrado!",
    "preview.storyTheme": "Tema de la Historia",
    "preview.yourHeroes": "Tus Héroes",
    "preview.chosenStyle": "Estilo Artístico",
    "preview.ready": "¡Todo se ve genial! ✨",
    "preview.readyDesc": "Crearemos una historia mágica con {{names}} en estilo {{style}}.",
    "pdfPreview.bookReady": "¡Tu libro está listo!",
    "pdfPreview.finishingIllustrations": "Terminando ilustraciones...",
    "pdfPreview.reviewMessage": "Revisa tu libro y descarga cuando estés listo.",
    "pdfPreview.preparing": "Preparando...",
    "pdfPreview.happyWithBook": "¡Estoy satisfecho con mi libro!",
    "pdfPreview.paymentComplete": "¡Pago completado!",
    "pdfPreview.generatingPdf": "Generando PDF...",
    "pdfPreview.downloadPdf": "Descargar PDF",
    "pdfPreview.loveIt": "¿Te gusta? ¡Obtén tu copia!",
    "pdfPreview.processing": "Procesando...",
    "pdfPreview.digitalPdf": "PDF Digital",
    "pdfPreview.printedBook": "Libro Impreso",
    "pdfPreview.pdfDownloadShortly": "Tu PDF se descargará en breve.",
    "pdfPreview.tryAgainMoment": "Por favor intenta de nuevo en un momento.",
    "pdfPreview.paymentSuccess": "¡Pago exitoso!",
    "pdfPreview.pdfReady": "\"{{title}}\" está listo para descargar",
    "pdfPreview.readyNextBook": "¡Listo para tu próximo libro!",
    "pdfPreview.canCreateNew": "Puedes crear un nuevo libro en cualquier momento.",
    "nav.back": "Atrás",
    "nav.next": "Siguiente",
    "nav.generateIllustrations": "Generar Ilustraciones",
    "download.pdf": "Descargar PDF",
    "complete.title": "¡Tu Libro está Listo! 🎉",
    "complete.subtitle": "¡Un libro mágico con {{names}} ha sido creado!",
    "complete.download": "Descargar PDF",
    "complete.startOver": "Crear Otro Libro",
    "generating.preparingStory": "Preparando tu historia...",
    "generating.creatingIllustrations": "Creando ilustraciones...",
    "generating.creatingPreviewIllustrations": "Creando ilustraciones de vista previa...",
    "generating.creatingCover": "Creando portada...",
    "generating.coverCreated": "¡Portada creada!",
    "generating.backCoverCreated": "¡Contraportada creada!",
    "finalize.paymentConfirmed": "¡Pago confirmado!",
    "finalize.generatingStory": "Generando tu historia completa...",
    "finalize.creatingIllustrations": "Creando ilustraciones...",
    "finalize.addingFinishingTouches": "Toques finales...",
    "finalize.creatingBackCover": "Creando contraportada...",
    "finalize.bookReadyReview": "¡Tu libro está listo para revisar!",
    "finalize.preparingPdf": "Preparando tu PDF...",
    "resume.loadingBook": "Cargando tu libro...",
    "resume.stillGenerating": "¡Tu libro aún se está creando!",
    "resume.stillGeneratingDesc": "Tus ilustraciones aún se están generando. Recibirás un correo cuando esté listo.",
    "resume.checkBackLater": "Puedes cerrar esta página y volver más tarde.",
    "illustration.noRedosLeft": "Sin rehacer disponibles",
    "illustration.limitReached": "Has usado todas tus repeticiones de ilustración. Compra más para continuar.",
    "toast.paymentCancelled": "Pago Cancelado",
    "toast.tryAgainReady": "Tu libro te espera. ¡Inténtalo cuando quieras!",
    "toast.redoCreditsAdded": "¡Créditos de Repetición Agregados!",
    "toast.threeMoreRedos": "Ahora tienes 3 repeticiones de ilustración más.",
    "toast.paymentSuccessful": "¡Pago Exitoso!",
    "toast.pdfReadyDownload": "Tu PDF está listo para descargar.",
    "toast.downloadStarted": "Descarga Iniciada",
    "toast.couldntDownload": "No se pudo Descargar",
    "toast.failedGenerate": "Error en la generación. Por favor intenta de nuevo.",
    "toast.pleaseRetry": "Por favor intenta de nuevo.",
    "toast.storyCreated": "¡Historia Creada!",
    "toast.previewStory": "Vista previa de tu libro abajo.",
    "toast.fillIllustrations": "Generando ilustraciones restantes...",
    "toast.generationFailed": "Error de Generación",
    "toast.generationTimeout": "La generación expiró. Por favor intenta de nuevo.",
    "toast.paymentFailed": "Error de Pago",
    "toast.somethingWrong": "Algo Salió Mal",
    "toast.createBookAgain": "Por favor intenta crear tu libro de nuevo.",
    "toast.errorGenerating": "Hubo un error al generar tu libro.",
    "toast.printOrderConfirmed": "¡Orden de Impresión Confirmada!",
    "toast.bookPrintedShipped": "Tu libro será impreso y enviado pronto.",
    "toast.paymentSuccessContact": "Pago exitoso. Contacta soporte si es necesario.",
    "toast.preparingPrintFiles": "Preparando Archivos de Impresión",
    "toast.readyForPrinting": "Preparando tu libro para impresión...",
    "toast.orderFailed": "Error de Pedido",
    "toast.paymentError": "Error de Pago",
    "toast.tryAgainLater": "Por favor intenta más tarde.",
    "toast.coverRegenerated": "¡Portada Regenerada!",
    "toast.newCoverReady": "Tu nueva portada está lista.",
    "toast.pageRegenerated": "¡Página {{page}} Regenerada!",
    "toast.feedbackApplied": "Tus comentarios han sido aplicados.",
    "toast.freshIllustration": "Se ha creado una nueva ilustración.",
    "toast.regenerationFailed": "Error de Regeneración",
    "toast.couldNotRegenerate": "No se pudo regenerar la ilustración. Por favor intenta de nuevo.",
    "toast.backCoverFailed": "Error de Contraportada",
    "toast.backCoverFrontUsed": "Se usará la portada en su lugar.",
    "toast.backCoverReady": "¡Contraportada Lista!",
    "toast.backCoverGenerated": "Tu contraportada ha sido generada.",
    "toast.frontCoverRequired": "Portada Requerida",
    "toast.frontCoverFirst": "Genera primero una portada.",
    "toast.backCoverRegenerated": "¡Contraportada Regenerada!",
    "toast.backCoverFeedbackApplied": "Tus comentarios han sido aplicados a la contraportada.",
    "toast.freshBackCover": "Se ha creado una nueva contraportada.",
    "toast.backCoverRegenerateFailed": "No se pudo regenerar la contraportada.",
    "toast.allIllustrationsComplete": "¡Todas las Ilustraciones Completas!",
    "toast.storybookReady": "Tu libro está completamente ilustrado.",
    "toast.someIllustrationsIncomplete": "Algunas Ilustraciones Incompletas",
    "toast.pagesMissing": "{{count}} páginas aún faltan ilustraciones.",
    "toast.bookNotComplete": "Libro No Completado",
    "toast.generateFirst": "Por favor genera tu historia primero.",
    "toast.preparingBook": "Preparando Tu Libro",
    "toast.uploadingPrint": "Subiendo archivos de impresión...",
    "toast.missingInfoCheckout": "Información faltante para el pago.",
    "toast.failedCheckout": "Error al crear el pago. Por favor intenta de nuevo.",
    "toast.checkEmail": "¡Revisa tu correo para el enlace del PDF!",
    "toast.couldntSendPdf": "No se pudo enviar el PDF. Intenta descargarlo.",
    "toast.emailSent": "¡Correo Enviado!",
    "toast.bookSentTo": "Tu libro ha sido enviado a {{email}}.",
    "toast.generatingPdf": "Generando PDF...",
    "paywall.title": "¡Finaliza el libro de {{names}}!",
    "paywall.subtitle": "¡Tu vista previa está lista! Obtén el PDF ilustrado completo para siempre.",
    "paywall.digitalPdf": "PDF Digital — $9.99",
    "paywall.price": "$9.99",
    "paywall.benefit1": "Libro ilustrado completo (20+ páginas)",
    "paywall.benefit2": "PDF de alta calidad para imprimir en casa",
    "paywall.benefit3": "Descarga instantánea después de la compra",
    "paywall.processing": "Procesando...",
    "paywall.getBook": "Obtener mi libro — $9.99",
    "paywall.stopTitle": "¿Abandonar tu historia?",
    "paywall.stopDesc": "El libro personalizado de {{names}} se perderá si te vas ahora.",
    "paywall.noContinue": "No, continuar",
    "paywall.yesStop": "Sí, salir",
    "upsell.title": "¡Pide un libro impreso!",
    "upsell.subtitle": "¡Ya tienes tu libro digital! ¿Quieres una tapa dura premium en tu puerta?",
    "upsell.specialOffer": "Oferta Especial",
    "upsell.addPrinted": "Agregar libro impreso – $19.99",
    "upsell.shippingNote": "Envío calculado al pagar",
    "upsell.benefit1": "Libro de tapa dura premium",
    "upsell.benefit2": "Calidad de impresión profesional",
    "upsell.benefit3": "Un recuerdo que atesorarán siempre",
    "upsell.benefit4": "Envío mundial disponible",
    "upsell.addButton": "Agregar copia impresa – $19.99",
    "upsell.noThanks": "No gracias, me quedo con mi copia digital",

    // Style Cards
    "styleCard.pixar.name": "Estilo Pixar",
    "styleCard.pixar.desc": "Estilo de dibujos animados 3D inspirado en películas animadas",
    "styleCard.watercolor.name": "Acuarela Clásica",
    "styleCard.watercolor.desc": "Ilustraciones de acuarela suaves y soñadoras",
    "styleCard.anime.name": "Estilo Anime",
    "styleCard.anime.desc": "Arte vibrante inspirado en el anime japonés",
    "generating.title": "Creando Tu Historia...",
    "generating.subtitle": "¡Estamos creando una aventura mágica solo para ti!",
    "generating.step.preparingStory": "Preparando tu historia...",
    "generating.step.creatingIllustrations": "Creando ilustraciones...",
    "generating.step.addingDetails": "Añadiendo detalles...",
    "generating.step.finishingTouches": "Toques finales...",
    "finalizing.title": "Finalizando Tu Libro...",
    "finalizing.subtitle": "Creando el libro personalizado de {{names}}",
    "finalizing.step1": "Configurando tu historia...",
    "finalizing.step2": "Generando el texto...",
    "finalizing.step3": "Creando ilustraciones...",
    "finalizing.step4": "Añadiendo toques finales...",
    "finalizing.step5": "Preparando tu libro...",
    "finalizing.illustrations": "{{completed}} de {{total}} ilustraciones completas",
    "finalizing.percentComplete": "{{percent}}% completo",
    "finalizing.emailWhenReady": "Envíame un email cuando esté listo",
    "finalizing.emailWhenReadyHint": "Terminaremos en segundo plano y te enviaremos un email cuando tu libro esté listo.",
    "finalizing.safeToClose": "Tu libro se está creando en nuestros servidores. Puedes cerrar esta pestaña con tranquilidad — seguiremos trabajando y te enviaremos un email cuando esté listo.",
    "finalizing.emailConfirmTitle": "¡Todo listo!",
    "finalizing.emailConfirmSubtitle": "Puedes cerrar esta página.",
    "finalizing.emailConfirmDesc": "Continuaremos generando tu libro en segundo plano y te enviaremos un email con un enlace de descarga cuando esté listo.",
    "finalizing.emailConfirmNote": "Revisa tu bandeja de entrada en unos minutos.",
    "toast.backgroundStarted": "Generación en segundo plano iniciada",
    "toast.backgroundStartedDesc": "¡Te enviaremos un email cuando tu libro esté listo!",
    "toast.backgroundFailed": "No se pudo iniciar la generación en segundo plano. Mantén esta página abierta.",
    "pdf.adventure": "Una aventura personalizada",
    "pdf.theEnd": "Fin",
    "pdf.storyFor": "Una historia para",
    "backCover.title": "Contraportada",
    "illustration.regenerating": "Regenerando ilustración...",
    "illustration.redoCover": "Rehacer Portada",
    "illustration.redoPage": "Rehacer Página",
    "illustration.redo": "Rehacer",
    "illustration.buyMoreDescription": "¿Necesitas más cambios? Obtén 3 reintentos adicionales.",
    "illustration.buyMore": "Comprar 3 Reintentos",
    "illustration.lastRedo": "¡Este es tu último reintento! Aprovéchalo.",
    "illustration.placeholder": "Describe lo que te gustaría cambiar...",
    "illustration.applyChanges": "Aplicar Cambios",
    "styleEdit.edit": "Editar",
    "styleEdit.placeholder": "Describe cómo te gustaría cambiar este personaje...",
    "styleEdit.apply": "Aplicar Cambios",
    "thankYou.title": "¡Gracias!",
    "thankYou.subtitle": "¡La aventura de {{names}} está lista!",
    "thankYou.emailResent": "¡Correo reenviado!",
    "thankYou.emailFailed": "Error de correo",
    "thankYou.emailSent": "¡Correo enviado!",
    "thankYou.emailPending": "Enviando correo...",
    "thankYou.resending": "Reenviando...",
    "thankYou.resendEmail": "Reenviar Correo",
    "thankYou.emailFailedDesc": "No pudimos enviar el correo a {{email}}. Intenta descargarlo.",
    "thankYou.emailDesc": "Tu libro ha sido enviado a {{email}}.",
    "thankYou.emailPendingDesc": "Estamos enviando tu libro a {{email}}...",
    "thankYou.printOrdered": "¡Copia impresa pedida!",
    "thankYou.printDesc": "Tu libro impreso será enviado pronto.",
    "thankYou.generatingPdf": "Generando PDF...",
    "thankYou.finishingIllustrations": "Terminando ilustraciones...",
    "thankYou.downloadBook": "Descargar Libro",
    "thankYou.createAnother": "Crear Otro Libro",
    "thankYou.hopeLove": "¡Esperamos que {{names}} ame su historia!",
    "thankYou.finalizing": "Estamos finalizando tu libro...",
    "thankYou.emailWhenReady": "Lo recibirás por correo en {{email}}",
    "thankYou.doNotClose": "Por favor, no cierres esta ventana mientras preparamos tu libro",
    "thankYou.bookOnItsWay": "¡Tu libro está en camino!",
    "thankYou.sentTo": "Enviado a {{email}}",
    "email.getBook": "Obtén Tu Libro",
    "email.sendToEmail": "Enviaremos tu libro a tu correo",
    "email.processingPayment": "Procesando tu pago...",
    "email.pleaseWait": "Por favor espera, no cierres esta ventana.",
    "email.bookSentAfterPayment": "Tu libro será enviado a tu correo después del pago.",
    "email.yourName": "Tu Nombre",
    "email.namePlaceholder": "Ingresa tu nombre",
    "email.emailAddress": "Correo Electrónico",
    "email.emailPlaceholder": "Ingresa tu correo",
    "email.marketingConsent": "Me gustaría recibir novedades sobre nuevas funciones e historias",
    "email.continuePayment": "Continuar al Pago",
    "email.redirect": "Serás redirigido a nuestra página de pago seguro",
    "email.stepPreparing": "Preparando tu libro...",
    "email.stepCreating": "Creando pago...",
    "email.stepRedirecting": "Redirigiendo al pago...",
    "printPayment.title": "Preparando tu pedido",
    "printPayment.subtitle": "Configurando el pago de tu libro impreso",
    "printPayment.stepUploading": "Preparando archivos de impresión...",
    "printPayment.stepCreating": "Creando pago...",
    "printPayment.stepRedirecting": "Abriendo pago...",
    "maintenance.title": "¡Estamos Creando Magia!",
    "maintenance.crafting": "Estamos preparando algo increíble. ¡Vuelve pronto!",
    "maintenance.devAccess": "Acceso de Desarrollador",
    "maintenance.enterPassword": "Ingresa la contraseña",
    "maintenance.accessSite": "Acceder al Sitio",
    "maintenance.incorrectPassword": "Contraseña incorrecta",
    "maintenance.comingSoon": "¡Volvemos pronto!",
    "contact.title": "Contáctanos",
    "contact.subtitle": "¿Tienes alguna pregunta? ¡Nos encantaría saber de ti!",
    "contact.emailUs": "Envíanos un Correo",
    "contact.responseTime": "Tiempo de Respuesta",
    "contact.within24": "En 24 horas",
    "contact.liveChat": "Chat en Vivo",
    "contact.chatHours": "Lun-Vie, 9am-5pm",
    "contact.sendMessage": "Envíanos un Mensaje",
    "contact.yourName": "Tu Nombre",
    "contact.namePlaceholder": "Juan Pérez",
    "contact.email": "Correo",
    "contact.emailPlaceholder": "juan@ejemplo.com",
    "contact.subject": "Asunto",
    "contact.subjectPlaceholder": "¿Cómo podemos ayudarte?",
    "contact.message": "Mensaje",
    "contact.messagePlaceholder": "Cuéntanos más...",
    "contact.sending": "Enviando...",
    "contact.send": "Enviar Mensaje",
    "contact.sent": "¡Mensaje enviado! Te responderemos pronto.",
    "contact.faq": "Preguntas Frecuentes",
    "contact.faq1.q": "¿Cuánto tiempo tarda en crearse un libro?",
    "contact.faq1.a": "Tu libro personalizado se genera en pocos minutos usando nuestra tecnología avanzada.",
    "contact.faq2.q": "¿Qué pasa con mis fotos?",
    "contact.faq2.a": "Tus fotos solo se usan para generar ilustraciones y se eliminan automáticamente después de crear tu libro.",
    "contact.faq3.q": "¿Puedo obtener una copia impresa?",
    "contact.faq3.a": "¡Sí! Puedes pedir una copia impresa premium en tapa dura entregada en tu puerta.",
    "notFound.title": "404",
    "notFound.message": "¡Ups! Esta página no existe.",
    "notFound.backHome": "Volver al inicio",
    "payment.orderConfirmed": "¡Pedido Confirmado!",
    "payment.printDelivery": "Tu libro impreso será enviado pronto.",
    "payment.orderIssue": "Problema con el Pedido",
    "payment.issueDesc": "Hubo un problema al procesar tu pedido. Por favor contacta soporte.",
    "payment.downloadReady": "¡Descarga Lista!",
    "payment.digitalDelivery": "Tu libro digital está listo para descargar.",
    "payment.submitting": "Procesando Tu Pedido...",
    "payment.paymentSuccess": "¡Tu pago fue exitoso! Configurando tu pedido...",
    "payment.contactSupport": "Por favor contacta soporte con tu ID de sesión:",
    "payment.backHome": "Volver al Inicio",
    "payment.digitalThankYou": "¡Gracias por tu compra! Tu libro digital está listo.",
    "payment.printThankYou": "¡Gracias por tu compra! Tu libro impreso será enviado pronto.",
    "payment.orderDetails": "Detalles del Pedido",
    "payment.bookTitle": "Libro Personalizado",
    "payment.orderId": "ID de Pedido:",
    "payment.createAnother": "Crear Otro Libro",
    "shipping.title": "Dirección de Envío",
    "shipping.for": "Para:",
    "shipping.fullName": "Nombre Completo",
    "shipping.nameRequired": "El nombre es requerido",
    "shipping.emailRequired": "El correo es requerido",
    "shipping.emailInvalid": "Por favor ingresa un correo válido",
    "shipping.streetRequired": "La dirección es requerida",
    "shipping.cityRequired": "La ciudad es requerida",
    "shipping.stateRequired": "El estado es requerido",
    "shipping.postalRequired": "El código postal es requerido",
    "shipping.phoneRequired": "El teléfono es requerido",
    "shipping.email": "Correo Electrónico",
    "shipping.streetAddress": "Dirección",
    "shipping.apartment": "Apartamento / Suite",
    "shipping.city": "Ciudad",
    "shipping.postalCode": "Código Postal",
    "shipping.country": "País",
    "shipping.stateProvince": "Estado / Provincia",
    "shipping.optional": "Opcional",
    "shipping.phoneNumber": "Número de Teléfono",
    "shipping.phoneHint": "Requerido para notificaciones de entrega",
    "shipping.cancel": "Cancelar",
    "shipping.continuePayment": "Continuar al Pago",
    "shipping.processing": "Procesando...",
    "shipping.state": "Estado",
    "shipping.selectCountry": "Seleccionar país",
    "shipping.selectState": "Seleccionar estado",
    "shipping.deliveryOptions": "Opciones de Envío",
    "shipping.chooseDelivery": "Elegir Envío",
    "shipping.loadingOptions": "Cargando opciones...",
    "shipping.selectDeliverySpeed": "Elige tu velocidad de envío preferida:",
    "shipping.businessDays": "días hábiles",
    "shipping.estimateUnavailable": "Estimación no disponible",
    "shipping.back": "Volver",
    "shipping.included": "Incluido",
    "pdfPreview.printComingSoon": "¡Próximamente!",
    "queue.position": "Posición en la cola: {{position}}",
    "queue.waiting": "Esperando en la cola...",

    // Order Tracking
    "trackOrder.title": "Rastrear Mi Pedido",
    "trackOrder.subtitle": "Ingresa tu email para ver el historial de pedidos",
    "trackOrder.emailPlaceholder": "tu@email.com",
    "trackOrder.search": "Buscar",
    "trackOrder.error": "Algo salió mal. Inténtalo de nuevo.",
    "trackOrder.noOrders": "Sin Pedidos",
    "trackOrder.noOrdersDesc": "No encontramos pedidos con este email.",
    "trackOrder.createBook": "Crea Tu Primer Libro",
    "trackOrder.foundOrders": "{{count}} pedido(s) encontrado(s)",
    "trackOrder.digital": "PDF Digital",
    "trackOrder.printed": "Libro Impreso",
    "trackOrder.status.pending": "Pendiente",
    "trackOrder.status.paid": "Pagado",
    "trackOrder.status.fulfilled": "Entregado",
    "trackOrder.status.shipped": "Enviado",
    "footer.myBooks": "Mi Cuenta",
    "auth.loginTitle": "Bienvenido de Nuevo",
    "auth.loginSubtitle": "Inicia sesión para acceder a tus libros",
    "auth.signupTitle": "Crear Cuenta",
    "auth.signupSubtitle": "Regístrate para guardar tus libros y personajes",
    "auth.forgotTitle": "Restablecer Contraseña",
    "auth.forgotSubtitle": "Introduce tu email para recibir un enlace de restablecimiento",
    "auth.displayName": "Nombre",
    "auth.email": "Email",
    "auth.password": "Contraseña",
    "auth.loginButton": "Iniciar Sesión",
    "auth.signupButton": "Crear Cuenta",
    "auth.resetButton": "Enviar Enlace",
    "auth.forgotPassword": "¿Olvidaste tu contraseña?",
    "auth.noAccount": "¿No tienes cuenta?",
    "auth.signupLink": "Regístrate",
    "auth.hasAccount": "¿Ya tienes cuenta?",
    "auth.loginLink": "Inicia sesión",
    "auth.backToLogin": "Volver al inicio de sesión",
    "auth.error": "Error",
    "auth.errorDesc": "Algo salió mal. Inténtalo de nuevo.",
    "auth.welcomeTitle": "¡Bienvenido!",
    "auth.welcomeDesc": "Tu cuenta ha sido creada exitosamente.",
    "auth.resetSent": "Enlace Enviado",
    "auth.resetSentDesc": "Revisa tu email para el enlace de restablecimiento.",
    "dashboard.title": "Mi Panel",
    "dashboard.subtitle": "Gestiona tus libros y personajes",
    "dashboard.logout": "Cerrar Sesión",
    "dashboard.newBook": "Nuevo Libro",
    "dashboard.myBooks": "Mis Libros",
    "dashboard.myKids": "Mis Personajes",
    "dashboard.inProgress": "En Progreso",
    "dashboard.digital": "Digital",
    "dashboard.printed": "Impreso",
    "dashboard.noInProgress": "No hay libros en progreso",
    "dashboard.noDigital": "No hay libros digitales",
    "dashboard.noPrinted": "No hay libros impresos",
    "dashboard.createFirst": "Crea Tu Primer Libro",
    "dashboard.untitled": "Libro Sin Título",
    "dashboard.addChild": "Añadir Niño",
    "dashboard.editChild": "Editar Niño",
    "dashboard.addFirstChild": "Añade Tu Primer Niño",
    "dashboard.noKids": "No hay personajes guardados",
    "dashboard.childFormDesc": "Guarda la info de tu hijo para crear libros rápidamente",
    "dashboard.photo": "Foto",
    "dashboard.yearsOld": "años",
    "dashboard.savedStyles": "Estilos guardados:",
    "dashboard.save": "Guardar",
    "dashboard.childAdded": "¡Personaje añadido!",
    "dashboard.childUpdated": "¡Personaje actualizado!",
    "dashboard.childRemoved": "Personaje eliminado",
    "dashboard.profile": "Perfil",
    "dashboard.personalInfo": "Información personal",
    "dashboard.fullName": "Nombre completo",
    "dashboard.fullNamePlaceholder": "Ingresa tu nombre completo",
    "dashboard.email": "Correo electrónico",
    "dashboard.phone": "Número de teléfono",
    "dashboard.phonePlaceholder": "Ingresa tu número de teléfono",
    "dashboard.shippingAddress": "Dirección de envío",
    "dashboard.addressLine1": "Dirección línea 1",
    "dashboard.addressLine1Placeholder": "Dirección",
    "dashboard.addressLine2": "Dirección línea 2",
    "dashboard.addressLine2Placeholder": "Apartamento, suite, etc. (opcional)",
    "dashboard.city": "Ciudad",
    "dashboard.cityPlaceholder": "Ciudad",
    "dashboard.state": "Estado / Provincia",
    "dashboard.statePlaceholder": "Estado / Provincia",
    "dashboard.postalCode": "Código postal",
    "dashboard.postalCodePlaceholder": "Código postal",
    "dashboard.country": "País",
    "dashboard.countryPlaceholder": "País",
    "dashboard.saveProfile": "Guardar perfil",
    "dashboard.saved": "¡Guardado!",
    "dashboard.profileSaved": "¡Perfil guardado!",
    "dashboard.profileSaveError": "Error al guardar el perfil",
    "dashboard.confirmDelete": "¿Eliminar personaje?",
    "dashboard.confirmDeleteDesc": "Esto eliminará permanentemente este personaje y sus estilos guardados.",
    "dashboard.delete": "Eliminar",
    "dashboard.generateStyle": "Generar Estilo",
    "dashboard.regenerateStyle": "Regenerar",
    "dashboard.selectStyle": "Seleccionar estilo artístico",
    "dashboard.generatingStyle": "Generando estilo...",
    "dashboard.styleGenerated": "¡Estilo generado!",
    "dashboard.needsPhoto": "Sube una foto primero",
    "dashboard.continueBook": "Continuar",
    "dashboard.downloadPdf": "Descargar",
    "dashboard.trackOrder": "Rastrear",
    "dashboard.statusPending": "En Progreso",
    "dashboard.statusGenerating": "Generando",
    "dashboard.statusProcessing": "Procesando",
    "dashboard.statusPaid": "Pagado",
    "dashboard.statusCompleted": "Completado",
    "dashboard.statusFulfilled": "Entregado",
    "dashboard.statusShipped": "Enviado",
    "dashboard.buyHardCopy": "Comprar Copia Impresa",
    "thankYou.illustrationProgress": "Ilustraciones: {{completed}} / {{total}}",

    // Theme Categories
    "category.friendship": "Amistad",
    "category.growingUp": "Crecer",
    "category.kindness": "Bondad y Emociones",
    "category.values": "Valores",
    "category.adventure": "Aventura",
    "category.fantasy": "Fantasía",
    "category.humor": "Humor",
    "category.learning": "Aprendizaje",
    "category.diversity": "Diversidad e Inclusión",
    "category.nature": "Naturaleza y Medio Ambiente",
    "category.problemSolving": "Resolución de Problemas",
    "category.lifeExperiences": "Experiencias de Vida",
    "category.cultural": "Historias Culturales",

    // Theme Titles & Descriptions
    "theme.makingFriends.title": "Hacer Amigos",
    "theme.makingFriends.desc": "Una historia sobre hacer nuevos amigos y la alegría de la conexión",
    "theme.bestFriends.title": "Mejores Amigos",
    "theme.bestFriends.desc": "Una aventura que celebra el vínculo entre mejores amigos",
    "theme.sharing.title": "Compartir",
    "theme.sharing.desc": "Aprender la felicidad que trae compartir con otros",
    "theme.siblings.title": "Hermanos",
    "theme.siblings.desc": "Un cuento conmovedor sobre el vínculo especial entre hermanos",
    "theme.sayingSorry.title": "Pedir Perdón",
    "theme.sayingSorry.desc": "Aprender cuándo y cómo disculparse y arreglar las cosas",
    "theme.beingYourself.title": "Ser Tú Mismo",
    "theme.beingYourself.desc": "Descubrir la magia de ser fiel a quien eres",
    "theme.tryingNew.title": "Probar Cosas Nuevas",
    "theme.tryingNew.desc": "Un emocionante viaje fuera de tu zona de confort",
    "theme.overcomingFear.title": "Superar el Miedo",
    "theme.overcomingFear.desc": "Una valiente aventura para enfrentar y vencer los miedos",
    "theme.findingTalent.title": "Encontrar Tu Talento",
    "theme.findingTalent.desc": "Descubrir un talento especial que te hace único",
    "theme.confidence.title": "Confianza",
    "theme.confidence.desc": "Construir la confianza en uno mismo y creer en ti",
    "theme.understandingFeelings.title": "Comprender los Sentimientos",
    "theme.understandingFeelings.desc": "Aprender a reconocer y comprender las emociones",
    "theme.helpingOthers.title": "Ayudar a los Demás",
    "theme.helpingOthers.desc": "La alegría y recompensa de echar una mano",
    "theme.dealingAnger.title": "Manejar el Enojo",
    "theme.dealingAnger.desc": "Formas saludables de manejar y expresar el enojo",
    "theme.copingSadness.title": "Superar la Tristeza",
    "theme.copingSadness.desc": "Encontrar consuelo y esperanza en momentos tristes",
    "theme.showingEmpathy.title": "Mostrar Empatía",
    "theme.showingEmpathy.desc": "Comprender y preocuparse por cómo se sienten los demás",
    "theme.honesty.title": "Honestidad",
    "theme.honesty.desc": "La importancia de decir la verdad",
    "theme.responsibility.title": "Responsabilidad",
    "theme.responsibility.desc": "Aprender a asumir la responsabilidad de tus actos",
    "theme.respect.title": "Respeto",
    "theme.respect.desc": "Tratar a los demás con amabilidad y respeto",
    "theme.perseverance.title": "Perseverancia",
    "theme.perseverance.desc": "Nunca rendirse aunque las cosas se pongan difíciles",
    "theme.goodChoices.title": "Tomar Buenas Decisiones",
    "theme.goodChoices.desc": "Aprender a tomar decisiones sabias",
    "theme.jungleAdventure.title": "Aventura en la Selva",
    "theme.jungleAdventure.desc": "Una emocionante expedición por una selva salvaje",
    "theme.desertJourney.title": "Viaje por el Desierto",
    "theme.desertJourney.desc": "Un emocionante viaje a través de un vasto desierto",
    "theme.oceanQuest.title": "Búsqueda Oceánica",
    "theme.oceanQuest.desc": "Sumergiéndose en una aventura submarina",
    "theme.spaceExploration.title": "Exploración Espacial",
    "theme.spaceExploration.desc": "Despegar en una increíble aventura espacial",
    "theme.mountainExpedition.title": "Expedición de Montaña",
    "theme.mountainExpedition.desc": "Escalar hasta la cima de una magnífica montaña",
    "theme.magicalWorlds.title": "Mundos Mágicos",
    "theme.magicalWorlds.desc": "Explorar tierras encantadas llenas de maravillas",
    "theme.talkingAnimals.title": "Animales que Hablan",
    "theme.talkingAnimals.desc": "Aventuras con animales que pueden hablar",
    "theme.wizardsSpells.title": "Magos y Hechizos",
    "theme.wizardsSpells.desc": "Aprender magia de sabios magos",
    "theme.mythicalCreatures.title": "Criaturas Míticas",
    "theme.mythicalCreatures.desc": "Conocer dragones, unicornios y otros seres mágicos",
    "theme.timeTravel.title": "Viaje en el Tiempo",
    "theme.timeTravel.desc": "Viajar a través del tiempo a épocas increíbles",
    "theme.sillyAnimals.title": "Animales Graciosos",
    "theme.sillyAnimals.desc": "Aventuras hilarantes con animales divertidos",
    "theme.wordplayJokes.title": "Juegos de Palabras y Chistes",
    "theme.wordplayJokes.desc": "Una historia llena de chistes y risas",
    "theme.absurdSituations.title": "Situaciones Absurdas",
    "theme.absurdSituations.desc": "Aventuras locas y maravillosamente absurdas",
    "theme.mischievous.title": "Aventuras Traviesas",
    "theme.mischievous.desc": "Bromas juguetonas y diversión traviesa",
    "theme.unexpectedSurprises.title": "Sorpresas Inesperadas",
    "theme.unexpectedSurprises.desc": "Una historia llena de giros y sorpresas encantadoras",
    "theme.numbersCounting.title": "Números y Conteo",
    "theme.numbersCounting.desc": "Una aventura divertida con números y conteo",
    "theme.lettersReading.title": "Letras y Lectura",
    "theme.lettersReading.desc": "Descubrir la magia de las letras y las palabras",
    "theme.scienceExperiments.title": "Experimentos Científicos",
    "theme.scienceExperiments.desc": "Descubrimientos emocionantes a través de la ciencia",
    "theme.history.title": "Historia",
    "theme.history.desc": "Viajar al pasado para aprender sobre la historia",
    "theme.problemSolvingSkills.title": "Resolución de Problemas",
    "theme.problemSolvingSkills.desc": "Usar la creatividad y la lógica para resolver problemas",
    "theme.differentCultures.title": "Diferentes Culturas",
    "theme.differentCultures.desc": "Explorar y celebrar culturas de todo el mundo",
    "theme.differentAbilities.title": "Diferentes Capacidades",
    "theme.differentAbilities.desc": "Celebrar lo que hace a cada persona única",
    "theme.familyDiversity.title": "Diversidad Familiar",
    "theme.familyDiversity.desc": "Todo tipo de familias y el amor que comparten",
    "theme.genderExpression.title": "Expresión de Género",
    "theme.genderExpression.desc": "Ser libre de expresar quién eres realmente",
    "theme.acceptanceBelonging.title": "Aceptación y Pertenencia",
    "theme.acceptanceBelonging.desc": "Encontrar tu lugar y ser aceptado",
    "theme.forestsTrees.title": "Bosques y Árboles",
    "theme.forestsTrees.desc": "Aventuras en bosques mágicos y árboles antiguos",
    "theme.oceansSealife.title": "Océanos y Vida Marina",
    "theme.oceansSealife.desc": "Descubrir las maravillas bajo las olas",
    "theme.animalsHabitats.title": "Animales y Hábitats",
    "theme.animalsHabitats.desc": "Aprender sobre los animales y dónde viven",
    "theme.weatherSeasons.title": "Clima y Estaciones",
    "theme.weatherSeasons.desc": "Explorar la magia de las estaciones cambiantes",
    "theme.protectingPlanet.title": "Proteger el Planeta",
    "theme.protectingPlanet.desc": "Cuidar de nuestra hermosa Tierra",
    "theme.mysteriesClues.title": "Misterios y Pistas",
    "theme.mysteriesClues.desc": "Resolver misterios emocionantes con pistas ingeniosas",
    "theme.puzzlesRiddles.title": "Rompecabezas y Acertijos",
    "theme.puzzlesRiddles.desc": "Rompecabezas y acertijos ingeniosos",
    "theme.causeEffect.title": "Causa y Efecto",
    "theme.causeEffect.desc": "Comprender cómo las acciones llevan a resultados",
    "theme.teamProblemSolving.title": "Resolución en Equipo",
    "theme.teamProblemSolving.desc": "Trabajar juntos para resolver grandes desafíos",
    "theme.creativeSolutions.title": "Soluciones Creativas",
    "theme.creativeSolutions.desc": "Pensar fuera de la caja para encontrar respuestas",
    "theme.startingSchool.title": "Empezar la Escuela",
    "theme.startingSchool.desc": "La emocionante aventura de empezar la escuela",
    "theme.movingHome.title": "Mudarse de Casa",
    "theme.movingHome.desc": "Adaptarse a un nuevo hogar y vecindario",
    "theme.losingPet.title": "Perder una Mascota",
    "theme.losingPet.desc": "Afrontar la pérdida de una mascota querida",
    "theme.newSibling.title": "Nuevo Hermano",
    "theme.newSibling.desc": "Dar la bienvenida a un nuevo hermanito o hermanita",
    "theme.dealingChange.title": "Afrontar el Cambio",
    "theme.dealingChange.desc": "Aprender a aceptar y adaptarse a los cambios",
    "theme.fairyTales.title": "Cuentos de Hadas",
    "theme.fairyTales.desc": "Aventuras clásicas de cuentos de hadas reinventadas",
    "theme.folktales.title": "Cuentos Populares",
    "theme.folktales.desc": "Historias tradicionales transmitidas de generación en generación",
    "theme.mythsLegends.title": "Mitos y Leyendas",
    "theme.mythsLegends.desc": "Relatos épicos de la mitología y las leyendas",
    "theme.fablesMorals.title": "Fábulas y Moralejas",
    "theme.fablesMorals.desc": "Historias con valiosas lecciones de vida",
    "theme.holidayTraditions.title": "Tradiciones Festivas",
    "theme.holidayTraditions.desc": "Celebrar fiestas y tradiciones especiales",
  },
  de: {
    "seo.title": "StoryHeroes – Personalisierte Kinderbücher",
    "seo.description": "Machen Sie Ihr Kind zum Helden eines magischen Abenteuerbuchs. Foto hochladen, Thema wählen und in wenigen Minuten ein personalisiertes PDF erhalten.",
    "hero.badge": "✨ Personalisierte Geschichtenbücher",
    "hero.title": "Machen Sie Ihr Kind zum Geschichtenhelden",
    "hero.title.create": "Erstellen Sie",
    "hero.title.magical": "Magische Geschichten",
    "hero.title.starring": "Mit Ihrem Kind in der Hauptrolle",
    "hero.subtitle": "Laden Sie ein Foto hoch, wählen Sie ein Abenteuer und erhalten Sie ein wunderschön illustriertes PDF oder Hardcover-Buch in wenigen Minuten.",
    "hero.cta": "Erstellen Sie Ihre Geschichte",
    "hero.freePreview": "Erstellen und Vorschau Ihres Buches kostenlos",
    "hero.privacyStamp": "🔒 Fotos nach Erstellung gelöscht",
    "hero.themes": "50+ Themen",
    "hero.aiArt": "Individuelle Illustrationen",
    "hero.pdfDownload": "Sofortiges PDF",
    "hero.features.personalized": "Personalisiert",
    "hero.features.ai": "Individuelle Kunst",
    "hero.features.instant": "Sofortiges PDF",
    "transform.badge": "✨ Erleben Sie die Magie",
    "transform.title": "Vom Foto zum Buchcharakter",
    "transform.subtitle": "Wir verwandeln das Foto Ihres Kindes in eine wunderschöne illustrierte Figur",
    "transform.pixar": "Pixar-Stil",
    "transform.watercolor": "Aquarell-Stil",
    "transform.anime": "Anime-Stil",
    "showcase.title": "Wunderschöne Bücher, Digital & Gedruckt",
    "showcase.subtitle": "Erhalten Sie Ihr personalisiertes Buch als digitales PDF oder als gedrucktes Hardcover",
    "showcase.pdfTitle": "Digitales PDF",
    "showcase.pdfDesc": "Sofortiger Download, auf jedem Gerät lesbar",
    "showcase.printTitle": "Gedrucktes Buch",
    "showcase.printDesc": "Premium Hardcover direkt an Ihre Tür geliefert",
    "showcase.pages": "32 Seiten",
    "showcase.popular": "Am Beliebtesten",
    "reviews.badge": "⭐ Von Eltern geliebt",
    "reviews.title": "Was Eltern Sagen",
    "reviews.subtitle": "Schließen Sie sich Tausenden glücklicher Familien an, die magische Geschichten erstellt haben",
    "reviews.child": "Alter des Kindes:",
    "reviews.1.text": "Meine Tochter war begeistert, sich als Heldin zu sehen! Die Illustrationen sind atemberaubend und die Geschichte war perfekt für ihr Alter.",
    "reviews.1.childAge": "5 Jahre",
    "reviews.2.text": "Eine unglaubliche Geschenkidee! Mein Sohn liest seine Geschichte jeden Abend vor dem Schlafengehen. Die PDF-Qualität ist erstaunlich.",
    "reviews.2.childAge": "4 Jahre",
    "reviews.3.text": "Ich war von der Qualität überwältigt. Die Ähnlichkeit meines Kindes wurde perfekt eingefangen. Sehr empfehlenswert!",
    "reviews.3.childAge": "6 Jahre",
    "reviews.4.text": "Wir haben drei Bücher bestellt, eins für jedes unserer Kinder. Sie streiten sich, wer seins zuerst lesen darf!",
    "reviews.4.childAge": "3, 5, 7 Jahre",
    "reviews.5.text": "Der Aquarell-Stil ist absolut wunderschön. Es sieht aus, als hätte es ein professioneller Kinderbuchillustrator gemacht.",
    "reviews.5.childAge": "4 Jahre",
    "reviews.6.text": "Bestes Geburtstagsgeschenk ever! Das Gesicht meines Sohnes, als er sich in der Geschichte sah, war unbezahlbar.",
    "reviews.6.childAge": "6 Jahre",
    "trust.gift.title": "Perfektes Geschenk",
    "trust.gift.desc": "Ein einzigartiges, personalisiertes Geschenk, das Kinder jahrelang schätzen",
    "trust.ai.title": "Atemberaubende Kunst",
    "trust.ai.desc": "Unsere Technologie erstellt atemberaubende Illustrationen im Abbild Ihres Kindes",
    "trust.love.title": "Mit Liebe Gemacht",
    "trust.love.desc": "Jede Geschichte wird mit Sorgfalt erstellt, um Fantasie und Freude zu inspirieren",
    "cta.title": "Bereit, Magie zu Erschaffen?",
    "cta.subtitle": "Machen Sie Ihr Kind zum Helden seines eigenen Abenteuerbuches",
    "cta.button": "Jetzt Erstellen",
    "cta.note": "Kein Konto nötig • In 5 Minuten fertig",
    
    // Language Selector
    "lang.title": "Wählen Sie Ihre Sprache",
    "lang.subtitle": "Wählen Sie Ihre bevorzugte Sprache, um fortzufahren",
    
    // Navigation
    "nav.backToHome": "Zurück zur Startseite",
    
    // Footer
    "footer.copyright": "© 2024 StoryHeroes. Alle Rechte vorbehalten.",
    "footer.privacy": "Datenschutzrichtlinie",
    "footer.terms": "Nutzungsbedingungen",
    "footer.contact": "Kontakt",
    "footer.trackOrder": "Bestellung Verfolgen",
    
    // Privacy Page
    "privacy.title": "Datenschutzrichtlinie",
    "privacy.updated": "Zuletzt aktualisiert: Januar 2024",
    "privacy.section1.title": "1. Informationen, die wir sammeln",
    "privacy.section1.intro": "Wir sammeln die folgenden Informationen, wenn Sie unseren Service nutzen:",
    "privacy.section1.item1": "Fotos, die Sie hochladen, um personalisierte Geschichtenbücher zu erstellen",
    "privacy.section1.item2": "Name und Alter des Kindes für die Personalisierung der Geschichte",
    "privacy.section1.item3": "E-Mail-Adresse für Bestellbestätigung und Lieferung",
    "privacy.section1.item4": "Zahlungsinformationen (sicher verarbeitet über Stripe)",
    "privacy.section2.title": "2. Wie wir Ihre Informationen verwenden",
    "privacy.section2.intro": "Wir verwenden Ihre Informationen, um:",
    "privacy.section2.item1": "Personalisierte Geschichtenbücher zu generieren",
    "privacy.section2.item2": "Zahlungen zu verarbeiten und Ihr PDF-Geschichtenbuch zu liefern",
    "privacy.section2.item3": "Bestellbestätigungen und Updates zu senden",
    "privacy.section2.item4": "Unseren Service und die Benutzererfahrung zu verbessern",
    "privacy.section2.item5": "Gesetzliche Verpflichtungen zu erfüllen",
    "privacy.section3.title": "3. Datenspeicherung und Sicherheit",
    "privacy.section3.text": "Wir nehmen Datensicherheit ernst. Alle hochgeladenen Fotos und persönlichen Informationen werden verschlüsselt und sicher gespeichert. Wir verwenden branchenübliche Sicherheitsmaßnahmen, um Ihre Daten vor unbefugtem Zugriff zu schützen.",
    "privacy.section4.title": "4. Drittanbieter-Dienste",
    "privacy.section4.text": "Wir nutzen vertrauenswürdige Drittanbieter-Dienste, einschließlich Stripe für die Zahlungsabwicklung und spezialisierte Dienste für die Geschichtengenerierung. Diese Dienste haben ihre eigenen Datenschutzrichtlinien und Sicherheitsmaßnahmen.",
    "privacy.section5.title": "5. Datenaufbewahrung",
    "privacy.section5.text": "Wir bewahren Ihre Daten nur so lange auf, wie es zur Bereitstellung unserer Dienste und zur Erfüllung gesetzlicher Verpflichtungen erforderlich ist. Sie können jederzeit die Löschung Ihrer Daten beantragen, indem Sie uns kontaktieren.",
    "privacy.section6.title": "6. Datenschutz für Kinder",
    "privacy.section6.text": "Wir verpflichten uns zum Schutz der Privatsphäre von Kindern. Wir sammeln wissentlich keine persönlichen Informationen von Kindern unter 13 Jahren ohne elterliche Zustimmung. Eltern haben die volle Kontrolle über die Informationen ihres Kindes.",
    "privacy.section7.title": "7. Ihre Rechte",
    "privacy.section7.intro": "Sie haben das Recht:",
    "privacy.section7.item1": "Auf Ihre persönlichen Daten zuzugreifen",
    "privacy.section7.item2": "Die Korrektur ungenauer Daten zu verlangen",
    "privacy.section7.item3": "Die Löschung Ihrer Daten zu verlangen",
    "privacy.section7.item4": "Marketing-Kommunikation abzulehnen",
    "privacy.section8.title": "8. Kontaktieren Sie uns",
    "privacy.section8.text": "Wenn Sie Fragen zu dieser Datenschutzrichtlinie haben, kontaktieren Sie uns bitte über unsere Kontaktseite.",
    
    // Terms Page
    "terms.title": "Nutzungsbedingungen",
    "terms.updated": "Zuletzt aktualisiert: Januar 2024",
    "terms.section1.title": "1. Annahme der Bedingungen",
    "terms.section1.text": "Durch die Nutzung von StoryHeroes stimmen Sie diesen Nutzungsbedingungen zu. Wenn Sie nicht einverstanden sind, nutzen Sie bitte unseren Service nicht.",
    "terms.section2.title": "2. Servicebeschreibung",
    "terms.section2.text": "StoryHeroes bietet personalisierte Geschichtenbücher für Kinder. Wir erstellen individuelle illustrierte Geschichten basierend auf den Fotos und Informationen, die Sie bereitstellen.",
    "terms.section3.title": "3. Benutzerverantwortlichkeiten",
    "terms.section3.intro": "Sie stimmen zu:",
    "terms.section3.item1": "Genaue Informationen bereitzustellen",
    "terms.section3.item2": "Nur Fotos hochzuladen, für die Sie Nutzungsrechte haben",
    "terms.section3.item3": "Den Service nicht für illegale Zwecke zu nutzen",
    "terms.section3.item4": "Keine unangemessenen oder anstößigen Inhalte hochzuladen",
    "terms.section3.item5": "Geistige Eigentumsrechte zu respektieren",
    "terms.section4.title": "4. Zahlung und Rückerstattungen",
    "terms.section4.text": "Alle Zahlungen werden sicher über Stripe abgewickelt. Aufgrund der personalisierten Natur unseres Services sind Rückerstattungen nur bei technischen Problemen verfügbar, die die Lieferung Ihres Geschichtenbuchs verhindern.",
    "terms.section5.title": "5. Geistiges Eigentum",
    "terms.section5.intro": "Bezüglich geistigen Eigentums:",
    "terms.section5.item1": "Sie behalten die Rechte an den Fotos, die Sie hochladen",
    "terms.section5.item2": "Generierte Geschichten und Illustrationen sind nur für den persönlichen Gebrauch",
    "terms.section5.item3": "Sie dürfen generierte Inhalte nicht weiterverkaufen oder verteilen",
    "terms.section5.item4": "StoryHeroes behält die Rechte am Service und der Technologie",
    "terms.section5.item5": "Sie gewähren uns eine Lizenz zur Nutzung hochgeladener Inhalte zur Generierung Ihres Geschichtenbuchs",
    "terms.section6.title": "6. Haftungsbeschränkung",
    "terms.section6.text": "StoryHeroes wird 'wie besehen' ohne Garantien bereitgestellt. Wir haften nicht für indirekte, zufällige oder Folgeschäden, die aus der Nutzung unseres Services entstehen.",
    "terms.section7.title": "7. Inhaltsrichtlinien",
    "terms.section7.text": "Wir behalten uns das Recht vor, den Service zu verweigern oder Inhalte zu entfernen, die gegen unsere Richtlinien verstoßen, einschließlich unangemessener, anstößiger oder urheberrechtlich geschützter Materialien.",
    "terms.section8.title": "8. Änderungen der Bedingungen",
    "terms.section8.text": "Wir können diese Nutzungsbedingungen aktualisieren. Die fortgesetzte Nutzung des Services nach Änderungen stellt die Annahme der neuen Bedingungen dar.",
    "terms.section9.title": "9. Kündigung",
    "terms.section9.text": "Wir behalten uns das Recht vor, den Zugang zu unserem Service bei Verstößen gegen diese Bedingungen oder aus anderen Gründen nach unserem Ermessen zu beenden oder auszusetzen.",
    "terms.section10.title": "10. Kontakt",
    "terms.section10.text": "Bei Fragen zu diesen Nutzungsbedingungen kontaktieren Sie uns bitte über unsere Kontaktseite.",

    // Wizard Steps
    "step.addCharacters": "Charaktere Hinzufügen",
    "step.chooseStory": "Geschichte Wählen",
    "step.pickTitle": "Titel Wählen",
    "step.chooseStyle": "Stil Wählen",
    "step.review": "Überprüfen",
    "step.storyOutline": "Handlung",
    "step.previewPdf": "PDF-Vorschau",
    "photo.title": "Füge Deine Charaktere Hinzu",
    "photo.subtitle": "Lade ein Foto hoch und gib die Details für jeden Charakter deiner Geschichte ein.",
    "photo.privacyNotice": "🔒 Deine Fotos werden nur für Illustrationen verwendet und nach der Erstellung gelöscht.",
    "photo.addPhoto": "Foto Hinzufügen",
    "photo.dragDrop": "oder per Drag & Drop",
    "photo.dropHere": "Hier ablegen!",
    "photo.browse": "Durchsuchen",
    "photo.camera": "Kamera",
    "photo.upload": "Hochladen",
    "photo.name": "Name",
    "photo.namePlaceholder": "Name eingeben",
    "photo.age": "Alter",
    "photo.agePlaceholder": "Alter eingeben",
    "photo.gender": "Charaktertyp",
    "photo.boy": "Junge",
    "photo.girl": "Mädchen",
    "photo.pet": "Haustier",
    "photo.addChild": "Weiteren Charakter Hinzufügen",
    "photo.savedKids": "Deine Gespeicherten Charaktere",
    "familyImport.button": "Aus Gruppenfoto Importieren",
    "style.choose": "Wähle Deinen Kunststil",
    "style.subtitleSelected": "Sieh wie deine {{count}} Charakter{{plural}} in diesem Stil aussehen",
    "style.subtitleUnselected": "Wähle einen Illustrationsstil für dein Buch",
    "style.yourCharactersIn": "Deine Charaktere in {{style}}",
    "style.creatingName": "{{name}} wird erstellt...",
    "style.altCharacterInStyle": "{{name}} im {{style}}-Stil",
    "style.failedShort": "Fehlgeschlagen",
    "style.retry": "Erneut versuchen",
    "style.hoverHint": "Bewege die Maus über Bilder, um sie größer zu sehen",
    "style.preserveHint": "Charakterbeschreibungen werden für Konsistenz beibehalten",
    "style.tryAgain": "Erneut Versuchen",
    "style.continueWith": "Weiter mit {{style}}",
    "style.switchHint": "Du kannst den Stil jederzeit vor dem Fortfahren wechseln",
    "title.choose": "Wähle Deinen Titel",
    "title.subtitle": "Wähle den perfekten Titel für dein personalisiertes Buch",
    "title.creating": "Magische Titel werden erstellt...",
    "title.selected": "Ausgewählt",
    "title.regenerate": "Titel Regenerieren",
    "title.usingSuggested": "Vorgeschlagene Titel",
    "title.pickFavorite": "Wähle deinen Favoriten aus diesen Vorschlägen!",
    "themes.title": "Wähle ein Thema für {{hero}}",
    "themes.subtitle": "Wähle ein Abenteuerthema für deine {{count}} {{characterWord}}. Jedes Thema erstellt einzigartige Geschichten.",
    "themes.changeTheme": "Thema Ändern",
    "themes.whatsStoryAbout": "Worum soll die Geschichte von {{hero}} handeln?",
    "themes.yourIdeas": "Deine Geschichtenideen",
    "themes.ideaHelperText": "Beschreibe das Abenteuer, das du möchtest. Sei so kreativ wie du willst!",
    "themes.ideaPlaceholder": "z.B. Ein tapferer Ritter, der sich mit einem Drachen anfreundet und zusammen ein magisches Königreich retten...",
    "themes.ideaHint": "Guter Anfang! Klicke unten für verbesserte Ideen.",
    "themes.generateRequired": "Schreibe deine eigene Idee oder klicke unten für Inspiration!",
    "themes.generatingIdeas": "Ideen werden generiert...",
    "themes.completeMyIdeas": "Meine Ideen Vervollständigen",
    "themes.inspireMe": "Inspiriere Mich!",
    "themes.chooseIdea": "Wähle eine Geschichtenidee",
    "themes.usingPreMade": "Vorgefertigte Ideen",
    "themes.greatIdeas": "Hier sind einige tolle Geschichtenideen!",
    "outline.title": "Handlung",
    "outline.subtitle": "Überprüfe und passe die Seiten deines Buches an",
    "outline.generating": "Deine Handlung wird erstellt...",
    "outline.titlePage": "Titelseite",
    "outline.ending": "Ende",
    "outline.page": "Seite",
    "outline.happy": "Zufrieden mit der Handlung?",
    "outline.happyDesc": "Du kannst den Text jeder Seite oben bearbeiten. Wenn du bereit bist, klicke auf weiter um Illustrationen zu erstellen!",
    "outline.sceneDescription": "Szenenbeschreibung",
    "outline.whatYouWillSee": "Was du auf dieser Seite sehen wirst",
    "preview.title": "Überprüfe Deine Geschichte",
    "preview.subtitle": "Alles sieht gut aus? Lass uns dein illustriertes Buch erstellen!",
    "preview.storyTheme": "Geschichtenthema",
    "preview.yourHeroes": "Deine Helden",
    "preview.chosenStyle": "Kunststil",
    "preview.ready": "Alles sieht großartig aus! ✨",
    "preview.readyDesc": "Wir erstellen eine magische Geschichte mit {{names}} im {{style}}-Stil.",
    "pdfPreview.bookReady": "Dein Buch ist fertig!",
    "pdfPreview.finishingIllustrations": "Illustrationen werden fertiggestellt...",
    "pdfPreview.reviewMessage": "Überprüfe dein Buch und lade es herunter, wenn du bereit bist.",
    "pdfPreview.preparing": "Wird vorbereitet...",
    "pdfPreview.happyWithBook": "Ich bin zufrieden mit meinem Buch!",
    "pdfPreview.paymentComplete": "Zahlung abgeschlossen!",
    "pdfPreview.generatingPdf": "PDF wird erstellt...",
    "pdfPreview.downloadPdf": "PDF Herunterladen",
    "pdfPreview.loveIt": "Gefällt es dir? Hol dir deine Kopie!",
    "pdfPreview.processing": "Verarbeitung...",
    "pdfPreview.digitalPdf": "Digitales PDF",
    "pdfPreview.printedBook": "Gedrucktes Buch",
    "pdfPreview.pdfDownloadShortly": "Dein PDF wird in Kürze heruntergeladen.",
    "pdfPreview.tryAgainMoment": "Bitte versuche es in einem Moment erneut.",
    "pdfPreview.paymentSuccess": "Zahlung erfolgreich!",
    "pdfPreview.pdfReady": "\"{{title}}\" ist bereit zum Download",
    "pdfPreview.readyNextBook": "Bereit für dein nächstes Buch!",
    "pdfPreview.canCreateNew": "Du kannst jederzeit ein neues Buch erstellen.",
    "nav.back": "Zurück",
    "nav.next": "Weiter",
    "nav.generateIllustrations": "Illustrationen Erstellen",
    "download.pdf": "PDF Herunterladen",
    "complete.title": "Dein Buch ist Fertig! 🎉",
    "complete.subtitle": "Ein magisches Buch mit {{names}} wurde erstellt!",
    "complete.download": "PDF Herunterladen",
    "complete.startOver": "Ein Weiteres Buch Erstellen",
    "generating.preparingStory": "Deine Geschichte wird vorbereitet...",
    "generating.creatingIllustrations": "Illustrationen werden erstellt...",
    "generating.creatingPreviewIllustrations": "Vorschau-Illustrationen werden erstellt...",
    "generating.creatingCover": "Cover wird erstellt...",
    "generating.coverCreated": "Cover erstellt!",
    "generating.backCoverCreated": "Rückseite erstellt!",
    "finalize.paymentConfirmed": "Zahlung bestätigt!",
    "finalize.generatingStory": "Deine vollständige Geschichte wird erstellt...",
    "finalize.creatingIllustrations": "Illustrationen werden erstellt...",
    "finalize.addingFinishingTouches": "Letzte Feinschliffe...",
    "finalize.creatingBackCover": "Rückseite wird erstellt...",
    "finalize.bookReadyReview": "Dein Buch ist bereit zur Überprüfung!",
    "finalize.preparingPdf": "Dein PDF wird vorbereitet...",
    "resume.loadingBook": "Dein Buch wird geladen...",
    "resume.stillGenerating": "Dein Buch wird noch erstellt!",
    "resume.stillGeneratingDesc": "Deine Illustrationen werden noch im Hintergrund generiert. Du erhältst eine E-Mail, wenn es fertig ist.",
    "resume.checkBackLater": "Du kannst diese Seite schließen und später zurückkommen.",
    "illustration.noRedosLeft": "Keine Wiederholungen mehr",
    "illustration.limitReached": "Du hast alle Illustrationswiederholungen aufgebraucht. Kaufe mehr um fortzufahren.",
    "toast.paymentCancelled": "Zahlung Abgebrochen",
    "toast.tryAgainReady": "Dein Buch wartet auf dich. Versuche es jederzeit erneut!",
    "toast.redoCreditsAdded": "Wiederholungs-Credits Hinzugefügt!",
    "toast.threeMoreRedos": "Du hast jetzt 3 weitere Illustrationswiederholungen.",
    "toast.paymentSuccessful": "Zahlung Erfolgreich!",
    "toast.pdfReadyDownload": "Dein PDF ist bereit zum Download.",
    "toast.downloadStarted": "Download Gestartet",
    "toast.couldntDownload": "Download Fehlgeschlagen",
    "toast.failedGenerate": "Generierung fehlgeschlagen. Bitte versuche es erneut.",
    "toast.pleaseRetry": "Bitte versuche es erneut.",
    "toast.storyCreated": "Geschichte Erstellt!",
    "toast.previewStory": "Vorschau deines Buches unten.",
    "toast.fillIllustrations": "Verbleibende Illustrationen werden erstellt...",
    "toast.generationFailed": "Generierung Fehlgeschlagen",
    "toast.generationTimeout": "Zeitüberschreitung. Bitte versuche es erneut.",
    "toast.paymentFailed": "Zahlung Fehlgeschlagen",
    "toast.somethingWrong": "Etwas ist Schiefgelaufen",
    "toast.createBookAgain": "Bitte versuche dein Buch erneut zu erstellen.",
    "toast.errorGenerating": "Beim Erstellen deines Buches ist ein Fehler aufgetreten.",
    "toast.printOrderConfirmed": "Druckbestellung Bestätigt!",
    "toast.bookPrintedShipped": "Dein Buch wird bald gedruckt und versendet.",
    "toast.paymentSuccessContact": "Zahlung erfolgreich. Kontaktiere den Support bei Bedarf.",
    "toast.preparingPrintFiles": "Druckdateien Werden Vorbereitet",
    "toast.readyForPrinting": "Dein Buch wird für den Druck vorbereitet...",
    "toast.orderFailed": "Bestellung Fehlgeschlagen",
    "toast.paymentError": "Zahlungsfehler",
    "toast.tryAgainLater": "Bitte versuche es später erneut.",
    "toast.coverRegenerated": "Cover Regeneriert!",
    "toast.newCoverReady": "Dein neues Cover ist fertig.",
    "toast.pageRegenerated": "Seite {{page}} Regeneriert!",
    "toast.feedbackApplied": "Dein Feedback wurde angewendet.",
    "toast.freshIllustration": "Eine neue Illustration wurde erstellt.",
    "toast.regenerationFailed": "Regenerierung Fehlgeschlagen",
    "toast.couldNotRegenerate": "Illustration konnte nicht regeneriert werden. Bitte versuche es erneut.",
    "toast.backCoverFailed": "Rückseite Fehlgeschlagen",
    "toast.backCoverFrontUsed": "Die Vorderseite wird stattdessen verwendet.",
    "toast.backCoverReady": "Rückseite Fertig!",
    "toast.backCoverGenerated": "Deine Rückseite wurde erstellt.",
    "toast.frontCoverRequired": "Vorderseite Erforderlich",
    "toast.frontCoverFirst": "Erstelle zuerst eine Vorderseite.",
    "toast.backCoverRegenerated": "Rückseite Regeneriert!",
    "toast.backCoverFeedbackApplied": "Dein Feedback wurde auf die Rückseite angewendet.",
    "toast.freshBackCover": "Eine neue Rückseite wurde erstellt.",
    "toast.backCoverRegenerateFailed": "Rückseite konnte nicht regeneriert werden.",
    "toast.allIllustrationsComplete": "Alle Illustrationen Fertig!",
    "toast.storybookReady": "Dein Buch ist vollständig illustriert.",
    "toast.someIllustrationsIncomplete": "Einige Illustrationen Unvollständig",
    "toast.pagesMissing": "{{count}} Seiten fehlen noch Illustrationen.",
    "toast.bookNotComplete": "Buch Nicht Fertig",
    "toast.generateFirst": "Bitte erstelle zuerst deine Geschichte.",
    "toast.preparingBook": "Dein Buch Wird Vorbereitet",
    "toast.uploadingPrint": "Druckdateien werden hochgeladen...",
    "toast.missingInfoCheckout": "Fehlende Informationen für die Zahlung.",
    "toast.failedCheckout": "Zahlung konnte nicht erstellt werden. Bitte versuche es erneut.",
    "toast.checkEmail": "Überprüfe auch deine E-Mail für den PDF-Link!",
    "toast.couldntSendPdf": "PDF konnte nicht gesendet werden. Versuche es herunterzuladen.",
    "toast.emailSent": "E-Mail Gesendet!",
    "toast.bookSentTo": "Dein Buch wurde an {{email}} gesendet.",
    "toast.generatingPdf": "PDF wird erstellt...",
    "paywall.title": "{{names}}s Buch fertigstellen!",
    "paywall.subtitle": "Deine Vorschau ist fertig! Hol dir das vollständig illustrierte PDF für immer.",
    "paywall.digitalPdf": "Digitales PDF — 9,99 $",
    "paywall.price": "9,99 $",
    "paywall.benefit1": "Vollständig illustriertes Buch (20+ Seiten)",
    "paywall.benefit2": "Hochwertiges PDF zum Ausdrucken",
    "paywall.benefit3": "Sofortiger Download nach dem Kauf",
    "paywall.processing": "Wird verarbeitet...",
    "paywall.getBook": "Mein Buch holen — 9,99 $",
    "paywall.stopTitle": "Geschichte verlassen?",
    "paywall.stopDesc": "{{names}}s personalisiertes Buch geht verloren, wenn du jetzt gehst.",
    "paywall.noContinue": "Nein, weitermachen",
    "paywall.yesStop": "Ja, verlassen",
    "upsell.title": "Bestelle ein gedrucktes Buch!",
    "upsell.subtitle": "Du hast dein digitales Buch! Möchtest du ein Premium-Hardcover an deine Tür geliefert?",
    "upsell.specialOffer": "Sonderangebot",
    "upsell.addPrinted": "Gedrucktes Buch hinzufügen – 19,99 $",
    "upsell.shippingNote": "Versandkosten werden beim Bezahlen berechnet",
    "upsell.benefit1": "Premium-Hardcover-Buch",
    "upsell.benefit2": "Professionelle Druckqualität",
    "upsell.benefit3": "Ein Andenken, das sie für immer schätzen werden",
    "upsell.benefit4": "Weltweiter Versand verfügbar",
    "upsell.addButton": "Gedruckte Kopie hinzufügen – 19,99 $",
    "upsell.noThanks": "Nein danke, ich behalte meine digitale Kopie",

    "styleCard.pixar.name": "Pixar-Stil",
    "styleCard.pixar.desc": "Lustiger 3D-Cartoon-Stil inspiriert von Animationsfilmen",
    "styleCard.watercolor.name": "Aquarell Klassisch",
    "styleCard.watercolor.desc": "Sanfte, verträumte Aquarellillustrationen",
    "styleCard.anime.name": "Anime-Stil",
    "styleCard.anime.desc": "Lebendige japanische Anime-inspirierte Kunst",
    "generating.title": "Ihre Geschichte wird erstellt...",
    "generating.subtitle": "Wir erstellen ein magisches Abenteuer nur für Sie!",
    "generating.step.preparingStory": "Ihre Geschichte wird vorbereitet...",
    "generating.step.creatingIllustrations": "Illustrationen werden erstellt...",
    "generating.step.addingDetails": "Details werden hinzugefügt...",
    "generating.step.finishingTouches": "Letzte Feinheiten...",
    "finalizing.title": "Ihr Buch wird finalisiert...",
    "finalizing.subtitle": "{{names}}s personalisiertes Buch wird erstellt",
    "finalizing.step1": "Geschichte wird eingerichtet...",
    "finalizing.step2": "Text wird generiert...",
    "finalizing.step3": "Illustrationen werden erstellt...",
    "finalizing.step4": "Letzte Feinheiten...",
    "finalizing.step5": "Ihr Buch wird vorbereitet...",
    "finalizing.illustrations": "{{completed}} von {{total}} Illustrationen fertig",
    "finalizing.percentComplete": "{{percent}}% abgeschlossen",
    "finalizing.emailWhenReady": "Email senden, wenn es fertig ist",
    "finalizing.emailWhenReadyHint": "Wir arbeiten im Hintergrund weiter und senden Ihnen eine Email, wenn Ihr Buch fertig ist.",
    "finalizing.safeToClose": "Ihr Buch wird auf unseren Servern erstellt. Sie können diesen Tab bedenkenlos schließen — wir arbeiten weiter und senden Ihnen eine Email, wenn es fertig ist!",
    "finalizing.emailConfirmTitle": "Alles erledigt!",
    "finalizing.emailConfirmSubtitle": "Sie können diese Seite jetzt schließen.",
    "finalizing.emailConfirmDesc": "Wir generieren Ihr Buch im Hintergrund weiter und senden Ihnen eine Email mit einem Download-Link, wenn es fertig ist.",
    "finalizing.emailConfirmNote": "Schauen Sie in ein paar Minuten in Ihren Posteingang.",
    "toast.backgroundStarted": "Hintergrundgenerierung gestartet",
    "toast.backgroundStartedDesc": "Wir senden Ihnen eine Email, wenn Ihr Buch fertig ist!",
    "toast.backgroundFailed": "Hintergrundgenerierung konnte nicht gestartet werden. Bitte lassen Sie diese Seite geöffnet.",
    "pdf.adventure": "Ein personalisiertes Abenteuer",
    "pdf.theEnd": "Ende",
    "pdf.storyFor": "Eine Geschichte für",
    "backCover.title": "Rückseite",
    "illustration.regenerating": "Illustration wird regeneriert...",
    "illustration.redoCover": "Cover wiederholen",
    "illustration.redoPage": "Seite wiederholen",
    "illustration.redo": "Wiederholen",
    "illustration.buyMoreDescription": "Mehr Änderungen nötig? Erhalten Sie 3 zusätzliche Wiederholungen.",
    "illustration.buyMore": "3 Wiederholungen kaufen",
    "illustration.lastRedo": "Dies ist Ihre letzte Wiederholung! Nutzen Sie sie gut.",
    "illustration.placeholder": "Beschreiben Sie, was Sie ändern möchten...",
    "illustration.applyChanges": "Änderungen anwenden",
    "styleEdit.edit": "Bearbeiten",
    "styleEdit.placeholder": "Beschreiben Sie, wie Sie diesen Charakter ändern möchten...",
    "styleEdit.apply": "Änderungen anwenden",
    "thankYou.title": "Vielen Dank!",
    "thankYou.subtitle": "{{names}}s Abenteuer ist bereit!",
    "thankYou.emailResent": "E-Mail erneut gesendet!",
    "thankYou.emailFailed": "E-Mail fehlgeschlagen",
    "thankYou.emailSent": "E-Mail gesendet!",
    "thankYou.emailPending": "E-Mail wird gesendet...",
    "thankYou.resending": "Wird erneut gesendet...",
    "thankYou.resendEmail": "E-Mail erneut senden",
    "thankYou.emailFailedDesc": "E-Mail konnte nicht an {{email}} gesendet werden. Versuchen Sie den Download.",
    "thankYou.emailDesc": "Ihr Buch wurde an {{email}} gesendet.",
    "thankYou.emailPendingDesc": "Wir senden Ihr Buch an {{email}}...",
    "thankYou.printOrdered": "Gedruckte Kopie bestellt!",
    "thankYou.printDesc": "Ihr gedrucktes Buch wird bald versandt.",
    "thankYou.generatingPdf": "PDF wird generiert...",
    "thankYou.finishingIllustrations": "Illustrationen werden fertiggestellt...",
    "thankYou.downloadBook": "Buch herunterladen",
    "thankYou.createAnother": "Ein weiteres Buch erstellen",
    "thankYou.hopeLove": "Wir hoffen, {{names}} liebt die Geschichte!",
    "thankYou.finalizing": "Wir finalisieren Ihr Buch...",
    "thankYou.emailWhenReady": "Sie erhalten es per E-Mail an {{email}}",
    "thankYou.doNotClose": "Bitte schließen Sie dieses Fenster nicht, während wir Ihr Buch vorbereiten",
    "thankYou.bookOnItsWay": "Ihr Buch ist unterwegs!",
    "thankYou.sentTo": "Gesendet an {{email}}",
    "email.getBook": "Holen Sie sich Ihr Buch",
    "email.sendToEmail": "Wir senden Ihr Buch an Ihre E-Mail",
    "email.processingPayment": "Ihre Zahlung wird verarbeitet...",
    "email.pleaseWait": "Bitte warten, schließen Sie dieses Fenster nicht.",
    "email.bookSentAfterPayment": "Ihr Buch wird nach der Zahlung an Ihre E-Mail gesendet.",
    "email.yourName": "Ihr Name",
    "email.namePlaceholder": "Geben Sie Ihren Namen ein",
    "email.emailAddress": "E-Mail-Adresse",
    "email.emailPlaceholder": "Geben Sie Ihre E-Mail ein",
    "email.marketingConsent": "Ich möchte Updates über neue Funktionen und Geschichten erhalten",
    "email.continuePayment": "Weiter zur Zahlung",
    "email.redirect": "Sie werden auf unsere sichere Zahlungsseite weitergeleitet",
    "email.stepPreparing": "Ihr Buch wird vorbereitet...",
    "email.stepCreating": "Zahlung wird erstellt...",
    "email.stepRedirecting": "Weiterleitung zur Zahlung...",
    "printPayment.title": "Bestellung wird vorbereitet",
    "printPayment.subtitle": "Zahlung für Ihr gedrucktes Buch wird eingerichtet",
    "printPayment.stepUploading": "Druckdateien werden vorbereitet...",
    "printPayment.stepCreating": "Zahlung wird erstellt...",
    "printPayment.stepRedirecting": "Zahlung wird geöffnet...",
    "maintenance.title": "Wir zaubern!",
    "maintenance.crafting": "Wir bereiten etwas Tolles vor. Schauen Sie bald wieder vorbei!",
    "maintenance.devAccess": "Entwicklerzugang",
    "maintenance.enterPassword": "Passwort eingeben",
    "maintenance.accessSite": "Seite aufrufen",
    "maintenance.incorrectPassword": "Falsches Passwort",
    "maintenance.comingSoon": "Bald zurück!",
    "contact.title": "Kontakt",
    "contact.subtitle": "Haben Sie eine Frage? Wir freuen uns von Ihnen zu hören!",
    "contact.emailUs": "E-Mail an uns",
    "contact.responseTime": "Antwortzeit",
    "contact.within24": "Innerhalb von 24 Stunden",
    "contact.liveChat": "Live-Chat",
    "contact.chatHours": "Mo-Fr, 9-17 Uhr",
    "contact.sendMessage": "Nachricht senden",
    "contact.yourName": "Ihr Name",
    "contact.namePlaceholder": "Max Mustermann",
    "contact.email": "E-Mail",
    "contact.emailPlaceholder": "max@beispiel.de",
    "contact.subject": "Betreff",
    "contact.subjectPlaceholder": "Wie können wir helfen?",
    "contact.message": "Nachricht",
    "contact.messagePlaceholder": "Erzählen Sie uns mehr...",
    "contact.sending": "Wird gesendet...",
    "contact.send": "Nachricht senden",
    "contact.sent": "Nachricht gesendet! Wir melden uns bald.",
    "contact.faq": "Häufig gestellte Fragen",
    "contact.faq1.q": "Wie lange dauert es, ein Buch zu erstellen?",
    "contact.faq1.a": "Ihr personalisiertes Buch wird in wenigen Minuten mit unserer fortschrittlichen Technologie erstellt.",
    "contact.faq2.q": "Was passiert mit meinen Fotos?",
    "contact.faq2.a": "Ihre Fotos werden nur zur Illustrationserstellung verwendet und nach der Bucherstellung automatisch gelöscht.",
    "contact.faq3.q": "Kann ich eine gedruckte Kopie bekommen?",
    "contact.faq3.a": "Ja! Sie können eine Premium-Hardcover-Kopie bestellen, die direkt zu Ihnen geliefert wird.",
    "notFound.title": "404",
    "notFound.message": "Hoppla! Diese Seite existiert nicht.",
    "notFound.backHome": "Zurück zur Startseite",
    "payment.orderConfirmed": "Bestellung Bestätigt!",
    "payment.printDelivery": "Ihr gedrucktes Buch wird bald versandt.",
    "payment.orderIssue": "Bestellungsproblem",
    "payment.issueDesc": "Bei der Verarbeitung Ihrer Bestellung ist ein Problem aufgetreten. Bitte kontaktieren Sie den Support.",
    "payment.downloadReady": "Download Bereit!",
    "payment.digitalDelivery": "Ihr digitales Buch ist zum Download bereit.",
    "payment.submitting": "Ihre Bestellung wird verarbeitet...",
    "payment.paymentSuccess": "Ihre Zahlung war erfolgreich! Bestellung wird eingerichtet...",
    "payment.contactSupport": "Bitte kontaktieren Sie den Support mit Ihrer Sitzungs-ID:",
    "payment.backHome": "Zurück zur Startseite",
    "payment.digitalThankYou": "Vielen Dank für Ihren Kauf! Ihr digitales Buch ist bereit.",
    "payment.printThankYou": "Vielen Dank für Ihren Kauf! Ihr gedrucktes Buch wird bald versandt.",
    "payment.orderDetails": "Bestelldetails",
    "payment.bookTitle": "Personalisiertes Buch",
    "payment.orderId": "Bestell-ID:",
    "payment.createAnother": "Ein weiteres Buch erstellen",
    "shipping.title": "Lieferadresse",
    "shipping.for": "Für:",
    "shipping.fullName": "Vollständiger Name",
    "shipping.nameRequired": "Name ist erforderlich",
    "shipping.emailRequired": "E-Mail ist erforderlich",
    "shipping.emailInvalid": "Bitte geben Sie eine gültige E-Mail ein",
    "shipping.streetRequired": "Straßenadresse ist erforderlich",
    "shipping.cityRequired": "Stadt ist erforderlich",
    "shipping.stateRequired": "Bundesland ist erforderlich",
    "shipping.postalRequired": "Postleitzahl ist erforderlich",
    "shipping.phoneRequired": "Telefonnummer ist erforderlich",
    "shipping.email": "E-Mail",
    "shipping.streetAddress": "Straßenadresse",
    "shipping.apartment": "Wohnung / Suite",
    "shipping.city": "Stadt",
    "shipping.postalCode": "Postleitzahl",
    "shipping.country": "Land",
    "shipping.stateProvince": "Bundesland / Provinz",
    "shipping.optional": "Optional",
    "shipping.phoneNumber": "Telefonnummer",
    "shipping.phoneHint": "Erforderlich für Lieferbenachrichtigungen",
    "shipping.cancel": "Abbrechen",
    "shipping.continuePayment": "Weiter zur Zahlung",
    "shipping.processing": "Verarbeitung...",
    "shipping.state": "Bundesland",
    "shipping.selectCountry": "Land auswählen",
    "shipping.selectState": "Bundesland auswählen",
    "shipping.deliveryOptions": "Lieferoptionen",
    "shipping.chooseDelivery": "Lieferung wählen",
    "shipping.loadingOptions": "Optionen laden...",
    "shipping.selectDeliverySpeed": "Wählen Sie Ihre bevorzugte Liefergeschwindigkeit:",
    "shipping.businessDays": "Werktage",
    "shipping.estimateUnavailable": "Schätzung nicht verfügbar",
    "shipping.back": "Zurück",
    "shipping.included": "Inklusive",
    "pdfPreview.printComingSoon": "Demnächst verfügbar!",
    "queue.position": "Position in der Warteschlange: {{position}}",
    "queue.waiting": "In der Warteschlange...",

    // Order Tracking
    "trackOrder.title": "Bestellung Verfolgen",
    "trackOrder.subtitle": "Geben Sie Ihre E-Mail ein, um Ihre Bestellhistorie zu sehen",
    "trackOrder.emailPlaceholder": "ihre@email.com",
    "trackOrder.search": "Suchen",
    "trackOrder.error": "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
    "trackOrder.noOrders": "Keine Bestellungen Gefunden",
    "trackOrder.noOrdersDesc": "Wir konnten keine Bestellungen mit dieser E-Mail finden.",
    "trackOrder.createBook": "Erstellen Sie Ihr Erstes Buch",
    "trackOrder.foundOrders": "{{count}} Bestellung(en) gefunden",
    "trackOrder.digital": "Digitales PDF",
    "trackOrder.printed": "Gedrucktes Buch",
    "trackOrder.status.pending": "Ausstehend",
    "trackOrder.status.paid": "Bezahlt",
    "trackOrder.status.fulfilled": "Erfüllt",
    "trackOrder.status.shipped": "Versendet",
    "footer.myBooks": "Mein Konto",
    "auth.loginTitle": "Willkommen Zurück",
    "auth.loginSubtitle": "Melden Sie sich an, um auf Ihre Bücher zuzugreifen",
    "auth.signupTitle": "Konto Erstellen",
    "auth.signupSubtitle": "Registrieren Sie sich, um Bücher und Charaktere zu speichern",
    "auth.forgotTitle": "Passwort Zurücksetzen",
    "auth.forgotSubtitle": "Geben Sie Ihre E-Mail ein, um einen Reset-Link zu erhalten",
    "auth.displayName": "Anzeigename",
    "auth.email": "E-Mail",
    "auth.password": "Passwort",
    "auth.loginButton": "Anmelden",
    "auth.signupButton": "Konto Erstellen",
    "auth.resetButton": "Link Senden",
    "auth.forgotPassword": "Passwort vergessen?",
    "auth.noAccount": "Kein Konto?",
    "auth.signupLink": "Registrieren",
    "auth.hasAccount": "Bereits ein Konto?",
    "auth.loginLink": "Anmelden",
    "auth.backToLogin": "Zurück zur Anmeldung",
    "auth.error": "Fehler",
    "auth.errorDesc": "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
    "auth.welcomeTitle": "Willkommen!",
    "auth.welcomeDesc": "Ihr Konto wurde erfolgreich erstellt.",
    "auth.resetSent": "Link Gesendet",
    "auth.resetSentDesc": "Überprüfen Sie Ihre E-Mail für den Reset-Link.",
    "dashboard.title": "Mein Dashboard",
    "dashboard.subtitle": "Verwalten Sie Ihre Bücher und Charaktere",
    "dashboard.logout": "Abmelden",
    "dashboard.newBook": "Neues Buch",
    "dashboard.myBooks": "Meine Bücher",
    "dashboard.myKids": "Meine Charaktere",
    "dashboard.inProgress": "In Bearbeitung",
    "dashboard.digital": "Digital",
    "dashboard.printed": "Gedruckt",
    "dashboard.noInProgress": "Keine Bücher in Bearbeitung",
    "dashboard.noDigital": "Noch keine digitalen Bücher",
    "dashboard.noPrinted": "Noch keine gedruckten Bücher",
    "dashboard.createFirst": "Erstellen Sie Ihr Erstes Buch",
    "dashboard.untitled": "Buch Ohne Titel",
    "dashboard.addChild": "Kind Hinzufügen",
    "dashboard.editChild": "Kind Bearbeiten",
    "dashboard.addFirstChild": "Fügen Sie Ihr Erstes Kind Hinzu",
    "dashboard.noKids": "Noch keine gespeicherten Charaktere",
    "dashboard.childFormDesc": "Speichern Sie die Infos Ihres Kindes, um schnell neue Bücher zu erstellen",
    "dashboard.photo": "Foto",
    "dashboard.yearsOld": "Jahre alt",
    "dashboard.savedStyles": "Gespeicherte Stile:",
    "dashboard.save": "Speichern",
    "dashboard.childAdded": "Charakter hinzugefügt!",
    "dashboard.childUpdated": "Charakter aktualisiert!",
    "dashboard.childRemoved": "Charakter entfernt",
    "dashboard.profile": "Profil",
    "dashboard.personalInfo": "Persönliche Informationen",
    "dashboard.fullName": "Vollständiger Name",
    "dashboard.fullNamePlaceholder": "Geben Sie Ihren vollständigen Namen ein",
    "dashboard.email": "E-Mail",
    "dashboard.phone": "Telefonnummer",
    "dashboard.phonePlaceholder": "Geben Sie Ihre Telefonnummer ein",
    "dashboard.shippingAddress": "Lieferadresse",
    "dashboard.addressLine1": "Adresszeile 1",
    "dashboard.addressLine1Placeholder": "Straße",
    "dashboard.addressLine2": "Adresszeile 2",
    "dashboard.addressLine2Placeholder": "Wohnung, Suite, etc. (optional)",
    "dashboard.city": "Stadt",
    "dashboard.cityPlaceholder": "Stadt",
    "dashboard.state": "Bundesland / Provinz",
    "dashboard.statePlaceholder": "Bundesland / Provinz",
    "dashboard.postalCode": "Postleitzahl",
    "dashboard.postalCodePlaceholder": "Postleitzahl",
    "dashboard.country": "Land",
    "dashboard.countryPlaceholder": "Land",
    "dashboard.saveProfile": "Profil speichern",
    "dashboard.saved": "Gespeichert!",
    "dashboard.profileSaved": "Profil gespeichert!",
    "dashboard.profileSaveError": "Fehler beim Speichern des Profils",
    "dashboard.confirmDelete": "Charakter entfernen?",
    "dashboard.confirmDeleteDesc": "Dies wird diesen Charakter und seine gespeicherten Stile dauerhaft entfernen.",
    "dashboard.delete": "Entfernen",
    "dashboard.generateStyle": "Stil Generieren",
    "dashboard.regenerateStyle": "Regenerieren",
    "dashboard.selectStyle": "Kunststil auswählen",
    "dashboard.generatingStyle": "Stil wird generiert...",
    "dashboard.styleGenerated": "Stil generiert!",
    "dashboard.needsPhoto": "Lade zuerst ein Foto hoch",
    "dashboard.continueBook": "Fortfahren",
    "dashboard.downloadPdf": "Herunterladen",
    "dashboard.trackOrder": "Verfolgen",
    "dashboard.statusPending": "In Bearbeitung",
    "dashboard.statusGenerating": "Wird generiert",
    "dashboard.statusProcessing": "Verarbeitung",
    "dashboard.statusPaid": "Bezahlt",
    "dashboard.statusCompleted": "Abgeschlossen",
    "dashboard.statusFulfilled": "Erfüllt",
    "dashboard.statusShipped": "Versendet",
    "dashboard.buyHardCopy": "Gedruckte Kopie Kaufen",
    "thankYou.illustrationProgress": "Illustrationen: {{completed}} / {{total}}",

    // Theme Categories
    "category.friendship": "Freundschaft",
    "category.growingUp": "Aufwachsen",
    "category.kindness": "Freundlichkeit & Gefühle",
    "category.values": "Werte",
    "category.adventure": "Abenteuer",
    "category.fantasy": "Fantasie",
    "category.humor": "Humor",
    "category.learning": "Lernen",
    "category.diversity": "Vielfalt & Inklusion",
    "category.nature": "Natur & Umwelt",
    "category.problemSolving": "Problemlösung",
    "category.lifeExperiences": "Lebenserfahrungen",
    "category.cultural": "Kulturelle Geschichten",

    // Theme Titles & Descriptions
    "theme.makingFriends.title": "Freunde Finden",
    "theme.makingFriends.desc": "Eine Geschichte über neue Freundschaften und die Freude der Verbindung",
    "theme.bestFriends.title": "Beste Freunde",
    "theme.bestFriends.desc": "Ein Abenteuer, das die Bindung bester Freunde feiert",
    "theme.sharing.title": "Teilen",
    "theme.sharing.desc": "Das Glück des Teilens mit anderen lernen",
    "theme.siblings.title": "Geschwister",
    "theme.siblings.desc": "Eine herzerwärmende Geschichte über die besondere Bindung zwischen Geschwistern",
    "theme.sayingSorry.title": "Sich Entschuldigen",
    "theme.sayingSorry.desc": "Lernen, wann und wie man sich entschuldigt",
    "theme.beingYourself.title": "Du Selbst Sein",
    "theme.beingYourself.desc": "Die Magie entdecken, sich selbst treu zu sein",
    "theme.tryingNew.title": "Neues Ausprobieren",
    "theme.tryingNew.desc": "Eine aufregende Reise aus der Komfortzone",
    "theme.overcomingFear.title": "Angst Überwinden",
    "theme.overcomingFear.desc": "Ein mutiges Abenteuer, um Ängste zu überwinden",
    "theme.findingTalent.title": "Dein Talent Finden",
    "theme.findingTalent.desc": "Ein besonderes Talent entdecken, das dich einzigartig macht",
    "theme.confidence.title": "Selbstvertrauen",
    "theme.confidence.desc": "Selbstvertrauen aufbauen und an sich glauben",
    "theme.understandingFeelings.title": "Gefühle Verstehen",
    "theme.understandingFeelings.desc": "Lernen, Emotionen zu erkennen und zu verstehen",
    "theme.helpingOthers.title": "Anderen Helfen",
    "theme.helpingOthers.desc": "Die Freude und Belohnung, anderen zu helfen",
    "theme.dealingAnger.title": "Mit Wut Umgehen",
    "theme.dealingAnger.desc": "Gesunde Wege, Wut zu bewältigen und auszudrücken",
    "theme.copingSadness.title": "Traurigkeit Bewältigen",
    "theme.copingSadness.desc": "Trost und Hoffnung in traurigen Zeiten finden",
    "theme.showingEmpathy.title": "Empathie Zeigen",
    "theme.showingEmpathy.desc": "Verstehen und sich darum kümmern, wie andere sich fühlen",
    "theme.honesty.title": "Ehrlichkeit",
    "theme.honesty.desc": "Die Wichtigkeit, die Wahrheit zu sagen",
    "theme.responsibility.title": "Verantwortung",
    "theme.responsibility.desc": "Lernen, Verantwortung für seine Handlungen zu übernehmen",
    "theme.respect.title": "Respekt",
    "theme.respect.desc": "Andere mit Freundlichkeit und Respekt behandeln",
    "theme.perseverance.title": "Ausdauer",
    "theme.perseverance.desc": "Niemals aufgeben, auch wenn es schwierig wird",
    "theme.goodChoices.title": "Gute Entscheidungen Treffen",
    "theme.goodChoices.desc": "Lernen, kluge Entscheidungen zu treffen",
    "theme.jungleAdventure.title": "Dschungel-Abenteuer",
    "theme.jungleAdventure.desc": "Eine aufregende Expedition durch einen wilden Dschungel",
    "theme.desertJourney.title": "Wüstenreise",
    "theme.desertJourney.desc": "Eine spannende Reise durch eine weite Wüste",
    "theme.oceanQuest.title": "Ozean-Abenteuer",
    "theme.oceanQuest.desc": "Tief eintauchen in ein Unterwasserabenteuer",
    "theme.spaceExploration.title": "Weltraumforschung",
    "theme.spaceExploration.desc": "Abheben zu einem erstaunlichen Weltraumabenteuer",
    "theme.mountainExpedition.title": "Bergexpedition",
    "theme.mountainExpedition.desc": "Auf den Gipfel eines prächtigen Berges klettern",
    "theme.magicalWorlds.title": "Magische Welten",
    "theme.magicalWorlds.desc": "Verzauberte Länder voller Wunder erkunden",
    "theme.talkingAnimals.title": "Sprechende Tiere",
    "theme.talkingAnimals.desc": "Abenteuer mit Tieren, die sprechen können",
    "theme.wizardsSpells.title": "Zauberer und Zauber",
    "theme.wizardsSpells.desc": "Magie von weisen Zauberern lernen",
    "theme.mythicalCreatures.title": "Mythische Wesen",
    "theme.mythicalCreatures.desc": "Drachen, Einhörner und andere magische Wesen treffen",
    "theme.timeTravel.title": "Zeitreise",
    "theme.timeTravel.desc": "Durch die Zeit zu erstaunlichen Epochen reisen",
    "theme.sillyAnimals.title": "Lustige Tiere",
    "theme.sillyAnimals.desc": "Urkomische Abenteuer mit lustigen Tieren",
    "theme.wordplayJokes.title": "Wortspiele und Witze",
    "theme.wordplayJokes.desc": "Eine Geschichte voller Wortspiele und Lacher",
    "theme.absurdSituations.title": "Absurde Situationen",
    "theme.absurdSituations.desc": "Verrückte und wunderbar absurde Abenteuer",
    "theme.mischievous.title": "Schelmische Abenteuer",
    "theme.mischievous.desc": "Verspielte Streiche und schelmischer Spaß",
    "theme.unexpectedSurprises.title": "Unerwartete Überraschungen",
    "theme.unexpectedSurprises.desc": "Eine Geschichte voller entzückender Wendungen",
    "theme.numbersCounting.title": "Zahlen und Zählen",
    "theme.numbersCounting.desc": "Ein lustiges Abenteuer mit Zahlen und Zählen",
    "theme.lettersReading.title": "Buchstaben und Lesen",
    "theme.lettersReading.desc": "Die Magie der Buchstaben und Wörter entdecken",
    "theme.scienceExperiments.title": "Wissenschaftsexperimente",
    "theme.scienceExperiments.desc": "Spannende Entdeckungen durch Wissenschaft",
    "theme.history.title": "Geschichte",
    "theme.history.desc": "In die Vergangenheit reisen, um Geschichte zu entdecken",
    "theme.problemSolvingSkills.title": "Problemlösung",
    "theme.problemSolvingSkills.desc": "Kreativität und Logik nutzen, um Probleme zu lösen",
    "theme.differentCultures.title": "Verschiedene Kulturen",
    "theme.differentCultures.desc": "Kulturen auf der ganzen Welt erkunden und feiern",
    "theme.differentAbilities.title": "Verschiedene Fähigkeiten",
    "theme.differentAbilities.desc": "Feiern, was jeden einzigartig macht",
    "theme.familyDiversity.title": "Familienvielfalt",
    "theme.familyDiversity.desc": "Alle Arten von Familien und die Liebe, die sie teilen",
    "theme.genderExpression.title": "Geschlechterausdruck",
    "theme.genderExpression.desc": "Frei sein, auszudrücken, wer du wirklich bist",
    "theme.acceptanceBelonging.title": "Akzeptanz und Zugehörigkeit",
    "theme.acceptanceBelonging.desc": "Seinen Platz finden und akzeptiert werden",
    "theme.forestsTrees.title": "Wälder und Bäume",
    "theme.forestsTrees.desc": "Abenteuer in magischen Wäldern und alten Bäumen",
    "theme.oceansSealife.title": "Ozeane und Meereslebewesen",
    "theme.oceansSealife.desc": "Die Wunder unter den Wellen entdecken",
    "theme.animalsHabitats.title": "Tiere und Lebensräume",
    "theme.animalsHabitats.desc": "Über Tiere und ihre Lebensräume lernen",
    "theme.weatherSeasons.title": "Wetter und Jahreszeiten",
    "theme.weatherSeasons.desc": "Die Magie der wechselnden Jahreszeiten erkunden",
    "theme.protectingPlanet.title": "Den Planeten Schützen",
    "theme.protectingPlanet.desc": "Sich um unsere schöne Erde kümmern",
    "theme.mysteriesClues.title": "Rätsel und Hinweise",
    "theme.mysteriesClues.desc": "Spannende Geheimnisse mit cleveren Hinweisen lösen",
    "theme.puzzlesRiddles.title": "Puzzle und Rätsel",
    "theme.puzzlesRiddles.desc": "Knifflige Puzzle und vertrackte Rätsel",
    "theme.causeEffect.title": "Ursache und Wirkung",
    "theme.causeEffect.desc": "Verstehen, wie Handlungen zu Ergebnissen führen",
    "theme.teamProblemSolving.title": "Problemlösung im Team",
    "theme.teamProblemSolving.desc": "Zusammenarbeiten, um große Herausforderungen zu lösen",
    "theme.creativeSolutions.title": "Kreative Lösungen",
    "theme.creativeSolutions.desc": "Querdenken, um Antworten zu finden",
    "theme.startingSchool.title": "Schulanfang",
    "theme.startingSchool.desc": "Das aufregende Abenteuer des Schulanfangs",
    "theme.movingHome.title": "Umziehen",
    "theme.movingHome.desc": "Sich an ein neues Zuhause und eine neue Nachbarschaft gewöhnen",
    "theme.losingPet.title": "Ein Haustier Verlieren",
    "theme.losingPet.desc": "Mit dem Verlust eines geliebten Haustieres umgehen",
    "theme.newSibling.title": "Neues Geschwisterchen",
    "theme.newSibling.desc": "Ein neues Baby-Geschwisterchen willkommen heißen",
    "theme.dealingChange.title": "Mit Veränderung Umgehen",
    "theme.dealingChange.desc": "Lernen, Veränderungen anzunehmen und sich anzupassen",
    "theme.fairyTales.title": "Märchen",
    "theme.fairyTales.desc": "Klassische Märchenabenteuer neu erzählt",
    "theme.folktales.title": "Volksmärchen",
    "theme.folktales.desc": "Traditionelle Geschichten, die von Generation zu Generation weitergegeben werden",
    "theme.mythsLegends.title": "Mythen und Legenden",
    "theme.mythsLegends.desc": "Epische Erzählungen aus Mythologie und Legende",
    "theme.fablesMorals.title": "Fabeln und Moral",
    "theme.fablesMorals.desc": "Geschichten mit wertvollen Lebenslektionen",
    "theme.holidayTraditions.title": "Feiertagstraditionen",
    "theme.holidayTraditions.desc": "Besondere Feiertage und Traditionen feiern",
  },
  it: {
    "seo.title": "StoryHeroes – Libri di Storie Personalizzati per Bambini",
    "seo.description": "Trasforma tuo figlio nell'eroe di un libro di storie magico. Carica una foto, scegli un'avventura e ricevi un PDF personalizzato e illustrato in pochi minuti.",
    "hero.badge": "✨ Libri Personalizzati",
    "hero.title": "Trasforma Tuo Figlio in un Eroe delle Storie",
    "hero.title.create": "Crea",
    "hero.title.magical": "Storie Magiche",
    "hero.title.starring": "Con Tuo Figlio Protagonista",
    "hero.subtitle": "Carica una foto, scegli un'avventura e ricevi un magnifico PDF illustrato o libro con copertina rigida in pochi minuti.",
    "hero.cta": "Crea la Tua Storia",
    "hero.freePreview": "Crea e visualizza il tuo libro gratis",
    "hero.privacyStamp": "🔒 Foto eliminate dopo la generazione",
    "hero.themes": "50+ Temi",
    "hero.aiArt": "Illustrazioni Personalizzate",
    "hero.pdfDownload": "PDF Istantaneo",
    "hero.features.personalized": "Personalizzato",
    "hero.features.ai": "Arte Personalizzata",
    "hero.features.instant": "PDF Istantaneo",
    "transform.badge": "✨ Guarda la Magia",
    "transform.title": "Dalla Foto al Personaggio del Libro",
    "transform.subtitle": "Trasformiamo la foto di tuo figlio in un bellissimo personaggio illustrato",
    "transform.pixar": "Stile Pixar",
    "transform.watercolor": "Stile Acquerello",
    "transform.anime": "Stile Anime",
    "showcase.title": "Bellissimi Libri, Digitali e Stampati",
    "showcase.subtitle": "Ottieni il tuo libro personalizzato come PDF digitale o in copertina rigida stampata",
    "showcase.pdfTitle": "PDF Digitale",
    "showcase.pdfDesc": "Download istantaneo, leggibile su qualsiasi dispositivo",
    "showcase.printTitle": "Libro Stampato",
    "showcase.printDesc": "Copertina rigida premium consegnata a casa tua",
    "showcase.pages": "32 pagine",
    "showcase.popular": "Più Popolare",
    "reviews.badge": "⭐ Amato dai Genitori",
    "reviews.title": "Cosa Dicono i Genitori",
    "reviews.subtitle": "Unisciti a migliaia di famiglie felici che hanno creato storie magiche",
    "reviews.child": "Età del bambino:",
    "reviews.1.text": "Mia figlia ha adorato vedersi come l'eroina! Le illustrazioni sono stupende e la storia era perfetta per la sua età.",
    "reviews.1.childAge": "5 anni",
    "reviews.2.text": "Un'idea regalo incredibile! Mio figlio legge la sua storia ogni sera prima di dormire. La qualità del PDF è straordinaria.",
    "reviews.2.childAge": "4 anni",
    "reviews.3.text": "Sono rimasta sbalordita dalla qualità. La somiglianza di mio figlio è stata catturata perfettamente. Lo consiglio vivamente!",
    "reviews.3.childAge": "6 anni",
    "reviews.4.text": "Abbiamo ordinato tre libri, uno per ciascuno dei nostri figli. Litigano su chi legge il suo per primo!",
    "reviews.4.childAge": "3, 5, 7 anni",
    "reviews.5.text": "Lo stile acquerello è assolutamente meraviglioso. Sembra fatto da un illustratore professionista di libri per bambini.",
    "reviews.5.childAge": "4 anni",
    "reviews.6.text": "Il miglior regalo di compleanno! La faccia di mio figlio quando si è visto nella storia non aveva prezzo.",
    "reviews.6.childAge": "6 anni",
    "trust.gift.title": "Regalo Perfetto",
    "trust.gift.desc": "Un regalo unico e personalizzato che i bambini custodiscono per anni",
    "trust.ai.title": "Arte Mozzafiato",
    "trust.ai.desc": "La nostra tecnologia crea illustrazioni stupende a immagine di tuo figlio",
    "trust.love.title": "Fatto con Amore",
    "trust.love.desc": "Ogni storia è creata con cura per ispirare immaginazione e gioia",
    "cta.title": "Pronto a Creare Magia?",
    "cta.subtitle": "Trasforma tuo figlio nell'eroe della sua avventura",
    "cta.button": "Inizia a Creare",
    "cta.note": "Nessun account necessario • Pronto in 5 minuti",
    
    // Language Selector
    "lang.title": "Scegli la Tua Lingua",
    "lang.subtitle": "Seleziona la tua lingua preferita per continuare",
    
    // Navigation
    "nav.backToHome": "Torna alla Home",
    
    // Footer
    "footer.copyright": "© 2024 StoryHeroes. Tutti i diritti riservati.",
    "footer.privacy": "Informativa sulla Privacy",
    "footer.terms": "Termini di Servizio",
    "footer.contact": "Contatto",
    "footer.trackOrder": "Traccia Ordine",
    
    // Privacy Page
    "privacy.title": "Informativa sulla Privacy",
    "privacy.updated": "Ultimo aggiornamento: Gennaio 2024",
    "privacy.section1.title": "1. Informazioni che Raccogliamo",
    "privacy.section1.intro": "Raccogliamo le seguenti informazioni quando utilizzi il nostro servizio:",
    "privacy.section1.item1": "Foto che carichi per creare libri di storie personalizzati",
    "privacy.section1.item2": "Nome ed età del bambino per la personalizzazione della storia",
    "privacy.section1.item3": "Indirizzo email per conferma dell'ordine e consegna",
    "privacy.section1.item4": "Informazioni di pagamento (elaborate in modo sicuro tramite Stripe)",
    "privacy.section2.title": "2. Come Utilizziamo le Tue Informazioni",
    "privacy.section2.intro": "Utilizziamo le tue informazioni per:",
    "privacy.section2.item1": "Generare libri di storie personalizzati",
    "privacy.section2.item2": "Elaborare i pagamenti e consegnare il tuo libro PDF",
    "privacy.section2.item3": "Inviare conferme d'ordine e aggiornamenti",
    "privacy.section2.item4": "Migliorare il nostro servizio e l'esperienza utente",
    "privacy.section2.item5": "Rispettare gli obblighi legali",
    "privacy.section3.title": "3. Archiviazione e Sicurezza dei Dati",
    "privacy.section3.text": "Prendiamo sul serio la sicurezza dei dati. Tutte le foto caricate e le informazioni personali sono crittografate e archiviate in modo sicuro. Utilizziamo misure di sicurezza standard del settore per proteggere i tuoi dati da accessi non autorizzati.",
    "privacy.section4.title": "4. Servizi di Terze Parti",
    "privacy.section4.text": "Utilizziamo servizi di terze parti affidabili, tra cui Stripe per l'elaborazione dei pagamenti e servizi specializzati per la generazione di storie. Questi servizi hanno le proprie politiche sulla privacy e misure di sicurezza.",
    "privacy.section5.title": "5. Conservazione dei Dati",
    "privacy.section5.text": "Conserviamo i tuoi dati solo per il tempo necessario a fornire i nostri servizi e rispettare gli obblighi legali. Puoi richiedere la cancellazione dei tuoi dati in qualsiasi momento contattandoci.",
    "privacy.section6.title": "6. Privacy dei Bambini",
    "privacy.section6.text": "Ci impegniamo a proteggere la privacy dei bambini. Non raccogliamo consapevolmente informazioni personali da bambini di età inferiore a 13 anni senza il consenso dei genitori. I genitori hanno il pieno controllo sulle informazioni del loro bambino.",
    "privacy.section7.title": "7. I Tuoi Diritti",
    "privacy.section7.intro": "Hai il diritto di:",
    "privacy.section7.item1": "Accedere ai tuoi dati personali",
    "privacy.section7.item2": "Richiedere la correzione di dati inesatti",
    "privacy.section7.item3": "Richiedere la cancellazione dei tuoi dati",
    "privacy.section7.item4": "Rinunciare alle comunicazioni di marketing",
    "privacy.section8.title": "8. Contattaci",
    "privacy.section8.text": "Se hai domande su questa Informativa sulla Privacy, contattaci tramite la nostra pagina di contatto.",
    
    // Terms Page
    "terms.title": "Termini di Servizio",
    "terms.updated": "Ultimo aggiornamento: Gennaio 2024",
    "terms.section1.title": "1. Accettazione dei Termini",
    "terms.section1.text": "Utilizzando StoryHeroes, accetti questi Termini di Servizio. Se non sei d'accordo, ti preghiamo di non utilizzare il nostro servizio.",
    "terms.section2.title": "2. Descrizione del Servizio",
    "terms.section2.text": "StoryHeroes fornisce libri di storie personalizzati per bambini. Creiamo storie illustrate personalizzate basate sulle foto e informazioni che fornisci.",
    "terms.section3.title": "3. Responsabilità dell'Utente",
    "terms.section3.intro": "Accetti di:",
    "terms.section3.item1": "Fornire informazioni accurate",
    "terms.section3.item2": "Caricare solo foto per cui hai i diritti d'uso",
    "terms.section3.item3": "Non utilizzare il servizio per scopi illegali",
    "terms.section3.item4": "Non caricare contenuti inappropriati o offensivi",
    "terms.section3.item5": "Rispettare i diritti di proprietà intellettuale",
    "terms.section4.title": "4. Pagamento e Rimborsi",
    "terms.section4.text": "Tutti i pagamenti sono elaborati in modo sicuro tramite Stripe. A causa della natura personalizzata del nostro servizio, i rimborsi sono disponibili solo in caso di problemi tecnici che impediscono la consegna del tuo libro di storie.",
    "terms.section5.title": "5. Proprietà Intellettuale",
    "terms.section5.intro": "Per quanto riguarda la proprietà intellettuale:",
    "terms.section5.item1": "Mantieni i diritti sulle foto che carichi",
    "terms.section5.item2": "Le storie e le illustrazioni generate sono solo per uso personale",
    "terms.section5.item3": "Non puoi rivendere o ridistribuire i contenuti generati",
    "terms.section5.item4": "StoryHeroes mantiene i diritti sul servizio e sulla tecnologia",
    "terms.section5.item5": "Ci concedi la licenza per utilizzare i contenuti caricati per generare il tuo libro di storie",
    "terms.section6.title": "6. Limitazione di Responsabilità",
    "terms.section6.text": "StoryHeroes è fornito 'così com'è' senza garanzie. Non siamo responsabili per danni indiretti, incidentali o consequenziali derivanti dall'uso del nostro servizio.",
    "terms.section7.title": "7. Linee Guida sui Contenuti",
    "terms.section7.text": "Ci riserviamo il diritto di rifiutare il servizio o rimuovere contenuti che violano le nostre linee guida, inclusi materiali inappropriati, offensivi o protetti da copyright.",
    "terms.section8.title": "8. Modifiche ai Termini",
    "terms.section8.text": "Potremmo aggiornare questi Termini di Servizio. L'uso continuato del servizio dopo le modifiche costituisce accettazione dei nuovi termini.",
    "terms.section9.title": "9. Risoluzione",
    "terms.section9.text": "Ci riserviamo il diritto di terminare o sospendere l'accesso al nostro servizio per violazioni di questi termini o per qualsiasi altro motivo a nostra discrezione.",
    "terms.section10.title": "10. Contatto",
    "terms.section10.text": "Per domande su questi Termini di Servizio, contattaci tramite la nostra pagina di contatto.",

    // Wizard Steps
    "step.addCharacters": "Aggiungi Personaggi",
    "step.chooseStory": "Scegli la Storia",
    "step.pickTitle": "Scegli il Titolo",
    "step.chooseStyle": "Scegli lo Stile",
    "step.review": "Rivedi",
    "step.storyOutline": "Schema della Storia",
    "step.previewPdf": "Anteprima PDF",
    "photo.title": "Aggiungi i Tuoi Personaggi",
    "photo.subtitle": "Carica una foto e inserisci i dettagli di ogni personaggio della tua storia.",
    "photo.privacyNotice": "🔒 Le tue foto sono usate solo per l'illustrazione e vengono eliminate dopo la generazione.",
    "photo.addPhoto": "Aggiungi Foto",
    "photo.dragDrop": "o trascina e rilascia",
    "photo.dropHere": "Rilascia qui!",
    "photo.browse": "Sfoglia",
    "photo.camera": "Fotocamera",
    "photo.upload": "Carica",
    "photo.name": "Nome",
    "photo.namePlaceholder": "Inserisci il nome",
    "photo.age": "Età",
    "photo.agePlaceholder": "Inserisci l'età",
    "photo.gender": "Tipo di Personaggio",
    "photo.boy": "Ragazzo",
    "photo.girl": "Ragazza",
    "photo.pet": "Animale",
    "photo.addChild": "Aggiungi un Altro Personaggio",
    "photo.savedKids": "I Tuoi Personaggi Salvati",
    "familyImport.button": "Importa da Foto di Gruppo",
    "style.choose": "Scegli il Tuo Stile Artistico",
    "style.subtitleSelected": "Guarda come appaiono i tuoi {{count}} personaggi{{plural}} in questo stile",
    "style.subtitleUnselected": "Scegli uno stile di illustrazione per il tuo libro",
    "style.yourCharactersIn": "I tuoi personaggi in {{style}}",
    "style.creatingName": "Creazione di {{name}}...",
    "style.altCharacterInStyle": "{{name}} in stile {{style}}",
    "style.failedShort": "Fallito",
    "style.retry": "Riprova",
    "style.hoverHint": "Passa il mouse sulle immagini per ingrandirle",
    "style.preserveHint": "Le descrizioni dei personaggi saranno preservate per coerenza",
    "style.tryAgain": "Riprova",
    "style.continueWith": "Continua con {{style}}",
    "style.switchHint": "Puoi cambiare stile in qualsiasi momento prima di continuare",
    "title.choose": "Scegli il Tuo Titolo",
    "title.subtitle": "Scegli il titolo perfetto per il tuo libro personalizzato",
    "title.creating": "Creazione di titoli magici...",
    "title.selected": "Selezionato",
    "title.regenerate": "Rigenera Titoli",
    "title.usingSuggested": "Titoli suggeriti",
    "title.pickFavorite": "Scegli il tuo preferito tra questi suggerimenti!",
    "themes.title": "Scegli un Tema per {{hero}}",
    "themes.subtitle": "Scegli un tema avventura per i tuoi {{count}} {{characterWord}}. Ogni tema crea storie uniche.",
    "themes.changeTheme": "Cambia Tema",
    "themes.whatsStoryAbout": "Di cosa dovrebbe parlare la storia di {{hero}}?",
    "themes.yourIdeas": "Le Tue Idee per la Storia",
    "themes.ideaHelperText": "Descrivi l'avventura che vorresti. Sii creativo quanto vuoi!",
    "themes.ideaPlaceholder": "es. Un cavaliere coraggioso che fa amicizia con un drago e insieme salvano un regno magico...",
    "themes.ideaHint": "Buon inizio! Clicca sotto per idee migliorate.",
    "themes.generateRequired": "Scrivi la tua idea o clicca sotto per ispirarti!",
    "themes.generatingIdeas": "Generazione idee...",
    "themes.completeMyIdeas": "Completa le Mie Idee",
    "themes.inspireMe": "Ispirami!",
    "themes.chooseIdea": "Scegli un'Idea per la Storia",
    "themes.usingPreMade": "Idee predefinite",
    "themes.greatIdeas": "Ecco alcune fantastiche idee per storie!",
    "outline.title": "Schema della Storia",
    "outline.subtitle": "Rivedi e personalizza le pagine del tuo libro",
    "outline.generating": "Creazione dello schema della tua storia...",
    "outline.titlePage": "Pagina del Titolo",
    "outline.ending": "Fine",
    "outline.page": "Pagina",
    "outline.happy": "Soddisfatto dello schema?",
    "outline.happyDesc": "Puoi modificare il testo di qualsiasi pagina sopra. Quando sei pronto, clicca avanti per generare le illustrazioni!",
    "outline.sceneDescription": "Descrizione della scena",
    "outline.whatYouWillSee": "Cosa vedrai in questa pagina",
    "preview.title": "Rivedi la Tua Storia",
    "preview.subtitle": "Tutto sembra a posto? Creiamo il tuo libro illustrato!",
    "preview.storyTheme": "Tema della Storia",
    "preview.yourHeroes": "I Tuoi Eroi",
    "preview.chosenStyle": "Stile Artistico",
    "preview.ready": "Tutto sembra fantastico! ✨",
    "preview.readyDesc": "Creeremo una storia magica con {{names}} in stile {{style}}.",
    "pdfPreview.bookReady": "Il tuo libro è pronto!",
    "pdfPreview.finishingIllustrations": "Completamento illustrazioni...",
    "pdfPreview.reviewMessage": "Rivedi il tuo libro e scarica quando sei pronto.",
    "pdfPreview.preparing": "Preparazione...",
    "pdfPreview.happyWithBook": "Sono soddisfatto del mio libro!",
    "pdfPreview.paymentComplete": "Pagamento completato!",
    "pdfPreview.generatingPdf": "Generazione PDF...",
    "pdfPreview.downloadPdf": "Scarica PDF",
    "pdfPreview.loveIt": "Ti piace? Ottieni la tua copia!",
    "pdfPreview.processing": "Elaborazione...",
    "pdfPreview.digitalPdf": "PDF Digitale",
    "pdfPreview.printedBook": "Libro Stampato",
    "pdfPreview.pdfDownloadShortly": "Il tuo PDF sarà scaricato a breve.",
    "pdfPreview.tryAgainMoment": "Per favore riprova tra un momento.",
    "pdfPreview.paymentSuccess": "Pagamento riuscito!",
    "pdfPreview.pdfReady": "\"{{title}}\" è pronto per il download",
    "pdfPreview.readyNextBook": "Pronto per il prossimo libro!",
    "pdfPreview.canCreateNew": "Puoi creare un nuovo libro in qualsiasi momento.",
    "nav.back": "Indietro",
    "nav.next": "Avanti",
    "nav.generateIllustrations": "Genera Illustrazioni",
    "download.pdf": "Scarica PDF",
    "complete.title": "Il Tuo Libro è Pronto! 🎉",
    "complete.subtitle": "Un libro magico con {{names}} è stato creato!",
    "complete.download": "Scarica PDF",
    "complete.startOver": "Crea un Altro Libro",
    "generating.preparingStory": "Preparazione della tua storia...",
    "generating.creatingIllustrations": "Creazione illustrazioni...",
    "generating.creatingPreviewIllustrations": "Creazione illustrazioni di anteprima...",
    "generating.creatingCover": "Creazione copertina...",
    "generating.coverCreated": "Copertina creata!",
    "generating.backCoverCreated": "Retro copertina creato!",
    "finalize.paymentConfirmed": "Pagamento confermato!",
    "finalize.generatingStory": "Generazione della tua storia completa...",
    "finalize.creatingIllustrations": "Creazione illustrazioni...",
    "finalize.addingFinishingTouches": "Ultimi ritocchi...",
    "finalize.creatingBackCover": "Creazione retro copertina...",
    "finalize.bookReadyReview": "Il tuo libro è pronto per la revisione!",
    "finalize.preparingPdf": "Preparazione del tuo PDF...",
    "resume.loadingBook": "Caricamento del tuo libro...",
    "resume.stillGenerating": "Il tuo libro è ancora in fase di creazione!",
    "resume.stillGeneratingDesc": "Le tue illustrazioni sono ancora in fase di generazione. Riceverai un'email quando sarà pronto.",
    "resume.checkBackLater": "Puoi chiudere questa pagina e tornare più tardi.",
    "illustration.noRedosLeft": "Nessun rifacimento rimasto",
    "illustration.limitReached": "Hai esaurito tutti i rifacimenti delle illustrazioni. Acquistane altri per continuare.",
    "toast.paymentCancelled": "Pagamento Annullato",
    "toast.tryAgainReady": "Il tuo libro ti aspetta. Riprova quando vuoi!",
    "toast.redoCreditsAdded": "Crediti di Rifacimento Aggiunti!",
    "toast.threeMoreRedos": "Ora hai 3 rifacimenti di illustrazione in più.",
    "toast.paymentSuccessful": "Pagamento Riuscito!",
    "toast.pdfReadyDownload": "Il tuo PDF è pronto per il download.",
    "toast.downloadStarted": "Download Avviato",
    "toast.couldntDownload": "Impossibile Scaricare",
    "toast.failedGenerate": "Generazione fallita. Per favore riprova.",
    "toast.pleaseRetry": "Per favore riprova.",
    "toast.storyCreated": "Storia Creata!",
    "toast.previewStory": "Anteprima del tuo libro qui sotto.",
    "toast.fillIllustrations": "Generazione illustrazioni rimanenti...",
    "toast.generationFailed": "Generazione Fallita",
    "toast.generationTimeout": "Timeout della generazione. Per favore riprova.",
    "toast.paymentFailed": "Pagamento Fallito",
    "toast.somethingWrong": "Qualcosa è Andato Storto",
    "toast.createBookAgain": "Per favore riprova a creare il tuo libro.",
    "toast.errorGenerating": "Si è verificato un errore durante la generazione del tuo libro.",
    "toast.printOrderConfirmed": "Ordine di Stampa Confermato!",
    "toast.bookPrintedShipped": "Il tuo libro sarà stampato e spedito presto.",
    "toast.paymentSuccessContact": "Pagamento riuscito. Contatta il supporto se necessario.",
    "toast.preparingPrintFiles": "Preparazione File di Stampa",
    "toast.readyForPrinting": "Preparazione del tuo libro per la stampa...",
    "toast.orderFailed": "Ordine Fallito",
    "toast.paymentError": "Errore di Pagamento",
    "toast.tryAgainLater": "Per favore riprova più tardi.",
    "toast.coverRegenerated": "Copertina Rigenerata!",
    "toast.newCoverReady": "La tua nuova copertina è pronta.",
    "toast.pageRegenerated": "Pagina {{page}} Rigenerata!",
    "toast.feedbackApplied": "Il tuo feedback è stato applicato.",
    "toast.freshIllustration": "Una nuova illustrazione è stata creata.",
    "toast.regenerationFailed": "Rigenerazione Fallita",
    "toast.couldNotRegenerate": "Impossibile rigenerare l'illustrazione. Per favore riprova.",
    "toast.backCoverFailed": "Retro Copertina Fallito",
    "toast.backCoverFrontUsed": "La copertina anteriore verrà utilizzata al suo posto.",
    "toast.backCoverReady": "Retro Copertina Pronto!",
    "toast.backCoverGenerated": "Il tuo retro copertina è stato generato.",
    "toast.frontCoverRequired": "Copertina Anteriore Richiesta",
    "toast.frontCoverFirst": "Genera prima una copertina anteriore.",
    "toast.backCoverRegenerated": "Retro Copertina Rigenerato!",
    "toast.backCoverFeedbackApplied": "Il tuo feedback è stato applicato al retro copertina.",
    "toast.freshBackCover": "Un nuovo retro copertina è stato creato.",
    "toast.backCoverRegenerateFailed": "Impossibile rigenerare il retro copertina.",
    "toast.allIllustrationsComplete": "Tutte le Illustrazioni Complete!",
    "toast.storybookReady": "Il tuo libro è completamente illustrato.",
    "toast.someIllustrationsIncomplete": "Alcune Illustrazioni Incomplete",
    "toast.pagesMissing": "{{count}} pagine mancano ancora di illustrazioni.",
    "toast.bookNotComplete": "Libro Non Completato",
    "toast.generateFirst": "Per favore genera prima la tua storia.",
    "toast.preparingBook": "Preparazione del Tuo Libro",
    "toast.uploadingPrint": "Caricamento file di stampa...",
    "toast.missingInfoCheckout": "Informazioni mancanti per il pagamento.",
    "toast.failedCheckout": "Impossibile creare il pagamento. Per favore riprova.",
    "toast.checkEmail": "Controlla anche la tua email per il link PDF!",
    "toast.couldntSendPdf": "Impossibile inviare il PDF. Prova a scaricarlo.",
    "toast.emailSent": "Email Inviata!",
    "toast.bookSentTo": "Il tuo libro è stato inviato a {{email}}.",
    "toast.generatingPdf": "Generazione PDF...",
    "paywall.title": "Finalizza il libro di {{names}}!",
    "paywall.subtitle": "La tua anteprima è pronta! Ottieni il PDF illustrato completo da conservare per sempre.",
    "paywall.digitalPdf": "PDF Digitale — $9,99",
    "paywall.price": "$9,99",
    "paywall.benefit1": "Libro illustrato completo (20+ pagine)",
    "paywall.benefit2": "PDF di alta qualità stampabile a casa",
    "paywall.benefit3": "Download istantaneo dopo l'acquisto",
    "paywall.processing": "Elaborazione in corso...",
    "paywall.getBook": "Ottieni il mio libro — $9,99",
    "paywall.stopTitle": "Lasciare la tua storia?",
    "paywall.stopDesc": "Il libro personalizzato di {{names}} andrà perso se esci ora.",
    "paywall.noContinue": "No, continua",
    "paywall.yesStop": "Sì, esci",
    "upsell.title": "Ordina un libro stampato!",
    "upsell.subtitle": "Hai il tuo libro digitale! Vuoi una copertina rigida premium consegnata a casa tua?",
    "upsell.specialOffer": "Offerta Speciale",
    "upsell.addPrinted": "Aggiungi libro stampato – $19,99",
    "upsell.shippingNote": "Spedizione calcolata al checkout",
    "upsell.benefit1": "Libro con copertina rigida premium",
    "upsell.benefit2": "Qualità di stampa professionale",
    "upsell.benefit3": "Un ricordo che custodiranno per sempre",
    "upsell.benefit4": "Spedizione mondiale disponibile",
    "upsell.addButton": "Aggiungi copia stampata – $19,99",
    "upsell.noThanks": "No grazie, tengo la mia copia digitale",

    "styleCard.pixar.name": "Stile Pixar",
    "styleCard.pixar.desc": "Stile cartoon 3D divertente ispirato ai film d'animazione",
    "styleCard.watercolor.name": "Acquerello Classico",
    "styleCard.watercolor.desc": "Illustrazioni acquerello morbide e sognanti",
    "styleCard.anime.name": "Stile Anime",
    "styleCard.anime.desc": "Arte vibrante ispirata all'anime giapponese",
    "generating.title": "Creazione della Tua Storia...",
    "generating.subtitle": "Stiamo creando un'avventura magica solo per te!",
    "generating.step.preparingStory": "Preparazione della storia...",
    "generating.step.creatingIllustrations": "Creazione illustrazioni...",
    "generating.step.addingDetails": "Aggiunta dettagli...",
    "generating.step.finishingTouches": "Ultimi ritocchi...",
    "finalizing.title": "Finalizzazione del Tuo Libro...",
    "finalizing.subtitle": "Creazione del libro personalizzato di {{names}}",
    "finalizing.step1": "Impostazione della storia...",
    "finalizing.step2": "Generazione del testo...",
    "finalizing.step3": "Creazione illustrazioni...",
    "finalizing.step4": "Ultimi ritocchi...",
    "finalizing.step5": "Preparazione del libro...",
    "finalizing.illustrations": "{{completed}} di {{total}} illustrazioni completate",
    "finalizing.percentComplete": "{{percent}}% completato",
    "finalizing.emailWhenReady": "Inviami un'email quando è pronto",
    "finalizing.emailWhenReadyHint": "Continueremo in background e ti invieremo un'email quando il tuo libro sarà pronto.",
    "finalizing.safeToClose": "Il tuo libro viene creato sui nostri server. Puoi chiudere questa scheda in sicurezza — continueremo a lavorare e ti invieremo un'email quando sarà pronto!",
    "finalizing.emailConfirmTitle": "Tutto pronto!",
    "finalizing.emailConfirmSubtitle": "Puoi chiudere questa pagina.",
    "finalizing.emailConfirmDesc": "Continueremo a generare il tuo libro in background e ti invieremo un'email con un link per scaricarlo quando sarà pronto.",
    "finalizing.emailConfirmNote": "Controlla la tua casella di posta tra qualche minuto.",
    "toast.backgroundStarted": "Generazione in background avviata",
    "toast.backgroundStartedDesc": "Ti invieremo un'email quando il tuo libro sarà pronto!",
    "toast.backgroundFailed": "Impossibile avviare la generazione in background. Mantieni questa pagina aperta.",
    "pdf.adventure": "Un'avventura personalizzata",
    "pdf.theEnd": "Fine",
    "pdf.storyFor": "Una storia per",
    "backCover.title": "Retro Copertina",
    "illustration.regenerating": "Rigenerazione illustrazione...",
    "illustration.redoCover": "Rifai Copertina",
    "illustration.redoPage": "Rifai Pagina",
    "illustration.redo": "Rifai",
    "illustration.buyMoreDescription": "Servono più modifiche? Ottieni 3 tentativi aggiuntivi.",
    "illustration.buyMore": "Compra 3 Tentativi",
    "illustration.lastRedo": "Questo è il tuo ultimo tentativo! Fallo contare.",
    "illustration.placeholder": "Descrivi cosa vorresti cambiare...",
    "illustration.applyChanges": "Applica Modifiche",
    "styleEdit.edit": "Modifica",
    "styleEdit.placeholder": "Descrivi come vorresti cambiare questo personaggio...",
    "styleEdit.apply": "Applica Modifiche",
    "thankYou.title": "Grazie!",
    "thankYou.subtitle": "L'avventura di {{names}} è pronta!",
    "thankYou.emailResent": "Email reinviata!",
    "thankYou.emailFailed": "Email fallita",
    "thankYou.emailSent": "Email inviata!",
    "thankYou.emailPending": "Invio email...",
    "thankYou.resending": "Reinvio...",
    "thankYou.resendEmail": "Reinvia Email",
    "thankYou.emailFailedDesc": "Non siamo riusciti a inviare l'email a {{email}}. Prova a scaricare.",
    "thankYou.emailDesc": "Il tuo libro è stato inviato a {{email}}.",
    "thankYou.emailPendingDesc": "Stiamo inviando il tuo libro a {{email}}...",
    "thankYou.printOrdered": "Copia stampata ordinata!",
    "thankYou.printDesc": "Il tuo libro stampato sarà spedito presto.",
    "thankYou.generatingPdf": "Generazione PDF...",
    "thankYou.finishingIllustrations": "Completamento illustrazioni...",
    "thankYou.downloadBook": "Scarica Libro",
    "thankYou.createAnother": "Crea un Altro Libro",
    "thankYou.hopeLove": "Speriamo che {{names}} ami la sua storia!",
    "thankYou.finalizing": "Stiamo finalizzando il tuo libro...",
    "thankYou.emailWhenReady": "Lo riceverai via email a {{email}}",
    "thankYou.doNotClose": "Per favore non chiudere questa finestra mentre prepariamo il tuo libro",
    "thankYou.bookOnItsWay": "Il tuo libro è in arrivo!",
    "thankYou.sentTo": "Inviato a {{email}}",
    "email.getBook": "Ottieni il Tuo Libro",
    "email.sendToEmail": "Invieremo il tuo libro alla tua email",
    "email.processingPayment": "Elaborazione del pagamento...",
    "email.pleaseWait": "Attendi, non chiudere questa finestra.",
    "email.bookSentAfterPayment": "Il tuo libro sarà inviato alla tua email dopo il pagamento.",
    "email.yourName": "Il Tuo Nome",
    "email.namePlaceholder": "Inserisci il tuo nome",
    "email.emailAddress": "Indirizzo Email",
    "email.emailPlaceholder": "Inserisci la tua email",
    "email.marketingConsent": "Vorrei ricevere aggiornamenti su nuove funzionalità e storie",
    "email.continuePayment": "Continua al Pagamento",
    "email.redirect": "Sarai reindirizzato alla nostra pagina di pagamento sicuro",
    "email.stepPreparing": "Preparazione del libro...",
    "email.stepCreating": "Creazione pagamento...",
    "email.stepRedirecting": "Reindirizzamento al pagamento...",
    "printPayment.title": "Preparazione dell'ordine",
    "printPayment.subtitle": "Configurazione del pagamento del libro stampato",
    "printPayment.stepUploading": "Preparazione file di stampa...",
    "printPayment.stepCreating": "Creazione pagamento...",
    "printPayment.stepRedirecting": "Apertura pagamento...",
    "maintenance.title": "Stiamo Creando Magia!",
    "maintenance.crafting": "Stiamo preparando qualcosa di incredibile. Torna presto!",
    "maintenance.devAccess": "Accesso Sviluppatore",
    "maintenance.enterPassword": "Inserisci password",
    "maintenance.accessSite": "Accedi al Sito",
    "maintenance.incorrectPassword": "Password errata",
    "maintenance.comingSoon": "Torniamo presto!",
    "contact.title": "Contattaci",
    "contact.subtitle": "Hai una domanda? Ci piacerebbe sentirti!",
    "contact.emailUs": "Inviaci un'Email",
    "contact.responseTime": "Tempo di Risposta",
    "contact.within24": "Entro 24 ore",
    "contact.liveChat": "Chat dal Vivo",
    "contact.chatHours": "Lun-Ven, 9-17",
    "contact.sendMessage": "Inviaci un Messaggio",
    "contact.yourName": "Il Tuo Nome",
    "contact.namePlaceholder": "Mario Rossi",
    "contact.email": "Email",
    "contact.emailPlaceholder": "mario@esempio.com",
    "contact.subject": "Oggetto",
    "contact.subjectPlaceholder": "Come possiamo aiutarti?",
    "contact.message": "Messaggio",
    "contact.messagePlaceholder": "Raccontaci di più...",
    "contact.sending": "Invio in corso...",
    "contact.send": "Invia Messaggio",
    "contact.sent": "Messaggio inviato! Ti risponderemo presto.",
    "contact.faq": "Domande Frequenti",
    "contact.faq1.q": "Quanto tempo ci vuole per creare un libro?",
    "contact.faq1.a": "Il tuo libro personalizzato viene generato in pochi minuti usando la nostra tecnologia avanzata.",
    "contact.faq2.q": "Cosa succede alle mie foto?",
    "contact.faq2.a": "Le tue foto vengono usate solo per la generazione delle illustrazioni e vengono eliminate automaticamente dopo la creazione del libro.",
    "contact.faq3.q": "Posso ottenere una copia stampata?",
    "contact.faq3.a": "Sì! Puoi ordinare una copia stampata premium in copertina rigida consegnata a casa tua.",
    "notFound.title": "404",
    "notFound.message": "Ops! Questa pagina non esiste.",
    "notFound.backHome": "Torna alla home",
    "payment.orderConfirmed": "Ordine Confermato!",
    "payment.printDelivery": "Il tuo libro stampato sarà spedito presto.",
    "payment.orderIssue": "Problema con l'Ordine",
    "payment.issueDesc": "Si è verificato un problema nell'elaborazione del tuo ordine. Contatta il supporto.",
    "payment.downloadReady": "Download Pronto!",
    "payment.digitalDelivery": "Il tuo libro digitale è pronto per il download.",
    "payment.submitting": "Elaborazione del Tuo Ordine...",
    "payment.paymentSuccess": "Pagamento riuscito! Configurazione ordine...",
    "payment.contactSupport": "Contatta il supporto con il tuo ID sessione:",
    "payment.backHome": "Torna alla Home",
    "payment.digitalThankYou": "Grazie per l'acquisto! Il tuo libro digitale è pronto.",
    "payment.printThankYou": "Grazie per l'acquisto! Il tuo libro stampato sarà spedito presto.",
    "payment.orderDetails": "Dettagli Ordine",
    "payment.bookTitle": "Libro Personalizzato",
    "payment.orderId": "ID Ordine:",
    "payment.createAnother": "Crea un Altro Libro",
    "shipping.title": "Indirizzo di Spedizione",
    "shipping.for": "Per:",
    "shipping.fullName": "Nome Completo",
    "shipping.nameRequired": "Il nome è richiesto",
    "shipping.emailRequired": "L'email è richiesta",
    "shipping.emailInvalid": "Inserisci un'email valida",
    "shipping.streetRequired": "L'indirizzo è richiesto",
    "shipping.cityRequired": "La città è richiesta",
    "shipping.stateRequired": "La regione è richiesta",
    "shipping.postalRequired": "Il CAP è richiesto",
    "shipping.phoneRequired": "Il numero di telefono è richiesto",
    "shipping.email": "Email",
    "shipping.streetAddress": "Indirizzo",
    "shipping.apartment": "Appartamento / Interno",
    "shipping.city": "Città",
    "shipping.postalCode": "CAP",
    "shipping.country": "Paese",
    "shipping.stateProvince": "Regione / Provincia",
    "shipping.optional": "Facoltativo",
    "shipping.phoneNumber": "Numero di Telefono",
    "shipping.phoneHint": "Necessario per le notifiche di consegna",
    "shipping.cancel": "Annulla",
    "shipping.continuePayment": "Continua al Pagamento",
    "shipping.processing": "Elaborazione...",
    "shipping.state": "Regione",
    "shipping.selectCountry": "Seleziona paese",
    "shipping.selectState": "Seleziona regione",
    "shipping.deliveryOptions": "Opzioni di Spedizione",
    "shipping.chooseDelivery": "Scegli Spedizione",
    "shipping.loadingOptions": "Caricamento opzioni...",
    "shipping.selectDeliverySpeed": "Scegli la velocità di spedizione preferita:",
    "shipping.businessDays": "giorni lavorativi",
    "shipping.estimateUnavailable": "Stima non disponibile",
    "shipping.back": "Indietro",
    "shipping.included": "Incluso",
    "pdfPreview.printComingSoon": "Prossimamente!",
    "queue.position": "Posizione nella coda: {{position}}",
    "queue.waiting": "In attesa nella coda...",

    // Order Tracking
    "trackOrder.title": "Traccia Il Mio Ordine",
    "trackOrder.subtitle": "Inserisci la tua email per vedere lo storico ordini",
    "trackOrder.emailPlaceholder": "tua@email.com",
    "trackOrder.search": "Cerca",
    "trackOrder.error": "Qualcosa è andato storto. Riprova.",
    "trackOrder.noOrders": "Nessun Ordine Trovato",
    "trackOrder.noOrdersDesc": "Non abbiamo trovato ordini con questa email.",
    "trackOrder.createBook": "Crea Il Tuo Primo Libro",
    "trackOrder.foundOrders": "{{count}} ordine/i trovato/i",
    "trackOrder.digital": "PDF Digitale",
    "trackOrder.printed": "Libro Stampato",
    "trackOrder.status.pending": "In attesa",
    "trackOrder.status.paid": "Pagato",
    "trackOrder.status.fulfilled": "Completato",
    "trackOrder.status.shipped": "Spedito",
    "footer.myBooks": "Il Mio Account",
    "auth.loginTitle": "Bentornato",
    "auth.loginSubtitle": "Accedi per vedere i tuoi libri",
    "auth.signupTitle": "Crea Account",
    "auth.signupSubtitle": "Registrati per salvare libri e personaggi",
    "auth.forgotTitle": "Reimposta Password",
    "auth.forgotSubtitle": "Inserisci la tua email per ricevere un link di reset",
    "auth.displayName": "Nome visualizzato",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.loginButton": "Accedi",
    "auth.signupButton": "Crea Account",
    "auth.resetButton": "Invia Link",
    "auth.forgotPassword": "Password dimenticata?",
    "auth.noAccount": "Non hai un account?",
    "auth.signupLink": "Registrati",
    "auth.hasAccount": "Hai già un account?",
    "auth.loginLink": "Accedi",
    "auth.backToLogin": "Torna al login",
    "auth.error": "Errore",
    "auth.errorDesc": "Qualcosa è andato storto. Riprova.",
    "auth.welcomeTitle": "Benvenuto!",
    "auth.welcomeDesc": "Il tuo account è stato creato con successo.",
    "auth.resetSent": "Link Inviato",
    "auth.resetSentDesc": "Controlla la tua email per il link di reset.",
    "dashboard.title": "La Mia Dashboard",
    "dashboard.subtitle": "Gestisci i tuoi libri e personaggi",
    "dashboard.logout": "Esci",
    "dashboard.newBook": "Nuovo Libro",
    "dashboard.myBooks": "I Miei Libri",
    "dashboard.myKids": "I Miei Personaggi",
    "dashboard.inProgress": "In Corso",
    "dashboard.digital": "Digitale",
    "dashboard.printed": "Stampato",
    "dashboard.noInProgress": "Nessun libro in corso",
    "dashboard.noDigital": "Nessun libro digitale",
    "dashboard.noPrinted": "Nessun libro stampato",
    "dashboard.createFirst": "Crea il Tuo Primo Libro",
    "dashboard.untitled": "Libro Senza Titolo",
    "dashboard.addChild": "Aggiungi Bambino",
    "dashboard.editChild": "Modifica Bambino",
    "dashboard.addFirstChild": "Aggiungi il Tuo Primo Bambino",
    "dashboard.noKids": "Nessun personaggio salvato",
    "dashboard.childFormDesc": "Salva le info del tuo bambino per creare libri velocemente",
    "dashboard.photo": "Foto",
    "dashboard.yearsOld": "anni",
    "dashboard.savedStyles": "Stili salvati:",
    "dashboard.save": "Salva",
    "dashboard.childAdded": "Personaggio aggiunto!",
    "dashboard.childUpdated": "Personaggio aggiornato!",
    "dashboard.childRemoved": "Personaggio rimosso",
    "dashboard.profile": "Profilo",
    "dashboard.personalInfo": "Informazioni personali",
    "dashboard.fullName": "Nome completo",
    "dashboard.fullNamePlaceholder": "Inserisci il tuo nome completo",
    "dashboard.email": "E-mail",
    "dashboard.phone": "Numero di telefono",
    "dashboard.phonePlaceholder": "Inserisci il tuo numero di telefono",
    "dashboard.shippingAddress": "Indirizzo di spedizione",
    "dashboard.addressLine1": "Indirizzo riga 1",
    "dashboard.addressLine1Placeholder": "Indirizzo",
    "dashboard.addressLine2": "Indirizzo riga 2",
    "dashboard.addressLine2Placeholder": "Appartamento, suite, ecc. (opzionale)",
    "dashboard.city": "Città",
    "dashboard.cityPlaceholder": "Città",
    "dashboard.state": "Stato / Provincia",
    "dashboard.statePlaceholder": "Stato / Provincia",
    "dashboard.postalCode": "Codice postale",
    "dashboard.postalCodePlaceholder": "Codice postale",
    "dashboard.country": "Paese",
    "dashboard.countryPlaceholder": "Paese",
    "dashboard.saveProfile": "Salva profilo",
    "dashboard.saved": "Salvato!",
    "dashboard.profileSaved": "Profilo salvato!",
    "dashboard.profileSaveError": "Errore nel salvataggio del profilo",
    "dashboard.confirmDelete": "Rimuovere il personaggio?",
    "dashboard.confirmDeleteDesc": "Questo rimuoverà permanentemente questo personaggio e i suoi stili salvati.",
    "dashboard.delete": "Rimuovi",
    "dashboard.generateStyle": "Genera Stile",
    "dashboard.regenerateStyle": "Rigenera",
    "dashboard.selectStyle": "Seleziona stile artistico",
    "dashboard.generatingStyle": "Generazione stile...",
    "dashboard.styleGenerated": "Stile generato!",
    "dashboard.needsPhoto": "Carica prima una foto",
    "dashboard.continueBook": "Continua",
    "dashboard.downloadPdf": "Scarica",
    "dashboard.trackOrder": "Traccia",
    "dashboard.statusPending": "In Corso",
    "dashboard.statusGenerating": "In Generazione",
    "dashboard.statusProcessing": "In Elaborazione",
    "dashboard.statusPaid": "Pagato",
    "dashboard.statusCompleted": "Completato",
    "dashboard.statusFulfilled": "Consegnato",
    "dashboard.statusShipped": "Spedito",
    "dashboard.buyHardCopy": "Acquista Copia Stampata",
    "thankYou.illustrationProgress": "Illustrazioni: {{completed}} / {{total}}",

    // Theme Categories
    "category.friendship": "Amicizia",
    "category.growingUp": "Crescere",
    "category.kindness": "Gentilezza ed Emozioni",
    "category.values": "Valori",
    "category.adventure": "Avventura",
    "category.fantasy": "Fantasia",
    "category.humor": "Umorismo",
    "category.learning": "Apprendimento",
    "category.diversity": "Diversità e Inclusione",
    "category.nature": "Natura e Ambiente",
    "category.problemSolving": "Problem Solving",
    "category.lifeExperiences": "Esperienze di Vita",
    "category.cultural": "Storie Culturali",

    // Theme Titles & Descriptions
    "theme.makingFriends.title": "Fare Amicizia",
    "theme.makingFriends.desc": "Una storia su come fare nuove amicizie e la gioia della connessione",
    "theme.bestFriends.title": "Migliori Amici",
    "theme.bestFriends.desc": "Un'avventura che celebra il legame tra migliori amici",
    "theme.sharing.title": "Condividere",
    "theme.sharing.desc": "Imparare la felicità che viene dal condividere con gli altri",
    "theme.siblings.title": "Fratelli e Sorelle",
    "theme.siblings.desc": "Un racconto commovente sul legame speciale tra fratelli",
    "theme.sayingSorry.title": "Chiedere Scusa",
    "theme.sayingSorry.desc": "Imparare quando e come scusarsi e rimediare",
    "theme.beingYourself.title": "Essere Se Stessi",
    "theme.beingYourself.desc": "Scoprire la magia di essere fedeli a se stessi",
    "theme.tryingNew.title": "Provare Cose Nuove",
    "theme.tryingNew.desc": "Un viaggio emozionante fuori dalla zona di comfort",
    "theme.overcomingFear.title": "Superare la Paura",
    "theme.overcomingFear.desc": "Un'avventura coraggiosa per affrontare e vincere le paure",
    "theme.findingTalent.title": "Trovare il Tuo Talento",
    "theme.findingTalent.desc": "Scoprire un talento speciale che ti rende unico",
    "theme.confidence.title": "Fiducia in Sé",
    "theme.confidence.desc": "Costruire la fiducia in se stessi e credere in sé",
    "theme.understandingFeelings.title": "Capire i Sentimenti",
    "theme.understandingFeelings.desc": "Imparare a riconoscere e capire le emozioni",
    "theme.helpingOthers.title": "Aiutare gli Altri",
    "theme.helpingOthers.desc": "La gioia e la ricompensa di dare una mano",
    "theme.dealingAnger.title": "Gestire la Rabbia",
    "theme.dealingAnger.desc": "Modi sani per gestire ed esprimere la rabbia",
    "theme.copingSadness.title": "Affrontare la Tristezza",
    "theme.copingSadness.desc": "Trovare conforto e speranza nei momenti tristi",
    "theme.showingEmpathy.title": "Mostrare Empatia",
    "theme.showingEmpathy.desc": "Capire e preoccuparsi di come si sentono gli altri",
    "theme.honesty.title": "Onestà",
    "theme.honesty.desc": "L'importanza di dire la verità",
    "theme.responsibility.title": "Responsabilità",
    "theme.responsibility.desc": "Imparare ad assumersi la responsabilità delle proprie azioni",
    "theme.respect.title": "Rispetto",
    "theme.respect.desc": "Trattare gli altri con gentilezza e rispetto",
    "theme.perseverance.title": "Perseveranza",
    "theme.perseverance.desc": "Non arrendersi mai anche quando le cose si fanno difficili",
    "theme.goodChoices.title": "Fare Buone Scelte",
    "theme.goodChoices.desc": "Imparare a prendere decisioni sagge",
    "theme.jungleAdventure.title": "Avventura nella Giungla",
    "theme.jungleAdventure.desc": "Un'emozionante spedizione attraverso una giungla selvaggia",
    "theme.desertJourney.title": "Viaggio nel Deserto",
    "theme.desertJourney.desc": "Un emozionante viaggio attraverso un vasto deserto",
    "theme.oceanQuest.title": "Missione Oceanica",
    "theme.oceanQuest.desc": "Immergersi in un'avventura sottomarina",
    "theme.spaceExploration.title": "Esplorazione Spaziale",
    "theme.spaceExploration.desc": "Decollare per un'incredibile avventura spaziale",
    "theme.mountainExpedition.title": "Spedizione in Montagna",
    "theme.mountainExpedition.desc": "Scalare la cima di una magnifica montagna",
    "theme.magicalWorlds.title": "Mondi Magici",
    "theme.magicalWorlds.desc": "Esplorare terre incantate piene di meraviglie",
    "theme.talkingAnimals.title": "Animali Parlanti",
    "theme.talkingAnimals.desc": "Avventure con animali che possono parlare",
    "theme.wizardsSpells.title": "Maghi e Incantesimi",
    "theme.wizardsSpells.desc": "Imparare la magia da saggi maghi",
    "theme.mythicalCreatures.title": "Creature Mitiche",
    "theme.mythicalCreatures.desc": "Incontrare draghi, unicorni e altri esseri magici",
    "theme.timeTravel.title": "Viaggio nel Tempo",
    "theme.timeTravel.desc": "Viaggiare nel tempo verso epoche incredibili",
    "theme.sillyAnimals.title": "Animali Buffi",
    "theme.sillyAnimals.desc": "Avventure esilaranti con animali divertenti",
    "theme.wordplayJokes.title": "Giochi di Parole e Battute",
    "theme.wordplayJokes.desc": "Una storia piena di battute e risate",
    "theme.absurdSituations.title": "Situazioni Assurde",
    "theme.absurdSituations.desc": "Avventure pazze e meravigliosamente assurde",
    "theme.mischievous.title": "Avventure Birichine",
    "theme.mischievous.desc": "Scherzi giocosi e divertimento birichino",
    "theme.unexpectedSurprises.title": "Sorprese Inaspettate",
    "theme.unexpectedSurprises.desc": "Una storia piena di colpi di scena e sorprese",
    "theme.numbersCounting.title": "Numeri e Conteggio",
    "theme.numbersCounting.desc": "Un'avventura divertente con numeri e conteggio",
    "theme.lettersReading.title": "Lettere e Lettura",
    "theme.lettersReading.desc": "Scoprire la magia delle lettere e delle parole",
    "theme.scienceExperiments.title": "Esperimenti Scientifici",
    "theme.scienceExperiments.desc": "Scoperte emozionanti attraverso la scienza",
    "theme.history.title": "Storia",
    "theme.history.desc": "Viaggiare nel passato per scoprire la storia",
    "theme.problemSolvingSkills.title": "Risoluzione Problemi",
    "theme.problemSolvingSkills.desc": "Usare creatività e logica per risolvere problemi",
    "theme.differentCultures.title": "Culture Diverse",
    "theme.differentCultures.desc": "Esplorare e celebrare le culture di tutto il mondo",
    "theme.differentAbilities.title": "Abilità Diverse",
    "theme.differentAbilities.desc": "Celebrare ciò che rende ognuno unico",
    "theme.familyDiversity.title": "Diversità Familiare",
    "theme.familyDiversity.desc": "Tutti i tipi di famiglie e l'amore che condividono",
    "theme.genderExpression.title": "Espressione di Genere",
    "theme.genderExpression.desc": "Essere liberi di esprimere chi sei veramente",
    "theme.acceptanceBelonging.title": "Accettazione e Appartenenza",
    "theme.acceptanceBelonging.desc": "Trovare il proprio posto ed essere accettati",
    "theme.forestsTrees.title": "Foreste e Alberi",
    "theme.forestsTrees.desc": "Avventure in foreste magiche e alberi antichi",
    "theme.oceansSealife.title": "Oceani e Vita Marina",
    "theme.oceansSealife.desc": "Scoprire le meraviglie sotto le onde",
    "theme.animalsHabitats.title": "Animali e Habitat",
    "theme.animalsHabitats.desc": "Imparare sugli animali e dove vivono",
    "theme.weatherSeasons.title": "Meteo e Stagioni",
    "theme.weatherSeasons.desc": "Esplorare la magia delle stagioni che cambiano",
    "theme.protectingPlanet.title": "Proteggere il Pianeta",
    "theme.protectingPlanet.desc": "Prendersi cura della nostra bella Terra",
    "theme.mysteriesClues.title": "Misteri e Indizi",
    "theme.mysteriesClues.desc": "Risolvere misteri emozionanti con indizi ingegnosi",
    "theme.puzzlesRiddles.title": "Puzzle e Indovinelli",
    "theme.puzzlesRiddles.desc": "Rompicapi e indovinelli intriganti",
    "theme.causeEffect.title": "Causa ed Effetto",
    "theme.causeEffect.desc": "Capire come le azioni portano a risultati",
    "theme.teamProblemSolving.title": "Problem Solving di Squadra",
    "theme.teamProblemSolving.desc": "Lavorare insieme per risolvere grandi sfide",
    "theme.creativeSolutions.title": "Soluzioni Creative",
    "theme.creativeSolutions.desc": "Pensare fuori dagli schemi per trovare risposte",
    "theme.startingSchool.title": "Iniziare la Scuola",
    "theme.startingSchool.desc": "L'emozionante avventura di iniziare la scuola",
    "theme.movingHome.title": "Trasferirsi",
    "theme.movingHome.desc": "Adattarsi a una nuova casa e un nuovo quartiere",
    "theme.losingPet.title": "Perdere un Animale",
    "theme.losingPet.desc": "Affrontare la perdita di un animale amato",
    "theme.newSibling.title": "Nuovo Fratellino",
    "theme.newSibling.desc": "Dare il benvenuto a un nuovo fratellino o sorellina",
    "theme.dealingChange.title": "Affrontare il Cambiamento",
    "theme.dealingChange.desc": "Imparare ad accettare e adattarsi ai cambiamenti",
    "theme.fairyTales.title": "Fiabe",
    "theme.fairyTales.desc": "Avventure classiche delle fiabe reinventate",
    "theme.folktales.title": "Racconti Popolari",
    "theme.folktales.desc": "Storie tradizionali tramandate di generazione in generazione",
    "theme.mythsLegends.title": "Miti e Leggende",
    "theme.mythsLegends.desc": "Racconti epici dalla mitologia e dalle leggende",
    "theme.fablesMorals.title": "Favole e Morale",
    "theme.fablesMorals.desc": "Storie con preziose lezioni di vita",
    "theme.holidayTraditions.title": "Tradizioni Festive",
    "theme.holidayTraditions.desc": "Celebrare feste e tradizioni speciali",
  },
  zh: {
    "seo.title": "StoryHeroes – 为孩子量身打造的个性化故事书",
    "seo.description": "让您的孩子成为神奇故事书的主角。上传照片，选择冒险主题，几分钟内即可获得精美插图的个性化 PDF。",
    "hero.badge": "✨ 个性化故事书",
    "hero.title": "让您的孩子成为故事书英雄",
    "hero.title.create": "创造",
    "hero.title.magical": "神奇故事",
    "hero.title.starring": "您的孩子主演",
    "hero.subtitle": "上传照片，选择冒险，几分钟内获得精美插图PDF或精装书。",
    "hero.cta": "创建您的故事",
    "hero.freePreview": "免费创建和预览您的书",
    "hero.privacyStamp": "🔒 生成后删除照片",
    "hero.themes": "50+ 主题",
    "hero.aiArt": "定制插图",
    "hero.pdfDownload": "即时PDF",
    "hero.features.personalized": "个性化",
    "hero.features.ai": "定制艺术",
    "hero.features.instant": "即时PDF",
    "transform.badge": "✨ 见证魔法",
    "transform.title": "从照片到故事书角色",
    "transform.subtitle": "我们将您孩子的照片转化为精美的插图角色",
    "transform.pixar": "皮克斯风格",
    "transform.watercolor": "水彩风格",
    "transform.anime": "动漫风格",
    "showcase.title": "精美书籍，数字与印刷",
    "showcase.subtitle": "获取您的个性化故事书，数字PDF或精美印刷精装本",
    "showcase.pdfTitle": "数字PDF",
    "showcase.pdfDesc": "即时下载，任何设备可读",
    "showcase.printTitle": "印刷书籍",
    "showcase.printDesc": "优质精装本送货上门",
    "showcase.pages": "32页",
    "showcase.popular": "最受欢迎",
    "reviews.badge": "⭐ 家长们的最爱",
    "reviews.title": "家长们怎么说",
    "reviews.subtitle": "加入成千上万创造了神奇故事的幸福家庭",
    "reviews.child": "孩子年龄：",
    "reviews.1.text": "我女儿特别喜欢看到自己成为英雄！插图令人惊叹，故事非常适合她的年龄。",
    "reviews.1.childAge": "5岁",
    "reviews.2.text": "令人难以置信的礼物创意！我儿子每晚睡前都会读他的故事。PDF的质量令人惊叹。",
    "reviews.2.childAge": "4岁",
    "reviews.3.text": "我被质量震撼了。完美地捕捉了我孩子的相似之处。强烈推荐！",
    "reviews.3.childAge": "6岁",
    "reviews.4.text": "我们已经订了三本书，每个孩子一本。他们争着谁先读自己的！",
    "reviews.4.childAge": "3、5、7岁",
    "reviews.5.text": "水彩风格绝对华丽。看起来就像专业的儿童书插画师制作的。",
    "reviews.5.childAge": "4岁",
    "reviews.6.text": "最好的生日礼物！我儿子看到自己出现在故事中时的表情是无价的。",
    "reviews.6.childAge": "6岁",
    "trust.gift.title": "完美礼物",
    "trust.gift.desc": "一份独特的个性化礼物，孩子们珍藏多年",
    "trust.ai.title": "精美艺术",
    "trust.ai.desc": "我们的技术以您孩子的形象创作令人惊叹的插图",
    "trust.love.title": "用爱制作",
    "trust.love.desc": "每个故事都精心制作，激发想象力和快乐",
    "cta.title": "准备好创造魔法了吗？",
    "cta.subtitle": "让您的孩子成为自己冒险故事的英雄",
    "cta.button": "立即开始创作",
    "cta.note": "无需账户 • 5分钟完成",
    
    // Language Selector
    "lang.title": "选择您的语言",
    "lang.subtitle": "选择您的首选语言以继续",
    
    // Navigation
    "nav.backToHome": "返回首页",
    
    // Footer
    "footer.copyright": "© 2024 StoryHeroes. 保留所有权利。",
    "footer.privacy": "隐私政策",
    "footer.terms": "服务条款",
    "footer.contact": "联系我们",
    "footer.trackOrder": "追踪订单",
    
    // Privacy Page
    "privacy.title": "隐私政策",
    "privacy.updated": "最后更新：2024年1月",
    "privacy.section1.title": "1. 我们收集的信息",
    "privacy.section1.intro": "当您使用我们的服务时，我们会收集以下信息：",
    "privacy.section1.item1": "您上传的用于创建个性化故事书的照片",
    "privacy.section1.item2": "用于故事个性化的孩子姓名和年龄",
    "privacy.section1.item3": "用于订单确认和交付的电子邮件地址",
    "privacy.section1.item4": "支付信息（通过Stripe安全处理）",
    "privacy.section2.title": "2. 我们如何使用您的信息",
    "privacy.section2.intro": "我们使用您的信息来：",
    "privacy.section2.item1": "生成个性化故事书",
    "privacy.section2.item2": "处理付款并交付您的PDF故事书",
    "privacy.section2.item3": "发送订单确认和更新",
    "privacy.section2.item4": "改进我们的服务和用户体验",
    "privacy.section2.item5": "遵守法律义务",
    "privacy.section3.title": "3. 数据存储和安全",
    "privacy.section3.text": "我们非常重视数据安全。所有上传的照片和个人信息都经过加密并安全存储。我们使用行业标准的安全措施来保护您的数据免受未经授权的访问。",
    "privacy.section4.title": "4. 第三方服务",
    "privacy.section4.text": "我们使用值得信赖的第三方服务，包括用于支付处理的Stripe和用于故事生成的专业服务。这些服务有自己的隐私政策和安全措施。",
    "privacy.section5.title": "5. 数据保留",
    "privacy.section5.text": "我们仅在提供服务和遵守法律义务所需的时间内保留您的数据。您可以随时通过联系我们请求删除您的数据。",
    "privacy.section6.title": "6. 儿童隐私",
    "privacy.section6.text": "我们致力于保护儿童的隐私。未经父母同意，我们不会故意收集13岁以下儿童的个人信息。父母对其孩子的信息拥有完全控制权。",
    "privacy.section7.title": "7. 您的权利",
    "privacy.section7.intro": "您有权：",
    "privacy.section7.item1": "访问您的个人数据",
    "privacy.section7.item2": "请求更正不准确的数据",
    "privacy.section7.item3": "请求删除您的数据",
    "privacy.section7.item4": "选择退出营销通信",
    "privacy.section8.title": "8. 联系我们",
    "privacy.section8.text": "如果您对本隐私政策有任何疑问，请通过我们的联系页面与我们联系。",
    
    // Terms Page
    "terms.title": "服务条款",
    "terms.updated": "最后更新：2024年1月",
    "terms.section1.title": "1. 接受条款",
    "terms.section1.text": "使用StoryHeroes即表示您同意这些服务条款。如果您不同意，请不要使用我们的服务。",
    "terms.section2.title": "2. 服务描述",
    "terms.section2.text": "StoryHeroes为儿童提供个性化故事书。我们根据您提供的照片和信息创建定制的插图故事。",
    "terms.section3.title": "3. 用户责任",
    "terms.section3.intro": "您同意：",
    "terms.section3.item1": "提供准确的信息",
    "terms.section3.item2": "仅上传您有权使用的照片",
    "terms.section3.item3": "不将服务用于非法目的",
    "terms.section3.item4": "不上传不当或冒犯性内容",
    "terms.section3.item5": "尊重知识产权",
    "terms.section4.title": "4. 付款和退款",
    "terms.section4.text": "所有付款均通过Stripe安全处理。由于我们服务的个性化性质，仅在技术问题导致无法交付您的故事书时才提供退款。",
    "terms.section5.title": "5. 知识产权",
    "terms.section5.intro": "关于知识产权：",
    "terms.section5.item1": "您保留上传照片的权利",
    "terms.section5.item2": "生成的故事和插图仅供个人使用",
    "terms.section5.item3": "您不得转售或重新分发生成的内容",
    "terms.section5.item4": "StoryHeroes保留服务和技术的权利",
    "terms.section5.item5": "您授予我们使用上传内容生成您的故事书的许可",
    "terms.section6.title": "6. 责任限制",
    "terms.section6.text": "StoryHeroes按\"原样\"提供，不提供任何保证。我们不对因使用我们的服务而产生的任何间接、附带或后果性损害负责。",
    "terms.section7.title": "7. 内容指南",
    "terms.section7.text": "我们保留拒绝服务或删除违反我们指南的内容的权利，包括不当、冒犯性或受版权保护的材料。",
    "terms.section8.title": "8. 条款变更",
    "terms.section8.text": "我们可能会更新这些服务条款。变更后继续使用服务即表示接受新条款。",
    "terms.section9.title": "9. 终止",
    "terms.section9.text": "我们保留因违反这些条款或出于任何其他原因自行决定终止或暂停访问我们服务的权利。",
    "terms.section10.title": "10. 联系",
    "terms.section10.text": "如有关于这些服务条款的问题，请通过我们的联系页面与我们联系。",

    // Wizard Steps
    "step.addCharacters": "添加角色",
    "step.chooseStory": "选择故事",
    "step.pickTitle": "选择标题",
    "step.chooseStyle": "选择风格",
    "step.review": "审核",
    "step.storyOutline": "故事大纲",
    "step.previewPdf": "PDF预览",
    "photo.title": "添加您的角色",
    "photo.subtitle": "上传照片并输入故事中每个角色的详细信息。",
    "photo.privacyNotice": "🔒 您的照片仅用于插图，生成后将被删除。",
    "photo.addPhoto": "添加照片",
    "photo.dragDrop": "或拖放",
    "photo.dropHere": "放在这里！",
    "photo.browse": "浏览",
    "photo.camera": "相机",
    "photo.upload": "上传",
    "photo.name": "姓名",
    "photo.namePlaceholder": "输入姓名",
    "photo.age": "年龄",
    "photo.agePlaceholder": "输入年龄",
    "photo.gender": "角色类型",
    "photo.boy": "男孩",
    "photo.girl": "女孩",
    "photo.pet": "宠物",
    "photo.addChild": "添加另一个角色",
    "photo.savedKids": "您保存的角色",
    "familyImport.button": "从合照导入",
    "style.choose": "选择您的艺术风格",
    "style.subtitleSelected": "看看您的{{count}}个角色在这种风格中的样子",
    "style.subtitleUnselected": "为您的故事书选择一种插图风格",
    "style.yourCharactersIn": "您的角色在{{style}}风格中",
    "style.creatingName": "正在创建{{name}}...",
    "style.altCharacterInStyle": "{{name}}的{{style}}风格",
    "style.failedShort": "失败",
    "style.retry": "重试",
    "style.hoverHint": "将鼠标悬停在图像上以查看大图",
    "style.preserveHint": "角色描述将被保留以保持一致性",
    "style.tryAgain": "重试",
    "style.continueWith": "使用{{style}}继续",
    "style.switchHint": "您可以在继续之前随时切换风格",
    "title.choose": "选择您的标题",
    "title.subtitle": "为您的个性化故事书选择完美的标题",
    "title.creating": "正在创建神奇的标题...",
    "title.selected": "已选择",
    "title.regenerate": "重新生成标题",
    "title.usingSuggested": "建议的标题",
    "title.pickFavorite": "从这些建议中选择您的最爱！",
    "themes.title": "为{{hero}}选择一个主题",
    "themes.subtitle": "为您的{{count}}个{{characterWord}}选择一个冒险主题。每个主题都会创造独特的故事。",
    "themes.changeTheme": "更改主题",
    "themes.whatsStoryAbout": "{{hero}}的故事应该关于什么？",
    "themes.yourIdeas": "您的故事创意",
    "themes.ideaHelperText": "描述您想要的冒险。尽情发挥创意！",
    "themes.ideaPlaceholder": "例如：一个勇敢的骑士与龙成为朋友，他们一起拯救了一个神奇的王国...",
    "themes.ideaHint": "好的开始！点击下方获取增强的故事创意。",
    "themes.generateRequired": "写下您自己的创意或点击下方获取灵感！",
    "themes.generatingIdeas": "正在生成创意...",
    "themes.completeMyIdeas": "完善我的创意",
    "themes.inspireMe": "给我灵感！",
    "themes.chooseIdea": "选择一个故事创意",
    "themes.usingPreMade": "预设创意",
    "themes.greatIdeas": "这里有一些很棒的故事创意！",
    "outline.title": "故事大纲",
    "outline.subtitle": "审核和自定义您的故事书页面",
    "outline.generating": "正在创建您的故事大纲...",
    "outline.titlePage": "标题页",
    "outline.ending": "结尾",
    "outline.page": "页",
    "outline.happy": "对大纲满意吗？",
    "outline.happyDesc": "您可以编辑上方任何页面的文字。准备好后，点击下一步生成插图！",
    "outline.sceneDescription": "场景描述",
    "outline.whatYouWillSee": "这一页你将看到的内容",
    "preview.title": "审核您的故事",
    "preview.subtitle": "一切看起来不错？让我们创建您的插图故事书！",
    "preview.storyTheme": "故事主题",
    "preview.yourHeroes": "您的英雄",
    "preview.chosenStyle": "艺术风格",
    "preview.ready": "一切看起来很棒！✨",
    "preview.readyDesc": "我们将用{{style}}风格创造一个以{{names}}为主角的神奇故事。",
    "pdfPreview.bookReady": "您的书准备好了！",
    "pdfPreview.finishingIllustrations": "正在完成插图...",
    "pdfPreview.reviewMessage": "审核您的故事书，准备好后下载。",
    "pdfPreview.preparing": "准备中...",
    "pdfPreview.happyWithBook": "我对我的书很满意！",
    "pdfPreview.paymentComplete": "付款完成！",
    "pdfPreview.generatingPdf": "正在生成PDF...",
    "pdfPreview.downloadPdf": "下载PDF",
    "pdfPreview.loveIt": "喜欢吗？获取您的副本！",
    "pdfPreview.processing": "处理中...",
    "pdfPreview.digitalPdf": "数字PDF",
    "pdfPreview.printedBook": "印刷书籍",
    "pdfPreview.pdfDownloadShortly": "您的PDF将很快下载。",
    "pdfPreview.tryAgainMoment": "请稍后重试。",
    "pdfPreview.paymentSuccess": "付款成功！",
    "pdfPreview.pdfReady": "\"{{title}}\"已准备好下载",
    "pdfPreview.readyNextBook": "准备好您的下一本书！",
    "pdfPreview.canCreateNew": "您可以随时创建新书。",
    "nav.back": "返回",
    "nav.next": "下一步",
    "nav.generateIllustrations": "生成插图",
    "download.pdf": "下载PDF",
    "complete.title": "您的书准备好了！🎉",
    "complete.subtitle": "以{{names}}为主角的神奇故事书已创建！",
    "complete.download": "下载PDF",
    "complete.startOver": "创建另一本书",
    "generating.preparingStory": "正在准备您的故事...",
    "generating.creatingIllustrations": "正在创建插图...",
    "generating.creatingPreviewIllustrations": "正在创建预览插图...",
    "generating.creatingCover": "正在创建封面...",
    "generating.coverCreated": "封面已创建！",
    "generating.backCoverCreated": "封底已创建！",
    "finalize.paymentConfirmed": "付款已确认！",
    "finalize.generatingStory": "正在生成完整故事...",
    "finalize.creatingIllustrations": "正在创建插图...",
    "finalize.addingFinishingTouches": "最后润色...",
    "finalize.creatingBackCover": "正在创建封底...",
    "finalize.bookReadyReview": "您的书已准备好审核！",
    "finalize.preparingPdf": "正在准备您的PDF...",
    "resume.loadingBook": "正在加载您的书...",
    "resume.stillGenerating": "您的书仍在创建中！",
    "resume.stillGeneratingDesc": "插图仍在后台生成中。准备好后您将收到电子邮件。",
    "resume.checkBackLater": "您可以关闭此页面，稍后再回来查看。",
    "illustration.noRedosLeft": "没有重做次数了",
    "illustration.limitReached": "您已用完所有插图重做次数。购买更多以继续。",
    "toast.paymentCancelled": "付款已取消",
    "toast.tryAgainReady": "您的书在等您。随时重试！",
    "toast.redoCreditsAdded": "重做积分已添加！",
    "toast.threeMoreRedos": "您现在有3次额外的插图重做机会。",
    "toast.paymentSuccessful": "付款成功！",
    "toast.pdfReadyDownload": "您的PDF已准备好下载。",
    "toast.downloadStarted": "下载已开始",
    "toast.couldntDownload": "无法下载",
    "toast.failedGenerate": "生成失败。请重试。",
    "toast.pleaseRetry": "请重试。",
    "toast.storyCreated": "故事已创建！",
    "toast.previewStory": "在下方预览您的故事书。",
    "toast.fillIllustrations": "正在生成剩余插图...",
    "toast.generationFailed": "生成失败",
    "toast.generationTimeout": "生成超时。请重试。",
    "toast.paymentFailed": "付款失败",
    "toast.somethingWrong": "出了点问题",
    "toast.createBookAgain": "请重新尝试创建您的书。",
    "toast.errorGenerating": "生成您的书时发生错误。",
    "toast.printOrderConfirmed": "印刷订单已确认！",
    "toast.bookPrintedShipped": "您的书将很快印刷并发货。",
    "toast.paymentSuccessContact": "付款成功。如需帮助请联系客服。",
    "toast.preparingPrintFiles": "正在准备印刷文件",
    "toast.readyForPrinting": "正在为印刷准备您的书...",
    "toast.orderFailed": "订单失败",
    "toast.paymentError": "付款错误",
    "toast.tryAgainLater": "请稍后重试。",
    "toast.coverRegenerated": "封面已重新生成！",
    "toast.newCoverReady": "您的新封面已准备好。",
    "toast.pageRegenerated": "第{{page}}页已重新生成！",
    "toast.feedbackApplied": "您的反馈已应用。",
    "toast.freshIllustration": "一幅新插图已创建。",
    "toast.regenerationFailed": "重新生成失败",
    "toast.couldNotRegenerate": "无法重新生成插图。请重试。",
    "toast.backCoverFailed": "封底失败",
    "toast.backCoverFrontUsed": "将使用封面代替。",
    "toast.backCoverReady": "封底已准备好！",
    "toast.backCoverGenerated": "您的封底已生成。",
    "toast.frontCoverRequired": "需要封面",
    "toast.frontCoverFirst": "请先生成封面。",
    "toast.backCoverRegenerated": "封底已重新生成！",
    "toast.backCoverFeedbackApplied": "您的反馈已应用到封底。",
    "toast.freshBackCover": "一个新封底已创建。",
    "toast.backCoverRegenerateFailed": "无法重新生成封底。",
    "toast.allIllustrationsComplete": "所有插图已完成！",
    "toast.storybookReady": "您的故事书已完全插图。",
    "toast.someIllustrationsIncomplete": "部分插图未完成",
    "toast.pagesMissing": "{{count}}页仍缺少插图。",
    "toast.bookNotComplete": "书未完成",
    "toast.generateFirst": "请先生成您的故事。",
    "toast.preparingBook": "正在准备您的书",
    "toast.uploadingPrint": "正在上传印刷文件...",
    "toast.missingInfoCheckout": "结账信息不完整。",
    "toast.failedCheckout": "创建结账失败。请重试。",
    "toast.checkEmail": "也请检查您的邮箱获取PDF链接！",
    "toast.couldntSendPdf": "无法发送PDF。请尝试下载。",
    "toast.emailSent": "邮件已发送！",
    "toast.bookSentTo": "您的书已发送到{{email}}。",
    "toast.generatingPdf": "正在生成PDF...",
    "paywall.title": "完成{{names}}的书！",
    "paywall.subtitle": "您的预览已就绪！获取完整插图PDF，永久保存。",
    "paywall.digitalPdf": "数字PDF — $9.99",
    "paywall.price": "$9.99",
    "paywall.benefit1": "完整插图故事书（20+页）",
    "paywall.benefit2": "高品质PDF，可在家打印",
    "paywall.benefit3": "购买后即时下载",
    "paywall.processing": "处理中...",
    "paywall.getBook": "获取我的书 — $9.99",
    "paywall.stopTitle": "离开您的故事？",
    "paywall.stopDesc": "如果您现在离开，{{names}}的个性化故事书将丢失。",
    "paywall.noContinue": "不，继续",
    "paywall.yesStop": "是的，离开",
    "upsell.title": "订购印刷书！",
    "upsell.subtitle": "您已拥有数字版！想要一本精装硬封面书送到家门口吗？",
    "upsell.specialOffer": "特别优惠",
    "upsell.addPrinted": "添加印刷书 – $19.99",
    "upsell.shippingNote": "运费在结账时计算",
    "upsell.benefit1": "精装硬封面书",
    "upsell.benefit2": "专业印刷品质",
    "upsell.benefit3": "他们将永远珍藏的纪念品",
    "upsell.benefit4": "全球配送",
    "upsell.addButton": "添加印刷版 – $19.99",
    "upsell.noThanks": "不用了，我保留数字版",

    "styleCard.pixar.name": "皮克斯风格",
    "styleCard.pixar.desc": "有趣的3D卡通风格，灵感来自动画电影",
    "styleCard.watercolor.name": "经典水彩",
    "styleCard.watercolor.desc": "柔和梦幻的水彩插图",
    "styleCard.anime.name": "动漫风格",
    "styleCard.anime.desc": "充满活力的日本动漫风格艺术",
    "generating.title": "正在创建您的故事...",
    "generating.subtitle": "我们正在为您打造一场神奇的冒险！",
    "generating.step.preparingStory": "正在准备您的故事...",
    "generating.step.creatingIllustrations": "正在创建插图...",
    "generating.step.addingDetails": "正在添加细节...",
    "generating.step.finishingTouches": "最后润色...",
    "finalizing.title": "正在完成您的书...",
    "finalizing.subtitle": "正在创建{{names}}的个性化故事书",
    "finalizing.step1": "正在设置故事...",
    "finalizing.step2": "正在生成文本...",
    "finalizing.step3": "正在创建插图...",
    "finalizing.step4": "正在添加最后润色...",
    "finalizing.step5": "正在准备您的书...",
    "finalizing.illustrations": "{{completed}} / {{total}} 插图已完成",
    "finalizing.percentComplete": "{{percent}}% 已完成",
    "finalizing.emailWhenReady": "准备好后发邮件给我",
    "finalizing.emailWhenReadyHint": "我们将在后台完成，并在您的书准备好后发送邮件。",
    "finalizing.safeToClose": "您的书正在我们的服务器上创建。您可以安全关闭此标签页——我们会继续制作，完成后发邮件通知您！",
    "finalizing.emailConfirmTitle": "一切就绪！",
    "finalizing.emailConfirmSubtitle": "您现在可以关闭此页面。",
    "finalizing.emailConfirmDesc": "我们将在后台继续生成您的书，并在准备好后发送带有下载链接的邮件。",
    "finalizing.emailConfirmNote": "请在几分钟后检查您的收件箱。",
    "toast.backgroundStarted": "后台生成已开始",
    "toast.backgroundStartedDesc": "您的书准备好后我们会发邮件给您！",
    "toast.backgroundFailed": "无法启动后台生成。请保持此页面打开。",
    "pdf.adventure": "个性化冒险",
    "pdf.theEnd": "结束",
    "pdf.storyFor": "一个关于",
    "backCover.title": "封底",
    "illustration.regenerating": "正在重新生成插图...",
    "illustration.redoCover": "重做封面",
    "illustration.redoPage": "重做页面",
    "illustration.redo": "重做",
    "illustration.buyMoreDescription": "需要更多修改？获取3次额外重做。",
    "illustration.buyMore": "购买3次重做",
    "illustration.lastRedo": "这是您最后一次重做！好好利用。",
    "illustration.placeholder": "描述您想要更改的内容...",
    "illustration.applyChanges": "应用更改",
    "styleEdit.edit": "编辑",
    "styleEdit.placeholder": "描述您想如何更改这个角色...",
    "styleEdit.apply": "应用更改",
    "thankYou.title": "谢谢！",
    "thankYou.subtitle": "{{names}}的冒险故事已准备好！",
    "thankYou.emailResent": "邮件已重发！",
    "thankYou.emailFailed": "邮件发送失败",
    "thankYou.emailSent": "邮件已发送！",
    "thankYou.emailPending": "正在发送邮件...",
    "thankYou.resending": "正在重发...",
    "thankYou.resendEmail": "重发邮件",
    "thankYou.emailFailedDesc": "无法发送邮件到{{email}}。请尝试下载。",
    "thankYou.emailDesc": "您的书已发送到{{email}}。",
    "thankYou.emailPendingDesc": "我们正在将您的书发送到{{email}}...",
    "thankYou.printOrdered": "印刷版已下单！",
    "thankYou.printDesc": "您的印刷书籍将很快发货。",
    "thankYou.generatingPdf": "正在生成PDF...",
    "thankYou.finishingIllustrations": "正在完成插图...",
    "thankYou.downloadBook": "下载书籍",
    "thankYou.createAnother": "创建另一本书",
    "thankYou.hopeLove": "希望{{names}}喜欢他们的故事！",
    "thankYou.finalizing": "我们正在完成您的书...",
    "thankYou.emailWhenReady": "您将通过 {{email}} 收到它",
    "thankYou.doNotClose": "请不要关闭此窗口，我们正在准备您的书",
    "thankYou.bookOnItsWay": "您的书即将送达！",
    "thankYou.sentTo": "已发送至 {{email}}",
    "email.getBook": "获取您的书",
    "email.sendToEmail": "我们将把书发送到您的邮箱",
    "email.processingPayment": "正在处理您的付款...",
    "email.pleaseWait": "请稍候，不要关闭此窗口。",
    "email.bookSentAfterPayment": "付款后，您的书将发送到您的邮箱。",
    "email.yourName": "您的姓名",
    "email.namePlaceholder": "输入您的姓名",
    "email.emailAddress": "电子邮件",
    "email.emailPlaceholder": "输入您的邮箱",
    "email.marketingConsent": "我想接收关于新功能和故事的更新",
    "email.continuePayment": "继续付款",
    "email.redirect": "您将被重定向到我们的安全支付页面",
    "email.stepPreparing": "正在准备您的书...",
    "email.stepCreating": "正在创建支付...",
    "email.stepRedirecting": "正在重定向到支付...",
    "printPayment.title": "准备您的订单",
    "printPayment.subtitle": "正在设置印刷书籍结账",
    "printPayment.stepUploading": "正在准备打印文件...",
    "printPayment.stepCreating": "正在创建支付...",
    "printPayment.stepRedirecting": "正在打开支付...",
    "maintenance.title": "我们正在创造魔法！",
    "maintenance.crafting": "我们正在准备一些精彩的东西。请稍后回来！",
    "maintenance.devAccess": "开发者访问",
    "maintenance.enterPassword": "输入密码",
    "maintenance.accessSite": "访问网站",
    "maintenance.incorrectPassword": "密码错误",
    "maintenance.comingSoon": "即将回来！",
    "contact.title": "联系我们",
    "contact.subtitle": "有问题？我们很乐意听到您的声音！",
    "contact.emailUs": "发送邮件",
    "contact.responseTime": "响应时间",
    "contact.within24": "24小时内",
    "contact.liveChat": "在线聊天",
    "contact.chatHours": "周一至周五，9点-17点",
    "contact.sendMessage": "给我们留言",
    "contact.yourName": "您的姓名",
    "contact.namePlaceholder": "张三",
    "contact.email": "邮箱",
    "contact.emailPlaceholder": "zhangsan@example.com",
    "contact.subject": "主题",
    "contact.subjectPlaceholder": "我们如何帮助您？",
    "contact.message": "消息",
    "contact.messagePlaceholder": "告诉我们更多...",
    "contact.sending": "发送中...",
    "contact.send": "发送消息",
    "contact.sent": "消息已发送！我们会尽快回复您。",
    "contact.faq": "常见问题",
    "contact.faq1.q": "创建一本书需要多长时间？",
    "contact.faq1.a": "使用我们的先进技术，您的个性化故事书只需几分钟即可生成。",
    "contact.faq2.q": "我的照片会怎样？",
    "contact.faq2.a": "您的照片仅用于插图生成，在书籍创建后会自动删除。",
    "contact.faq3.q": "我可以获得印刷版吗？",
    "contact.faq3.a": "可以！您可以订购精装印刷版，直接送到您家门口。",
    "notFound.title": "404",
    "notFound.message": "糟糕！此页面不存在。",
    "notFound.backHome": "返回首页",
    "payment.orderConfirmed": "订单已确认！",
    "payment.printDelivery": "您的印刷书籍将很快发货。",
    "payment.orderIssue": "订单问题",
    "payment.issueDesc": "处理您的订单时出现问题。请联系客服。",
    "payment.downloadReady": "下载就绪！",
    "payment.digitalDelivery": "您的数字书籍已准备好下载。",
    "payment.submitting": "正在处理您的订单...",
    "payment.paymentSuccess": "付款成功！正在设置您的订单...",
    "payment.contactSupport": "请联系客服并提供您的会话ID：",
    "payment.backHome": "返回首页",
    "payment.digitalThankYou": "感谢您的购买！您的数字故事书已准备好。",
    "payment.printThankYou": "感谢您的购买！您的印刷书籍将很快发货。",
    "payment.orderDetails": "订单详情",
    "payment.bookTitle": "个性化故事书",
    "payment.orderId": "订单ID：",
    "payment.createAnother": "创建另一本书",
    "shipping.title": "收货地址",
    "shipping.for": "收件人：",
    "shipping.fullName": "全名",
    "shipping.nameRequired": "姓名为必填项",
    "shipping.emailRequired": "邮箱为必填项",
    "shipping.emailInvalid": "请输入有效的邮箱",
    "shipping.streetRequired": "街道地址为必填项",
    "shipping.cityRequired": "城市为必填项",
    "shipping.stateRequired": "省/州为必填项",
    "shipping.postalRequired": "邮编为必填项",
    "shipping.phoneRequired": "电话号码为必填项",
    "shipping.email": "邮箱",
    "shipping.streetAddress": "街道地址",
    "shipping.apartment": "公寓 / 套房",
    "shipping.city": "城市",
    "shipping.postalCode": "邮编",
    "shipping.country": "国家",
    "shipping.stateProvince": "省 / 州",
    "shipping.optional": "选填",
    "shipping.phoneNumber": "电话号码",
    "shipping.phoneHint": "用于配送通知",
    "shipping.cancel": "取消",
    "shipping.continuePayment": "继续付款",
    "shipping.processing": "处理中...",
    "shipping.state": "州",
    "shipping.selectCountry": "选择国家",
    "shipping.selectState": "选择州",
    "shipping.deliveryOptions": "配送选项",
    "shipping.chooseDelivery": "选择配送",
    "shipping.loadingOptions": "加载选项...",
    "shipping.selectDeliverySpeed": "选择您偏好的配送速度：",
    "shipping.businessDays": "个工作日",
    "shipping.estimateUnavailable": "暂无预估",
    "shipping.back": "返回",
    "shipping.included": "已包含",
    "pdfPreview.printComingSoon": "即将推出！",
    "queue.position": "队列位置：{{position}}",
    "queue.waiting": "正在排队等候...",

    // Order Tracking
    "trackOrder.title": "追踪我的订单",
    "trackOrder.subtitle": "输入您的邮箱查看订单历史",
    "trackOrder.emailPlaceholder": "your@email.com",
    "trackOrder.search": "搜索",
    "trackOrder.error": "出了点问题，请重试。",
    "trackOrder.noOrders": "未找到订单",
    "trackOrder.noOrdersDesc": "未找到与此邮箱关联的订单。",
    "trackOrder.createBook": "创建您的第一本书",
    "trackOrder.foundOrders": "找到 {{count}} 个订单",
    "trackOrder.digital": "数字PDF",
    "trackOrder.printed": "印刷书籍",
    "trackOrder.status.pending": "待处理",
    "trackOrder.status.paid": "已付款",
    "trackOrder.status.fulfilled": "已完成",
    "trackOrder.status.shipped": "已发货",
    "footer.myBooks": "我的账户",
    "auth.loginTitle": "欢迎回来",
    "auth.loginSubtitle": "登录以访问您的书籍",
    "auth.signupTitle": "创建账户",
    "auth.signupSubtitle": "注册以保存您的书籍和角色",
    "auth.forgotTitle": "重置密码",
    "auth.forgotSubtitle": "输入您的邮箱以接收重置链接",
    "auth.displayName": "显示名称",
    "auth.email": "邮箱",
    "auth.password": "密码",
    "auth.loginButton": "登录",
    "auth.signupButton": "创建账户",
    "auth.resetButton": "发送链接",
    "auth.forgotPassword": "忘记密码？",
    "auth.noAccount": "没有账户？",
    "auth.signupLink": "注册",
    "auth.hasAccount": "已有账户？",
    "auth.loginLink": "登录",
    "auth.backToLogin": "返回登录",
    "auth.error": "错误",
    "auth.errorDesc": "出了点问题，请重试。",
    "auth.welcomeTitle": "欢迎！",
    "auth.welcomeDesc": "您的账户已成功创建。",
    "auth.resetSent": "链接已发送",
    "auth.resetSentDesc": "请检查您的邮箱以获取重置链接。",
    "dashboard.title": "我的仪表板",
    "dashboard.subtitle": "管理您的书籍和角色",
    "dashboard.logout": "退出",
    "dashboard.newBook": "新书",
    "dashboard.myBooks": "我的书籍",
    "dashboard.myKids": "我的角色",
    "dashboard.inProgress": "进行中",
    "dashboard.digital": "数字版",
    "dashboard.printed": "印刷版",
    "dashboard.noInProgress": "没有进行中的书籍",
    "dashboard.noDigital": "还没有数字书籍",
    "dashboard.noPrinted": "还没有印刷书籍",
    "dashboard.createFirst": "创建您的第一本书",
    "dashboard.untitled": "无标题书籍",
    "dashboard.addChild": "添加孩子",
    "dashboard.editChild": "编辑孩子",
    "dashboard.addFirstChild": "添加您的第一个孩子",
    "dashboard.noKids": "还没有保存的角色",
    "dashboard.childFormDesc": "保存孩子信息以快速创建新书",
    "dashboard.photo": "照片",
    "dashboard.yearsOld": "岁",
    "dashboard.savedStyles": "保存的风格：",
    "dashboard.save": "保存",
    "dashboard.childAdded": "角色已添加！",
    "dashboard.childUpdated": "角色已更新！",
    "dashboard.childRemoved": "角色已删除",
    "dashboard.profile": "个人资料",
    "dashboard.personalInfo": "个人信息",
    "dashboard.fullName": "全名",
    "dashboard.fullNamePlaceholder": "输入您的全名",
    "dashboard.email": "电子邮件",
    "dashboard.phone": "电话号码",
    "dashboard.phonePlaceholder": "输入您的电话号码",
    "dashboard.shippingAddress": "收货地址",
    "dashboard.addressLine1": "地址行 1",
    "dashboard.addressLine1Placeholder": "街道地址",
    "dashboard.addressLine2": "地址行 2",
    "dashboard.addressLine2Placeholder": "公寓、套房等（可选）",
    "dashboard.city": "城市",
    "dashboard.cityPlaceholder": "城市",
    "dashboard.state": "州 / 省",
    "dashboard.statePlaceholder": "州 / 省",
    "dashboard.postalCode": "邮政编码",
    "dashboard.postalCodePlaceholder": "邮政编码",
    "dashboard.country": "国家",
    "dashboard.countryPlaceholder": "国家",
    "dashboard.saveProfile": "保存个人资料",
    "dashboard.saved": "已保存！",
    "dashboard.profileSaved": "个人资料已保存！",
    "dashboard.profileSaveError": "保存个人资料失败",
    "dashboard.confirmDelete": "删除角色？",
    "dashboard.confirmDeleteDesc": "这将永久删除此角色及其保存的风格。",
    "dashboard.delete": "删除",
    "dashboard.generateStyle": "生成风格",
    "dashboard.regenerateStyle": "重新生成",
    "dashboard.selectStyle": "选择艺术风格",
    "dashboard.generatingStyle": "正在生成风格...",
    "dashboard.styleGenerated": "风格已生成！",
    "dashboard.needsPhoto": "请先上传照片",
    "dashboard.continueBook": "继续",
    "dashboard.downloadPdf": "下载",
    "dashboard.trackOrder": "追踪",
    "dashboard.statusPending": "进行中",
    "dashboard.statusGenerating": "生成中",
    "dashboard.statusProcessing": "处理中",
    "dashboard.statusPaid": "已付款",
    "dashboard.statusCompleted": "已完成",
    "dashboard.statusFulfilled": "已交付",
    "dashboard.statusShipped": "已发货",
    "dashboard.buyHardCopy": "购买印刷版",
    "thankYou.illustrationProgress": "插图：{{completed}} / {{total}}",

    // Theme Categories
    "category.friendship": "友谊",
    "category.growingUp": "成长",
    "category.kindness": "善良与情感",
    "category.values": "价值观",
    "category.adventure": "冒险",
    "category.fantasy": "奇幻",
    "category.humor": "幽默",
    "category.learning": "学习",
    "category.diversity": "多元与包容",
    "category.nature": "自然与环境",
    "category.problemSolving": "解决问题",
    "category.lifeExperiences": "生活经历",
    "category.cultural": "文化故事",

    // Theme Titles & Descriptions
    "theme.makingFriends.title": "交朋友",
    "theme.makingFriends.desc": "一个关于交新朋友和连接快乐的故事",
    "theme.bestFriends.title": "最好的朋友",
    "theme.bestFriends.desc": "一场庆祝好朋友之间纽带的冒险",
    "theme.sharing.title": "分享",
    "theme.sharing.desc": "学习与他人分享带来的快乐",
    "theme.siblings.title": "兄弟姐妹",
    "theme.siblings.desc": "一个关于兄弟姐妹之间特殊纽带的温馨故事",
    "theme.sayingSorry.title": "道歉",
    "theme.sayingSorry.desc": "学习何时以及如何道歉和弥补",
    "theme.beingYourself.title": "做自己",
    "theme.beingYourself.desc": "发现做真实自己的魔力",
    "theme.tryingNew.title": "尝试新事物",
    "theme.tryingNew.desc": "走出舒适区的激动旅程",
    "theme.overcomingFear.title": "克服恐惧",
    "theme.overcomingFear.desc": "一场勇敢面对和征服恐惧的冒险",
    "theme.findingTalent.title": "发现天赋",
    "theme.findingTalent.desc": "发现让你独特的特殊才能",
    "theme.confidence.title": "自信",
    "theme.confidence.desc": "建立自信，相信自己",
    "theme.understandingFeelings.title": "理解情感",
    "theme.understandingFeelings.desc": "学习识别和理解情感",
    "theme.helpingOthers.title": "帮助他人",
    "theme.helpingOthers.desc": "伸出援手的快乐和回报",
    "theme.dealingAnger.title": "应对愤怒",
    "theme.dealingAnger.desc": "管理和表达愤怒的健康方式",
    "theme.copingSadness.title": "应对悲伤",
    "theme.copingSadness.desc": "在悲伤时刻找到安慰和希望",
    "theme.showingEmpathy.title": "展示同理心",
    "theme.showingEmpathy.desc": "理解和关心他人的感受",
    "theme.honesty.title": "诚实",
    "theme.honesty.desc": "说真话的重要性",
    "theme.responsibility.title": "责任",
    "theme.responsibility.desc": "学习为自己的行为负责",
    "theme.respect.title": "尊重",
    "theme.respect.desc": "用善意和尊重对待他人",
    "theme.perseverance.title": "毅力",
    "theme.perseverance.desc": "即使困难也永不放弃",
    "theme.goodChoices.title": "做正确的选择",
    "theme.goodChoices.desc": "学习做出明智的决定",
    "theme.jungleAdventure.title": "丛林冒险",
    "theme.jungleAdventure.desc": "穿越野生丛林的激动探险",
    "theme.desertJourney.title": "沙漠之旅",
    "theme.desertJourney.desc": "穿越广阔沙漠的惊险旅程",
    "theme.oceanQuest.title": "海洋探索",
    "theme.oceanQuest.desc": "深入水下的海洋冒险",
    "theme.spaceExploration.title": "太空探索",
    "theme.spaceExploration.desc": "飞向太空的奇妙冒险",
    "theme.mountainExpedition.title": "登山探险",
    "theme.mountainExpedition.desc": "攀登壮丽山峰的顶端",
    "theme.magicalWorlds.title": "魔法世界",
    "theme.magicalWorlds.desc": "探索充满奇迹的魔法大陆",
    "theme.talkingAnimals.title": "会说话的动物",
    "theme.talkingAnimals.desc": "与会说话的动物一起冒险",
    "theme.wizardsSpells.title": "巫师与魔法",
    "theme.wizardsSpells.desc": "向睿智的巫师学习魔法",
    "theme.mythicalCreatures.title": "神话生物",
    "theme.mythicalCreatures.desc": "遇见龙、独角兽和其他神奇生物",
    "theme.timeTravel.title": "时间旅行",
    "theme.timeTravel.desc": "穿越时空到令人惊叹的时代",
    "theme.sillyAnimals.title": "搞笑动物",
    "theme.sillyAnimals.desc": "与搞笑动物一起的欢乐冒险",
    "theme.wordplayJokes.title": "文字游戏和笑话",
    "theme.wordplayJokes.desc": "一个充满笑话和欢笑的故事",
    "theme.absurdSituations.title": "荒诞情境",
    "theme.absurdSituations.desc": "疯狂而美妙的荒诞冒险",
    "theme.mischievous.title": "淘气冒险",
    "theme.mischievous.desc": "俏皮的恶作剧和淘气的乐趣",
    "theme.unexpectedSurprises.title": "意外惊喜",
    "theme.unexpectedSurprises.desc": "充满精彩转折和惊喜的故事",
    "theme.numbersCounting.title": "数字与计数",
    "theme.numbersCounting.desc": "一场有趣的数字和计数冒险",
    "theme.lettersReading.title": "字母与阅读",
    "theme.lettersReading.desc": "发现字母和文字的魔力",
    "theme.scienceExperiments.title": "科学实验",
    "theme.scienceExperiments.desc": "通过科学进行令人兴奋的发现",
    "theme.history.title": "历史",
    "theme.history.desc": "回到过去了解历史",
    "theme.problemSolvingSkills.title": "解决问题",
    "theme.problemSolvingSkills.desc": "用创造力和逻辑解决问题",
    "theme.differentCultures.title": "不同文化",
    "theme.differentCultures.desc": "探索和庆祝世界各地的文化",
    "theme.differentAbilities.title": "不同能力",
    "theme.differentAbilities.desc": "庆祝每个人的独特之处",
    "theme.familyDiversity.title": "家庭多样性",
    "theme.familyDiversity.desc": "各种家庭及其分享的爱",
    "theme.genderExpression.title": "性别表达",
    "theme.genderExpression.desc": "自由表达真实的自己",
    "theme.acceptanceBelonging.title": "接纳与归属",
    "theme.acceptanceBelonging.desc": "找到自己的位置并被接纳",
    "theme.forestsTrees.title": "森林与树木",
    "theme.forestsTrees.desc": "在神奇的森林和古老的树木中冒险",
    "theme.oceansSealife.title": "海洋与海洋生物",
    "theme.oceansSealife.desc": "发现波浪下的奇观",
    "theme.animalsHabitats.title": "动物与栖息地",
    "theme.animalsHabitats.desc": "了解动物及其栖息地",
    "theme.weatherSeasons.title": "天气与季节",
    "theme.weatherSeasons.desc": "探索四季变化的魔力",
    "theme.protectingPlanet.title": "保护地球",
    "theme.protectingPlanet.desc": "爱护我们美丽的地球",
    "theme.mysteriesClues.title": "谜团与线索",
    "theme.mysteriesClues.desc": "用巧妙的线索解开令人兴奋的谜团",
    "theme.puzzlesRiddles.title": "拼图与谜语",
    "theme.puzzlesRiddles.desc": "烧脑的拼图和巧妙的谜语",
    "theme.causeEffect.title": "因果关系",
    "theme.causeEffect.desc": "理解行为如何导致结果",
    "theme.teamProblemSolving.title": "团队解决问题",
    "theme.teamProblemSolving.desc": "一起合作解决大挑战",
    "theme.creativeSolutions.title": "创造性解决方案",
    "theme.creativeSolutions.desc": "跳出框架思考找到答案",
    "theme.startingSchool.title": "开始上学",
    "theme.startingSchool.desc": "开始上学的激动冒险",
    "theme.movingHome.title": "搬家",
    "theme.movingHome.desc": "适应新家和新社区",
    "theme.losingPet.title": "失去宠物",
    "theme.losingPet.desc": "应对失去心爱宠物的悲伤",
    "theme.newSibling.title": "新弟弟妹妹",
    "theme.newSibling.desc": "迎接新的弟弟或妹妹",
    "theme.dealingChange.title": "应对变化",
    "theme.dealingChange.desc": "学习接受和适应生活中的变化",
    "theme.fairyTales.title": "童话故事",
    "theme.fairyTales.desc": "经典童话冒险的重新演绎",
    "theme.folktales.title": "民间故事",
    "theme.folktales.desc": "代代相传的传统故事",
    "theme.mythsLegends.title": "神话与传说",
    "theme.mythsLegends.desc": "来自神话和传说的史诗故事",
    "theme.fablesMorals.title": "寓言与道德",
    "theme.fablesMorals.desc": "蕴含宝贵人生教训的故事",
    "theme.holidayTraditions.title": "节日传统",
    "theme.holidayTraditions.desc": "庆祝特别的节日和传统",
  },
  ja: {
    "seo.title": "StoryHeroes – 子供向けパーソナライズ絵本",
    "seo.description": "お子様を魔法の絵本のヒーローにしましょう。写真をアップロードし、冒険テーマを選べば、数分で美しいイラスト付きのパーソナライズされたPDFが届きます。",
    "hero.badge": "✨ パーソナライズ絵本",
    "hero.title": "お子様を絵本のヒーローに",
    "hero.title.create": "作ろう",
    "hero.title.magical": "魔法のストーリー",
    "hero.title.starring": "お子様が主役の",
    "hero.subtitle": "写真をアップロードし、冒険を選べば、数分で美しいイラスト付きPDFまたはハードカバー本が届きます。",
    "hero.cta": "ストーリーを作成",
    "hero.freePreview": "無料で本を作成してプレビュー",
    "hero.privacyStamp": "🔒 生成後に写真を削除",
    "hero.themes": "50+ テーマ",
    "hero.aiArt": "カスタムイラスト",
    "hero.pdfDownload": "即座にPDF",
    "hero.features.personalized": "パーソナライズ",
    "hero.features.ai": "カスタムアート",
    "hero.features.instant": "即座にPDF",
    "transform.badge": "✨ 魔法を見よう",
    "transform.title": "写真から絵本のキャラクターへ",
    "transform.subtitle": "お子様の写真を美しいイラストキャラクターに変換",
    "transform.pixar": "ピクサースタイル",
    "transform.watercolor": "水彩スタイル",
    "transform.anime": "アニメスタイル",
    "showcase.title": "美しい本、デジタル＆プリント",
    "showcase.subtitle": "デジタルPDFまたは美しい印刷版ハードカバーで入手",
    "showcase.pdfTitle": "デジタルPDF",
    "showcase.pdfDesc": "即時ダウンロード、あらゆるデバイスで閲覧",
    "showcase.printTitle": "印刷本",
    "showcase.printDesc": "プレミアムハードカバーをご自宅にお届け",
    "showcase.pages": "32ページ",
    "showcase.popular": "一番人気",
    "reviews.badge": "⭐ 保護者に大人気",
    "reviews.title": "保護者の声",
    "reviews.subtitle": "魔法のストーリーを作った何千もの幸せな家族に参加",
    "reviews.child": "お子様の年齢：",
    "reviews.1.text": "娘はヒロインとして自分を見るのが大好きでした！イラストは見事で、年齢にぴったりのストーリーでした。",
    "reviews.1.childAge": "5歳",
    "reviews.2.text": "素晴らしいギフトアイデア！息子は毎晩寝る前にストーリーを読んでいます。PDFの品質は驚くほどです。",
    "reviews.2.childAge": "4歳",
    "reviews.3.text": "品質に圧倒されました。子供の似顔絵が完璧に捉えられています。強くお勧めします！",
    "reviews.3.childAge": "6歳",
    "reviews.4.text": "3冊注文しました、子供たち一人一冊ずつ。誰が先に読むか争っています！",
    "reviews.4.childAge": "3、5、7歳",
    "reviews.5.text": "水彩スタイルは本当に美しいです。プロの児童書イラストレーターが描いたようです。",
    "reviews.5.childAge": "4歳",
    "reviews.6.text": "最高の誕生日プレゼント！息子がストーリーの中の自分を見た時の顔はプライスレスでした。",
    "reviews.6.childAge": "6歳",
    "trust.gift.title": "完璧なギフト",
    "trust.gift.desc": "子供たちが何年も大切にするユニークでパーソナライズされたギフト",
    "trust.ai.title": "美しいアート",
    "trust.ai.desc": "私たちの技術がお子様の姿で素晴らしいイラストを作成",
    "trust.love.title": "愛を込めて",
    "trust.love.desc": "すべてのストーリーは想像力と喜びを育むよう丁寧に作られています",
    "cta.title": "魔法を創る準備はできましたか？",
    "cta.subtitle": "お子様を自分だけの冒険物語のヒーローに",
    "cta.button": "今すぐ始める",
    "cta.note": "アカウント不要 • 5分で完成",
    
    // Language Selector
    "lang.title": "言語を選択",
    "lang.subtitle": "続行するには、お好みの言語を選択してください",
    
    // Navigation
    "nav.backToHome": "ホームに戻る",
    
    // Footer
    "footer.copyright": "© 2024 StoryHeroes. 全著作権所有。",
    "footer.privacy": "プライバシーポリシー",
    "footer.terms": "利用規約",
    "footer.contact": "お問い合わせ",
    "footer.trackOrder": "注文追跡",
    
    // Privacy Page
    "privacy.title": "プライバシーポリシー",
    "privacy.updated": "最終更新：2024年1月",
    "privacy.section1.title": "1. 収集する情報",
    "privacy.section1.intro": "サービスをご利用いただく際に、以下の情報を収集します：",
    "privacy.section1.item1": "パーソナライズ絵本を作成するためにアップロードされた写真",
    "privacy.section1.item2": "ストーリーのパーソナライズのためのお子様の名前と年齢",
    "privacy.section1.item3": "注文確認と配信のためのメールアドレス",
    "privacy.section1.item4": "支払い情報（Stripeを通じて安全に処理）",
    "privacy.section2.title": "2. 情報の使用方法",
    "privacy.section2.intro": "お客様の情報は以下の目的で使用されます：",
    "privacy.section2.item1": "パーソナライズ絵本を生成",
    "privacy.section2.item2": "支払いを処理し、PDF絵本を配信",
    "privacy.section2.item3": "注文確認と更新情報を送信",
    "privacy.section2.item4": "サービスとユーザー体験を改善",
    "privacy.section2.item5": "法的義務を遵守",
    "privacy.section3.title": "3. データの保存とセキュリティ",
    "privacy.section3.text": "データセキュリティを真剣に考えています。アップロードされたすべての写真と個人情報は暗号化され、安全に保存されます。業界標準のセキュリティ対策を使用して、不正アクセスからデータを保護します。",
    "privacy.section4.title": "4. サードパーティサービス",
    "privacy.section4.text": "支払い処理のためのStripeやストーリー生成のための専門サービスなど、信頼できるサードパーティサービスを使用しています。これらのサービスには独自のプライバシーポリシーとセキュリティ対策があります。",
    "privacy.section5.title": "5. データの保持",
    "privacy.section5.text": "サービスの提供と法的義務の遵守に必要な期間のみ、お客様のデータを保持します。いつでもお問い合わせいただければ、データの削除をリクエストできます。",
    "privacy.section6.title": "6. 子供のプライバシー",
    "privacy.section6.text": "子供のプライバシー保護に取り組んでいます。保護者の同意なしに13歳未満の子供から個人情報を故意に収集することはありません。保護者はお子様の情報を完全に管理できます。",
    "privacy.section7.title": "7. お客様の権利",
    "privacy.section7.intro": "お客様には以下の権利があります：",
    "privacy.section7.item1": "個人データへのアクセス",
    "privacy.section7.item2": "不正確なデータの訂正を要求",
    "privacy.section7.item3": "データの削除を要求",
    "privacy.section7.item4": "マーケティング通信のオプトアウト",
    "privacy.section8.title": "8. お問い合わせ",
    "privacy.section8.text": "このプライバシーポリシーについてご質問がある場合は、お問い合わせページからご連絡ください。",
    
    // Terms Page
    "terms.title": "利用規約",
    "terms.updated": "最終更新：2024年1月",
    "terms.section1.title": "1. 規約の承諾",
    "terms.section1.text": "StoryHeroesを使用することにより、本利用規約に同意したものとみなされます。同意されない場合は、サービスをご利用にならないでください。",
    "terms.section2.title": "2. サービスの説明",
    "terms.section2.text": "StoryHeroesは、子供向けのパーソナライズ絵本を提供します。お客様が提供する写真と情報に基づいて、カスタムイラスト付きストーリーを作成します。",
    "terms.section3.title": "3. ユーザーの責任",
    "terms.section3.intro": "お客様は以下に同意します：",
    "terms.section3.item1": "正確な情報を提供する",
    "terms.section3.item2": "使用権のある写真のみをアップロードする",
    "terms.section3.item3": "違法な目的でサービスを使用しない",
    "terms.section3.item4": "不適切または攻撃的なコンテンツをアップロードしない",
    "terms.section3.item5": "知的財産権を尊重する",
    "terms.section4.title": "4. 支払いと返金",
    "terms.section4.text": "すべての支払いはStripeを通じて安全に処理されます。サービスのパーソナライズされた性質上、絵本の配信を妨げる技術的問題が発生した場合にのみ返金が可能です。",
    "terms.section5.title": "5. 知的財産",
    "terms.section5.intro": "知的財産に関して：",
    "terms.section5.item1": "アップロードした写真の権利は保持されます",
    "terms.section5.item2": "生成されたストーリーとイラストは個人使用のみ",
    "terms.section5.item3": "生成されたコンテンツを再販または再配布することはできません",
    "terms.section5.item4": "StoryHeroesはサービスと技術の権利を保持します",
    "terms.section5.item5": "絵本を生成するためにアップロードされたコンテンツを使用するライセンスを当社に付与します",
    "terms.section6.title": "6. 責任の制限",
    "terms.section6.text": "StoryHeroesは「現状のまま」保証なしで提供されます。当社のサービスの使用から生じる間接的、付随的、または結果的損害について責任を負いません。",
    "terms.section7.title": "7. コンテンツガイドライン",
    "terms.section7.text": "不適切、攻撃的、または著作権で保護された素材を含む、ガイドラインに違反するコンテンツのサービス提供を拒否または削除する権利を留保します。",
    "terms.section8.title": "8. 規約の変更",
    "terms.section8.text": "本利用規約を更新する場合があります。変更後もサービスを継続して使用することは、新しい規約への同意を構成します。",
    "terms.section9.title": "9. 終了",
    "terms.section9.text": "これらの規約の違反またはその他の理由により、当社の裁量でサービスへのアクセスを終了または一時停止する権利を留保します。",
    "terms.section10.title": "10. お問い合わせ",
    "terms.section10.text": "本利用規約についてご質問がある場合は、お問い合わせページからご連絡ください。",

    // Wizard Steps
    "step.addCharacters": "キャラクター追加",
    "step.chooseStory": "ストーリー選択",
    "step.pickTitle": "タイトル選択",
    "step.chooseStyle": "スタイル選択",
    "step.review": "確認",
    "step.storyOutline": "ストーリー概要",
    "step.previewPdf": "PDFプレビュー",
    "photo.title": "キャラクターを追加",
    "photo.subtitle": "写真をアップロードして、ストーリーの各キャラクターの詳細を入力してください。",
    "photo.privacyNotice": "🔒 写真はイラスト作成にのみ使用され、生成後に削除されます。",
    "photo.addPhoto": "写真を追加",
    "photo.dragDrop": "またはドラッグ＆ドロップ",
    "photo.dropHere": "ここにドロップ！",
    "photo.browse": "参照",
    "photo.camera": "カメラ",
    "photo.upload": "アップロード",
    "photo.name": "名前",
    "photo.namePlaceholder": "名前を入力",
    "photo.age": "年齢",
    "photo.agePlaceholder": "年齢を入力",
    "photo.gender": "キャラクタータイプ",
    "photo.boy": "男の子",
    "photo.girl": "女の子",
    "photo.pet": "ペット",
    "photo.addChild": "別のキャラクターを追加",
    "photo.savedKids": "保存されたキャラクター",
    "familyImport.button": "グループ写真からインポート",
    "style.choose": "アートスタイルを選択",
    "style.subtitleSelected": "{{count}}人のキャラクターがこのスタイルでどう見えるか確認",
    "style.subtitleUnselected": "絵本のイラストスタイルを選択",
    "style.yourCharactersIn": "{{style}}スタイルのキャラクター",
    "style.creatingName": "{{name}}を作成中...",
    "style.altCharacterInStyle": "{{style}}スタイルの{{name}}",
    "style.failedShort": "失敗",
    "style.retry": "再試行",
    "style.hoverHint": "画像にカーソルを合わせると拡大表示されます",
    "style.preserveHint": "一貫性のためにキャラクターの説明は保持されます",
    "style.tryAgain": "再試行",
    "style.continueWith": "{{style}}で続行",
    "style.switchHint": "続行する前にいつでもスタイルを切り替えることができます",
    "title.choose": "タイトルを選択",
    "title.subtitle": "パーソナライズされた絵本にぴったりのタイトルを選びましょう",
    "title.creating": "魔法のタイトルを作成中...",
    "title.selected": "選択済み",
    "title.regenerate": "タイトルを再生成",
    "title.usingSuggested": "おすすめタイトル",
    "title.pickFavorite": "これらの提案からお気に入りを選んでください！",
    "themes.title": "{{hero}}のテーマを選択",
    "themes.subtitle": "{{count}}人の{{characterWord}}のための冒険テーマを選びましょう。各テーマでユニークなストーリーが作られます。",
    "themes.changeTheme": "テーマを変更",
    "themes.whatsStoryAbout": "{{hero}}のストーリーは何について？",
    "themes.yourIdeas": "あなたのストーリーアイデア",
    "themes.ideaHelperText": "お好みの冒険を説明してください。自由に創造してください！",
    "themes.ideaPlaceholder": "例：ドラゴンと友達になった勇敢な騎士が一緒に魔法の王国を救う...",
    "themes.ideaHint": "いい出だしです！下をクリックして強化されたアイデアを入手。",
    "themes.generateRequired": "自分のアイデアを書くか、下をクリックしてインスピレーションを得ましょう！",
    "themes.generatingIdeas": "アイデアを生成中...",
    "themes.completeMyIdeas": "アイデアを完成させる",
    "themes.inspireMe": "インスピレーションをください！",
    "themes.chooseIdea": "ストーリーアイデアを選択",
    "themes.usingPreMade": "既製アイデア",
    "themes.greatIdeas": "素晴らしいストーリーアイデアをご紹介！",
    "outline.title": "ストーリー概要",
    "outline.subtitle": "絵本のページを確認してカスタマイズ",
    "outline.generating": "ストーリー概要を作成中...",
    "outline.titlePage": "タイトルページ",
    "outline.ending": "おわり",
    "outline.page": "ページ",
    "outline.happy": "概要に満足ですか？",
    "outline.happyDesc": "上のページテキストを編集できます。準備ができたら、次をクリックしてイラストを生成しましょう！",
    "outline.sceneDescription": "シーンの説明",
    "outline.whatYouWillSee": "このページに表示される内容",
    "preview.title": "ストーリーを確認",
    "preview.subtitle": "すべてOKですか？イラスト付き絵本を作りましょう！",
    "preview.storyTheme": "ストーリーテーマ",
    "preview.yourHeroes": "あなたのヒーロー",
    "preview.chosenStyle": "アートスタイル",
    "preview.ready": "すべて素晴らしいです！✨",
    "preview.readyDesc": "{{names}}が主役の{{style}}スタイルの魔法のストーリーを作ります。",
    "pdfPreview.bookReady": "本ができました！",
    "pdfPreview.finishingIllustrations": "イラストを仕上げ中...",
    "pdfPreview.reviewMessage": "絵本を確認して、準備ができたらダウンロードしてください。",
    "pdfPreview.preparing": "準備中...",
    "pdfPreview.happyWithBook": "この本に満足です！",
    "pdfPreview.paymentComplete": "お支払い完了！",
    "pdfPreview.generatingPdf": "PDF生成中...",
    "pdfPreview.downloadPdf": "PDFをダウンロード",
    "pdfPreview.loveIt": "気に入りましたか？コピーを入手！",
    "pdfPreview.processing": "処理中...",
    "pdfPreview.digitalPdf": "デジタルPDF",
    "pdfPreview.printedBook": "印刷本",
    "pdfPreview.pdfDownloadShortly": "PDFがまもなくダウンロードされます。",
    "pdfPreview.tryAgainMoment": "しばらくしてからもう一度お試しください。",
    "pdfPreview.paymentSuccess": "お支払い成功！",
    "pdfPreview.pdfReady": "\"{{title}}\"がダウンロード準備完了",
    "pdfPreview.readyNextBook": "次の本の準備ができました！",
    "pdfPreview.canCreateNew": "いつでも新しい本を作成できます。",
    "nav.back": "戻る",
    "nav.next": "次へ",
    "nav.generateIllustrations": "イラストを生成",
    "download.pdf": "PDFをダウンロード",
    "complete.title": "本ができました！🎉",
    "complete.subtitle": "{{names}}が主役の魔法の絵本ができました！",
    "complete.download": "PDFをダウンロード",
    "complete.startOver": "別の本を作る",
    "generating.preparingStory": "ストーリーを準備中...",
    "generating.creatingIllustrations": "イラストを作成中...",
    "generating.creatingPreviewIllustrations": "プレビューイラストを作成中...",
    "generating.creatingCover": "表紙を作成中...",
    "generating.coverCreated": "表紙ができました！",
    "generating.backCoverCreated": "裏表紙ができました！",
    "finalize.paymentConfirmed": "お支払い確認済み！",
    "finalize.generatingStory": "完全なストーリーを生成中...",
    "finalize.creatingIllustrations": "イラストを作成中...",
    "finalize.addingFinishingTouches": "仕上げ中...",
    "finalize.creatingBackCover": "裏表紙を作成中...",
    "finalize.bookReadyReview": "本の確認準備ができました！",
    "finalize.preparingPdf": "PDFを準備中...",
    "resume.loadingBook": "本を読み込み中...",
    "resume.stillGenerating": "まだ本を作成中です！",
    "resume.stillGeneratingDesc": "イラストはまだバックグラウンドで生成中です。準備ができたらメールでお知らせします。",
    "resume.checkBackLater": "このページを閉じて、後でまた確認できます。",
    "illustration.noRedosLeft": "やり直し回数がありません",
    "illustration.limitReached": "イラストのやり直し回数をすべて使い切りました。続行するにはさらに購入してください。",
    "toast.paymentCancelled": "お支払いキャンセル",
    "toast.tryAgainReady": "本はいつでも準備できています。いつでもお試しください！",
    "toast.redoCreditsAdded": "やり直しクレジット追加！",
    "toast.threeMoreRedos": "イラストのやり直しが3回追加されました。",
    "toast.paymentSuccessful": "お支払い成功！",
    "toast.pdfReadyDownload": "PDFのダウンロード準備ができました。",
    "toast.downloadStarted": "ダウンロード開始",
    "toast.couldntDownload": "ダウンロードできませんでした",
    "toast.failedGenerate": "生成に失敗しました。もう一度お試しください。",
    "toast.pleaseRetry": "もう一度お試しください。",
    "toast.storyCreated": "ストーリー作成完了！",
    "toast.previewStory": "下で絵本をプレビューしてください。",
    "toast.fillIllustrations": "残りのイラストを生成中...",
    "toast.generationFailed": "生成失敗",
    "toast.generationTimeout": "生成がタイムアウトしました。もう一度お試しください。",
    "toast.paymentFailed": "お支払い失敗",
    "toast.somethingWrong": "問題が発生しました",
    "toast.createBookAgain": "本の作成をもう一度お試しください。",
    "toast.errorGenerating": "本の生成中にエラーが発生しました。",
    "toast.printOrderConfirmed": "印刷注文確認！",
    "toast.bookPrintedShipped": "本はまもなく印刷・発送されます。",
    "toast.paymentSuccessContact": "お支払い成功。必要に応じてサポートにお問い合わせください。",
    "toast.preparingPrintFiles": "印刷ファイルを準備中",
    "toast.readyForPrinting": "印刷用に本を準備中...",
    "toast.orderFailed": "注文失敗",
    "toast.paymentError": "お支払いエラー",
    "toast.tryAgainLater": "後でもう一度お試しください。",
    "toast.coverRegenerated": "表紙再生成完了！",
    "toast.newCoverReady": "新しい表紙ができました。",
    "toast.pageRegenerated": "{{page}}ページ再生成完了！",
    "toast.feedbackApplied": "フィードバックが適用されました。",
    "toast.freshIllustration": "新しいイラストが作成されました。",
    "toast.regenerationFailed": "再生成失敗",
    "toast.couldNotRegenerate": "イラストを再生成できませんでした。もう一度お試しください。",
    "toast.backCoverFailed": "裏表紙失敗",
    "toast.backCoverFrontUsed": "代わりに表紙が使用されます。",
    "toast.backCoverReady": "裏表紙準備完了！",
    "toast.backCoverGenerated": "裏表紙が生成されました。",
    "toast.frontCoverRequired": "表紙が必要",
    "toast.frontCoverFirst": "まず表紙を生成してください。",
    "toast.backCoverRegenerated": "裏表紙再生成完了！",
    "toast.backCoverFeedbackApplied": "裏表紙にフィードバックが適用されました。",
    "toast.freshBackCover": "新しい裏表紙が作成されました。",
    "toast.backCoverRegenerateFailed": "裏表紙を再生成できませんでした。",
    "toast.allIllustrationsComplete": "全イラスト完了！",
    "toast.storybookReady": "絵本のイラストがすべて完成しました。",
    "toast.someIllustrationsIncomplete": "一部のイラストが未完了",
    "toast.pagesMissing": "{{count}}ページのイラストがまだ不足しています。",
    "toast.bookNotComplete": "本が未完成",
    "toast.generateFirst": "まずストーリーを生成してください。",
    "toast.preparingBook": "本を準備中",
    "toast.uploadingPrint": "印刷ファイルをアップロード中...",
    "toast.missingInfoCheckout": "決済に必要な情報が不足しています。",
    "toast.failedCheckout": "決済の作成に失敗しました。もう一度お試しください。",
    "toast.checkEmail": "PDFリンクのメールも確認してください！",
    "toast.couldntSendPdf": "PDFを送信できませんでした。ダウンロードをお試しください。",
    "toast.emailSent": "メール送信完了！",
    "toast.bookSentTo": "本が{{email}}に送信されました。",
    "toast.generatingPdf": "PDF生成中...",
    "paywall.title": "{{names}}の本を完成させよう！",
    "paywall.subtitle": "プレビューの準備ができました！完全なイラスト付きPDFを手に入れましょう。",
    "paywall.digitalPdf": "デジタルPDF — $9.99",
    "paywall.price": "$9.99",
    "paywall.benefit1": "完全なイラスト付き絵本（20ページ以上）",
    "paywall.benefit2": "自宅で印刷できる高品質PDF",
    "paywall.benefit3": "購入後すぐにダウンロード",
    "paywall.processing": "処理中...",
    "paywall.getBook": "本を手に入れる — $9.99",
    "paywall.stopTitle": "ストーリーを離れますか？",
    "paywall.stopDesc": "今離れると{{names}}のパーソナライズされた絵本が失われます。",
    "paywall.noContinue": "いいえ、続ける",
    "paywall.yesStop": "はい、離れる",
    "upsell.title": "印刷書を注文しよう！",
    "upsell.subtitle": "デジタル版はもうお持ちです！プレミアムハードカバーをお届けしませんか？",
    "upsell.specialOffer": "特別オファー",
    "upsell.addPrinted": "印刷書を追加 – $19.99",
    "upsell.shippingNote": "送料はお支払い時に計算されます",
    "upsell.benefit1": "プレミアムハードカバー本",
    "upsell.benefit2": "プロフェッショナルな印刷品質",
    "upsell.benefit3": "永遠に大切にする思い出の品",
    "upsell.benefit4": "世界中への配送可能",
    "upsell.addButton": "印刷版を追加 – $19.99",
    "upsell.noThanks": "結構です、デジタル版で満足です",

    "styleCard.pixar.name": "ピクサースタイル",
    "styleCard.pixar.desc": "アニメ映画にインスパイアされた楽しい3Dカートゥーンスタイル",
    "styleCard.watercolor.name": "クラシック水彩",
    "styleCard.watercolor.desc": "柔らかく夢のような水彩イラスト",
    "styleCard.anime.name": "アニメスタイル",
    "styleCard.anime.desc": "日本のアニメにインスパイアされた鮮やかなアート",
    "generating.title": "ストーリーを作成中...",
    "generating.subtitle": "あなただけの魔法の冒険を作っています！",
    "generating.step.preparingStory": "ストーリーを準備中...",
    "generating.step.creatingIllustrations": "イラストを作成中...",
    "generating.step.addingDetails": "詳細を追加中...",
    "generating.step.finishingTouches": "仕上げ中...",
    "finalizing.title": "本を仕上げています...",
    "finalizing.subtitle": "{{names}}のパーソナライズ絵本を作成中",
    "finalizing.step1": "ストーリーを設定中...",
    "finalizing.step2": "テキストを生成中...",
    "finalizing.step3": "イラストを作成中...",
    "finalizing.step4": "仕上げ中...",
    "finalizing.step5": "本を準備中...",
    "finalizing.illustrations": "{{completed}} / {{total}} イラスト完了",
    "finalizing.percentComplete": "{{percent}}% 完了",
    "finalizing.emailWhenReady": "準備ができたらメールで通知",
    "finalizing.emailWhenReadyHint": "バックグラウンドで完成させ、本の準備ができたらメールをお送りします。",
    "finalizing.safeToClose": "お客様の本はサーバーで作成中です。このタブを閉じても大丈夫です。作業を続け、完成したらメールでお知らせします！",
    "finalizing.emailConfirmTitle": "準備完了！",
    "finalizing.emailConfirmSubtitle": "このページを閉じても大丈夫です。",
    "finalizing.emailConfirmDesc": "バックグラウンドで本の生成を続け、準備ができたらダウンロードリンク付きのメールをお送りします。",
    "finalizing.emailConfirmNote": "数分後に受信トレイをご確認ください。",
    "toast.backgroundStarted": "バックグラウンド生成を開始",
    "toast.backgroundStartedDesc": "本の準備ができたらメールでお知らせします！",
    "toast.backgroundFailed": "バックグラウンド生成を開始できませんでした。このページを開いたままにしてください。",
    "pdf.adventure": "パーソナライズされた冒険",
    "pdf.theEnd": "おしまい",
    "pdf.storyFor": "のための物語",
    "backCover.title": "裏表紙",
    "illustration.regenerating": "イラストを再生成中...",
    "illustration.redoCover": "表紙をやり直す",
    "illustration.redoPage": "ページをやり直す",
    "illustration.redo": "やり直す",
    "illustration.buyMoreDescription": "もっと変更が必要？追加の3回やり直しを入手。",
    "illustration.buyMore": "3回やり直しを購入",
    "illustration.lastRedo": "これが最後のやり直しです！大切に使ってください。",
    "illustration.placeholder": "変更したい内容を説明してください...",
    "illustration.applyChanges": "変更を適用",
    "styleEdit.edit": "編集",
    "styleEdit.placeholder": "このキャラクターをどう変更したいか説明してください...",
    "styleEdit.apply": "変更を適用",
    "thankYou.title": "ありがとうございます！",
    "thankYou.subtitle": "{{names}}の冒険の準備ができました！",
    "thankYou.emailResent": "メール再送済み！",
    "thankYou.emailFailed": "メール送信失敗",
    "thankYou.emailSent": "メール送信済み！",
    "thankYou.emailPending": "メール送信中...",
    "thankYou.resending": "再送中...",
    "thankYou.resendEmail": "メールを再送",
    "thankYou.emailFailedDesc": "{{email}}にメールを送信できませんでした。ダウンロードをお試しください。",
    "thankYou.emailDesc": "本が{{email}}に送信されました。",
    "thankYou.emailPendingDesc": "{{email}}に本を送信中...",
    "thankYou.printOrdered": "印刷版を注文しました！",
    "thankYou.printDesc": "印刷された本はまもなく発送されます。",
    "thankYou.generatingPdf": "PDF生成中...",
    "thankYou.finishingIllustrations": "イラストを仕上げ中...",
    "thankYou.downloadBook": "本をダウンロード",
    "thankYou.createAnother": "別の本を作る",
    "thankYou.hopeLove": "{{names}}がこの物語を気に入ってくれることを願っています！",
    "thankYou.finalizing": "本を仕上げています...",
    "thankYou.emailWhenReady": "{{email}} にメールでお届けします",
    "thankYou.doNotClose": "本を準備している間、このウィンドウを閉じないでください",
    "thankYou.bookOnItsWay": "本がまもなく届きます！",
    "thankYou.sentTo": "{{email}} に送信済み",
    "email.getBook": "本を入手",
    "email.sendToEmail": "あなたのメールに本を送信します",
    "email.processingPayment": "お支払いを処理中...",
    "email.pleaseWait": "お待ちください。このウィンドウを閉じないでください。",
    "email.bookSentAfterPayment": "お支払い後、メールに本が送信されます。",
    "email.yourName": "お名前",
    "email.namePlaceholder": "お名前を入力",
    "email.emailAddress": "メールアドレス",
    "email.emailPlaceholder": "メールを入力",
    "email.marketingConsent": "新機能やストーリーのアップデートを受け取りたい",
    "email.continuePayment": "お支払いに進む",
    "email.redirect": "安全な決済ページにリダイレクトされます",
    "email.stepPreparing": "本を準備中...",
    "email.stepCreating": "決済を作成中...",
    "email.stepRedirecting": "決済にリダイレクト中...",
    "printPayment.title": "注文を準備中",
    "printPayment.subtitle": "印刷本の決済を設定中",
    "printPayment.stepUploading": "印刷ファイルを準備中...",
    "printPayment.stepCreating": "決済を作成中...",
    "printPayment.stepRedirecting": "決済を開いています...",
    "maintenance.title": "魔法を作っています！",
    "maintenance.crafting": "素晴らしいものを準備中です。またお越しください！",
    "maintenance.devAccess": "開発者アクセス",
    "maintenance.enterPassword": "パスワードを入力",
    "maintenance.accessSite": "サイトにアクセス",
    "maintenance.incorrectPassword": "パスワードが正しくありません",
    "maintenance.comingSoon": "まもなく再開！",
    "contact.title": "お問い合わせ",
    "contact.subtitle": "ご質問がありますか？お気軽にどうぞ！",
    "contact.emailUs": "メールを送る",
    "contact.responseTime": "応答時間",
    "contact.within24": "24時間以内",
    "contact.liveChat": "ライブチャット",
    "contact.chatHours": "月～金、9時～17時",
    "contact.sendMessage": "メッセージを送る",
    "contact.yourName": "お名前",
    "contact.namePlaceholder": "山田太郎",
    "contact.email": "メール",
    "contact.emailPlaceholder": "taro@example.com",
    "contact.subject": "件名",
    "contact.subjectPlaceholder": "どのようにお手伝いできますか？",
    "contact.message": "メッセージ",
    "contact.messagePlaceholder": "詳しく教えてください...",
    "contact.sending": "送信中...",
    "contact.send": "メッセージを送信",
    "contact.sent": "メッセージが送信されました！まもなくお返事します。",
    "contact.faq": "よくある質問",
    "contact.faq1.q": "本を作るのにどれくらいかかりますか？",
    "contact.faq1.a": "先進技術により、パーソナライズされた本はわずか数分で生成されます。",
    "contact.faq2.q": "写真はどうなりますか？",
    "contact.faq2.a": "写真はイラスト生成にのみ使用され、本の作成後に自動的に削除されます。",
    "contact.faq3.q": "印刷版は入手できますか？",
    "contact.faq3.a": "はい！プレミアムハードカバーの印刷版を注文して、ご自宅にお届けできます。",
    "notFound.title": "404",
    "notFound.message": "おっと！このページは存在しません。",
    "notFound.backHome": "ホームに戻る",
    "payment.orderConfirmed": "注文確認！",
    "payment.printDelivery": "印刷された本はまもなく発送されます。",
    "payment.orderIssue": "注文の問題",
    "payment.issueDesc": "注文の処理中に問題が発生しました。サポートにお問い合わせください。",
    "payment.downloadReady": "ダウンロード準備完了！",
    "payment.digitalDelivery": "デジタル本のダウンロード準備ができました。",
    "payment.submitting": "注文を処理中...",
    "payment.paymentSuccess": "お支払いが成功しました！注文を設定中...",
    "payment.contactSupport": "セッションIDをお伝えの上、サポートにお問い合わせください：",
    "payment.backHome": "ホームに戻る",
    "payment.digitalThankYou": "ご購入ありがとうございます！デジタル絵本の準備ができました。",
    "payment.printThankYou": "ご購入ありがとうございます！印刷された本はまもなく発送されます。",
    "payment.orderDetails": "注文詳細",
    "payment.bookTitle": "パーソナライズ絵本",
    "payment.orderId": "注文ID：",
    "payment.createAnother": "別の本を作る",
    "shipping.title": "配送先住所",
    "shipping.for": "宛先：",
    "shipping.fullName": "氏名",
    "shipping.nameRequired": "名前は必須です",
    "shipping.emailRequired": "メールは必須です",
    "shipping.emailInvalid": "有効なメールを入力してください",
    "shipping.streetRequired": "住所は必須です",
    "shipping.cityRequired": "市区町村は必須です",
    "shipping.stateRequired": "都道府県は必須です",
    "shipping.postalRequired": "郵便番号は必須です",
    "shipping.phoneRequired": "電話番号は必須です",
    "shipping.email": "メール",
    "shipping.streetAddress": "住所",
    "shipping.apartment": "マンション名 / 部屋番号",
    "shipping.city": "市区町村",
    "shipping.postalCode": "郵便番号",
    "shipping.country": "国",
    "shipping.stateProvince": "都道府県",
    "shipping.optional": "任意",
    "shipping.phoneNumber": "電話番号",
    "shipping.phoneHint": "配送通知に必要です",
    "shipping.cancel": "キャンセル",
    "shipping.continuePayment": "お支払いへ進む",
    "shipping.processing": "処理中...",
    "shipping.state": "州",
    "shipping.selectCountry": "国を選択",
    "shipping.selectState": "州を選択",
    "shipping.deliveryOptions": "配送オプション",
    "shipping.chooseDelivery": "配送を選択",
    "shipping.loadingOptions": "オプション読み込み中...",
    "shipping.selectDeliverySpeed": "ご希望の配送速度をお選びください：",
    "shipping.businessDays": "営業日",
    "shipping.estimateUnavailable": "見積もり不可",
    "shipping.back": "戻る",
    "shipping.included": "含まれています",
    "pdfPreview.printComingSoon": "近日公開！",
    "queue.position": "キューの位置：{{position}}",
    "queue.waiting": "キューで待機中...",

    // Order Tracking
    "trackOrder.title": "注文を追跡",
    "trackOrder.subtitle": "メールアドレスを入力して注文履歴を確認",
    "trackOrder.emailPlaceholder": "your@email.com",
    "trackOrder.search": "検索",
    "trackOrder.error": "エラーが発生しました。もう一度お試しください。",
    "trackOrder.noOrders": "注文が見つかりません",
    "trackOrder.noOrdersDesc": "このメールアドレスに関連する注文は見つかりませんでした。",
    "trackOrder.createBook": "最初の本を作成",
    "trackOrder.foundOrders": "{{count}}件の注文が見つかりました",
    "trackOrder.digital": "デジタルPDF",
    "trackOrder.printed": "印刷本",
    "trackOrder.status.pending": "保留中",
    "trackOrder.status.paid": "支払い済み",
    "trackOrder.status.fulfilled": "完了",
    "trackOrder.status.shipped": "発送済み",
    "footer.myBooks": "マイアカウント",
    "auth.loginTitle": "おかえりなさい",
    "auth.loginSubtitle": "サインインして本にアクセス",
    "auth.signupTitle": "アカウント作成",
    "auth.signupSubtitle": "登録して本とキャラクターを保存",
    "auth.forgotTitle": "パスワードリセット",
    "auth.forgotSubtitle": "リセットリンクを受け取るメールを入力",
    "auth.displayName": "表示名",
    "auth.email": "メール",
    "auth.password": "パスワード",
    "auth.loginButton": "サインイン",
    "auth.signupButton": "アカウント作成",
    "auth.resetButton": "リンクを送信",
    "auth.forgotPassword": "パスワードをお忘れですか？",
    "auth.noAccount": "アカウントをお持ちでないですか？",
    "auth.signupLink": "登録",
    "auth.hasAccount": "すでにアカウントをお持ちですか？",
    "auth.loginLink": "サインイン",
    "auth.backToLogin": "ログインに戻る",
    "auth.error": "エラー",
    "auth.errorDesc": "問題が発生しました。もう一度お試しください。",
    "auth.welcomeTitle": "ようこそ！",
    "auth.welcomeDesc": "アカウントが正常に作成されました。",
    "auth.resetSent": "リンク送信済み",
    "auth.resetSentDesc": "リセットリンクのメールを確認してください。",
    "dashboard.title": "マイダッシュボード",
    "dashboard.subtitle": "本とキャラクターを管理",
    "dashboard.logout": "サインアウト",
    "dashboard.newBook": "新しい本",
    "dashboard.myBooks": "マイブック",
    "dashboard.myKids": "マイキャラクター",
    "dashboard.inProgress": "進行中",
    "dashboard.digital": "デジタル",
    "dashboard.printed": "印刷",
    "dashboard.noInProgress": "進行中の本はありません",
    "dashboard.noDigital": "デジタル本はまだありません",
    "dashboard.noPrinted": "印刷本はまだありません",
    "dashboard.createFirst": "最初の本を作成",
    "dashboard.untitled": "無題の本",
    "dashboard.addChild": "子供を追加",
    "dashboard.editChild": "子供を編集",
    "dashboard.addFirstChild": "最初の子供を追加",
    "dashboard.noKids": "保存されたキャラクターはありません",
    "dashboard.childFormDesc": "お子様の情報を保存して素早く新しい本を作成",
    "dashboard.photo": "写真",
    "dashboard.yearsOld": "歳",
    "dashboard.savedStyles": "保存されたスタイル：",
    "dashboard.save": "保存",
    "dashboard.childAdded": "キャラクター追加！",
    "dashboard.childUpdated": "キャラクター更新！",
    "dashboard.childRemoved": "キャラクター削除",
    "dashboard.profile": "プロフィール",
    "dashboard.personalInfo": "個人情報",
    "dashboard.fullName": "氏名",
    "dashboard.fullNamePlaceholder": "氏名を入力",
    "dashboard.email": "メールアドレス",
    "dashboard.phone": "電話番号",
    "dashboard.phonePlaceholder": "電話番号を入力",
    "dashboard.shippingAddress": "配送先住所",
    "dashboard.addressLine1": "住所1",
    "dashboard.addressLine1Placeholder": "住所",
    "dashboard.addressLine2": "住所2",
    "dashboard.addressLine2Placeholder": "マンション名、部屋番号など（任意）",
    "dashboard.city": "市区町村",
    "dashboard.cityPlaceholder": "市区町村",
    "dashboard.state": "都道府県",
    "dashboard.statePlaceholder": "都道府県",
    "dashboard.postalCode": "郵便番号",
    "dashboard.postalCodePlaceholder": "郵便番号",
    "dashboard.country": "国",
    "dashboard.countryPlaceholder": "国",
    "dashboard.saveProfile": "プロフィールを保存",
    "dashboard.saved": "保存しました！",
    "dashboard.profileSaved": "プロフィールを保存しました！",
    "dashboard.profileSaveError": "プロフィールの保存に失敗しました",
    "dashboard.confirmDelete": "キャラクターを削除しますか？",
    "dashboard.confirmDeleteDesc": "このキャラクターと保存されたスタイルが完全に削除されます。",
    "dashboard.delete": "削除",
    "dashboard.generateStyle": "スタイルを生成",
    "dashboard.regenerateStyle": "再生成",
    "dashboard.selectStyle": "アートスタイルを選択",
    "dashboard.generatingStyle": "スタイル生成中...",
    "dashboard.styleGenerated": "スタイル生成完了！",
    "dashboard.needsPhoto": "まず写真をアップロードしてください",
    "dashboard.continueBook": "続行",
    "dashboard.downloadPdf": "ダウンロード",
    "dashboard.trackOrder": "追跡",
    "dashboard.statusPending": "進行中",
    "dashboard.statusGenerating": "生成中",
    "dashboard.statusProcessing": "処理中",
    "dashboard.statusPaid": "支払い済み",
    "dashboard.statusCompleted": "完了",
    "dashboard.statusFulfilled": "配信済み",
    "dashboard.statusShipped": "発送済み",
    "dashboard.buyHardCopy": "印刷版を購入",
    "thankYou.illustrationProgress": "イラスト：{{completed}} / {{total}}",

    // Theme Categories
    "category.friendship": "友情",
    "category.growingUp": "成長",
    "category.kindness": "優しさと感情",
    "category.values": "価値観",
    "category.adventure": "冒険",
    "category.fantasy": "ファンタジー",
    "category.humor": "ユーモア",
    "category.learning": "学び",
    "category.diversity": "多様性とインクルージョン",
    "category.nature": "自然と環境",
    "category.problemSolving": "問題解決",
    "category.lifeExperiences": "人生経験",
    "category.cultural": "文化的な物語",

    // Theme Titles & Descriptions
    "theme.makingFriends.title": "友達をつくる",
    "theme.makingFriends.desc": "新しい友達をつくるつながりの喜びの物語",
    "theme.bestFriends.title": "親友",
    "theme.bestFriends.desc": "親友の絆を祝う冒険",
    "theme.sharing.title": "分かち合い",
    "theme.sharing.desc": "他の人と分かち合う幸せを学ぶ",
    "theme.siblings.title": "兄弟姉妹",
    "theme.siblings.desc": "兄弟姉妹の特別な絆の心温まる物語",
    "theme.sayingSorry.title": "ごめんなさいを言う",
    "theme.sayingSorry.desc": "いつ、どのように謝るかを学ぶ",
    "theme.beingYourself.title": "自分らしく",
    "theme.beingYourself.desc": "自分に忠実でいることの魔法を発見する",
    "theme.tryingNew.title": "新しいことに挑戦",
    "theme.tryingNew.desc": "快適ゾーンを飛び出すワクワクする旅",
    "theme.overcomingFear.title": "恐怖を克服する",
    "theme.overcomingFear.desc": "恐怖に立ち向かい克服する勇敢な冒険",
    "theme.findingTalent.title": "才能を見つける",
    "theme.findingTalent.desc": "あなたをユニークにする特別な才能を発見する",
    "theme.confidence.title": "自信",
    "theme.confidence.desc": "自信を育て、自分を信じる",
    "theme.understandingFeelings.title": "気持ちを理解する",
    "theme.understandingFeelings.desc": "感情を認識し理解することを学ぶ",
    "theme.helpingOthers.title": "人を助ける",
    "theme.helpingOthers.desc": "手を差し伸べる喜びと報い",
    "theme.dealingAnger.title": "怒りと向き合う",
    "theme.dealingAnger.desc": "怒りを健康的に管理し表現する方法",
    "theme.copingSadness.title": "悲しみと向き合う",
    "theme.copingSadness.desc": "悲しい時に慰めと希望を見つける",
    "theme.showingEmpathy.title": "思いやりを示す",
    "theme.showingEmpathy.desc": "他の人の気持ちを理解し気にかける",
    "theme.honesty.title": "正直さ",
    "theme.honesty.desc": "本当のことを言うことの大切さ",
    "theme.responsibility.title": "責任感",
    "theme.responsibility.desc": "自分の行動に責任を持つことを学ぶ",
    "theme.respect.title": "尊敬",
    "theme.respect.desc": "他の人を優しさと敬意を持って接する",
    "theme.perseverance.title": "忍耐力",
    "theme.perseverance.desc": "困難な時も決してあきらめない",
    "theme.goodChoices.title": "良い選択をする",
    "theme.goodChoices.desc": "賢い判断をすることを学ぶ",
    "theme.jungleAdventure.title": "ジャングル冒険",
    "theme.jungleAdventure.desc": "野生のジャングルを探検するワクワクする冒険",
    "theme.desertJourney.title": "砂漠の旅",
    "theme.desertJourney.desc": "広大な砂漠を横断するスリリングな旅",
    "theme.oceanQuest.title": "海の冒険",
    "theme.oceanQuest.desc": "水中の海洋冒険に潜る",
    "theme.spaceExploration.title": "宇宙探検",
    "theme.spaceExploration.desc": "驚くべき宇宙の冒険に飛び立つ",
    "theme.mountainExpedition.title": "山岳探検",
    "theme.mountainExpedition.desc": "壮大な山の頂上に登る",
    "theme.magicalWorlds.title": "魔法の世界",
    "theme.magicalWorlds.desc": "不思議に満ちた魔法の国を探検する",
    "theme.talkingAnimals.title": "しゃべる動物",
    "theme.talkingAnimals.desc": "話せる動物たちとの冒険",
    "theme.wizardsSpells.title": "魔法使いと魔法",
    "theme.wizardsSpells.desc": "賢い魔法使いから魔法を学ぶ",
    "theme.mythicalCreatures.title": "神話の生き物",
    "theme.mythicalCreatures.desc": "ドラゴン、ユニコーンなどの神秘的な生き物に出会う",
    "theme.timeTravel.title": "タイムトラベル",
    "theme.timeTravel.desc": "驚くべき時代へ時間旅行する",
    "theme.sillyAnimals.title": "おもしろ動物",
    "theme.sillyAnimals.desc": "面白い動物たちとの爆笑冒険",
    "theme.wordplayJokes.title": "ことば遊びとジョーク",
    "theme.wordplayJokes.desc": "ダジャレやジョークが満載の物語",
    "theme.absurdSituations.title": "ばかばかしい状況",
    "theme.absurdSituations.desc": "クレイジーで素晴らしくばかばかしい冒険",
    "theme.mischievous.title": "いたずら冒険",
    "theme.mischievous.desc": "楽しいいたずらとやんちゃな遊び",
    "theme.unexpectedSurprises.title": "予想外のサプライズ",
    "theme.unexpectedSurprises.desc": "楽しいどんでん返しとサプライズがいっぱいの物語",
    "theme.numbersCounting.title": "数字と数え方",
    "theme.numbersCounting.desc": "数字と数え方の楽しい冒険",
    "theme.lettersReading.title": "文字と読み方",
    "theme.lettersReading.desc": "文字と言葉の魔法を発見する",
    "theme.scienceExperiments.title": "科学実験",
    "theme.scienceExperiments.desc": "科学を通じたワクワクする発見",
    "theme.history.title": "歴史",
    "theme.history.desc": "過去に旅して歴史を学ぶ",
    "theme.problemSolvingSkills.title": "問題解決",
    "theme.problemSolvingSkills.desc": "創造力と論理で問題を解決する",
    "theme.differentCultures.title": "異なる文化",
    "theme.differentCultures.desc": "世界中の文化を探検し祝う",
    "theme.differentAbilities.title": "異なる能力",
    "theme.differentAbilities.desc": "一人一人のユニークさを祝う",
    "theme.familyDiversity.title": "家族の多様性",
    "theme.familyDiversity.desc": "あらゆる種類の家族と分かち合う愛",
    "theme.genderExpression.title": "ジェンダー表現",
    "theme.genderExpression.desc": "本当の自分を自由に表現する",
    "theme.acceptanceBelonging.title": "受容と帰属",
    "theme.acceptanceBelonging.desc": "自分の居場所を見つけ受け入れられる",
    "theme.forestsTrees.title": "森と木々",
    "theme.forestsTrees.desc": "魔法の森と古い木々の冒険",
    "theme.oceansSealife.title": "海と海の生き物",
    "theme.oceansSealife.desc": "波の下の不思議を発見する",
    "theme.animalsHabitats.title": "動物と生息地",
    "theme.animalsHabitats.desc": "動物とその住む場所について学ぶ",
    "theme.weatherSeasons.title": "天気と季節",
    "theme.weatherSeasons.desc": "移り変わる季節の魔法を探検する",
    "theme.protectingPlanet.title": "地球を守る",
    "theme.protectingPlanet.desc": "私たちの美しい地球を大切にする",
    "theme.mysteriesClues.title": "謎と手がかり",
    "theme.mysteriesClues.desc": "巧みな手がかりでワクワクする謎を解く",
    "theme.puzzlesRiddles.title": "パズルとなぞなぞ",
    "theme.puzzlesRiddles.desc": "頭を使うパズルと難しいなぞなぞ",
    "theme.causeEffect.title": "原因と結果",
    "theme.causeEffect.desc": "行動がどのように結果につながるかを理解する",
    "theme.teamProblemSolving.title": "チームで問題解決",
    "theme.teamProblemSolving.desc": "一緒に協力して大きな課題を解決する",
    "theme.creativeSolutions.title": "創造的な解決策",
    "theme.creativeSolutions.desc": "型にはまらない考え方で答えを見つける",
    "theme.startingSchool.title": "学校を始める",
    "theme.startingSchool.desc": "学校を始めるワクワクする冒険",
    "theme.movingHome.title": "引っ越し",
    "theme.movingHome.desc": "新しい家と近所に適応する",
    "theme.losingPet.title": "ペットを失う",
    "theme.losingPet.desc": "愛するペットの喪失に向き合う",
    "theme.newSibling.title": "新しい兄弟",
    "theme.newSibling.desc": "新しい赤ちゃんの兄弟を迎える",
    "theme.dealingChange.title": "変化に対応する",
    "theme.dealingChange.desc": "人生の変化を受け入れ適応することを学ぶ",
    "theme.fairyTales.title": "おとぎ話",
    "theme.fairyTales.desc": "クラシックなおとぎ話の冒険を再び",
    "theme.folktales.title": "民話",
    "theme.folktales.desc": "世代から世代へ伝えられた伝統的な物語",
    "theme.mythsLegends.title": "神話と伝説",
    "theme.mythsLegends.desc": "神話と伝説からの壮大な物語",
    "theme.fablesMorals.title": "寓話と教訓",
    "theme.fablesMorals.desc": "貴重な人生の教訓を含む物語",
    "theme.holidayTraditions.title": "祝日の伝統",
    "theme.holidayTraditions.desc": "特別な祝日や伝統を祝う",
  },
  ar: {
    "seo.title": "StoryHeroes – قصص مخصصة للأطفال",
    "seo.description": "اجعل طفلك بطل كتاب قصص سحري. ارفع صورة، اختر موضوع المغامرة، واحصل على ملف PDF مخصص ومصور بشكل جميل في دقائق.",
    "hero.badge": "✨ كتب قصص مخصصة",
    "hero.title": "حوّل طفلك إلى بطل قصة",
    "hero.title.create": "اصنع",
    "hero.title.magical": "قصصاً سحرية",
    "hero.title.starring": "ببطولة طفلك",
    "hero.subtitle": "ارفع صورة، اختر مغامرة، واحصل على PDF مصور رائع أو كتاب غلاف صلب في دقائق.",
    "hero.cta": "أنشئ قصتك",
    "hero.freePreview": "أنشئ واستعرض كتابك مجانًا",
    "hero.privacyStamp": "🔒 يتم حذف الصور بعد التوليد",
    "hero.themes": "50+ موضوع",
    "hero.aiArt": "رسوم مخصصة",
    "hero.pdfDownload": "PDF فوري",
    "hero.features.personalized": "مخصص",
    "hero.features.ai": "فن مخصص",
    "hero.features.instant": "PDF فوري",
    "transform.badge": "✨ شاهد السحر",
    "transform.title": "من صورة إلى شخصية قصة",
    "transform.subtitle": "نحول صورة طفلك إلى شخصية مصورة جميلة",
    "transform.pixar": "أسلوب بيكسار",
    "transform.watercolor": "أسلوب ألوان مائية",
    "transform.anime": "أسلوب أنيمي",
    "showcase.title": "كتب جميلة، رقمية ومطبوعة",
    "showcase.subtitle": "احصل على كتابك المخصص كـ PDF رقمي أو غلاف صلب مطبوع",
    "showcase.pdfTitle": "PDF رقمي",
    "showcase.pdfDesc": "تنزيل فوري، قراءة على أي جهاز",
    "showcase.printTitle": "كتاب مطبوع",
    "showcase.printDesc": "غلاف صلب فاخر يصل إلى باب منزلك",
    "showcase.pages": "32 صفحة",
    "showcase.popular": "الأكثر شعبية",
    "reviews.badge": "⭐ محبوب من الآباء",
    "reviews.title": "ماذا يقول الآباء",
    "reviews.subtitle": "انضم إلى آلاف العائلات السعيدة التي أنشأت قصصاً سحرية",
    "reviews.child": "عمر الطفل:",
    "reviews.1.text": "ابنتي أحبت رؤية نفسها كبطلة! الرسوم مذهلة والقصة كانت مثالية لعمرها.",
    "reviews.1.childAge": "5 سنوات",
    "reviews.2.text": "فكرة هدية رائعة! ابني يقرأ قصته كل ليلة قبل النوم. جودة PDF مدهشة.",
    "reviews.2.childAge": "4 سنوات",
    "reviews.3.text": "أذهلتني الجودة. تم التقاط شبه طفلي بشكل مثالي. أنصح بشدة!",
    "reviews.3.childAge": "6 سنوات",
    "reviews.4.text": "طلبنا ثلاثة كتب، واحد لكل طفل من أطفالنا. يتشاجرون على من يقرأ كتابه أولاً!",
    "reviews.4.childAge": "3، 5، 7 سنوات",
    "reviews.5.text": "أسلوب الألوان المائية رائع تماماً. يبدو وكأن رساماً محترفاً لكتب الأطفال صنعه.",
    "reviews.5.childAge": "4 سنوات",
    "reviews.6.text": "أفضل هدية عيد ميلاد! وجه ابني عندما رأى نفسه في القصة لا يقدر بثمن.",
    "reviews.6.childAge": "6 سنوات",
    "trust.gift.title": "هدية مثالية",
    "trust.gift.desc": "هدية فريدة ومخصصة يعتز بها الأطفال لسنوات",
    "trust.ai.title": "فن مذهل",
    "trust.ai.desc": "تقنيتنا تصنع رسوماً مذهلة بصورة طفلك",
    "trust.love.title": "صُنع بحب",
    "trust.love.desc": "كل قصة مصنوعة بعناية لإلهام الخيال والفرح",
    "cta.title": "مستعد لصنع السحر؟",
    "cta.subtitle": "حوّل طفلك إلى بطل مغامرته الخاصة",
    "cta.button": "ابدأ الإنشاء الآن",
    "cta.note": "لا حاجة لحساب • جاهز في 5 دقائق",
    
    // Language Selector
    "lang.title": "اختر لغتك",
    "lang.subtitle": "حدد لغتك المفضلة للمتابعة",
    
    // Navigation
    "nav.backToHome": "العودة إلى الصفحة الرئيسية",
    
    // Footer
    "footer.copyright": "© 2024 StoryHeroes. جميع الحقوق محفوظة.",
    "footer.privacy": "سياسة الخصوصية",
    "footer.terms": "شروط الخدمة",
    "footer.contact": "اتصل بنا",
    "footer.trackOrder": "تتبع الطلب",
    
    // Privacy Page
    "privacy.title": "سياسة الخصوصية",
    "privacy.updated": "آخر تحديث: يناير 2024",
    "privacy.section1.title": "1. المعلومات التي نجمعها",
    "privacy.section1.intro": "نجمع المعلومات التالية عند استخدامك لخدمتنا:",
    "privacy.section1.item1": "الصور التي ترفعها لإنشاء كتب قصص مخصصة",
    "privacy.section1.item2": "اسم الطفل وعمره لتخصيص القصة",
    "privacy.section1.item3": "عنوان البريد الإلكتروني لتأكيد الطلب والتسليم",
    "privacy.section1.item4": "معلومات الدفع (تتم معالجتها بشكل آمن عبر Stripe)",
    "privacy.section2.title": "2. كيف نستخدم معلوماتك",
    "privacy.section2.intro": "نستخدم معلوماتك من أجل:",
    "privacy.section2.item1": "إنشاء كتب قصص مخصصة",
    "privacy.section2.item2": "معالجة المدفوعات وتسليم كتاب القصص PDF الخاص بك",
    "privacy.section2.item3": "إرسال تأكيدات الطلب والتحديثات",
    "privacy.section2.item4": "تحسين خدمتنا وتجربة المستخدم",
    "privacy.section2.item5": "الامتثال للالتزامات القانونية",
    "privacy.section3.title": "3. تخزين البيانات والأمان",
    "privacy.section3.text": "نأخذ أمان البيانات على محمل الجد. يتم تشفير جميع الصور المرفوعة والمعلومات الشخصية وتخزينها بشكل آمن. نستخدم تدابير أمنية قياسية في الصناعة لحماية بياناتك من الوصول غير المصرح به.",
    "privacy.section4.title": "4. خدمات الطرف الثالث",
    "privacy.section4.text": "نستخدم خدمات طرف ثالث موثوقة بما في ذلك Stripe لمعالجة الدفع وخدمات متخصصة لإنشاء القصص. هذه الخدمات لديها سياسات الخصوصية وتدابير الأمان الخاصة بها.",
    "privacy.section5.title": "5. الاحتفاظ بالبيانات",
    "privacy.section5.text": "نحتفظببياناتك فقط طالما كان ذلك ضروريًا لتقديم خدماتنا والامتثال للالتزامات القانونية. يمكنك طلب حذف بياناتك في أي وقت عن طريق الاتصال بنا.",
    "privacy.section6.title": "6. خصوصية الأطفال",
    "privacy.section6.text": "نحن ملتزمون بحماية خصوصية الأطفال. لا نجمع عن قصد معلومات شخصية من الأطفال دون سن 13 عامًا دون موافقة الوالدين. الآباء لديهم السيطرة الكاملة على معلومات أطفالهم.",
    "privacy.section7.title": "7. حقوقك",
    "privacy.section7.intro": "لديك الحق في:",
    "privacy.section7.item1": "الوصول إلى بياناتك الشخصية",
    "privacy.section7.item2": "طلب تصحيح البيانات غير الدقيقة",
    "privacy.section7.item3": "طلب حذف بياناتك",
    "privacy.section7.item4": "إلغاء الاشتراك في الاتصالات التسويقية",
    "privacy.section8.title": "8. اتصل بنا",
    "privacy.section8.text": "إذا كان لديك أي أسئلة حول سياسة الخصوصية هذه، يرجى الاتصال بنا من خلال صفحة الاتصال الخاصة بنا.",
    
    // Terms Page
    "terms.title": "شروط الخدمة",
    "terms.updated": "آخر تحديث: يناير 2024",
    "terms.section1.title": "1. قبول الشروط",
    "terms.section1.text": "باستخدام StoryHeroes، فإنك توافق على شروط الخدمة هذه. إذا كنت لا توافق، يرجى عدم استخدام خدمتنا.",
    "terms.section2.title": "2. وصف الخدمة",
    "terms.section2.text": "يوفر StoryHeroes كتب قصص مخصصة للأطفال. نقوم بإنشاء قصص مصورة مخصصة بناءً على الصور والمعلومات التي تقدمها.",
    "terms.section3.title": "3. مسؤوليات المستخدم",
    "terms.section3.intro": "أنت توافق على:",
    "terms.section3.item1": "تقديم معلومات دقيقة",
    "terms.section3.item2": "رفع الصور التي لديك حقوق استخدامها فقط",
    "terms.section3.item3": "عدم استخدام الخدمة لأغراض غير قانونية",
    "terms.section3.item4": "عدم رفع محتوى غير لائق أو مسيء",
    "terms.section3.item5": "احترام حقوق الملكية الفكرية",
    "terms.section4.title": "4. الدفع والاسترداد",
    "terms.section4.text": "تتم معالجة جميع المدفوعات بشكل آمن من خلال Stripe. نظرًا للطبيعة المخصصة لخدمتنا، فإن المبالغ المستردة متاحة فقط في حالة وجود مشاكل تقنية تمنع تسليم كتاب القصص الخاص بك.",
    "terms.section5.title": "5. الملكية الفكرية",
    "terms.section5.intro": "فيما يتعلق بالملكية الفكرية:",
    "terms.section5.item1": "تحتفظ بحقوق الصور التي ترفعها",
    "terms.section5.item2": "القصص والرسوم التوضيحية المولدة للاستخدام الشخصي فقط",
    "terms.section5.item3": "لا يمكنك إعادة بيع أو إعادة توزيع المحتوى المولد",
    "terms.section5.item4": "يحتفظ StoryHeroes بحقوق الخدمة والتكنولوجيا",
    "terms.section5.item5": "تمنحنا ترخيصًا لاستخدام المحتوى المرفوع لإنشاء كتاب القصص الخاص بك",
    "terms.section6.title": "6. تحديد المسؤولية",
    "terms.section6.text": "يتم توفير StoryHeroes 'كما هو' دون ضمانات. نحن غير مسؤولين عن أي أضرار غير مباشرة أو عرضية أو تبعية ناشئة عن استخدام خدمتنا.",
    "terms.section7.title": "7. إرشادات المحتوى",
    "terms.section7.text": "نحتفظ بالحق في رفض الخدمة أو إزالة المحتوى الذي يخالف إرشاداتنا، بما في ذلك المواد غير اللائقة أو المسيئة أو المحمية بحقوق الطبع والنشر.",
    "terms.section8.title": "8. التغييرات على الشروط",
    "terms.section8.text": "قد نقوم بتحديث شروط الخدمة هذه. الاستمرار في استخدام الخدمة بعد التغييرات يشكل قبولًا للشروط الجديدة.",
    "terms.section9.title": "9. الإنهاء",
    "terms.section9.text": "نحتفظ بالحق في إنهاء أو تعليق الوصول إلى خدمتنا لانتهاكات هذه الشروط أو لأي سبب آخر وفقًا لتقديرنا.",
    "terms.section10.title": "10. اتصل",
    "terms.section10.text": "للأسئلة حول شروط الخدمة هذه، يرجى الاتصال بنا من خلال صفحة الاتصال الخاصة بنا.",

    // Wizard Steps
    "step.addCharacters": "إضافة شخصيات",
    "step.chooseStory": "اختر القصة",
    "step.pickTitle": "اختر العنوان",
    "step.chooseStyle": "اختر الأسلوب",
    "step.review": "مراجعة",
    "step.storyOutline": "مخطط القصة",
    "step.previewPdf": "معاينة PDF",
    "photo.title": "أضف شخصياتك",
    "photo.subtitle": "ارفع صورة وأدخل تفاصيل كل شخصية في قصتك.",
    "photo.privacyNotice": "🔒 تُستخدم صورك فقط للرسم التوضيحي وتُحذف بعد التوليد.",
    "photo.addPhoto": "إضافة صورة",
    "photo.dragDrop": "أو اسحب وأفلت",
    "photo.dropHere": "أفلت هنا!",
    "photo.browse": "تصفح",
    "photo.camera": "كاميرا",
    "photo.upload": "رفع",
    "photo.name": "الاسم",
    "photo.namePlaceholder": "أدخل الاسم",
    "photo.age": "العمر",
    "photo.agePlaceholder": "أدخل العمر",
    "photo.gender": "نوع الشخصية",
    "photo.boy": "ولد",
    "photo.girl": "بنت",
    "photo.pet": "حيوان أليف",
    "photo.addChild": "إضافة شخصية أخرى",
    "photo.savedKids": "شخصياتك المحفوظة",
    "familyImport.button": "استيراد من صورة جماعية",
    "style.choose": "اختر أسلوبك الفني",
    "style.subtitleSelected": "شاهد كيف تبدو شخصياتك الـ{{count}} في هذا الأسلوب",
    "style.subtitleUnselected": "اختر أسلوب رسم لكتابك",
    "style.yourCharactersIn": "شخصياتك بأسلوب {{style}}",
    "style.creatingName": "جاري إنشاء {{name}}...",
    "style.altCharacterInStyle": "{{name}} بأسلوب {{style}}",
    "style.failedShort": "فشل",
    "style.retry": "إعادة المحاولة",
    "style.hoverHint": "مرر الماوس فوق الصور لرؤيتها بحجم أكبر",
    "style.preserveHint": "سيتم الحفاظ على أوصاف الشخصيات للاتساق",
    "style.tryAgain": "حاول مرة أخرى",
    "style.continueWith": "المتابعة مع {{style}}",
    "style.switchHint": "يمكنك تبديل الأساليب في أي وقت قبل المتابعة",
    "title.choose": "اختر عنوانك",
    "title.subtitle": "اختر العنوان المثالي لكتابك المخصص",
    "title.creating": "إنشاء عناوين سحرية...",
    "title.selected": "تم التحديد",
    "title.regenerate": "إعادة توليد العناوين",
    "title.usingSuggested": "عناوين مقترحة",
    "title.pickFavorite": "اختر المفضل لديك من هذه الاقتراحات!",
    "themes.title": "اختر موضوعاً لـ{{hero}}",
    "themes.subtitle": "اختر موضوع مغامرة لشخصياتك الـ{{count}} {{characterWord}}. كل موضوع ينشئ قصصاً فريدة.",
    "themes.changeTheme": "تغيير الموضوع",
    "themes.whatsStoryAbout": "عن ماذا يجب أن تكون قصة {{hero}}؟",
    "themes.yourIdeas": "أفكار قصتك",
    "themes.ideaHelperText": "صف المغامرة التي تريدها. كن مبدعاً كما تشاء!",
    "themes.ideaPlaceholder": "مثال: فارس شجاع يصادق تنيناً ومعاً ينقذان مملكة سحرية...",
    "themes.ideaHint": "بداية رائعة! انقر أدناه للحصول على أفكار محسنة.",
    "themes.generateRequired": "اكتب فكرتك الخاصة أو انقر أدناه للإلهام!",
    "themes.generatingIdeas": "توليد الأفكار...",
    "themes.completeMyIdeas": "أكمل أفكاري",
    "themes.inspireMe": "ألهمني!",
    "themes.chooseIdea": "اختر فكرة قصة",
    "themes.usingPreMade": "أفكار جاهزة",
    "themes.greatIdeas": "إليك بعض أفكار القصص الرائعة!",
    "outline.title": "مخطط القصة",
    "outline.subtitle": "راجع وخصص صفحات كتابك",
    "outline.generating": "جاري إنشاء مخطط قصتك...",
    "outline.titlePage": "صفحة العنوان",
    "outline.ending": "النهاية",
    "outline.page": "صفحة",
    "outline.happy": "راضٍ عن المخطط؟",
    "outline.happyDesc": "يمكنك تعديل نص أي صفحة أعلاه. عندما تكون جاهزاً، انقر على التالي لتوليد الرسومات!",
    "outline.sceneDescription": "وصف المشهد",
    "outline.whatYouWillSee": "ما ستراه في هذه الصفحة",
    "preview.title": "راجع قصتك",
    "preview.subtitle": "كل شيء يبدو جيداً؟ لنصنع كتابك المصور!",
    "preview.storyTheme": "موضوع القصة",
    "preview.yourHeroes": "أبطالك",
    "preview.chosenStyle": "الأسلوب الفني",
    "preview.ready": "كل شيء يبدو رائعاً! ✨",
    "preview.readyDesc": "سننشئ قصة سحرية بطلها {{names}} بأسلوب {{style}}.",
    "pdfPreview.bookReady": "كتابك جاهز!",
    "pdfPreview.finishingIllustrations": "إنهاء الرسومات...",
    "pdfPreview.reviewMessage": "راجع كتابك وحمّله عندما تكون جاهزاً.",
    "pdfPreview.preparing": "جاري التحضير...",
    "pdfPreview.happyWithBook": "أنا راضٍ عن كتابي!",
    "pdfPreview.paymentComplete": "تم الدفع!",
    "pdfPreview.generatingPdf": "توليد PDF...",
    "pdfPreview.downloadPdf": "تحميل PDF",
    "pdfPreview.loveIt": "أعجبك؟ احصل على نسختك!",
    "pdfPreview.processing": "جاري المعالجة...",
    "pdfPreview.digitalPdf": "PDF رقمي",
    "pdfPreview.printedBook": "كتاب مطبوع",
    "pdfPreview.pdfDownloadShortly": "سيتم تحميل PDF قريباً.",
    "pdfPreview.tryAgainMoment": "يرجى المحاولة مرة أخرى بعد لحظة.",
    "pdfPreview.paymentSuccess": "تم الدفع بنجاح!",
    "pdfPreview.pdfReady": "\"{{title}}\" جاهز للتحميل",
    "pdfPreview.readyNextBook": "جاهز لكتابك التالي!",
    "pdfPreview.canCreateNew": "يمكنك إنشاء كتاب جديد في أي وقت.",
    "nav.back": "رجوع",
    "nav.next": "التالي",
    "nav.generateIllustrations": "توليد الرسومات",
    "download.pdf": "تحميل PDF",
    "complete.title": "كتابك جاهز! 🎉",
    "complete.subtitle": "تم إنشاء كتاب قصص سحري بطله {{names}}!",
    "complete.download": "تحميل PDF",
    "complete.startOver": "إنشاء كتاب آخر",
    "generating.preparingStory": "جاري تحضير قصتك...",
    "generating.creatingIllustrations": "جاري إنشاء الرسومات...",
    "generating.creatingPreviewIllustrations": "جاري إنشاء رسومات المعاينة...",
    "generating.creatingCover": "جاري إنشاء الغلاف...",
    "generating.coverCreated": "تم إنشاء الغلاف!",
    "generating.backCoverCreated": "تم إنشاء الغلاف الخلفي!",
    "finalize.paymentConfirmed": "تم تأكيد الدفع!",
    "finalize.generatingStory": "توليد قصتك الكاملة...",
    "finalize.creatingIllustrations": "إنشاء الرسومات...",
    "finalize.addingFinishingTouches": "لمسات أخيرة...",
    "finalize.creatingBackCover": "إنشاء الغلاف الخلفي...",
    "finalize.bookReadyReview": "كتابك جاهز للمراجعة!",
    "finalize.preparingPdf": "جارٍ تحضير ملف PDF...",
    "resume.loadingBook": "جارٍ تحميل كتابك...",
    "resume.stillGenerating": "كتابك لا يزال قيد الإنشاء!",
    "resume.stillGeneratingDesc": "لا تزال الرسوم التوضيحية قيد الإنشاء في الخلفية. ستتلقى بريدًا إلكترونيًا عندما يكون جاهزًا.",
    "resume.checkBackLater": "يمكنك إغلاق هذه الصفحة والعودة لاحقًا.",
    "illustration.noRedosLeft": "لم تتبقَ إعادات",
    "illustration.limitReached": "لقد استخدمت جميع إعادات الرسم. اشترِ المزيد للمتابعة.",
    "toast.paymentCancelled": "تم إلغاء الدفع",
    "toast.tryAgainReady": "كتابك ينتظرك. حاول مرة أخرى في أي وقت!",
    "toast.redoCreditsAdded": "تمت إضافة رصيد الإعادة!",
    "toast.threeMoreRedos": "لديك الآن 3 إعادات رسم إضافية.",
    "toast.paymentSuccessful": "تم الدفع بنجاح!",
    "toast.pdfReadyDownload": "PDF جاهز للتحميل.",
    "toast.downloadStarted": "بدأ التحميل",
    "toast.couldntDownload": "تعذر التحميل",
    "toast.failedGenerate": "فشل التوليد. يرجى المحاولة مرة أخرى.",
    "toast.pleaseRetry": "يرجى المحاولة مرة أخرى.",
    "toast.storyCreated": "تم إنشاء القصة!",
    "toast.previewStory": "معاينة كتابك أدناه.",
    "toast.fillIllustrations": "توليد الرسومات المتبقية...",
    "toast.generationFailed": "فشل التوليد",
    "toast.generationTimeout": "انتهت مهلة التوليد. يرجى المحاولة مرة أخرى.",
    "toast.paymentFailed": "فشل الدفع",
    "toast.somethingWrong": "حدث خطأ ما",
    "toast.createBookAgain": "يرجى محاولة إنشاء كتابك مرة أخرى.",
    "toast.errorGenerating": "حدث خطأ أثناء توليد كتابك.",
    "toast.printOrderConfirmed": "تم تأكيد طلب الطباعة!",
    "toast.bookPrintedShipped": "سيتم طباعة كتابك وشحنه قريباً.",
    "toast.paymentSuccessContact": "تم الدفع بنجاح. تواصل مع الدعم إذا لزم الأمر.",
    "toast.preparingPrintFiles": "تحضير ملفات الطباعة",
    "toast.readyForPrinting": "جاري تحضير كتابك للطباعة...",
    "toast.orderFailed": "فشل الطلب",
    "toast.paymentError": "خطأ في الدفع",
    "toast.tryAgainLater": "يرجى المحاولة لاحقاً.",
    "toast.coverRegenerated": "تم إعادة توليد الغلاف!",
    "toast.newCoverReady": "غلافك الجديد جاهز.",
    "toast.pageRegenerated": "تم إعادة توليد الصفحة {{page}}!",
    "toast.feedbackApplied": "تم تطبيق ملاحظاتك.",
    "toast.freshIllustration": "تم إنشاء رسم توضيحي جديد.",
    "toast.regenerationFailed": "فشل إعادة التوليد",
    "toast.couldNotRegenerate": "تعذر إعادة توليد الرسم. يرجى المحاولة مرة أخرى.",
    "toast.backCoverFailed": "فشل الغلاف الخلفي",
    "toast.backCoverFrontUsed": "سيتم استخدام الغلاف الأمامي بدلاً منه.",
    "toast.backCoverReady": "الغلاف الخلفي جاهز!",
    "toast.backCoverGenerated": "تم توليد غلافك الخلفي.",
    "toast.frontCoverRequired": "الغلاف الأمامي مطلوب",
    "toast.frontCoverFirst": "قم بتوليد غلاف أمامي أولاً.",
    "toast.backCoverRegenerated": "تم إعادة توليد الغلاف الخلفي!",
    "toast.backCoverFeedbackApplied": "تم تطبيق ملاحظاتك على الغلاف الخلفي.",
    "toast.freshBackCover": "تم إنشاء غلاف خلفي جديد.",
    "toast.backCoverRegenerateFailed": "تعذر إعادة توليد الغلاف الخلفي.",
    "toast.allIllustrationsComplete": "اكتملت جميع الرسومات!",
    "toast.storybookReady": "كتابك مصور بالكامل.",
    "toast.someIllustrationsIncomplete": "بعض الرسومات غير مكتملة",
    "toast.pagesMissing": "{{count}} صفحات لا تزال تفتقر إلى رسومات.",
    "toast.bookNotComplete": "الكتاب غير مكتمل",
    "toast.generateFirst": "يرجى توليد قصتك أولاً.",
    "toast.preparingBook": "تحضير كتابك",
    "toast.uploadingPrint": "رفع ملفات الطباعة...",
    "toast.missingInfoCheckout": "معلومات ناقصة للدفع.",
    "toast.failedCheckout": "فشل إنشاء الدفع. يرجى المحاولة مرة أخرى.",
    "toast.checkEmail": "تحقق من بريدك الإلكتروني أيضاً للحصول على رابط PDF!",
    "toast.couldntSendPdf": "تعذر إرسال PDF. جرب التحميل.",
    "toast.emailSent": "تم إرسال البريد!",
    "toast.bookSentTo": "تم إرسال كتابك إلى {{email}}.",
    "toast.generatingPdf": "توليد PDF...",
    "paywall.title": "أكمل كتاب {{names}}!",
    "paywall.subtitle": "معاينتك جاهزة! احصل على PDF المصور الكامل للاحتفاظ به للأبد.",
    "paywall.digitalPdf": "PDF رقمي — $9.99",
    "paywall.price": "$9.99",
    "paywall.benefit1": "كتاب مصور كامل (20+ صفحة)",
    "paywall.benefit2": "PDF عالي الجودة للطباعة في المنزل",
    "paywall.benefit3": "تحميل فوري بعد الشراء",
    "paywall.processing": "جارٍ المعالجة...",
    "paywall.getBook": "احصل على كتابي — $9.99",
    "paywall.stopTitle": "مغادرة قصتك؟",
    "paywall.stopDesc": "سيضيع كتاب {{names}} المخصص إذا غادرت الآن.",
    "paywall.noContinue": "لا، استمر",
    "paywall.yesStop": "نعم، غادر",
    "upsell.title": "اطلب كتابًا مطبوعًا!",
    "upsell.subtitle": "لديك كتابك الرقمي! هل تريد غلافًا صلبًا فاخرًا يصل إلى بابك؟",
    "upsell.specialOffer": "عرض خاص",
    "upsell.addPrinted": "إضافة كتاب مطبوع – $19.99",
    "upsell.shippingNote": "يتم حساب الشحن عند الدفع",
    "upsell.benefit1": "كتاب غلاف صلب فاخر",
    "upsell.benefit2": "جودة طباعة احترافية",
    "upsell.benefit3": "تذكار سيعتزون به للأبد",
    "upsell.benefit4": "شحن عالمي متاح",
    "upsell.addButton": "إضافة نسخة مطبوعة – $19.99",
    "upsell.noThanks": "لا شكرًا، سأحتفظ بنسختي الرقمية",

    "styleCard.pixar.name": "أسلوب بيكسار",
    "styleCard.pixar.desc": "أسلوب كرتوني ثلاثي الأبعاد مستوحى من أفلام الرسوم المتحركة",
    "styleCard.watercolor.name": "ألوان مائية كلاسيكية",
    "styleCard.watercolor.desc": "رسومات ألوان مائية ناعمة وحالمة",
    "styleCard.anime.name": "أسلوب أنمي",
    "styleCard.anime.desc": "فن نابض بالحياة مستوحى من الأنمي الياباني",
    "generating.title": "جارٍ إنشاء قصتك...",
    "generating.subtitle": "نحن نصنع مغامرة سحرية لك!",
    "generating.step.preparingStory": "جارٍ تحضير قصتك...",
    "generating.step.creatingIllustrations": "جارٍ إنشاء الرسومات...",
    "generating.step.addingDetails": "جارٍ إضافة التفاصيل...",
    "generating.step.finishingTouches": "اللمسات الأخيرة...",
    "finalizing.title": "جارٍ إنهاء كتابك...",
    "finalizing.subtitle": "جارٍ إنشاء كتاب {{names}} المخصص",
    "finalizing.step1": "جارٍ إعداد القصة...",
    "finalizing.step2": "جارٍ إنشاء النص...",
    "finalizing.step3": "جارٍ إنشاء الرسومات...",
    "finalizing.step4": "جارٍ إضافة اللمسات الأخيرة...",
    "finalizing.step5": "جارٍ تحضير كتابك...",
    "finalizing.illustrations": "{{completed}} من {{total}} رسمة مكتملة",
    "finalizing.percentComplete": "{{percent}}% مكتمل",
    "finalizing.emailWhenReady": "أرسل لي بريدًا إلكترونيًا عندما يكون جاهزًا",
    "finalizing.emailWhenReadyHint": "سنكمل في الخلفية ونرسل لك بريدًا إلكترونيًا عندما يكون كتابك جاهزًا.",
    "finalizing.safeToClose": "يتم إنشاء كتابك على خوادمنا. يمكنك إغلاق هذه النافذة بأمان — سنواصل العمل ونرسل لك بريدًا إلكترونيًا عند الانتهاء!",
    "finalizing.emailConfirmTitle": "كل شيء جاهز!",
    "finalizing.emailConfirmSubtitle": "يمكنك إغلاق هذه الصفحة الآن.",
    "finalizing.emailConfirmDesc": "سنستمر في إنشاء كتابك في الخلفية وسنرسل لك بريدًا إلكترونيًا مع رابط التحميل عندما يكون جاهزًا.",
    "finalizing.emailConfirmNote": "تحقق من بريدك الوارد بعد بضع دقائق.",
    "toast.backgroundStarted": "بدأ الإنشاء في الخلفية",
    "toast.backgroundStartedDesc": "سنرسل لك بريدًا إلكترونيًا عندما يكون كتابك جاهزًا!",
    "toast.backgroundFailed": "تعذر بدء الإنشاء في الخلفية. يرجى إبقاء هذه الصفحة مفتوحة.",
    "pdf.adventure": "مغامرة مخصصة",
    "pdf.theEnd": "النهاية",
    "pdf.storyFor": "قصة لـ",
    "backCover.title": "الغلاف الخلفي",
    "illustration.regenerating": "جارٍ إعادة إنشاء الرسمة...",
    "illustration.redoCover": "إعادة الغلاف",
    "illustration.redoPage": "إعادة الصفحة",
    "illustration.redo": "إعادة",
    "illustration.buyMoreDescription": "تحتاج تغييرات أكثر؟ احصل على 3 إعادات إضافية.",
    "illustration.buyMore": "شراء 3 إعادات",
    "illustration.lastRedo": "هذه آخر إعادة لك! استخدمها بحكمة.",
    "illustration.placeholder": "صف ما تريد تغييره...",
    "illustration.applyChanges": "تطبيق التغييرات",
    "styleEdit.edit": "تعديل",
    "styleEdit.placeholder": "صف كيف تريد تغيير هذه الشخصية...",
    "styleEdit.apply": "تطبيق التغييرات",
    "thankYou.title": "شكرًا لك!",
    "thankYou.subtitle": "مغامرة {{names}} جاهزة!",
    "thankYou.emailResent": "تمت إعادة إرسال البريد!",
    "thankYou.emailFailed": "فشل البريد الإلكتروني",
    "thankYou.emailSent": "تم إرسال البريد!",
    "thankYou.emailPending": "جارٍ إرسال البريد...",
    "thankYou.resending": "جارٍ إعادة الإرسال...",
    "thankYou.resendEmail": "إعادة إرسال البريد",
    "thankYou.emailFailedDesc": "لم نتمكن من إرسال البريد إلى {{email}}. جرب التحميل.",
    "thankYou.emailDesc": "تم إرسال كتابك إلى {{email}}.",
    "thankYou.emailPendingDesc": "نحن نرسل كتابك إلى {{email}}...",
    "thankYou.printOrdered": "تم طلب النسخة المطبوعة!",
    "thankYou.printDesc": "سيتم شحن كتابك المطبوع قريبًا.",
    "thankYou.generatingPdf": "جارٍ إنشاء PDF...",
    "thankYou.finishingIllustrations": "جارٍ إنهاء الرسومات...",
    "thankYou.downloadBook": "تحميل الكتاب",
    "thankYou.createAnother": "إنشاء كتاب آخر",
    "thankYou.hopeLove": "نأمل أن يحب {{names}} قصتهم!",
    "thankYou.finalizing": "نحن ننهي كتابك...",
    "thankYou.emailWhenReady": "ستتلقاه عبر البريد الإلكتروني على {{email}}",
    "thankYou.doNotClose": "يرجى عدم إغلاق هذه النافذة أثناء تحضير كتابك",
    "thankYou.bookOnItsWay": "كتابك في الطريق!",
    "thankYou.sentTo": "تم الإرسال إلى {{email}}",
    "email.getBook": "احصل على كتابك",
    "email.sendToEmail": "سنرسل كتابك إلى بريدك",
    "email.processingPayment": "جارٍ معالجة الدفع...",
    "email.pleaseWait": "يرجى الانتظار، لا تغلق هذه النافذة.",
    "email.bookSentAfterPayment": "سيتم إرسال كتابك بعد الدفع.",
    "email.yourName": "اسمك",
    "email.namePlaceholder": "أدخل اسمك",
    "email.emailAddress": "البريد الإلكتروني",
    "email.emailPlaceholder": "أدخل بريدك",
    "email.marketingConsent": "أرغب في تلقي تحديثات حول الميزات والقصص الجديدة",
    "email.continuePayment": "المتابعة إلى الدفع",
    "email.redirect": "سيتم توجيهك إلى صفحة الدفع الآمنة",
    "email.stepPreparing": "جارٍ تحضير كتابك...",
    "email.stepCreating": "جارٍ إنشاء الدفع...",
    "email.stepRedirecting": "جارٍ التوجيه إلى الدفع...",
    "printPayment.title": "جارٍ تحضير طلبك",
    "printPayment.subtitle": "إعداد الدفع لكتابك المطبوع",
    "printPayment.stepUploading": "جارٍ تحضير ملفات الطباعة...",
    "printPayment.stepCreating": "جارٍ إنشاء الدفع...",
    "printPayment.stepRedirecting": "جارٍ فتح الدفع...",
    "maintenance.title": "نحن نصنع السحر!",
    "maintenance.crafting": "نحن نحضر شيئًا مذهلاً. عد قريبًا!",
    "maintenance.devAccess": "وصول المطور",
    "maintenance.enterPassword": "أدخل كلمة المرور",
    "maintenance.accessSite": "الوصول للموقع",
    "maintenance.incorrectPassword": "كلمة مرور خاطئة",
    "maintenance.comingSoon": "نعود قريبًا!",
    "contact.title": "اتصل بنا",
    "contact.subtitle": "هل لديك سؤال؟ يسعدنا سماعك!",
    "contact.emailUs": "أرسل لنا بريدًا",
    "contact.responseTime": "وقت الاستجابة",
    "contact.within24": "خلال 24 ساعة",
    "contact.liveChat": "محادثة مباشرة",
    "contact.chatHours": "الإثنين-الجمعة، 9ص-5م",
    "contact.sendMessage": "أرسل لنا رسالة",
    "contact.yourName": "اسمك",
    "contact.namePlaceholder": "محمد أحمد",
    "contact.email": "البريد",
    "contact.emailPlaceholder": "mohammed@example.com",
    "contact.subject": "الموضوع",
    "contact.subjectPlaceholder": "كيف يمكننا مساعدتك؟",
    "contact.message": "الرسالة",
    "contact.messagePlaceholder": "أخبرنا المزيد...",
    "contact.sending": "جارٍ الإرسال...",
    "contact.send": "إرسال الرسالة",
    "contact.sent": "تم إرسال الرسالة! سنعود إليك قريبًا.",
    "contact.faq": "الأسئلة الشائعة",
    "contact.faq1.q": "كم يستغرق إنشاء كتاب؟",
    "contact.faq1.a": "يتم إنشاء كتابك المخصص في دقائق معدودة باستخدام تقنيتنا المتقدمة.",
    "contact.faq2.q": "ماذا يحدث لصوري؟",
    "contact.faq2.a": "تُستخدم صورك فقط لإنشاء الرسومات ويتم حذفها تلقائيًا بعد إنشاء الكتاب.",
    "contact.faq3.q": "هل يمكنني الحصول على نسخة مطبوعة؟",
    "contact.faq3.a": "نعم! يمكنك طلب نسخة مطبوعة فاخرة بغلاف صلب وتوصيلها إلى باب منزلك.",
    "notFound.title": "404",
    "notFound.message": "عفوًا! هذه الصفحة غير موجودة.",
    "notFound.backHome": "العودة للصفحة الرئيسية",
    "payment.orderConfirmed": "تم تأكيد الطلب!",
    "payment.printDelivery": "سيتم شحن كتابك المطبوع قريبًا.",
    "payment.orderIssue": "مشكلة في الطلب",
    "payment.issueDesc": "حدثت مشكلة في معالجة طلبك. يرجى الاتصال بالدعم.",
    "payment.downloadReady": "التحميل جاهز!",
    "payment.digitalDelivery": "كتابك الرقمي جاهز للتحميل.",
    "payment.submitting": "جارٍ معالجة طلبك...",
    "payment.paymentSuccess": "تم الدفع بنجاح! جارٍ إعداد طلبك...",
    "payment.contactSupport": "يرجى الاتصال بالدعم مع معرف الجلسة:",
    "payment.backHome": "العودة للرئيسية",
    "payment.digitalThankYou": "شكرًا لشرائك! كتابك الرقمي جاهز.",
    "payment.printThankYou": "شكرًا لشرائك! سيتم شحن كتابك المطبوع قريبًا.",
    "payment.orderDetails": "تفاصيل الطلب",
    "payment.bookTitle": "كتاب مخصص",
    "payment.orderId": "معرف الطلب:",
    "payment.createAnother": "إنشاء كتاب آخر",
    "shipping.title": "عنوان الشحن",
    "shipping.for": "لـ:",
    "shipping.fullName": "الاسم الكامل",
    "shipping.nameRequired": "الاسم مطلوب",
    "shipping.emailRequired": "البريد مطلوب",
    "shipping.emailInvalid": "يرجى إدخال بريد صحيح",
    "shipping.streetRequired": "العنوان مطلوب",
    "shipping.cityRequired": "المدينة مطلوبة",
    "shipping.stateRequired": "المنطقة مطلوبة",
    "shipping.postalRequired": "الرمز البريدي مطلوب",
    "shipping.phoneRequired": "رقم الهاتف مطلوب",
    "shipping.email": "البريد الإلكتروني",
    "shipping.streetAddress": "العنوان",
    "shipping.apartment": "شقة / جناح",
    "shipping.city": "المدينة",
    "shipping.postalCode": "الرمز البريدي",
    "shipping.country": "البلد",
    "shipping.stateProvince": "المنطقة / المحافظة",
    "shipping.optional": "اختياري",
    "shipping.phoneNumber": "رقم الهاتف",
    "shipping.phoneHint": "مطلوب لإشعارات التوصيل",
    "shipping.cancel": "إلغاء",
    "shipping.continuePayment": "متابعة الدفع",
    "shipping.processing": "جاري المعالجة...",
    "shipping.state": "الولاية",
    "shipping.selectCountry": "اختر البلد",
    "shipping.selectState": "اختر الولاية",
    "shipping.deliveryOptions": "خيارات التوصيل",
    "shipping.chooseDelivery": "اختر التوصيل",
    "shipping.loadingOptions": "جارٍ تحميل الخيارات...",
    "shipping.selectDeliverySpeed": "اختر سرعة التوصيل المفضلة:",
    "shipping.businessDays": "أيام عمل",
    "shipping.estimateUnavailable": "التقدير غير متاح",
    "shipping.back": "رجوع",
    "shipping.included": "مشمول",
    "pdfPreview.printComingSoon": "قريبًا!",
    "queue.position": "الموقع في الطابور: {{position}}",
    "queue.waiting": "في انتظار الدور...",

    // Order Tracking
    "trackOrder.title": "تتبع طلبي",
    "trackOrder.subtitle": "أدخل بريدك الإلكتروني لعرض سجل الطلبات",
    "trackOrder.emailPlaceholder": "your@email.com",
    "trackOrder.search": "بحث",
    "trackOrder.error": "حدث خطأ. يرجى المحاولة مرة أخرى.",
    "trackOrder.noOrders": "لا توجد طلبات",
    "trackOrder.noOrdersDesc": "لم نعثر على طلبات مرتبطة بهذا البريد الإلكتروني.",
    "trackOrder.createBook": "أنشئ كتابك الأول",
    "trackOrder.foundOrders": "تم العثور على {{count}} طلب(ات)",
    "trackOrder.digital": "PDF رقمي",
    "trackOrder.printed": "كتاب مطبوع",
    "trackOrder.status.pending": "قيد الانتظار",
    "trackOrder.status.paid": "مدفوع",
    "trackOrder.status.fulfilled": "مكتمل",
    "trackOrder.status.shipped": "تم الشحن",
    "footer.myBooks": "حسابي",
    "auth.loginTitle": "مرحباً بعودتك",
    "auth.loginSubtitle": "سجل الدخول للوصول إلى كتبك",
    "auth.signupTitle": "إنشاء حساب",
    "auth.signupSubtitle": "سجل لحفظ كتبك وشخصياتك",
    "auth.forgotTitle": "إعادة تعيين كلمة المرور",
    "auth.forgotSubtitle": "أدخل بريدك الإلكتروني لتلقي رابط إعادة التعيين",
    "auth.displayName": "الاسم المعروض",
    "auth.email": "البريد الإلكتروني",
    "auth.password": "كلمة المرور",
    "auth.loginButton": "تسجيل الدخول",
    "auth.signupButton": "إنشاء حساب",
    "auth.resetButton": "إرسال الرابط",
    "auth.forgotPassword": "نسيت كلمة المرور؟",
    "auth.noAccount": "ليس لديك حساب؟",
    "auth.signupLink": "سجل",
    "auth.hasAccount": "لديك حساب بالفعل؟",
    "auth.loginLink": "سجل الدخول",
    "auth.backToLogin": "العودة لتسجيل الدخول",
    "auth.error": "خطأ",
    "auth.errorDesc": "حدث خطأ ما. يرجى المحاولة مرة أخرى.",
    "auth.welcomeTitle": "مرحباً!",
    "auth.welcomeDesc": "تم إنشاء حسابك بنجاح.",
    "auth.resetSent": "تم إرسال الرابط",
    "auth.resetSentDesc": "تحقق من بريدك الإلكتروني للحصول على رابط إعادة التعيين.",
    "dashboard.title": "لوحة التحكم",
    "dashboard.subtitle": "إدارة كتبك وشخصياتك",
    "dashboard.logout": "تسجيل الخروج",
    "dashboard.newBook": "كتاب جديد",
    "dashboard.myBooks": "كتبي",
    "dashboard.myKids": "شخصياتي",
    "dashboard.inProgress": "قيد التنفيذ",
    "dashboard.digital": "رقمي",
    "dashboard.printed": "مطبوع",
    "dashboard.noInProgress": "لا توجد كتب قيد التنفيذ",
    "dashboard.noDigital": "لا توجد كتب رقمية بعد",
    "dashboard.noPrinted": "لا توجد كتب مطبوعة بعد",
    "dashboard.createFirst": "أنشئ كتابك الأول",
    "dashboard.untitled": "كتاب بدون عنوان",
    "dashboard.addChild": "إضافة طفل",
    "dashboard.editChild": "تعديل الطفل",
    "dashboard.addFirstChild": "أضف طفلك الأول",
    "dashboard.noKids": "لا توجد شخصيات محفوظة",
    "dashboard.childFormDesc": "احفظ معلومات طفلك لإنشاء كتب بسرعة",
    "dashboard.photo": "صورة",
    "dashboard.yearsOld": "سنوات",
    "dashboard.savedStyles": "الأنماط المحفوظة:",
    "dashboard.save": "حفظ",
    "dashboard.childAdded": "تمت إضافة الشخصية!",
    "dashboard.childUpdated": "تم تحديث الشخصية!",
    "dashboard.childRemoved": "تم حذف الشخصية",
    "dashboard.profile": "الملف الشخصي",
    "dashboard.personalInfo": "المعلومات الشخصية",
    "dashboard.fullName": "الاسم الكامل",
    "dashboard.fullNamePlaceholder": "أدخل اسمك الكامل",
    "dashboard.email": "البريد الإلكتروني",
    "dashboard.phone": "رقم الهاتف",
    "dashboard.phonePlaceholder": "أدخل رقم هاتفك",
    "dashboard.shippingAddress": "عنوان الشحن",
    "dashboard.addressLine1": "سطر العنوان 1",
    "dashboard.addressLine1Placeholder": "عنوان الشارع",
    "dashboard.addressLine2": "سطر العنوان 2",
    "dashboard.addressLine2Placeholder": "شقة، جناح، إلخ. (اختياري)",
    "dashboard.city": "المدينة",
    "dashboard.cityPlaceholder": "المدينة",
    "dashboard.state": "الولاية / المقاطعة",
    "dashboard.statePlaceholder": "الولاية / المقاطعة",
    "dashboard.postalCode": "الرمز البريدي",
    "dashboard.postalCodePlaceholder": "الرمز البريدي",
    "dashboard.country": "البلد",
    "dashboard.countryPlaceholder": "البلد",
    "dashboard.saveProfile": "حفظ الملف الشخصي",
    "dashboard.saved": "تم الحفظ!",
    "dashboard.profileSaved": "تم حفظ الملف الشخصي!",
    "dashboard.profileSaveError": "فشل حفظ الملف الشخصي",
    "dashboard.confirmDelete": "حذف الشخصية؟",
    "dashboard.confirmDeleteDesc": "سيؤدي هذا إلى حذف هذه الشخصية وأنماطها المحفوظة بشكل دائم.",
    "dashboard.delete": "حذف",
    "dashboard.generateStyle": "إنشاء النمط",
    "dashboard.regenerateStyle": "إعادة الإنشاء",
    "dashboard.selectStyle": "اختر أسلوب فني",
    "dashboard.generatingStyle": "جارٍ إنشاء النمط...",
    "dashboard.styleGenerated": "تم إنشاء النمط!",
    "dashboard.needsPhoto": "ارفع صورة أولاً",
    "dashboard.continueBook": "متابعة",
    "dashboard.downloadPdf": "تحميل",
    "dashboard.trackOrder": "تتبع",
    "dashboard.statusPending": "قيد التنفيذ",
    "dashboard.statusGenerating": "جارٍ الإنشاء",
    "dashboard.statusProcessing": "جارٍ المعالجة",
    "dashboard.statusPaid": "مدفوع",
    "dashboard.statusCompleted": "مكتمل",
    "dashboard.statusFulfilled": "تم التسليم",
    "dashboard.statusShipped": "تم الشحن",
    "dashboard.buyHardCopy": "شراء نسخة مطبوعة",
    "thankYou.illustrationProgress": "الرسومات: {{completed}} / {{total}}",

    // Theme Categories
    "category.friendship": "صداقة",
    "category.growingUp": "النمو",
    "category.kindness": "اللطف والمشاعر",
    "category.values": "القيم",
    "category.adventure": "مغامرة",
    "category.fantasy": "خيال",
    "category.humor": "فكاهة",
    "category.learning": "تعلم",
    "category.diversity": "التنوع والشمول",
    "category.nature": "الطبيعة والبيئة",
    "category.problemSolving": "حل المشكلات",
    "category.lifeExperiences": "تجارب الحياة",
    "category.cultural": "قصص ثقافية",

    // Theme Titles & Descriptions
    "theme.makingFriends.title": "تكوين صداقات",
    "theme.makingFriends.desc": "قصة عن تكوين صداقات جديدة وفرحة التواصل",
    "theme.bestFriends.title": "أفضل الأصدقاء",
    "theme.bestFriends.desc": "مغامرة تحتفي برابطة أفضل الأصدقاء",
    "theme.sharing.title": "المشاركة",
    "theme.sharing.desc": "تعلم السعادة التي تأتي من المشاركة مع الآخرين",
    "theme.siblings.title": "الإخوة",
    "theme.siblings.desc": "قصة دافئة عن الرابطة الخاصة بين الإخوة",
    "theme.sayingSorry.title": "الاعتذار",
    "theme.sayingSorry.desc": "تعلم متى وكيف تعتذر وتصلح الأمور",
    "theme.beingYourself.title": "كن نفسك",
    "theme.beingYourself.desc": "اكتشاف سحر كونك صادقاً مع نفسك",
    "theme.tryingNew.title": "تجربة أشياء جديدة",
    "theme.tryingNew.desc": "رحلة مثيرة خارج منطقة الراحة",
    "theme.overcomingFear.title": "التغلب على الخوف",
    "theme.overcomingFear.desc": "مغامرة شجاعة لمواجهة المخاوف والتغلب عليها",
    "theme.findingTalent.title": "اكتشاف موهبتك",
    "theme.findingTalent.desc": "اكتشاف موهبة خاصة تجعلك فريداً",
    "theme.confidence.title": "الثقة بالنفس",
    "theme.confidence.desc": "بناء الثقة بالنفس والإيمان بنفسك",
    "theme.understandingFeelings.title": "فهم المشاعر",
    "theme.understandingFeelings.desc": "تعلم التعرف على المشاعر وفهمها",
    "theme.helpingOthers.title": "مساعدة الآخرين",
    "theme.helpingOthers.desc": "فرحة ومكافأة مد يد المساعدة",
    "theme.dealingAnger.title": "التعامل مع الغضب",
    "theme.dealingAnger.desc": "طرق صحية لإدارة الغضب والتعبير عنه",
    "theme.copingSadness.title": "التعامل مع الحزن",
    "theme.copingSadness.desc": "إيجاد العزاء والأمل في الأوقات الحزينة",
    "theme.showingEmpathy.title": "إظهار التعاطف",
    "theme.showingEmpathy.desc": "فهم والاهتمام بمشاعر الآخرين",
    "theme.honesty.title": "الصدق",
    "theme.honesty.desc": "أهمية قول الحقيقة",
    "theme.responsibility.title": "المسؤولية",
    "theme.responsibility.desc": "تعلم تحمل المسؤولية عن أفعالك",
    "theme.respect.title": "الاحترام",
    "theme.respect.desc": "معاملة الآخرين بلطف واحترام",
    "theme.perseverance.title": "المثابرة",
    "theme.perseverance.desc": "عدم الاستسلام أبداً حتى عندما تصبح الأمور صعبة",
    "theme.goodChoices.title": "اتخاذ قرارات جيدة",
    "theme.goodChoices.desc": "تعلم اتخاذ قرارات حكيمة",
    "theme.jungleAdventure.title": "مغامرة في الغابة",
    "theme.jungleAdventure.desc": "رحلة استكشافية مثيرة عبر غابة برية",
    "theme.desertJourney.title": "رحلة في الصحراء",
    "theme.desertJourney.desc": "رحلة مثيرة عبر صحراء شاسعة",
    "theme.oceanQuest.title": "مهمة المحيط",
    "theme.oceanQuest.desc": "الغوص في مغامرة تحت الماء",
    "theme.spaceExploration.title": "استكشاف الفضاء",
    "theme.spaceExploration.desc": "الانطلاق في مغامرة فضائية مذهلة",
    "theme.mountainExpedition.title": "رحلة جبلية",
    "theme.mountainExpedition.desc": "تسلق قمة جبل رائع",
    "theme.magicalWorlds.title": "عوالم سحرية",
    "theme.magicalWorlds.desc": "استكشاف أراضٍ مسحورة مليئة بالعجائب",
    "theme.talkingAnimals.title": "حيوانات تتكلم",
    "theme.talkingAnimals.desc": "مغامرات مع حيوانات يمكنها التحدث",
    "theme.wizardsSpells.title": "سحرة وتعاويذ",
    "theme.wizardsSpells.desc": "تعلم السحر من السحرة الحكماء",
    "theme.mythicalCreatures.title": "مخلوقات أسطورية",
    "theme.mythicalCreatures.desc": "مقابلة التنانين وحيدات القرن وكائنات سحرية أخرى",
    "theme.timeTravel.title": "السفر عبر الزمن",
    "theme.timeTravel.desc": "السفر عبر الزمن إلى عصور مذهلة",
    "theme.sillyAnimals.title": "حيوانات مضحكة",
    "theme.sillyAnimals.desc": "مغامرات مضحكة مع حيوانات طريفة",
    "theme.wordplayJokes.title": "تلاعب بالكلمات ونكت",
    "theme.wordplayJokes.desc": "قصة مليئة بالنكت والضحك",
    "theme.absurdSituations.title": "مواقف سخيفة",
    "theme.absurdSituations.desc": "مغامرات مجنونة وسخيفة بشكل رائع",
    "theme.mischievous.title": "مغامرات مشاغبة",
    "theme.mischievous.desc": "مقالب مرحة ومرح مشاغب",
    "theme.unexpectedSurprises.title": "مفاجآت غير متوقعة",
    "theme.unexpectedSurprises.desc": "قصة مليئة بالتحولات والمفاجآت الممتعة",
    "theme.numbersCounting.title": "الأرقام والعد",
    "theme.numbersCounting.desc": "مغامرة ممتعة مع الأرقام والعد",
    "theme.lettersReading.title": "الحروف والقراءة",
    "theme.lettersReading.desc": "اكتشاف سحر الحروف والكلمات",
    "theme.scienceExperiments.title": "تجارب علمية",
    "theme.scienceExperiments.desc": "اكتشافات مثيرة من خلال العلم",
    "theme.history.title": "التاريخ",
    "theme.history.desc": "السفر إلى الماضي لتعلم التاريخ",
    "theme.problemSolvingSkills.title": "حل المشكلات",
    "theme.problemSolvingSkills.desc": "استخدام الإبداع والمنطق لحل المشكلات",
    "theme.differentCultures.title": "ثقافات مختلفة",
    "theme.differentCultures.desc": "استكشاف والاحتفاء بالثقافات حول العالم",
    "theme.differentAbilities.title": "قدرات مختلفة",
    "theme.differentAbilities.desc": "الاحتفاء بما يجعل كل شخص فريداً",
    "theme.familyDiversity.title": "تنوع الأسرة",
    "theme.familyDiversity.desc": "جميع أنواع العائلات والحب الذي يتشاركونه",
    "theme.genderExpression.title": "التعبير عن الهوية",
    "theme.genderExpression.desc": "كن حراً في التعبير عن هويتك الحقيقية",
    "theme.acceptanceBelonging.title": "القبول والانتماء",
    "theme.acceptanceBelonging.desc": "إيجاد مكانك والحصول على القبول",
    "theme.forestsTrees.title": "الغابات والأشجار",
    "theme.forestsTrees.desc": "مغامرات في غابات سحرية وأشجار قديمة",
    "theme.oceansSealife.title": "المحيطات والحياة البحرية",
    "theme.oceansSealife.desc": "اكتشاف العجائب تحت الأمواج",
    "theme.animalsHabitats.title": "الحيوانات وموائلها",
    "theme.animalsHabitats.desc": "التعرف على الحيوانات وأماكن عيشها",
    "theme.weatherSeasons.title": "الطقس والفصول",
    "theme.weatherSeasons.desc": "استكشاف سحر الفصول المتغيرة",
    "theme.protectingPlanet.title": "حماية الكوكب",
    "theme.protectingPlanet.desc": "الاعتناء بكوكبنا الجميل",
    "theme.mysteriesClues.title": "ألغاز وأدلة",
    "theme.mysteriesClues.desc": "حل ألغاز مثيرة بأدلة ذكية",
    "theme.puzzlesRiddles.title": "أحجيات وألغاز",
    "theme.puzzlesRiddles.desc": "أحجيات محيرة وألغاز صعبة",
    "theme.causeEffect.title": "السبب والنتيجة",
    "theme.causeEffect.desc": "فهم كيف تؤدي الأفعال إلى نتائج",
    "theme.teamProblemSolving.title": "حل المشكلات كفريق",
    "theme.teamProblemSolving.desc": "العمل معاً لحل تحديات كبيرة",
    "theme.creativeSolutions.title": "حلول إبداعية",
    "theme.creativeSolutions.desc": "التفكير خارج الصندوق لإيجاد إجابات",
    "theme.startingSchool.title": "بدء المدرسة",
    "theme.startingSchool.desc": "مغامرة مثيرة لبدء المدرسة",
    "theme.movingHome.title": "الانتقال",
    "theme.movingHome.desc": "التأقلم مع منزل وحي جديد",
    "theme.losingPet.title": "فقدان حيوان أليف",
    "theme.losingPet.desc": "التعامل مع فقدان حيوان أليف محبوب",
    "theme.newSibling.title": "أخ أو أخت جديد",
    "theme.newSibling.desc": "الترحيب بأخ أو أخت رضيع جديد",
    "theme.dealingChange.title": "التعامل مع التغيير",
    "theme.dealingChange.desc": "تعلم قبول التغييرات في الحياة والتكيف معها",
    "theme.fairyTales.title": "حكايات خرافية",
    "theme.fairyTales.desc": "مغامرات حكايات خرافية كلاسيكية معاد تصورها",
    "theme.folktales.title": "حكايات شعبية",
    "theme.folktales.desc": "قصص تقليدية تناقلتها الأجيال",
    "theme.mythsLegends.title": "أساطير وحكايات",
    "theme.mythsLegends.desc": "حكايات ملحمية من الأساطير",
    "theme.fablesMorals.title": "خرافات وعبر",
    "theme.fablesMorals.desc": "قصص تحمل دروساً قيمة في الحياة",
    "theme.holidayTraditions.title": "تقاليد الأعياد",
    "theme.holidayTraditions.desc": "الاحتفال بالأعياد والتقاليد الخاصة",
  },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  showLanguageSelector: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>("en");
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);

  useEffect(() => {
    const savedLang = localStorage.getItem("preferred-language") as Language | null;
    const supportedLanguages: Language[] = ["en", "fr", "es", "de", "it", "zh", "ja", "ar"];
    
    if (savedLang && supportedLanguages.includes(savedLang)) {
      setLanguageState(savedLang);
    } else {
      const browserLang = navigator.language.split("-")[0] as Language;
      if (supportedLanguages.includes(browserLang)) {
        setLanguageState(browserLang);
      } else {
        setShowLanguageSelector(true);
      }
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
    
    // Update SEO Meta Tags
    const title = translations[language]["seo.title"];
    const description = translations[language]["seo.description"];
    
    if (title) {
      document.title = title;
    }
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && description) {
      metaDescription.setAttribute("content", description);
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle && title) {
      ogTitle.setAttribute("content", title);
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription && description) {
      ogDescription.setAttribute("content", description);
    }
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("preferred-language", lang);
    setShowLanguageSelector(false);
  };

  const t = (key: string): string => {
    return translations[language][key] || translations["en"][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, showLanguageSelector }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
